import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, FlatList, TextInput, Platform, Image, Alert, Animated, ActivityIndicator, Linking } from 'react-native';
import { venueListStyles } from '../styles/venueListStyles';
import { BREAKPOINTS } from '../styles/breakpoints';
import { useUser } from '../components/LoginPersistence';
import { getFirestore, collection, getDocs, query, where, doc, getDoc, setDoc, updateDoc, arrayUnion, addDoc, deleteDoc, onSnapshot, orderBy, limit, serverTimestamp, deleteField, writeBatch } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  queryPoisFromFirestore,
  fetchOverpassPoiElements,
  groupedFeaturesFromPoiRecords,
  groupedFeaturesFromOverpassElements,
  mergeGroupedPoiFeatures,
  persistPoisToFirestore,
  searchOverpassPoisAlongRoute,
  fetchOverpassPoiElementsAlongRoute,
} from '../utils/poiDataService';
import mapboxgl from 'mapbox-gl';
import { filterContactsNearStops, SAVED_CONTACT_RADIUS_MILES, stopsFromTour } from '../utils/savedContacts';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_ACCESS_TOKEN } from '../config/mapbox-config';
import {
  getDirections,
  getFlightLegGeometry,
  getTrainLegGeometry,
} from '../utils/tourPlannerRouting';
import _ from 'lodash';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import * as turf from '@turf/turf';
import { Toast } from '../components/Toast';
import FullscreenSafeModal, { MAP_STAGE_FS_ATTR } from '../components/FullscreenSafeModal';
import MapAccessPrompt, { MapContributorGateContext } from '../components/tour-planner/MapAccessPrompt';
import { MAP_ACTION, getMapActionGate, isRealUser } from '../utils/mapAccess';
import { parseResourceTags } from '../utils/resourceTags';
import { VENUE_DERIVED_RESOURCE_CATEGORIES, venueDerivedResourceCategories } from '../utils/venueDerivedResources';
import PostGridDisplay from '../components/PostGridDisplay';
import { Clipboard } from 'react-native';
// Import map embed utilities
import { extractCoordinatesFromMapEmbed, extractPlaceNameFromMapEmbed, resolveEmbedCoordinatesForSave } from '../utils/mapEmbedUtils';
import { searchVenuesByQuery, getVenueSearchDetailParts, sortSearchResultsByMapVicinity } from '../utils/searchUtils';
import { HighlightedSearchText, HighlightedSearchTextParts } from '../components/HighlightedSearchText';
// Import theme
import { useTheme } from '../contexts/ThemeContext';
import { useFeatureFlags, isTourPlannerFeatureEnabledForDeploy, useUserLocationsFilterVisible, useMasterTourCalendarUi } from '../contexts/FeatureFlagsContext';
import { getDeployTarget } from '../config/environment';
import { BASE_LOCATION_CATEGORIES } from '../constants/locationCategories';
import { getParentIdForSavedTourFolder } from '../utils/tourPlannerLibrarySync';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import WeatherPanel from '../components/tour-planner/WeatherPanel';
import MapSideRail, { MAP_SIDE_RAIL_WIDTH, MAP_SIDE_RAIL_MOBILE_WIDTH } from '../components/tour-planner/MapSideRail';
import RightMapToursRail, { RIGHT_RAIL_CONTENT_TOP, RIGHT_RAIL_PANEL_WIDTH } from '../components/tour-planner/RightMapToursRail';
import { LAYER_COLOR } from '../styles/tourTheme';
import MapDisplayModeRail from '../components/tour-planner/MapDisplayModeRail';
// REQ-104: filter drawer, Video quick control, accessibility section (logic lives in these modules).
import MapQuickControls from '../components/map-filter/MapQuickControls';
import VenueFilterDrawer from '../components/map-filter/VenueFilterDrawer';
import VenueAccessibilitySection from '../components/venue/VenueAccessibilitySection';
import { useVenueFilters, useVenueFilterDraft } from '../hooks/useVenueFilters';
import { useApprovedAccessibilityNotes } from '../hooks/useApprovedAccessibilityNotes';
import { applyVenueFilters, countActiveFilters, clearVenueFilters, toggleFilterValue, VENUE_FILTER_GROUPS } from '../utils/venueFilters';
import { MAP_LAYERS, RADIO_LAYER_URL, visibleMapLayers, activeMapLayerIds } from '../constants/mapLayers';
import { syncRadioLayer, loadRadioStations, buildRadioPopupHtml } from '../components/tour-planner/layers/radioLayer';
import { resolveVenueAccessibility } from '../constants/venueAccessibility';
import { hasValidMapCoords } from '../utils/mapCoords';
import {
  DEFAULT_FARM_RADIUS_MILES,
  FARM_CORRIDOR_SOURCE,
  FARM_CORRIDOR_FILL_LAYER,
  FARM_CORRIDOR_LINE_LAYER,
  clampFarmRadiusMiles,
  collectRouteLineCoordinates,
  collectTourRouteGeometries,
  listTourRouteLegs,
  farmRouteDaySummaries,
  resolveFarmSearchDayId,
  approvedLocationToFoodHub,
  mergeFoodHubs,
  filterHubsAlongRoute,
  hubMatchesFoodFilters,
  isFoodDirectoryItem,
  normalizeFoodDirectoryItem,
  foodStopVenueFromHub,
  riderFoodSnippetHtml,
  upsertRiderFoodSnippet,
  foodStopRecordFromHub,
  searchFoodHubsAlongRoute,
  haversineMiles,
  isPointWithinRouteRadius,
  readHubCoords,
  milesFromRoute,
  sampleRouteCoordinates,
} from '../utils/farmRouteSearch';
import { v2TabEntryId } from '../utils/calendarV2Persist';
import { getTabEntryDraftText, buildTabEntryDraftFields } from '../utils/calendarTabDraftService';
import { fetchRadarTimestamp, buildRadarTileUrl, fetchWeather, RADAR_MAX_ZOOM } from '../utils/weatherService';
import {
  ensureBikeLaneLayers,
  setBikeLaneVisibility,
  setBikeLaneData,
  fetchBikeLaneGeoJSON,
} from '../utils/bikeLaneService';
import { RESOURCE_CATEGORIES, getResourceCategoryLabel } from '../constants/resourceCategories';
import {
  MERCH_RESOURCE_CATEGORY,
  subscribeResourceCategoryCatalog,
} from '../utils/resourceCategoryCatalog';
import { ACCESSIBILITY_LINKS, openAccessibilityLink } from '../constants/accessibilityLinks';
import { venueHasVeganFood, isVeganTagSearchQuery } from '../utils/venueFeatureUtils';
import { hasMapAccess } from '../utils/subscriptionEntitlements';
import { usePopup } from '../contexts/PopupContext';
import { useAppLayout } from '../contexts/AppLayoutContext';
import {
  DndContext,
  useDraggable,
  useDroppable,
  DragOverlay,
  PointerSensor,
  useSensors,
  useSensor,
  pointerWithin,
  closestCenter,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { TourStopSocialFooter } from '../components/calendar/TourStopSocialFooter';
import { useTourPartyMembers } from '../hooks/useTourPartyMembers';
import { getStopParticipants } from '../utils/tourDayLodgingUtils';
import {
  stopKeyForVenue,
  isDayLodgingVenue,
  LODGING_ACCENT_COLOR,
  lodgingCardStyle,
} from '../utils/tourLodgingUi';
import { updateTourDayFields } from '../utils/tourCalendarSyncService';
import {
  AFFILIATE_BUCKETS,
  classifyAffiliateBucket,
  ensureRegionGeo,
  getStatesFeatureCollection,
  getCountiesFeatureCollection,
  getCbsaFeatureCollection,
  aggregateRegions,
  aggregateAgainstEntries,
  fetchZctaForBounds,
} from '../utils/affiliateRegionMap';
import {
  collectResourceCoverage,
  fetchCityPlacePolygons,
  findStateFeature,
  hullForCityVenues,
  resourceAreaStyle,
  buildVenueResourceIndex,
  venueHasCategoryInResourceIndex,
} from '../utils/resourceAreaMap';
import {
  collectVenueCountries,
  ensureCountryRegions,
  ensureWorldCountriesGeo,
  getWorldCountriesGeo,
} from '../utils/internationalRegionMap';

// Shift a hex color toward white (percent > 0 = lighter) or black (< 0 = darker).
function shadeHexColor(hex, percent) {
  const h = String(hex || '').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  let r = parseInt(full.slice(0, 2), 16) || 0;
  let g = parseInt(full.slice(2, 4), 16) || 0;
  let b = parseInt(full.slice(4, 6), 16) || 0;
  const target = percent < 0 ? 0 : 255;
  const p = Math.min(1, Math.abs(percent));
  r = Math.round((target - r) * p) + r;
  g = Math.round((target - g) * p) + g;
  b = Math.round((target - b) * p) + b;
  const to2 = (n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}

// Per-mode shade overrides for the concentration affiliate *toggle buttons*
// only — the map keeps each bucket's true color. Some hues are too pale on the
// light panel (darken them) and Coffee Shop is too dark on the dark panel
// (lighten it).
const BUCKET_UI_SHADE = {
  light: { eventSpace: -0.4, city: -0.4, other: -0.45, bar: -0.38 },
  dark: { coffeeShop: 0.55 },
};
function bucketUiColor(bucket, isDarkMode) {
  const pct = (isDarkMode ? BUCKET_UI_SHADE.dark : BUCKET_UI_SHADE.light)[bucket.id];
  return pct ? shadeHexColor(bucket.color, pct) : bucket.color;
}

// Individual venue pins always use their affiliate bucket color (set per-feature
// as `affiliateColor` in drawClusters). Cluster groups keep the theme blues.
const VENUE_PIN_AFFILIATE_COLOR_EXPR = ['coalesce', ['get', 'affiliateColor'], '#9b59b6'];

// Food hub map pins. Colours come from tourTheme LAYER_COLOR so the panel row,
// the tab and the pin all read from one place. Glyphs are used instead of an
// icon font because these are raw DOM markers, not React components.
const FOOD_HUB_PIN = {
  // Arrow glyphs rather than emoji: they carry the pickup/drop-off direction,
  // render identically across platforms, and inherit the pin's text colour.
  pickup:  { label: 'Farms (pickup)',        color: LAYER_COLOR.farm,     glyph: '\u2191' },
  dropoff: { label: 'Food Banks (drop off)', color: LAYER_COLOR.foodBank, glyph: '\u2193' },
};

// Set Mapbox access token
mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

function injectTourPlannerInputStyles(isDarkMode, theme) {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;
  const styleId = 'vrc-tour-planner-inputs';
  let el = document.getElementById(styleId);
  if (!el) {
    el = document.createElement('style');
    el.id = styleId;
    document.head.appendChild(el);
  }
  const searchPh = isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.55)';
  const searchColor = isDarkMode ? '#ffffff' : theme.text.primary;
  const namePh = isDarkMode ? 'rgba(213,183,145,0.7)' : 'rgba(230,126,34,0.65)';
  const nameColor = isDarkMode ? '#d5b791' : theme.brand.primary;
  el.textContent = `
    #tour-planner-search::placeholder, #tour-planner-search-mobile::placeholder { color: ${searchPh} !important; opacity: 1 !important; }
    #tour-planner-search::-webkit-input-placeholder, #tour-planner-search-mobile::-webkit-input-placeholder { color: ${searchPh} !important; opacity: 1 !important; }
    #tour-planner-search, #tour-planner-search-mobile { color: ${searchColor} !important; }
    #tour-planner-name::placeholder, #tour-planner-name-mobile::placeholder, #tour-planner-sidebar-name::placeholder { color: ${namePh} !important; opacity: 1 !important; }
    #tour-planner-name::-webkit-input-placeholder, #tour-planner-name-mobile::-webkit-input-placeholder, #tour-planner-sidebar-name::-webkit-input-placeholder { color: ${namePh} !important; opacity: 1 !important; }
    #tour-planner-name, #tour-planner-name-mobile, #tour-planner-sidebar-name { color: ${nameColor} !important; }
  `;
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Display stored YYYY-MM-DD as short "Mar 17" (month abbr + day). */
function formatScheduledDateShort(raw) {
  const t = raw != null ? String(raw).trim() : '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return t || '';
  const [y, m, d] = t.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return t;
  return dt.toLocaleString(undefined, { month: 'short', day: 'numeric' });
}

// Debug: show cluster coverage radius on hover (development only)
const CLUSTER_HOVER_RADIUS_DEBUG = typeof __DEV__ !== 'undefined' ? __DEV__ : (process.env.NODE_ENV === 'development');

// Stable wrapper so DndContext does not cause map container to remount (inline component would get new identity each render).
function TourPlannerDndWrapper({ sensors, onDragStart, onDragEnd, onDragOver, onDragCancel, renderOverlay, children }) {
  if (Platform.OS !== 'web') return <>{children}</>;
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragCancel={onDragCancel}
    >
      <DragOverlay zIndex={10000} dropAnimation={null}>
        <View style={{ pointerEvents: 'none' }} collapsable={false}>
          {renderOverlay ? renderOverlay() : null}
        </View>
      </DragOverlay>
      {children}
    </DndContext>
  );
}

// Add SVG icon components at the top of the file
const MapIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6z" />
    <path d="M9 3v15M15 6v15" />
  </svg>
);

const SatelliteIcon = ({ color = 'currentColor' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 10l8-4 8 4" />
    <path d="M12 6v14M8 8v10M16 8v10" />
    <circle cx="12" cy="20" r="2" />
  </svg>
);

const DarkModeIcon = ({ color = 'currentColor' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z" />
  </svg>
);

const ClusterIcon = ({ color = 'currentColor' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <circle cx="6" cy="6" r="2" />
    <circle cx="18" cy="6" r="2" />
    <circle cx="6" cy="18" r="2" />
    <circle cx="18" cy="18" r="2" />
  </svg>
);

const VenueIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
    <circle cx="12" cy="9" r="2.5" />
  </svg>
);

const TourIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <path d="M12 2l-5.5 9h11L12 2z" />
    <path d="M17.5 11L12 20l-5.5-9" />
  </svg>
);

const HotelIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <path d="M3 21h18v-2H3v2zm2-4h14v2H5v-2zm-2-3h18v2H3v-2zm2-4h14v2H5V10zm0-5v2h14V5H5z" />
    <path d="M10 3h4v7h-4z" />
  </svg>
);

const GasIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <path d="M19 11h2v7c0 1.1-.9 2-2 2h-2v-7h2v-2z" />
    <path d="M7 3h10v15c0 1.1-.9 2-2 2H7c-1.1 0-2-.9-2-2V5c0-1.1.9-2 2-2z" />
    <path d="M7 8h10M7 12h10M7 16h10" />
  </svg>
);

const ThreeDIcon = ({ color = 'currentColor' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l9 4.5-9 4.5-9-4.5L12 3z" />
    <path d="M21 7.5v9L12 21l-9-4.5v-9" />
    <path d="M12 12v9" />
  </svg>
);

// Add new icon components
const WeatherIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.76 4.84L5.35 3.43C4.96 3.04 4.33 3.04 3.94 3.43C3.55 3.83 3.55 4.46 3.94 4.85L5.35 6.26C5.74 6.65 6.37 6.65 6.76 6.26C7.15 5.87 7.15 5.23 6.76 4.84ZM4 12.5C4 13.05 4.45 13.5 5 13.5H7C7.55 13.5 8 13.05 8 12.5C8 11.95 7.55 11.5 7 11.5H5C4.45 11.5 4 11.95 4 12.5ZM13 4.5V2.5C13 1.95 12.55 1.5 12 1.5C11.45 1.5 11 1.95 11 2.5V4.5C11 5.05 11.45 5.5 12 5.5C12.55 5.5 13 5.05 13 4.5ZM20.06 3.43C19.67 3.04 19.04 3.04 18.65 3.43L17.24 4.84C16.85 5.23 16.85 5.86 17.24 6.25C17.63 6.64 18.26 6.64 18.65 6.25L20.06 4.84C20.45 4.46 20.45 3.83 20.06 3.43ZM19 12.5C19 13.05 19.45 13.5 20 13.5H22C22.55 13.5 23 13.05 23 12.5C23 11.95 22.55 11.5 22 11.5H20C19.45 11.5 19 11.95 19 12.5ZM16 12.5C16 10.29 14.21 8.5 12 8.5C9.79 8.5 8 10.29 8 12.5C8 14.71 9.79 16.5 12 16.5C14.21 16.5 16 14.71 16 12.5ZM11 20.5V22.5C11 23.05 11.45 23.5 12 23.5C12.55 23.5 13 23.05 13 22.5V20.5C13 19.95 12.55 19.5 12 19.5C11.45 19.5 11 19.95 11 20.5ZM5.35 18.74L6.76 20.15C7.15 20.54 7.78 20.54 8.17 20.15C8.56 19.76 8.56 19.13 8.17 18.74L6.76 17.33C6.37 16.94 5.74 16.94 5.35 17.33C4.96 17.72 4.96 18.35 5.35 18.74ZM17.24 20.15L18.65 18.74C19.04 18.35 19.04 17.72 18.65 17.33C18.26 16.94 17.63 16.94 17.24 17.33L15.83 18.74C15.44 19.13 15.44 19.76 15.83 20.15C16.22 20.54 16.85 20.54 17.24 20.15Z" fill="currentColor"/>
  </svg>
);

/** Visible strip below the map on mobile — keeps FABs above the viewport edge. */
const MOBILE_MAP_BOTTOM_GAP = 32;
const MOBILE_FAB_ROW_HEIGHT = 44;
const MOBILE_FAB_BOTTOM_OFFSET = 8;
const MOBILE_WEATHER_ABOVE_FAB_GAP = 8;
const MOBILE_MAP_ABOVE_CHROME_GAP = 8;
const MOBILE_DAY_PLANNER_SHEET_RATIO = 0.7;
const MOBILE_WEATHER_COLLAPSED_HEIGHT = 52;
const MOBILE_WEATHER_EXPANDED_ABOVE_PLANNER = 300;
const MOBILE_WEATHER_EXPANDED_STANDALONE = 330;

// Function to generate themed styles
const getThemedStyles = (theme, isDarkMode) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.background.primary,
    paddingHorizontal: 24,
    paddingBottom: 24,
    paddingTop: 12,
    width: '100%',
    maxWidth: '100%',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  containerMobileFullScreen: {
    paddingHorizontal: 0,
    paddingBottom: 0,
    paddingTop: 0,
    minHeight: undefined,
    flex: 1,
    height: '100%',
    maxHeight: '100%',
    overflow: 'visible',
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
    paddingHorizontal: 24,
    paddingVertical: 5,
    // backgroundColor: theme.background.card,
    // borderRadius: 16,
    // boxShadow: `0 2px 12px ${theme.ui.shadow}`,
    // borderWidth: 1,
    // borderColor: theme.ui.border,
  },
  title: {
    fontSize: 18,
    fontWeight: '400',
    fontFamily: 'EduSAHand',
    color: isDarkMode ? '#507fa6' : theme.text.primary,
    letterSpacing: 2,
  },
  tourNameContainerRow: {
    flexDirection: 'column',
    // flex: 1,
    gap: 30,
    width: 220,
    alignItems: 'center',
  },
  tourNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  tourNameInput: {
    fontSize: 14,
    fontFamily: 'EduSAHand',
    textAlign: 'center',

    lineHeight: 20,
    width: 200,
    letterSpacing: 3.4,
    borderBottomWidth: 2,
    borderTopWidth: 0,
    borderLeftWidth: 0,
    borderRightWidth: 0,
  },
  routeSummary: {
    flexGrow: 0,
    flexShrink: 0,
    maxHeight: 320,
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.08)',
  },
  routeSummaryContent: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 24,
  },
  routeSummaryTitle: {
    textAlign: 'center',
    color: '#000000',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.8,
  },
  routeSummaryEmpty: {
    textAlign: 'center',
    color: '#787a99',
    fontSize: 12,
  },
  routeSummaryTotals: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 32,
  },
  routeSummaryTotal: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  routeSummaryTotalValue: {
    color: '#000000',
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 4,
  },
  routeSummaryMuted: {
    color: '#787a99',
    fontSize: 11,
  },
  routeSummaryBadge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  routeSummaryBadgeText: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: '600',
  },
  routeSummaryStopName: {
    color: '#000000',
    fontSize: 14,
    fontWeight: '600',
  },
  routeSummaryStopAddress: {
    color: '#787a99',
    fontSize: 11,
    marginTop: 3,
  },
  // "Your route" stops table: fixed columns so date/time/travel sit right next to the stop name.
  routeTable: {
    minWidth: 760,
  },
  routeTableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.06)',
  },
  routeTableHeaderRow: {
    paddingVertical: 6,
    borderBottomColor: 'rgba(0,0,0,0.14)',
  },
  routeTableHeader: {
    color: '#787a99',
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.8,
  },
  routeTableType: {
    color: '#787a99',
    fontSize: 10,
    letterSpacing: 0.4,
  },
  routeTableCell: {
    color: '#000000',
    fontSize: 13,
  },
  routeTableCellMuted: {
    color: '#787a99',
    fontSize: 13,
  },
  routeTableTravel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  routeColNum: { width: 26, alignItems: 'center' },
  routeColStop: { width: 230 },
  routeColType: { width: 92 },
  routeColDate: { width: 92 },
  routeColTime: { width: 64 },
  routeColTravel: { width: 110 },
  routeColDistance: { width: 70 },
  routeColGas: { width: 50 },
  routeLayerBar: {
    flexGrow: 0,
    flexShrink: 0,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.08)',
  },
  routeLayerBarContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  routeLayerBarLabel: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.8,
    color: '#1d1d1d',
    marginRight: 4,
  },
  routeLayerBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.18)',
    backgroundColor: '#ffffff',
  },
  routeLayerBtnText: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 0.5,
    color: '#000000',
  },
  routeLayerBtnTextOn: {
    color: '#ffffff',
  },
  contentContainer: {
    flex: 1,
    flexDirection: 'row',
    position: 'relative',
    minHeight: 0,
    height: 'calc(100dvh - 130px)',
    // gap: 12,
    overflow: 'hidden',
    borderRadius: 16,
    [BREAKPOINTS.phone]: {
      flexDirection: 'column',
      height: 'auto',
    },
  },
  contentContainerMobileFullScreen: {
    flex: 1,
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    gap: 0,
    borderRadius: 0,
    position: 'relative',
    backgroundColor: theme.background.primary,
  },
  hiddenPanelMobile: {
    position: 'absolute',
    width: 0,
    height: 0,
    overflow: 'hidden',
    opacity: 0,
    pointerEvents: 'none',
  },
  // Toggle buttons
  // Sidebar chevron toggle buttons - positioned absolutely in contentContainer
  sidebarToggleBtn: {
    position: 'absolute',
    top: 20,
    width: 32,
    height: 48,
    backgroundColor: isDarkMode ? 'rgba(6,9,22,0.72)' : 'rgba(255,255,255,0.88)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001,
    cursor: 'pointer',
    boxShadow: isDarkMode ? '0 2px 16px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.12)',
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)',
    transition: 'right 0.3s ease, left 0.3s ease',
    overflow: 'hidden',
  },
  leftSidebarToggle: {
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
    borderLeftWidth: 0,
  },
  rightSidebarToggle: {
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
    borderRightWidth: 0,
  },
  sidebarToggleBtnText: {
    color: isDarkMode ? 'rgba(255,255,255,0.7)' : theme.text.secondary,
    fontSize: 18,
    fontWeight: '500',
  },
  // Vertical Day Planner side-tab
  dayPlannerTab: {
    position: 'absolute',
    top: '50%',
    marginTop: -64,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    paddingTop: 10,
    paddingBottom: 10,
    backgroundColor: isDarkMode ? 'rgba(6,9,22,0.82)' : 'rgba(255,255,255,0.88)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.13)' : 'rgba(0,0,0,0.10)',
    borderTopLeftRadius: 10,
    borderBottomLeftRadius: 10,
    borderRightWidth: 0,
    boxShadow: isDarkMode ? '-4px 0 20px rgba(0,0,0,0.4)' : '-4px 0 20px rgba(0,0,0,0.10)',
    zIndex: 1001,
    cursor: 'pointer',
    transition: 'right 0.3s ease',
    overflow: 'visible',
  },
  dayPlannerTabOpen: {
    borderRadius: 10,
    borderRightWidth: 1,
  },
  dayPlannerTabArrow: {
    color: isDarkMode ? 'rgba(255,255,255,0.7)' : theme.text.secondary,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 20,
    textAlign: 'center',
  },
  dayPlannerTabLabel: {
    color: isDarkMode ? 'rgba(255,255,255,0.72)' : theme.text.secondary,
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    writingMode: 'vertical-rl',
    textOrientation: 'mixed',
    transform: [{ rotate: '180deg' }],
    marginTop: 6,
  },
  // Mobile floating pill button (used inside an absolute container row)
  dayPlannerFab: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 11,
    paddingHorizontal: 20,
    backgroundColor: isDarkMode ? 'rgba(6,9,22,0.85)' : 'rgba(255,255,255,0.92)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
    boxShadow: isDarkMode ? '0 4px 24px rgba(0,0,0,0.5)' : '0 4px 24px rgba(0,0,0,0.15)',
    cursor: 'pointer',
  },
  dayPlannerFabText: {
    color: isDarkMode ? 'rgba(255,255,255,0.9)' : theme.text.primary,
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  // Venue list panel
  venueList: {
    backgroundColor: theme.background.card,
    borderRadius: 16,
    overflow: 'hidden',
    height: 'calc(100vh - 200px)',
    maxHeight: 'calc(100vh - 200px)',
    flexShrink: 0,
    position: 'relative',
    transition: 'width 0.3s ease, padding 0.3s ease, opacity 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: `0 4px 20px ${theme.ui.shadow}`,
    [BREAKPOINTS.phone]: {
      maxHeight: 300,
      width: '100%',
      minWidth: 'auto',
    },
  },
  venueListScroll: {
    flex: 1,
    marginTop: 12,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    height: '100%',
  },
  sectionHeader: {
    marginBottom: 10,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: isDarkMode ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.08)',
    flexShrink: 0,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: isDarkMode ? 'rgba(255,255,255,0.92)' : theme.text.primary,
    marginBottom: 2,
    letterSpacing: 0.3,
  },
  venueCountText: {
    color: isDarkMode ? 'rgba(255,255,255,0.42)' : theme.text.secondary,
    fontSize: 12,
    marginBottom: 6,
    fontWeight: '500',
  },
  leftPanelSectionLabel: {
    color: theme.text.secondary,
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 6,
    marginTop: 14,
  },
  leftPanelToolbar: {
    marginTop: 4,
    marginBottom: 8,
  },
  searchInputCompact: {
    backgroundColor: theme.input.background,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 8,
    color: theme.text.primary,
    fontSize: 13,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: theme.input.border,
  },
  leftPanelToolbarRow: {
    flexDirection: 'row',
    gap: 4,
  },
  leftPanelToolbarButton: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  leftPanelToolbarButtonFilter: {
    backgroundColor: '#2563eb',
    borderWidth: 1,
    borderColor: '#1d4ed8',
  },
  leftPanelToolbarButtonFilterText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
  },
  leftPanelToolbarButtonAdd: {
    backgroundColor: theme.brand.primary,
    borderWidth: 1,
    borderColor: theme.brand.primary,
  },
  leftPanelToolbarButtonAddText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
  },
  leftPanelToolbarButtonAddActive: {
    backgroundColor: '#dc2626',
    borderColor: '#b91c1c',
  },
  leftPanelToolbarButtonTextActive: {
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
  },
  addLocationButtonStandalone: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: theme.background.tertiary,
    borderWidth: 1,
    borderColor: theme.ui.border,
    gap: 6,
    marginTop: 6,
  },
  addLocationButtonStandaloneActive: {
    backgroundColor: '#27ae60',
    borderColor: '#27ae60',
  },
  routesCalculatingBar: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginBottom: 12,
    backgroundColor: 'rgba(230,126,34,0.18)',
    borderWidth: 1,
    borderColor: 'rgba(230,126,34,0.35)',
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  routesCalculatingText: {
    color: '#e67e22',
    fontSize: 12,
    fontWeight: '600',
  },
  searchInput: {
    backgroundColor: theme.input.background,
    padding: 14,
    borderRadius: 10,
    color: theme.text.primary,
    fontSize: 14,
    marginTop: 8,
    borderWidth: 1,
    borderColor: theme.input.border,
    outlineColor: theme.brand.primary,
  },
  venueCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: theme.background.card,
    marginBottom: 12,
    borderRadius: 12,
    overflow: 'hidden',
    padding: 14,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: `0 1px 4px ${theme.ui.shadow}`,
    borderWidth: 1,
    borderColor: theme.ui.border,
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 12px ${theme.ui.shadowStrong}`,
      borderColor: theme.brand.primary,
    },
  },
  venueImage: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: theme.background.tertiary,
    marginRight: 14,
  },
  venueInfo: {
    flex: 1,
    marginLeft: 12,
  },
  venueName: {
    color: theme.text.primary,
    fontWeight: '600',
    fontSize: 15,
    marginBottom: 3,
    lineHeight: 20,
  },
  venueLocation: {
    color: theme.text.tertiary,
    fontSize: 13,
    marginBottom: 4,
  },
  venueImagePlaceholder: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: theme.background.tertiary,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  venueImageIcon: {
    fontSize: 20,
  },
  venueMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    marginTop: 2,
  },
  venueMetaText: {
    fontSize: 12,
    color: theme.text.muted,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  venueMetaDot: {
    fontSize: 12,
    color: theme.text.muted,
    marginHorizontal: 6,
  },
  venueCardSearchDimmed: {
    opacity: 0.42,
  },
  addButtonSmall: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: theme.brand.primary,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginLeft: 10,
  },
  zoomButtonSmall: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: theme.background.tertiary,
    borderWidth: 1,
    borderColor: theme.ui.border,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginLeft: 6,
  },
  zoomButtonSmallText: {
    fontSize: 14,
  },
  addButtonSmallText: {
    color: '#fff',
    fontSize: 22,
    fontWeight: '600',
    lineHeight: 24,
  },
  addButtonSmallDisabled: {
    opacity: 0.5,
  },
  addButton: {
    backgroundColor: theme.brand.primary,
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.2s ease',
  },
  addButtonText: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 18,
    lineHeight: 18,
  },
  tourVenuesList: {
    flex: 1,
    marginBottom: 12,
  },
  tourVenueCard: {
    zIndex: 10,
    flexDirection: 'column',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
    marginBottom: 6,
    marginHorizontal: 4,
    borderRadius: 10,
    overflow: 'visible',
    transition: 'all 0.2s ease',
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.09)',
    borderLeftWidth: 3,
    boxShadow: isDarkMode ? '0 2px 12px rgba(0,0,0,0.35)' : '0 2px 12px rgba(0,0,0,0.09)',
    '&:hover': {
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
    },
  },
  tourVenueHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  tourVenueHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
  },
  tourVenueOptionsButton: {
    padding: 6,
    marginLeft: 2,
    minWidth: 30,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
  },
  tourVenueOptionsButtonText: {
    fontSize: 18,
    fontWeight: '700',
    color: isDarkMode ? 'rgba(255,255,255,0.45)' : theme.text.secondary,
  },
  tourVenueZoomButton: {
    padding: 6,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tourVenueZoomButtonIcon: {
    fontSize: 15,
    color: isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)',
  },
  tourStopNumberBadge: {
    width: 26,
    height: 26,
    borderRadius: 13,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
    flexShrink: 0,
  },
  tourStopNumberBadgeText: {
    fontSize: 11,
    fontWeight: '800',
    lineHeight: 14,
  },
  tourVenueDropdownWrap: {
    position: 'relative',
  },
  tourStopTypeBadge: {
    marginBottom: 1,
  },
  tourStopTypeBadgeText: {
    color: isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)',
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.7,
    textTransform: 'uppercase',
  },
  tourStopLodgingPill: {
    color: LODGING_ACCENT_COLOR,
    fontSize: 9,
    fontWeight: '800',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  tourStopSocialWrap: {
    paddingHorizontal: 10,
    paddingBottom: 8,
  },
  tourVenueHeaderRowWithTypeBadge: {
    paddingTop: 10,
  },
  tourVenueDropdown: {
    position: 'absolute',
    right: 40,
    top: 0,
    zIndex: 1000,
    minWidth: 160,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: isDarkMode ? theme.ui.border : 'rgba(0,0,0,0.18)',
    backgroundColor: isDarkMode ? theme.background.elevated : 'rgba(255,255,255,0.98)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    overflow: 'hidden',
    boxShadow: isDarkMode ? `0 8px 28px ${theme.ui.shadowStrong}` : '0 8px 28px rgba(0,0,0,0.16)',
  },
  tourVenueDropdownItem: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: isDarkMode ? theme.ui.border : 'rgba(0,0,0,0.10)',
  },
  tourVenueDropdownItemDanger: {
    borderBottomWidth: 0,
  },
  tourVenueDropdownItemText: {
    fontSize: 13,
    color: theme.text.primary,
  },
  tourVenueDropdownItemTextDanger: {
    color: '#e74c3c',
  },
  tourVenueDropdownItemDisabled: {
    opacity: 0.5,
  },
  tourVenueDropdownItemTextDisabled: {
    color: theme.text.muted,
  },
  tourVenueActions: {
    flexDirection: 'column',
    gap: 8,
    marginTop: 8,
    width: '100%',
  },
  tourVenueButton: {
    padding: 8,
    borderRadius: 4,
    alignItems: 'center',
    width: '100%',
  },
  removeVenueButton: {
    backgroundColor: theme.button.danger,
    '&:hover': {
      backgroundColor: theme.button.dangerHover,
    },
  },
  viewVenueButton: {
    backgroundColor: theme.button.info,
    '&:hover': {
      backgroundColor: theme.button.infoHover,
    },
  },
  tourVenueButtonText: {
    color: theme.button.infoText,
    fontSize: 14,
    fontWeight: '600',
  },
  dragging: {
    opacity: 0.5,
    transform: [{ scale: 1.05 }],
  },
  tourVenueIndex: {
    color: '#fff',
    fontWeight: '700',
    backgroundColor: 'rgba(230,126,34,0.7)',
    width: 26,
    height: 26,
    borderRadius: 6,
    textAlign: 'center',
    lineHeight: 26,
    marginRight: 10,
    fontSize: 12,
  },
  tourVenueInfo: {
    flex: 1,
    paddingRight: 4,
  },
  tourVenueName: {
    color: isDarkMode ? 'rgba(255,255,255,0.92)' : theme.text.primary,
    fontWeight: '700',
    fontSize: 14,
    marginBottom: 2,
    lineHeight: 18,
  },
  tourVenueLocation: {
    color: isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
    fontSize: 11,
    lineHeight: 15,
  },
  tourVenueCardVenuesDb: {
    backgroundColor: isDarkMode ? 'rgba(74,144,217,0.18)' : 'rgba(74,144,217,0.12)',
    borderColor: 'rgba(74,144,217,0.3)',
    borderLeftColor: '#4a90d9',
  },
  tourVenueCardVenuesDbActive: {
    backgroundColor: `${theme.brand.primary}20`,
    borderLeftColor: `${theme.brand.primary}40`,
  },
  tourVenueCardVenuesDbSelected: {
    backgroundColor: isDarkMode ? 'rgba(74,144,217,0.28)' : 'rgba(74,144,217,0.18)',
    borderLeftColor: '#4a90d9',
    boxShadow: '0 4px 16px rgba(74, 144, 217, 0.35)',
  },
  tourVenueNameVenuesDb: {
    color: isDarkMode ? '#a8d4ff' : '#1a6abf',
  },
  tourVenueLocationVenuesDb: {
    color: isDarkMode ? 'rgba(168,212,255,0.7)' : 'rgba(26,106,191,0.65)',
  },
  tourVenueIndexVenuesDb: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: '#ffffff',
  },
  tourVenueIndexVenuesDbActive: {
    backgroundColor: 'rgba(255, 255, 255, 0.38)',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
  },
  tourVenueOptionsButtonTextVenuesDb: {
    color: isDarkMode ? 'rgba(168,212,255,0.7)' : 'rgba(26,106,191,0.65)',
  },
  removeButton: {
    padding: 8,
    borderRadius: 4,
    '&:hover': {
      backgroundColor: isDarkMode ? 'rgba(255, 107, 107, 0.2)' : 'rgba(255, 107, 107, 0.1)',
    },
  },
  removeButtonText: {
    color: theme.status.error,
    fontWeight: 'bold',
    fontSize: 16,
  },
  emptyText: {
    color: theme.text.muted,
    textAlign: 'center',
    marginTop: 20,
    fontStyle: 'italic',
  },
  routeInfo: {
    backgroundColor: isDarkMode ? theme.background.elevated : '#1a1a1a',
    padding: 16,
    borderRadius: 12,
    marginTop: 16,
  },
  routeInfoTitle: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 15,
    marginBottom: 12,
  },
  routeInfoText: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 13,
    marginBottom: 6,
  },
  saveButton: {
    backgroundColor: theme.brand.primary,
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
  },
  saveButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
  disabledButton: {
    opacity: 0.5,
  },
  mobileMapText: {
    color: theme.text.muted,
    textAlign: 'center',
    marginTop: 20,
    padding: 20,
  },
  message: {
    color: theme.text.muted,
    textAlign: 'center',
    marginTop: 10,
  },
  // Add new styles for venue clustering and display controls
  mapControlsOverlay: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 10,
  },
  displayControls: {
    backgroundColor: theme.background.elevated,
    padding: 15,
    borderRadius: 8,
    marginBottom: 15,
  },
  controlsTitle: {
    color: theme.text.primary,
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 15,
  },
  controlGroup: {
    marginBottom: 15,
  },
  controlLabel: {
    color: theme.text.secondary,
    fontSize: 14,
    marginBottom: 8,
  },
  buttonGroup: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 12,
    width: '100%',
  },
  displayButton: {
    backgroundColor: theme.background.tertiary,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 6,
    minWidth: 80,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 36,
  },
  displayButtonActive: {
    backgroundColor: theme.brand.primary,
  },
  displayButtonText: {
    color: theme.text.primary,
    fontSize: 12,
    fontWeight: '600',
  },
  displayButtonTextActive: {
    color: '#fff',
    fontWeight: '600',
  },
  locationButton: {
    padding: 10,
    borderRadius: 8,
    backgroundColor: theme.brand.secondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  locationButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  locationPrompt: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.background.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 20,
    borderRadius: 8,
  },
  locationPromptText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'center',
    paddingHorizontal: 30,
  },
  locationPromptButton: {
    padding: 10,
    borderRadius: 5,
    backgroundColor: theme.brand.secondary,
  },
  locationPromptButtonText: {
    color: '#fff',
    fontWeight: '600',
  },
  statsOverlay: {
    position: 'absolute',
    bottom: 16,
    left: 16,
    backgroundColor: isDarkMode ? 'rgba(26, 26, 36, 0.95)' : 'rgba(255, 255, 255, 0.95)',
    padding: 10,
    borderRadius: 8,
    zIndex: 5,
    boxShadow: `0 2px 8px ${theme.ui.shadow}`,
  },
  statsText: {
    color: theme.text.primary,
    fontSize: 12,
    marginBottom: 2,
    fontWeight: '500',
  },
  poiLoadingPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: isDarkMode ? 'rgba(26, 26, 36, 0.95)' : 'rgba(255, 255, 255, 0.96)',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 10,
    boxShadow: `0 2px 12px ${theme.ui.shadow}`,
    borderWidth: 1,
    borderColor: theme.ui.border,
  },
  poiLoadingText: {
    color: theme.text.primary,
    fontSize: 13,
    fontWeight: '600',
    marginLeft: 8,
  },
  proximityText: {
    fontSize: 11,
    marginTop: 2,
  },
  // Add new styles for days (alternating backgrounds to distinguish days)
  dayContainer: {
    borderRadius: 10,
    marginBottom: 10,
    zIndex: 10,
    overflow: 'visible',
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.10)',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
    transition: 'all 0.25s ease',
  },
  dayContainerAlternate: {
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
  },
  activeDayContainer: {
    borderColor: 'rgba(230,126,34,0.55)',
    boxShadow: '0 0 16px rgba(230,126,34,0.2)',
  },
  dayContainerDropTarget: {
    borderColor: 'rgba(44,123,229,0.8)',
    backgroundColor: 'rgba(44,123,229,0.12)',
  },
  insertLine: {
    height: 3,
    backgroundColor: 'none',
    marginVertical: 2,
    borderRadius: 2,
    width: '100%',
  },
  droppableSlot: {
    minHeight: 20,
    width: '100%',
    borderColor: 'transparent',
    borderWidth: 2,
    borderRadius: 2,
  },
  dayHeader: {
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
    padding: 12,
    gap: 5,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    borderBottomWidth: 1,
    borderBottomColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)',
  },
  activeDayHeader: {
    backgroundColor: 'rgba(230,126,34,0.15)',
    borderBottomColor: 'rgba(230,126,34,0.5)',
  },
  dayTitle: {
    color: isDarkMode ? 'rgba(255,255,255,0.88)' : theme.text.primary,
    fontWeight: '600',
    fontSize: 14,
    marginLeft: 8,
    letterSpacing: 0.3,
  },
  activeDayTitle: {
    color: isDarkMode ? 'rgba(255,255,255,0.98)' : theme.text.primary,
    fontWeight: '700',
  },
  dayTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  activeDayIndicator: {
    color: theme.brand.primary,
    marginRight: 8,
    fontSize: 10,
    textShadow: '0 0 8px rgba(230, 126, 34, 0.6)',
  },
  editDayButton: {
    marginLeft: 10,
    padding: 4,
    borderRadius: 4,
    opacity: 0.6,
    transition: 'opacity 0.2s ease',
  },
  editIcon: {
    color: theme.text.tertiary,
    fontSize: 14,
  },
  saveIcon: {
    color: theme.status.success,
    fontSize: 18,
    marginLeft: 8,
  },
  dayNameInput: {
    backgroundColor: theme.input.background,
    padding: 8,
    borderRadius: 6,
    color: theme.text.primary,
    width: '100%',
    flex: 1,
    borderWidth: 1,
    borderColor: 'rgba(230, 126, 34, 0.3)',
  },
  dayTitleRowMain: {
    flex: 1,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 0,
  },
  dayHeaderDateBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 8,
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.ui.border,
    backgroundColor: theme.background.secondary,
    gap: 6,
    flexShrink: 0,
  },
  dayHeaderDateText: {
    fontSize: 10,
    fontWeight: '700',
    color: theme.text.secondary,
    maxWidth: 96,
  },
  dayHeaderDateHint: {
    fontSize: 12,
    fontWeight: '600',
  },
  dayDatePickerOverlay: {
    flex: 1,
    backgroundColor: theme.background.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    ...(Platform.OS === 'web' ? { ...StyleSheet.absoluteFillObject, width: '100%', height: '100%' } : {}),
  },
  dayDatePickerCard: {
    width: '100%',
    maxWidth: 400,
    backgroundColor: theme.background.elevated,
    borderRadius: 14,
    padding: 18,
    borderWidth: 1,
    borderColor: theme.ui.border,
  },
  dayDatePickerTitle: {
    fontSize: 17,
    fontWeight: '800',
    color: theme.text.primary,
    marginBottom: 14,
  },
  dayDatePickerLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: theme.text.secondary,
    marginBottom: 6,
    marginTop: 4,
  },
  dayDatePickerYearInput: {
    borderWidth: 1,
    borderColor: theme.input.border,
    borderRadius: 8,
    padding: 10,
    fontSize: 16,
    color: theme.text.primary,
    backgroundColor: theme.input.background,
    marginBottom: 8,
  },
  dayDatePickerMonthScroll: { marginBottom: 10, maxHeight: 44 },
  dayDatePickerMonthChip: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginRight: 6,
    borderWidth: 1,
    borderColor: theme.ui.border,
    backgroundColor: theme.input.background,
  },
  dayDatePickerMonthChipActive: {
    borderColor: theme.brand.primary,
    backgroundColor: `${theme.brand.primary}20`,
  },
  dayDatePickerMonthChipText: { fontSize: 13, fontWeight: '600', color: theme.text.primary },
  dayDatePickerDayWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 14 },
  dayDatePickerDayChip: {
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: theme.ui.border,
    backgroundColor: theme.input.background,
  },
  dayDatePickerDayChipActive: {
    borderColor: theme.brand.primary,
    backgroundColor: isDarkMode ? 'rgba(230, 126, 34, 0.25)' : 'rgba(230, 126, 34, 0.15)',
  },
  dayDatePickerDayChipText: { fontSize: 14, fontWeight: '700', color: theme.text.primary },
  dayDatePickerActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 10,
    marginTop: 8,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: theme.ui.divider,
  },
  dayDatePickerBtnGhost: { paddingVertical: 10, paddingHorizontal: 12 },
  dayDatePickerBtnGhostText: { fontSize: 14, fontWeight: '700', color: theme.text.secondary },
  dayDatePickerBtnPrimary: {
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 8,
    backgroundColor: theme.brand.primary,
  },
  dayDatePickerBtnPrimaryText: { fontSize: 14, fontWeight: '800', color: '#fff' },
  dayEditContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    flex: 1,
  },
  removeDayButton: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.button.danger,
    borderRadius: 6,
  },
  removeDayButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  addDayButton: {
    zIndex: 1,
    backgroundColor: 'rgba(230,126,34,0.75)',
    borderWidth: 1,
    borderColor: 'rgba(230,126,34,0.5)',
    paddingVertical: 11,
    paddingHorizontal: 16,
    borderRadius: 10,
    marginTop: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 42,
    boxShadow: '0 2px 12px rgba(230,126,34,0.3)',
  },
  addDayButtonText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  emptyDayContainer: {
    padding: 20,
    borderStyle: 'dashed',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
    margin: 10,
    borderRadius: 10,
    minHeight: 72,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  emptyDayText: {
    color: isDarkMode ? 'rgba(255,255,255,0.36)' : 'rgba(0,0,0,0.45)',
    fontStyle: 'italic',
    textAlign: 'center',
    fontSize: 14,
  },
  tourHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  activeEmptyDayContainer: {
    borderColor: theme.brand.primary,
    borderStyle: 'dashed',
    backgroundColor: `${theme.brand.primary}10`,
  },
  activeVenueCard: {
    backgroundColor: `${theme.brand.primary}10`,
    borderColor: `${theme.brand.primary}40`,
  },
  activeTourVenueIndex: {
    backgroundColor: theme.brand.primary,
    boxShadow: '0 2px 12px rgba(243, 156, 18, 0.4)',
  },
  // Add new styles for coming soon message
  comingSoonContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#121212',
  },
  comingSoonTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 10,
  },
  comingSoonSubtitle: {
    fontSize: 24,
    color: '#e67e22',
    marginBottom: 20,
  },
  comingSoonMessage: {
    fontSize: 16,
    color: '#aaa',
    textAlign: 'center',
    marginBottom: 30,
    maxWidth: 400,
    lineHeight: 24,
  },
  backButton: {
    backgroundColor: '#e67e22',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 5,
  },
  backButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  bottomControls: {
    position: 'absolute',
    bottom: 16,
    right: 16,
    flexDirection: 'row',
    backgroundColor: 'rgba(26, 26, 26, 0.95)',
    borderRadius: 12,
    padding: 8,
    gap: 8,
    zIndex: 10,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
  },
  controlIconGroup: {
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 8,
    borderRightWidth: 1,
    borderRightColor: 'rgba(255, 255, 255, 0.1)',
    '&:last-child': {
      borderRightWidth: 0,
    },
  },
  controlIcon: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    minWidth: 48,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },
  },
  activeIcon: {
    backgroundColor: '#e67e22',
    '&:hover': {
      backgroundColor: '#d35400',
    },
  },
  iconLabel: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '500',
    marginTop: 4,
    textAlign: 'center',
  },
  clusterSection: {
    padding: 10,
    backgroundColor: 'rgba(230, 126, 34, 0.1)',
    borderRadius: 5,
    marginBottom: 10,
  },
  clusterTitle: {
    color: theme.brand.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  filtersContainer: {
    marginTop: 12,
    marginBottom: 16,
    backgroundColor: theme.background.tertiary,
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,
    borderColor: theme.ui.border,
  },
  filterRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
    gap: 10,
  },
  filterGroup: {
    flex: 1,
  },
  filterLabel: {
    color: theme.text.secondary,
    fontSize: 11,
    marginBottom: 6,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  filterButton: {
    backgroundColor: isDarkMode ? theme.background.elevated : '#1a1a1a',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 36,
  },
  filterButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  filterCheckboxPanel: {
    marginTop: 6,
    padding: 8,
    backgroundColor: isDarkMode ? theme.background.elevated : '#252525',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: theme.ui.border,
  },
  filterCheckboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  filterCheckboxLabel: {
    color: theme.text.primary,
    fontSize: 12,
    flex: 1,
  },
  filterCheckboxCheck: {
    color: theme.brand?.primary || '#2c7be5',
    fontSize: 14,
    fontWeight: '700',
  },
  filtersButtonWrapper: {
    backgroundColor: isDarkMode ? theme.background.elevated : '#1a1a1a',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    ...(Platform.OS === 'web' ? { ...StyleSheet.absoluteFillObject, width: '100%', height: '100%' } : {}),
  },
  filterModalContent: {
    backgroundColor: theme.background.elevated,
    backdropFilter: 'blur(28px) saturate(180%)',
    WebkitBackdropFilter: 'blur(28px) saturate(180%)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: theme.ui.border,
    boxShadow: `0 16px 56px ${theme.ui.shadowStrong}`,
    maxWidth: 480,
    width: '100%',
    maxHeight: '85%',
    overflow: 'hidden',
  },
  filterModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 18,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: theme.ui.border,
  },
  filterModalTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: theme.text.primary,
    letterSpacing: 0.2,
  },
  filterModalCloseBtn: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
  },
  filterModalCloseText: {
    color: theme.text.secondary,
    fontSize: 13,
    fontWeight: '600',
  },
  filterModalScroll: {
    maxHeight: 420,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  filterModalSection: {
    marginBottom: 16,
  },
  filterModalVisibilityRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 6,
  },
  filterModalToggle: {
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.ui.border,
  },
  filterModalToggleActive: {
    backgroundColor: 'rgba(230,126,34,0.25)',
    borderColor: 'rgba(230,126,34,0.6)',
  },
  filterModalToggleText: {
    color: theme.text.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  importFolderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: theme.ui.border,
  },
  importFolderName: {
    color: theme.text.primary,
    fontSize: 15,
    fontWeight: '600',
    flex: 1,
    marginRight: 8,
  },
  importFolderCount: {
    color: theme.text.secondary,
    fontSize: 13,
  },
  newTourModalSection: {
    marginBottom: 16,
  },
  newTourModalLabel: {
    color: theme.text.secondary,
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 6,
  },
  newTourModalInput: {
    backgroundColor: theme.input?.background || (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'),
    borderWidth: 1,
    borderColor: theme.ui?.border || 'rgba(0,0,0,0.1)',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 14,
    fontSize: 15,
    color: theme.text.primary,
  },
  newTourModalCreateButton: {
    backgroundColor: theme.brand?.primary || '#e67e22',
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 8,
    marginBottom: 20,
  },
  newTourModalCreateButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  newTourModalHint: {
    fontSize: 12,
    color: theme.text.secondary,
    marginBottom: 8,
    lineHeight: 18,
  },
  newTourPostersLoading: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingHorizontal: 12,
    gap: 10,
  },
  newTourPostersLoadingText: {
    fontSize: 14,
    color: theme.text.secondary,
  },
  newTourPostersSection: {
    marginBottom: 16,
  },
  newTourPostersList: {
    paddingVertical: 8,
    gap: 5,
    paddingRight: 16,
  },
  newTourPostersScroll: {
    maxHeight: 300
  },
  newTourPosterCard: {
    width: 200,
    height: 250,
    marginRight: 12,
    backgroundColor: theme.input?.background || (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'),
    borderRadius: 8,
    borderWidth: 2,
    borderColor: theme.ui?.border || 'rgba(0,0,0,0.1)',
    padding: 6,
    overflow: 'hidden',
  },
  newTourPosterCardSelected: {
    borderWidth: 2,
  },
  newTourPosterHeader: {
    marginBottom: 4,
  },
  newTourPosterTitle: {
    fontSize: 11,
    color: theme.text.primary,
    fontWeight: '600',
  },
  newTourPosterMeta: {
    fontSize: 10,
    color: theme.text.secondary,
    marginTop: 2,
  },
  newTourPosterGrid: {
    minHeight: 48,
    borderRadius: 4,
    overflow: 'hidden',
  },
  newTourLinkedPostRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: theme.input?.background || 'rgba(0,0,0,0.06)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.ui?.border || 'rgba(0,0,0,0.1)',
  },
  newTourLinkedPostTitle: {
    flex: 1,
    fontSize: 14,
    color: theme.text.primary,
    marginRight: 8,
  },
  newTourLinkedPostClear: {
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  newTourModalClearText: {
    fontSize: 14,
    color: theme.brand?.primary || '#e67e22',
    fontWeight: '600',
  },
  confirmPopupMessage: {
    color: theme.text.secondary,
    fontSize: 15,
    lineHeight: 22,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  confirmPopupActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    paddingHorizontal: 16,
    paddingBottom: 16,
    paddingTop: 4,
  },
  confirmPopupButton: {
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.ui?.border || 'rgba(0,0,0,0.15)',
  },
  confirmPopupButtonPrimary: {
    backgroundColor: theme.brand?.primary || '#2c7be5',
    borderColor: theme.brand?.primary || '#2c7be5',
  },
  confirmPopupButtonText: {
    color: theme.text.primary,
    fontSize: 15,
    fontWeight: '600',
  },
  confirmPopupButtonPrimaryText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
  newTourDropdown: {
    marginTop: 4,
    maxHeight: 220,
    backgroundColor: theme.background?.secondary || (isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.06)'),
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.ui?.border || 'rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  newTourDropdownHint: {
    padding: 12,
    fontSize: 13,
    color: theme.text.secondary,
  },
  newTourDropdownRow: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: theme.ui?.border || 'rgba(0,0,0,0.06)',
  },
  newTourDropdownName: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.text.primary,
  },
  newTourDropdownMeta: {
    fontSize: 12,
    color: theme.text.secondary,
    marginTop: 2,
  },
  coordFilterRow: {
    flexDirection: 'row',
    gap: 6,
  },
  coordFilterButton: {
    backgroundColor: theme.background.tertiary,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: theme.ui.border,
  },
  coordFilterButtonActive: {
    backgroundColor: isDarkMode ? theme.background.elevated : '#1a1a1a',
    borderColor: isDarkMode ? theme.ui.borderStrong : '#1a1a1a',
  },
  coordFilterButtonActiveBlue: {
    backgroundColor: theme.markers.embedCoords,
    borderColor: theme.markers.embedCoords,
  },
  coordFilterButtonActivePurple: {
    backgroundColor: theme.markers.storedCoords,
    borderColor: theme.markers.storedCoords,
  },
  coordFilterText: {
    color: theme.text.secondary,
    fontSize: 11,
    fontWeight: '600',
  },
  coordFilterTextActive: {
    color: '#fff',
  },
  // Visibility toggles styles
  visibilityToggles: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  toggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 20,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
    borderWidth: 1.5,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.25)',
    gap: 6,
  },
  toggleButtonActive: {
    backgroundColor: 'rgba(230,126,34,0.75)',
    borderColor: 'rgba(230,126,34,0.9)',
  },
  toggleButtonActiveRed: {
    backgroundColor: 'rgba(231,76,60,0.75)',
    borderColor: 'rgba(231,76,60,0.9)',
  },
  toggleButtonActiveGreen: {
    backgroundColor: '#27ae60',
    borderColor: '#27ae60',
  },
  toggleIcon: {
    fontSize: 14,
  },
  toggleText: {
    fontSize: 12,
    fontWeight: '600',
    color: isDarkMode ? 'rgba(255,255,255,0.5)' : theme.text.primary,
  },
  toggleTextActive: {
    color: 'rgba(255,255,255,0.95)',
  },
  addLocationSearchSection: {
    backgroundColor: isDarkMode ? 'rgba(39, 174, 96, 0.1)' : 'rgba(39, 174, 96, 0.08)',
    borderRadius: 10,
    padding: 14,
    marginBottom: 16,
    marginTop: 8,
    borderWidth: 1,
    borderColor: 'rgba(39, 174, 96, 0.3)',
  },
  addLocationHint: {
    backgroundColor: isDarkMode ? 'rgba(39, 174, 96, 0.15)' : 'rgba(39, 174, 96, 0.1)',
    borderRadius: 8,
    padding: 10,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: 'rgba(39, 174, 96, 0.3)',
  },
  addLocationHintText: {
    fontSize: 12,
    color: '#27ae60',
    textAlign: 'center',
    fontWeight: '500',
  },
  venueColorLegend: {
    marginTop: 10,
    marginBottom: 12,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: theme.background.tertiary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.ui.border,
  },
  venueColorLegendTitle: {
    fontSize: 11,
    fontWeight: '600',
    color: theme.text.secondary,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  venueColorLegendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  venueColorDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginRight: 8,
  },
  venueColorLegendText: {
    fontSize: 12,
    color: theme.text.secondary,
  },
  filterDivider: {
    height: 1,
    backgroundColor: theme.ui.border,
    marginBottom: 12,
  },
  // User locations section styles
  userLocationsSection: {
    marginTop: 16,
    borderTopWidth: 1,
    borderTopColor: theme.ui.border,
    paddingTop: 16,
  },
  userLocationsSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.text.primary,
    marginBottom: 12,
  },
  userLocationCard: {
    backgroundColor: theme.background.card,
    borderRadius: 10,
    padding: 12,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: theme.ui.border,
    borderLeftWidth: 3,
  },
  userLocationCardPublic: {
  },
  userLocationName: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.text.primary,
    marginBottom: 4,
  },
  userLocationNotes: {
    fontSize: 12,
    color: theme.text.secondary,
    marginBottom: 8,
  },
  userLocationMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  userLocationBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    backgroundColor: isDarkMode ? 'rgba(231, 76, 60, 0.2)' : 'rgba(231, 76, 60, 0.1)',
  },
  userLocationBadgePublic: {
    backgroundColor: isDarkMode ? 'rgba(243, 156, 18, 0.2)' : 'rgba(243, 156, 18, 0.1)',
  },
  userLocationBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#e74c3c',
  },
  userLocationBadgeTextPublic: {
    color: '#f39c12',
  },
  userLocationActions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 10,
  },
  userLocationActionBtn: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: theme.ui.border,
    backgroundColor: 'transparent',
  },
  userLocationActionBtnText: {
    fontSize: 11,
    fontWeight: '500',
    color: theme.text.secondary,
  },
  editLocationModal: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  editLocationContent: {
    backgroundColor: theme.background.card,
    borderRadius: 12,
    padding: 20,
    width: '90%',
    maxWidth: 400,
  },
  editLocationTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.text.primary,
    marginBottom: 16,
  },
  editLocationInput: {
    backgroundColor: theme.input.background,
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: theme.input.border,
    color: theme.text.primary,
    fontSize: 14,
  },
  editLocationCheckbox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  editLocationCheckboxLabel: {
    fontSize: 14,
    color: theme.text.secondary,
  },
  editLocationButtons: {
    flexDirection: 'row',
    gap: 10,
    justifyContent: 'flex-end',
  },
  editLocationBtn: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  editLocationBtnCancel: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: theme.ui.border,
  },
  editLocationBtnSave: {
    backgroundColor: '#27ae60',
  },
  editLocationBtnText: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.text.secondary,
  },
  editLocationBtnTextSave: {
    color: '#fff',
  },
  // Add Location Panel Styles
  addLocationPanel: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 380,
    height: '100%',
    backgroundColor: isDarkMode ? 'rgba(6, 9, 22, 0.92)' : 'rgba(255,255,255,0.96)',
    backdropFilter: 'blur(28px) saturate(180%)',
    WebkitBackdropFilter: 'blur(28px) saturate(180%)',
    borderLeftWidth: 1,
    borderLeftColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)',
    zIndex: 2000,
    boxShadow: isDarkMode ? '-12px 0 48px rgba(0,0,0,0.55)' : '-12px 0 48px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
  },
  addLocationPanelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 18,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: isDarkMode ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.08)',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
  },
  addLocationPanelTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: theme.text.primary,
    letterSpacing: 0.2,
  },
  addLocationPanelClose: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)',
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addLocationPanelCloseText: {
    fontSize: 16,
    color: theme.text.secondary,
  },
  addLocationPanelContent: {
    padding: 16,
    flex: 1,
  },
  addLocationSection: {
    marginBottom: 20,
    zIndex: 1000,
  },
  addLocationLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: theme.text.tertiary,
    marginBottom: 7,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  addLocationInput: {
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
    borderRadius: 9,
    padding: 11,
    borderWidth: 1,
    borderColor: theme.ui.border,
    color: theme.text.primary,
    fontSize: 14,
  },
  addLocationTextarea: {
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
    borderRadius: 9,
    padding: 11,
    borderWidth: 1,
    borderColor: theme.ui.border,
    color: theme.text.primary,
    fontSize: 14,
    minHeight: 80,
    textAlignVertical: 'top',
  },
  addressSearchContainer: {
    position: 'relative',
    zIndex: 1000,
  },
  addressSearchResults: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: theme.background.elevated,
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: theme.ui.border,
    marginTop: 4,
    maxHeight: 200,
    zIndex: 10000,
    elevation: 10000,
    boxShadow: `0 8px 24px ${theme.ui.shadowStrong}`,
  },
  addressSearchResultsContainer: {
    position: 'relative',
    zIndex: 1000,
  },
  addressSearchResult: {
    padding: 11,
    borderBottomWidth: 1,
    borderBottomColor: theme.ui.border,
  },
  addressSearchResultText: {
    fontSize: 13,
    color: theme.text.primary,
  },
  addressSearchResultSubtext: {
    fontSize: 11,
    color: theme.text.secondary,
    marginTop: 2,
  },
  categoryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  categoryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 7,
    paddingHorizontal: 11,
    borderRadius: 20,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.11)' : 'rgba(0,0,0,0.10)',
    gap: 5,
  },
  categoryButtonActive: {
    backgroundColor: 'rgba(37,150,190,0.65)',
    borderColor: 'rgba(37,150,190,0.5)',
  },
  categoryButtonIcon: {
    fontSize: 14,
  },
  categoryButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: theme.text.secondary,
  },
  categoryButtonTextActive: {
    color: '#fff',
    fontWeight: '700',
  },
  coordsDisplay: {
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
    borderRadius: 8,
    padding: 10,
    marginTop: 8,
    borderWidth: 1,
    borderColor: theme.ui.border,
  },
  coordsDisplayText: {
    fontSize: 11,
    color: theme.text.secondary,
    fontFamily: 'monospace',
  },
  addLocationActions: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: isDarkMode ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.08)',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
  },
  saveLocationButton: {
    backgroundColor: 'rgba(39, 174, 96, 0.85)',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    marginBottom: 10,
    borderWidth: 1,
    borderColor: 'rgba(39,174,96,0.4)',
  },
  saveLocationButtonDisabled: {
    backgroundColor: 'rgba(149,165,166,0.35)',
    borderColor: 'rgba(149,165,166,0.2)',
  },
  saveLocationButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
  cancelLocationButton: {
    backgroundColor: 'transparent',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.ui.border,
  },
  cancelLocationButtonText: {
    color: theme.text.secondary,
    fontSize: 14,
    fontWeight: '500',
  },
  distanceText: {
    color: theme.brand.primary,
    fontSize: 12,
    marginTop: 2,
  },
  venueDetails: {
    marginTop: 4,
  },
  detailText: {
    color: theme.text.tertiary,
    fontSize: 12,
    marginBottom: 2,
  },
  venueDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 4,
  },
  recenterButton: {
    position: 'absolute',
    top: 60,
    right: 20,
    zIndex: 1,
    backgroundColor: 'rgba(46, 46, 46, 0.9)',
    width: 32,
    height: 32,
    borderRadius: 4,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(46, 46, 46, 1)',
    },
  },
  recenterButtonText: {
    color: '#fff',
    fontSize: 24,
    fontWeight: '500',
  },
  bottomLeftControls: {
    position: 'absolute',
    bottom: 20,
    left: 20,
    zIndex: 10,
  },
  locationButtonContainer: {
    position: 'absolute',
    top: 20,
    right: 20,
    zIndex: 1,
  },
  locationButton: {
    backgroundColor: 'rgba(46, 46, 46, 0.9)',
    width: 32,
    height: 32,
    borderRadius: 4,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(46, 46, 46, 1)',
    },
  },
  locationButtonIcon: {
    color: '#fff',
    fontSize: 20,
  },
  mapSetupSectionLabel: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: 'rgba(130,130,130,0.95)',
    marginBottom: 6,
  },
  mapTypeSegment: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    alignSelf: 'flex-start',
  },
  displayModeSegment: {
    flexDirection: 'row',
    gap: 3,
    padding: 3,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(130,130,130,0.22)',
    backgroundColor: 'rgba(130,130,130,0.08)',
  },
  displayModeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
  },
  displayModeOptionActive: {
    backgroundColor: '#e67e22',
  },
  displayModeText: {
    fontSize: 11,
    fontWeight: '700',
  },
  mapTypeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(130,130,130,0.22)',
    backgroundColor: 'rgba(130,130,130,0.08)',
    transition: 'all 0.15s ease',
  },
  mapTypeOptionActive: {
    backgroundColor: '#e67e22',
    borderColor: '#e67e22',
    boxShadow: '0 2px 8px rgba(230,126,34,0.35)',
  },
  mapTypeLabel: {
    fontSize: 12.5,
    fontWeight: '700',
    letterSpacing: 0.2,
  },
  mapTypeHint: {
    fontSize: 9.5,
    fontWeight: '500',
    marginTop: 1,
  },
  mapContainer: {
    flex: 1,
    minHeight: '400px',
    minWidth: '300px',
    position: 'relative',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.06)',
    transition: 'all 0.3s ease',
    [BREAKPOINTS.phone]: {
      height: '400px',
      minHeight: '400px',
    },
  },
  mapColumn: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    flexDirection: 'column',
    gap: 8,
  },
  mapBelowBar: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingHorizontal: 2,
    paddingBottom: 2,
    flexShrink: 0,
  },
  mapBelowBarChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 11,
    borderRadius: 6,
    borderWidth: 1,
    flexShrink: 0,
  },
  mapBelowBarChipText: {
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 0.1,
  },
  mapBelowBarLegend: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingTop: 6,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)',
    minWidth: 0,
  },
  mapBelowBarLegendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  mapBelowBarLegendText: {
    fontSize: 10,
    fontWeight: '500',
    letterSpacing: 0.12,
  },
  mapBelowBarMetroBtn: {
    paddingVertical: 5,
    paddingHorizontal: 12,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.70)' : 'rgba(0,0,0,0.35)',
    backgroundColor: 'transparent',
    flexShrink: 0,
  },
  mapBelowBarMetroBtnText: {
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 0.07,
    color: isDarkMode ? '#ACACAC' : 'rgba(0,0,0,0.55)',
    textAlign: 'center',
  },
  mapContainerMobileFullScreen: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    flex: 1,
    height: 'auto',
    minHeight: 0,
    minWidth: 0,
    borderRadius: 0,
    borderBottomLeftRadius: 14,
    borderBottomRightRadius: 14,
    boxShadow: isDarkMode
      ? '0 8px 28px rgba(0,0,0,0.35)'
      : '0 4px 20px rgba(0,0,0,0.12)',
    borderWidth: 0,
    borderBottomWidth: 1,
    borderBottomColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
  mapContainerViewportFullscreen: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100dvh',
    minHeight: '100dvh',
    minWidth: 0,
    // Above app header (zIndex 1000) and other chrome — CSS fallback when Fullscreen API is unavailable
    zIndex: 2147483000,
    borderRadius: 0,
    borderWidth: 0,
    boxShadow: 'none',
    overflow: 'hidden',
    backgroundColor: isDarkMode ? '#0b0f1a' : '#0f1419',
  },
  mobileMapCanvasWrap: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
  },
  mobileMapFabRow: {
    position: 'fixed',
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 10,
    zIndex: 2500,
    pointerEvents: 'box-none',
    paddingHorizontal: 12,
  },
  mobileBottomInsetBar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    height: MOBILE_MAP_BOTTOM_GAP,
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    backgroundColor: theme.background.primary,
    zIndex: 2400,
    pointerEvents: 'none',
    borderTopWidth: 1,
    borderTopColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    boxSizing: 'border-box',
  },
  mileageText: {
    color: '#2c7be5', // Light blue color
    fontSize: 13,
    fontWeight: '500',
  },
  dayRouteInfo: {
    backgroundColor: isDarkMode ? 'rgba(44, 123, 229, 0.1)' : theme.background.primary,
    padding: 8,
    borderRadius: 4,
    marginTop: 8,
  },
  dayRouteLeg: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  dayRouteText: {
    color: isDarkMode ? '#fff' : '#000',
    fontSize: 12,
  },
  dayRouteMileage: {
    color: isDarkMode ? '#2c7be5' : '#93c5fd',
    fontSize: 12,
    fontWeight: '500',
  },
  totalMileage: {
    color: isDarkMode ? '#2c7be5' : '#93c5fd',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'right',
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: isDarkMode ? 'rgba(44, 123, 229, 0.2)' : 'rgba(147, 197, 253, 0.3)',
    paddingTop: 4,
  },
  fuelEstimateText: {
    color: isDarkMode ? '#2c7be5' : '#93c5fd',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'right',
    marginTop: 6,
  },
  legModeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8,
    marginTop: 4,
    marginBottom: 4,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
    borderRadius: 8,
    gap: 6,
  },
  legModeLabel: {
    fontSize: 11,
    color: theme.text.secondary,
    marginRight: 4,
  },
  interDayLegRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    gap: 8,
    flexWrap: 'wrap',
  },
  interDayLegLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: isDarkMode ? 'rgba(255,255,255,0.42)' : 'rgba(0,0,0,0.42)',
    marginRight: 4,
  },
  interDayConnector: {
    marginHorizontal: 8,
    marginVertical: 4,
    borderRadius: 10,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
  },
  interDayConnectorHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 10,
    gap: 10,
    borderBottomWidth: 1,
    borderBottomColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
  },
  interDayConnectorLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: isDarkMode ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.65)',
    flex: 1,
    letterSpacing: 0.3,
  },
  interDayConnectorStat: {
    fontSize: 11,
    fontWeight: '600',
    color: isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)',
  },
  busCallCard: {
    marginHorizontal: 8,
    marginVertical: 4,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(244,63,94,0.35)',
    borderLeftWidth: 3,
    borderLeftColor: '#f43f5e',
    backgroundColor: isDarkMode ? 'rgba(244,63,94,0.10)' : 'rgba(244,63,94,0.07)',
    overflow: 'visible',
  },
  busCallCardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 10,
  },
  busCallDot: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(244,63,94,0.18)',
    borderWidth: 1.5,
    borderColor: 'rgba(244,63,94,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  busCallDotText: {
    fontSize: 14,
    lineHeight: 18,
  },
  busCallInfo: {
    flex: 1,
  },
  busCallEyebrow: {
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.7,
    color: '#f43f5e',
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  busCallName: {
    fontSize: 13,
    fontWeight: '700',
    color: isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.85)',
    marginBottom: 1,
    lineHeight: 17,
  },
  busCallTime: {
    fontSize: 11,
    color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
  },
  busCallActions: {
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
  },
  busCallActionBtn: {
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
  },
  busCallActionBtnText: {
    fontSize: 11,
    fontWeight: '600',
    color: isDarkMode ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)',
  },
  busCallForm: {
    paddingHorizontal: 12,
    paddingBottom: 12,
    paddingTop: 4,
    gap: 8,
    borderTopWidth: 1,
    borderTopColor: 'rgba(244,63,94,0.2)',
  },
  busCallFormRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  busCallFormLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)',
    width: 30,
  },
  busCallFormInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: 'rgba(244,63,94,0.3)',
    borderRadius: 6,
    paddingHorizontal: 9,
    paddingVertical: 6,
    fontSize: 12,
    color: isDarkMode ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.8)',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.8)',
    outline: 'none',
  },
  busCallPickMapBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingVertical: 7,
    paddingHorizontal: 11,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: 'rgba(244,63,94,0.4)',
    backgroundColor: 'rgba(244,63,94,0.1)',
    marginTop: 2,
    alignSelf: 'flex-start',
  },
  busCallPickMapBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#f43f5e',
  },
  addBusCallButton: {
    marginHorizontal: 8,
    marginTop: 2,
    marginBottom: 6,
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: 'rgba(244,63,94,0.35)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    backgroundColor: 'rgba(244,63,94,0.05)',
  },
  addBusCallButtonText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#f43f5e',
  },
  busCallPickingBanner: {
    position: 'absolute',
    top: 12,
    left: '50%',
    transform: [{ translateX: '-50%' }],
    zIndex: 100,
    backgroundColor: '#f43f5e',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 18,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    boxShadow: '0 4px 20px rgba(244,63,94,0.5)',
  },
  busCallPickingBannerText: {
    color: '#ffffff',
    fontWeight: '700',
    fontSize: 13,
    letterSpacing: 0.2,
  },
  legModeButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.14)',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  legModeButtonSelected: {
    backgroundColor: isDarkMode ? 'rgba(230,126,34,0.55)' : '#e8a020',
    borderColor: isDarkMode ? 'rgba(232,160,32,0.7)' : '#c78a10',
  },
  legModeButtonText: {
    fontSize: 11,
    color: isDarkMode ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.65)',
  },
  legModeButtonTextSelected: {
    color: '#ffffff',
    fontWeight: '700',
  },
  buttonIcon: {
    color: '#fff',
    width: 16,
    height: 16,
  },
  bottomRightControls: {
    position: 'absolute',
    bottom: 12,
    left: 10,
    zIndex: 15,
    flexDirection: 'row',
    gap: 3,
    backgroundColor: isDarkMode ? 'rgba(8,10,20,0.72)' : 'rgba(255,255,255,0.88)',
    backdropFilter: 'blur(18px) saturate(160%)',
    WebkitBackdropFilter: 'blur(18px) saturate(160%)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
    boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.12)',
    padding: 5,
    alignItems: 'center',
    transition: 'right 0.3s ease',
  },
  controlButton: {
    width: 30,
    height: 30,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  },
  controlButtonActive: {
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.09)',
  },
  // Right-center floating map tools (3D / zoom / locate / eco)
  mapSideTools: {
    position: 'absolute',
    top: '50%',
    marginTop: -130,
    zIndex: 16,
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    ...(Platform.OS === 'web' ? {
      transition: 'right 0.22s ease, left 0.22s ease',
    } : {}),
  },
  mapSideToolsMobile: {
    marginTop: -88,
    gap: 5,
  },
  mapSideToolsCluster: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    padding: 4,
    borderRadius: 12,
    backgroundColor: isDarkMode ? 'rgba(8,10,20,0.78)' : 'rgba(255,255,255,0.92)',
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
    ...(Platform.OS === 'web' ? {
      backdropFilter: 'blur(18px) saturate(160%)',
      WebkitBackdropFilter: 'blur(18px) saturate(160%)',
      boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 16px rgba(0,0,0,0.12)',
    } : {}),
  },
  mapSideToolsClusterMobile: {
    padding: 2,
    borderRadius: 10,
    gap: 1,
  },
  mapSideToolBtn: {
    width: 40,
    height: 40,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mapSideToolBtnMobile: {
    width: 30,
    height: 30,
    borderRadius: 8,
  },
  mapSideToolBtnActive: {
    backgroundColor: isDarkMode ? 'rgba(230,126,34,0.28)' : 'rgba(230,126,34,0.18)',
  },
  mapSideToolLabel: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.4,
    color: isDarkMode ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.7)',
  },
  mapSideToolLabelMobile: {
    fontSize: 9,
  },
  mapSideToolLabelActive: {
    color: '#e67e22',
  },
  mapSideEcoBtn: {
    width: 52,
    height: 52,
    borderRadius: 26,
    borderWidth: 2.5,
    borderColor: 'rgba(39,174,96,0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: isDarkMode ? 'rgba(8,10,20,0.78)' : 'rgba(255,255,255,0.92)',
    ...(Platform.OS === 'web' ? {
      backdropFilter: 'blur(18px) saturate(160%)',
      WebkitBackdropFilter: 'blur(18px) saturate(160%)',
      boxShadow: isDarkMode ? '0 4px 16px rgba(0,0,0,0.45)' : '0 4px 12px rgba(0,0,0,0.1)',
    } : {}),
  },
  mapSideEcoBtnMobile: {
    width: 38,
    height: 38,
    borderRadius: 19,
    borderWidth: 2,
  },
  mapSideEcoBtnActive: {
    borderColor: '#27ae60',
    backgroundColor: 'rgba(39,174,96,0.18)',
  },
  mapSideEcoText: {
    fontSize: 9,
    fontWeight: '800',
    lineHeight: 11,
    textAlign: 'center',
    letterSpacing: 0.3,
    color: 'rgba(39,174,96,0.75)',
  },
  mapSideEcoTextMobile: {
    fontSize: 7,
    lineHeight: 8.5,
    letterSpacing: 0.2,
  },
  mapSideEcoTextActive: {
    color: '#27ae60',
  },
  mapAccessibilityBtn: {
    position: 'absolute',
    bottom: 42,
    zIndex: 16,
    width: 48,
    height: 48,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: isDarkMode ? 'rgba(8,10,20,0.82)' : 'rgba(255,255,255,0.94)',
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.1)',
    ...(Platform.OS === 'web' ? {
      transition: 'right 0.22s ease, left 0.22s ease',
      backdropFilter: 'blur(18px) saturate(160%)',
      WebkitBackdropFilter: 'blur(18px) saturate(160%)',
      boxShadow: isDarkMode ? '0 4px 18px rgba(0,0,0,0.5)' : '0 4px 14px rgba(0,0,0,0.12)',
      cursor: 'pointer',
    } : {}),
  },
  mapAccessibilityBtnMobile: {
    bottom: 28,
    width: 36,
    height: 36,
    borderRadius: 10,
  },
  mapAccessibilityBtnImg: {
    width: 28,
    height: 28,
  },
  mapAccessibilityBtnImgMobile: {
    width: 20,
    height: 20,
  },
  // Accessibility note modal — mirrors MapSideRail resource form chrome
  a11yModalBackdrop: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    backgroundColor: 'rgba(0,0,0,0.62)',
    ...(Platform.OS === 'web' ? { ...StyleSheet.absoluteFillObject, width: '100%', height: '100%' } : {}),
  },
  a11yModalCard: {
    width: 420,
    maxWidth: '100%',
    maxHeight: '90%',
    padding: 20,
    borderRadius: 12,
    backgroundColor: isDarkMode ? '#171a24' : '#ffffff',
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)',
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 14px 42px rgba(0,0,0,0.35)',
      overflow: 'visible',
    } : {}),
  },
  a11yModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  a11yModalTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '700',
    color: isDarkMode ? '#ffffff' : '#1a1a1a',
  },
  a11yModalClose: {
    fontSize: 20,
    lineHeight: 22,
    color: isDarkMode ? 'rgba(255,255,255,0.62)' : 'rgba(0,0,0,0.52)',
  },
  a11yCategoryBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    marginTop: 14,
    padding: 11,
    borderRadius: 8,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.035)',
  },
  a11yCategoryLabel: {
    fontSize: 10,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: isDarkMode ? 'rgba(255,255,255,0.48)' : 'rgba(0,0,0,0.46)',
  },
  a11yCategoryValue: {
    marginTop: 2,
    fontSize: 13,
    fontWeight: '700',
    color: isDarkMode ? '#ffffff' : '#1a1a1a',
  },
  a11yHelpText: {
    marginTop: 10,
    fontSize: 11,
    lineHeight: 16,
    color: isDarkMode ? 'rgba(255,255,255,0.56)' : 'rgba(0,0,0,0.54)',
  },
  a11yFieldLabel: {
    marginTop: 14,
    marginBottom: 0,
    fontSize: 12,
    fontWeight: '700',
    color: isDarkMode ? 'rgba(255,255,255,0.84)' : 'rgba(0,0,0,0.74)',
  },
  a11yInput: {
    marginTop: 10,
    minHeight: 40,
    paddingVertical: 9,
    paddingHorizontal: 11,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.035)',
    color: isDarkMode ? '#ffffff' : '#1a1a1a',
    fontSize: 12,
    outlineStyle: 'none',
  },
  a11yTextArea: {
    minHeight: 110,
    textAlignVertical: 'top',
    paddingTop: 10,
  },
  a11yVenueSearchWrap: {
    position: 'relative',
    zIndex: 20,
    marginTop: 10,
  },
  a11yVenueSelected: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 11,
    paddingVertical: 10,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: '#e67e22',
    backgroundColor: isDarkMode ? 'rgba(230,126,34,0.14)' : 'rgba(230,126,34,0.08)',
  },
  a11yVenueSelectedName: {
    fontSize: 13,
    fontWeight: '700',
    color: isDarkMode ? '#ffffff' : '#1a1a1a',
  },
  a11yVenueSelectedLoc: {
    marginTop: 2,
    fontSize: 11,
    color: isDarkMode ? 'rgba(255,255,255,0.56)' : 'rgba(0,0,0,0.52)',
  },
  a11yVenueDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    maxHeight: 168,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)',
    backgroundColor: isDarkMode ? '#1c2030' : '#ffffff',
    zIndex: 30,
    ...(Platform.OS === 'web' ? {
      boxShadow: isDarkMode ? '0 10px 28px rgba(0,0,0,0.45)' : '0 10px 24px rgba(0,0,0,0.14)',
    } : {}),
  },
  a11yVenueResult: {
    paddingHorizontal: 11,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
  },
  a11yVenueResultName: {
    fontSize: 12,
    fontWeight: '600',
    color: isDarkMode ? '#ffffff' : '#1a1a1a',
  },
  a11yVenueResultLoc: {
    marginTop: 2,
    fontSize: 11,
    color: isDarkMode ? 'rgba(255,255,255,0.52)' : 'rgba(0,0,0,0.5)',
  },
  a11yEmptyHint: {
    marginTop: 8,
    fontSize: 11,
    color: isDarkMode ? 'rgba(255,255,255,0.48)' : 'rgba(0,0,0,0.46)',
  },
  a11yAreaRow: {
    marginTop: 10,
    gap: 8,
  },
  a11yAreaOption: {
    paddingVertical: 11,
    paddingHorizontal: 12,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.035)',
  },
  a11yAreaOptionActive: {
    borderColor: '#e67e22',
    backgroundColor: isDarkMode ? 'rgba(230,126,34,0.18)' : 'rgba(230,126,34,0.1)',
  },
  a11yAreaOptionText: {
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 0.4,
    textAlign: 'center',
    color: isDarkMode ? 'rgba(255,255,255,0.86)' : 'rgba(0,0,0,0.76)',
  },
  a11yAreaOptionTextActive: {
    color: '#e67e22',
  },
  a11yActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
    marginTop: 20,
  },
  a11yCancelBtn: {
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  a11yCancelText: {
    fontSize: 12,
    fontWeight: '700',
    color: isDarkMode ? 'rgba(255,255,255,0.56)' : 'rgba(0,0,0,0.52)',
  },
  a11yPrimaryBtn: {
    paddingVertical: 12,
    paddingHorizontal: 18,
    borderRadius: 8,
    backgroundColor: '#00A575',
    alignItems: 'center',
    minWidth: 110,
  },
  a11yPrimaryBtnDisabled: {
    opacity: 0.45,
  },
  a11yPrimaryBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#ffffff',
  },
  a11yConeHero: {
    width: 56,
    height: 56,
  },
  a11yConeSmall: {
    width: 30,
    height: 30,
  },
  buttonIcon: {
    width: 16,
    height: 16,
  },
  filterContainer: {
    padding: 10,
    backgroundColor: '#2c2c2c',
    borderRadius: 8,
    margin: 10,
  },
  filterTitle: {
    color: '#fff',
    fontSize: 16,
    marginBottom: 10,
  },
  filterGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterButton: {
    backgroundColor: '#444',
    padding: 8,
    borderRadius: 4,
    minWidth: 80,
    alignItems: 'center',
  },
  filterButtonActive: {
    backgroundColor: '#2c7be5',
  },
  filterButtonText: {
    color: '#fff',
    fontSize: 14,
  },
  venueFeatures: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 5,
  },
  venueFeature: {
    color: '#2c7be5',
    fontSize: 12,
  },
  tourVenueButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  viewDetailsButton: {
    backgroundColor: '#2c7be5',
    padding: 8,
    borderRadius: 4,
    alignItems: 'center',
    width: '100%',
    '&:hover': {
      backgroundColor: '#1b69c2',
    },
  },
  viewDetailsButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  // Update button styles for consistency
  actionButton: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    minHeight: 40,
    transition: 'all 0.2s ease',
  },
  primaryButton: {
    backgroundColor: '#e67e22',
    '&:hover': {
      backgroundColor: '#d35400',
    },
  },
  secondaryButton: {
    backgroundColor: '#6c757d',
    '&:hover': {
      backgroundColor: '#5a6268',
    },
  },
  dangerButton: {
    backgroundColor: '#dc3545',
    '&:hover': {
      backgroundColor: '#ff5252',
    },
  },
  buttonText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
    textAlign: 'center',
  },
  modeControls: {
    position: 'absolute',
    top: 10,
    left: 10 + MAP_SIDE_RAIL_WIDTH,
    backgroundColor: isDarkMode ? 'rgba(8, 10, 20, 0.62)' : 'rgba(255,255,255,0.82)',
    backdropFilter: 'blur(18px) saturate(160%)',
    WebkitBackdropFilter: 'blur(18px) saturate(160%)',
    borderRadius: 14,
    padding: 10,
    zIndex: 10,
    flexDirection: 'column',
    width: 380,
    maxWidth: 540,
    gap: 8,
    alignItems: 'flex-start',
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
    boxShadow: isDarkMode
      ? '0 8px 32px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.07)'
      : '0 8px 32px rgba(0,0,0,0.12)',
    [`@media (max-width: 768px)`]: {
      width: 'auto',
      maxWidth: 'calc(100vw - 20px)',
      left: 10,
      right: 10,
    },
  },
  modeControlsFilled: {
    // Hug content height, but never grow past the map above the weather panel
    overflow: 'hidden',
  },
  modeControlsScroll: {
    width: '100%',
    alignSelf: 'stretch',
    flexGrow: 0,
    flexShrink: 1,
    minHeight: 0,
    // Firefox / standards: slim scrollbar (WebKit via .vrc-venue-filters-scroll)
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(128,128,128,0.45) transparent',
  },
  modeControlsScrollContent: {
    paddingBottom: 4,
    gap: 8,
  },
  modeButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 7,
    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0,0,0,0.07)',
    minWidth: 70,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.20)',
  },
  modeButtonActive: {
    backgroundColor: 'rgba(37, 150, 190, 0.75)',
    borderColor: 'rgba(37, 150, 190, 0.5)',
  },
  modeButtonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  modeButtonText: {
    color: isDarkMode ? 'rgba(255,255,255,0.9)' : theme.text.primary,
    fontSize: 12,
    fontWeight: '500',
  },
  // Map search bar
  mapSearchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    width: '100%',
  },
  mapSearchInput: {
    flex: 1,
    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.09)' : 'rgba(0,0,0,0.06)',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 11,
    color: isDarkMode ? '#fff' : theme.text.primary,
    fontSize: 13,
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)',
    minWidth: 0,
  },
  mapSearchBtn: {
    paddingVertical: 8,
    paddingHorizontal: 11,
    borderRadius: 8,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
  },
  mapSearchBtnActive: {
    backgroundColor: 'rgba(38,132,255,0.35)',
    borderColor: 'rgba(38,132,255,0.4)',
  },
  mapSearchBtnAdd: {
    backgroundColor: 'rgba(230, 126, 34, 0.65)',
    borderColor: 'rgba(230,126,34,0.3)',
  },
  mapSearchBtnCancel: {
    backgroundColor: 'rgba(180,60,60,0.6)',
    borderColor: 'rgba(180,60,60,0.3)',
  },
  mapSearchBtnText: {
    color: isDarkMode ? 'rgba(255,255,255,0.92)' : theme.text.primary,
    fontSize: 12,
    fontWeight: '600',
  },
  mapSearchDropdown: {
    width: '100%',
    backgroundColor: isDarkMode ? 'rgba(10, 12, 24, 0.96)' : 'rgba(255,255,255,0.97)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.10)',
    overflow: 'hidden',
    maxHeight: 280,
    boxShadow: isDarkMode ? '0 8px 24px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.15)',
  },
  mapSearchResultRow: {
    paddingVertical: 10,
    paddingHorizontal: 13,
    borderBottomWidth: 1,
    borderBottomColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
  },
  mapSearchResultName: {
    color: isDarkMode ? '#f1f5f9' : theme.text.primary,
    fontSize: 13,
    fontWeight: '600',
  },
  mapSearchResultSub: {
    color: isDarkMode ? '#94a3b8' : theme.text.secondary,
    fontSize: 11,
    marginTop: 2,
  },
  mapSearchResultRowDimmed: {
    opacity: 0.42,
  },
  // Zoom hint toast
  mapZoomHint: {
    alignSelf: 'center',
    backgroundColor: 'rgba(230, 126, 34, 0.85)',
    borderRadius: 6,
    paddingVertical: 5,
    paddingHorizontal: 10,
    marginTop: -2,
  },
  mapZoomHintText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '600',
  },
  // Venue filter chips row
  venueFilterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 5,
    width: '100%',
  },
  venueFilterChip: {
    paddingVertical: 4,
    paddingHorizontal: 9,
    borderRadius: 5,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
    borderWidth: 1.5,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.22)',
  },
  venueFilterChipLink: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 5,
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  venueFilterChipActive: {
    backgroundColor: isDarkMode ? 'rgba(120,210,255,0.55)' : 'rgba(37,150,190,0.5)',
    borderColor: isDarkMode ? 'rgba(120,210,255,0.75)' : 'rgba(37,150,190,0.4)',
  },
  venueFilterChipText: {
    color: isDarkMode ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.65)',
    fontSize: 11,
    fontWeight: '500',
  },
  venueFilterChipTextActive: {
    color: isDarkMode ? '#062a38' : '#fff',
    fontWeight: '600',
  },
  // Top-level filter category pills
  categoryPill: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 6,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
    borderWidth: 1.5,
    borderColor: isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.16)',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  categoryPillOpen: {
    backgroundColor: isDarkMode ? 'rgba(120,210,255,0.55)' : 'rgba(37,150,190,0.18)',
    borderColor: isDarkMode ? 'rgba(120,210,255,0.75)' : 'rgba(37,150,190,0.55)',
  },
  categoryPillHasFilter: {
    borderColor: 'rgba(230,126,34,0.6)',
  },
  categoryPillText: {
    color: isDarkMode ? 'rgba(255,255,255,0.82)' : 'rgba(0,0,0,0.68)',
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 0.1,
  },
  categoryPillTextOpen: {
    color: isDarkMode ? '#062a38' : '#0a4a6e',
    fontWeight: '700',
  },
  categoryPillBadge: {
    fontSize: 9,
    fontWeight: '700',
    color: 'rgba(230,126,34,0.95)',
    backgroundColor: 'rgba(230,126,34,0.14)',
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 1,
    overflow: 'hidden',
  },
  // Eco-specific chip variant
  venueFilterChipEco: {
    borderColor: 'rgba(46,204,113,0.5)',
    backgroundColor: isDarkMode ? 'rgba(46,204,113,0.1)' : 'rgba(46,204,113,0.08)',
  },
  venueFilterChipEcoActive: {
    backgroundColor: 'rgba(46,204,113,0.55)',
    borderColor: 'rgba(46,204,113,0.7)',
  },
  // Expanded sub-panel below category pills
  filterSubPanel: {
    width: '100%',
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
    marginTop: 2,
  },
  filterSubPanelLabel: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: isDarkMode ? 'rgba(255,255,255,0.32)' : 'rgba(0,0,0,0.38)',
    marginBottom: 6,
  },
  poiTypeToggle: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 4,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
    borderWidth: 1.5,
    borderColor: isDarkMode ? 'transparent' : 'rgba(0,0,0,0.18)',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  poiTypeToggleActive: {
    backgroundColor: theme.brand.primary,
    borderColor: 'transparent',
  },
  poiTypeToggleText: {
    color: isDarkMode ? '#fff' : 'rgba(0,0,0,0.65)',
    fontSize: 11,
    fontWeight: '600',
  },
  poiControlWrap: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4,
  },
  poiControlChip: {
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  poiControlChipActive: {
    backgroundColor: '#e67e22',
  },
  poiControlChipText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '500',
  },
  drawInstructions: {
    position: 'absolute',
    top: 80,
    left: 20,
    backgroundColor: 'rgba(30, 30, 30, 0.95)',
    padding: 12,
    borderRadius: 8,
    zIndex: 10,
    maxWidth: 300,
  },
  drawInstructionsText: {
    color: '#fff',
    fontSize: 14,
    lineHeight: 20,
  },
  drawModeContainer: {
    position: 'absolute',
    top: 80,
    left: 20,
    backgroundColor: 'rgba(30, 30, 30, 0.95)',
    padding: 12,
    borderRadius: 8,
    zIndex: 10,
    maxWidth: 300,
  },
  drawModeInstructions: {
    color: '#fff',
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 12,
  },
  drawButton: {
    backgroundColor: '#e67e22',
    padding: 8,
    borderRadius: 4,
    alignItems: 'center',
    marginBottom: 8,
  },
  drawButtonActive: {
    backgroundColor: '#d35400',
  },
  drawButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  clearButton: {
    backgroundColor: '#ff6b6b',
    padding: 8,
    borderRadius: 4,
    alignItems: 'center',
  },
  clearButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  tourControls: {
    flexDirection: 'row',
    gap: 10,
    flex: 0,
    flexShrink: 0,
    marginLeft: 20,
    alignItems: 'center',
  },
  tourMenuWrap: {
    position: 'relative',
  },
  plusTourIconButton: {
    backgroundColor: 'transparent',
    padding: 2,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 0,
    ...(Platform.OS === 'web'
      ? { cursor: 'pointer', outlineStyle: 'none' }
      : {}),
  },
  folderMenuButton: {
    backgroundColor: 'transparent',
    padding: 2,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 0,
    ...(Platform.OS === 'web'
      ? { cursor: 'pointer', outlineStyle: 'none' }
      : {}),
  },
  envelopeMenuButton: {
    backgroundColor: 'transparent',
    padding: 2,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 0,
    ...(Platform.OS === 'web'
      ? { cursor: 'pointer', outlineStyle: 'none' }
      : {}),
  },
  tourHeaderDropdown: {
    position: 'absolute',
    top: 46,
    right: 0,
    minWidth: 150,
    borderRadius: 10,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: isDarkMode ? theme.ui.border : 'rgba(0,0,0,0.18)',
    backgroundColor: isDarkMode ? theme.background.elevated : 'rgba(255,255,255,0.98)',
    zIndex: 9999,
    ...(Platform.OS === 'web' ? {
      boxShadow: isDarkMode ? `0 8px 24px ${theme.ui.shadowStrong}` : '0 8px 24px rgba(0,0,0,0.16)',
    } : {
      elevation: 8,
    }),
  },
  tourHeaderDropdownItem: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: isDarkMode ? theme.ui.border : 'rgba(0,0,0,0.10)',
  },
  tourHeaderDropdownItemText: {
    color: theme.text.primary,
    fontSize: 13,
    fontWeight: '600',
  },
  importTourButton: {
    backgroundColor: '#000000',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    minWidth: 80,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
  },
  importTourButtonText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  newTourButton: {
    backgroundColor: theme.brand.secondary,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    minWidth: 100,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
  },
  saveButton: {
    backgroundColor: theme.brand.accent,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    minWidth: 100,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
  },
  newTourButtonText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  loadButton: {
    backgroundColor: '#6c757d',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    minWidth: 100,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
  },
  loadButtonText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  shareButton: {
    backgroundColor: '#9b59b6',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    minWidth: 100,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
  },
  shareButtonText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  selectedVenueCard: {
    // backgroundColor: `${theme.brand.primary}10`,
    borderColor: theme.text.inverse,
    boxShadow: `0 4px 16px ${theme.brand.primary}20`,
  },
  venueCardGreen: {
    borderColor: 'rgba(46,204,113,0.55)',
    borderWidth: 2,
    boxShadow: isDarkMode
      ? '0 2px 10px rgba(46,204,113,0.22)'
      : '0 2px 10px rgba(46,204,113,0.18)',
    backgroundColor: isDarkMode
      ? 'rgba(46,204,113,0.06)'
      : 'rgba(46,204,113,0.04)',
  },
  tourVenueHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    padding: 8,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
  },
  '.dark-popup .mapboxgl-popup-content': {
    backgroundColor: 'rgba(6,9,22,0.82)',
    backdropFilter: 'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: '#ffffff',
    padding: '0',
    borderRadius: '14px',
    minWidth: '220px',
    boxShadow: '0 12px 48px rgba(0,0,0,0.55)',
    overflow: 'hidden',
  },
  '.dark-popup .mapboxgl-popup-close-button': {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '18px',
    padding: '6px 10px',
    right: '2px',
    top: '2px',
    background: 'transparent',
    border: 'none',
  },
  '.dark-popup .mapboxgl-popup-close-button:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: '6px',
  },
  // Reuse dark-popup style for light-popup (glass works on all map styles)
  '.light-popup .mapboxgl-popup-content': {
    backgroundColor: 'rgba(6,9,22,0.82)',
    backdropFilter: 'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    border: '1px solid rgba(255,255,255,0.12)',
    padding: '0',
    borderRadius: '14px',
    minWidth: '220px',
    boxShadow: '0 12px 48px rgba(0,0,0,0.55)',
    overflow: 'hidden',
  },
  '.light-popup .mapboxgl-popup-close-button': {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '18px',
    padding: '6px 10px',
    right: '2px',
    top: '2px',
    background: 'transparent',
    border: 'none',
  },
  '.light-popup .mapboxgl-popup-close-button:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: '6px',
  },
});

// ─── Letter-grade helper (mirrors VenueScreen) ───────────────────────────────
const getLetterGrade = (grade) => {
  if (!grade && grade !== 0) return null;
  if (typeof grade === 'string') {
    const g = grade.toUpperCase();
    if (['A', 'B', 'C', 'D', 'F'].includes(g)) return g;
  }
  const n = Number(grade);
  if (!isNaN(n)) {
    if (n >= 4.5) return 'A';
    if (n >= 3.5) return 'B';
    if (n >= 2.5) return 'C';
    if (n >= 1.5) return 'D';
    if (n >= 1)   return 'F';
  }
  return null;
};

const gradeColor = (letter) => {
  switch (letter) {
    case 'A': return '#2ecc71';
    case 'B': return '#3498db';
    case 'C': return '#f1c40f';
    case 'D': return '#e67e22';
    case 'F': return '#e74c3c';
    default:  return '#888';
  }
};

// ─── Unified glass popup HTML builder ────────────────────────────────────────
const TRAFFIC_CONE_IMG = require('../../assets/traffic-cone.png');
const TRAFFIC_CONE_URI =
  typeof TRAFFIC_CONE_IMG === 'string'
    ? TRAFFIC_CONE_IMG
    : (Image.resolveAssetSource?.(TRAFFIC_CONE_IMG)?.uri || '');

const buildVenuePopupHTML = ({
  id, name, city, state, capacity, type, notes,
  hasLodging = false, hasVeganFood = false,
  hasShorePower = false, hasMerch100 = false, isNearTransit = false,
  isEcoConscious = false,
  grade = null,
  showFocus = false, isUserLocation = false, isOwn = false,
  addAction, detailAction, editAction, deleteAction,
  coneImgSrc = TRAFFIC_CONE_URI,
}) => {
  const locationLine = [city, state].filter(Boolean).join(', ');
  const addAct = addAction || `window.handleMapPopupAction('addToTour','${id}')`;
  const detailAct = detailAction || `window.handleMapPopupAction('viewDetails','${id}')`;
  const focusAct = `window.handleMapPopupAction('zoomToVenue','${id}')`;
  const flagAct = `window.handleMapPopupAction('accessibilityFlag','${id}')`;
  const coneImg = coneImgSrc
    ? `<img src="${coneImgSrc}" alt="" width="14" height="14" style="display:inline-block;vertical-align:-2px;margin-right:6px;" />`
    : '🚧 ';

  const pill = (text) =>
    `<span style="font-size:11px;color:rgba(255,255,255,0.55);background:rgba(255,255,255,0.08);padding:3px 9px;border-radius:6px;border:1px solid rgba(255,255,255,0.1);white-space:nowrap;">${text}</span>`;

  const ecoPill = (text, color = '#2ecc71') =>
    `<span style="font-size:11px;color:${color};background:${color}1a;padding:3px 9px;border-radius:6px;border:1px solid ${color}59;white-space:nowrap;">${text}</span>`;

  const btn = (label, action, bg, hover) =>
    `<button onclick="${action}" style="width:100%;padding:11px 0;background:${bg};color:#fff;border:none;border-top:1px solid rgba(255,255,255,0.07);cursor:pointer;font-size:13px;font-weight:600;letter-spacing:0.2px;transition:background 0.15s;" onmouseover="this.style.background='${hover}'" onmouseout="this.style.background='${bg}'">${label}</button>`;

  const ghostBtn = (label, action, color) =>
    `<button onclick="${action}" style="flex:1;padding:9px 0;background:rgba(255,255,255,0.05);color:${color};border:none;border-top:1px solid rgba(255,255,255,0.07);cursor:pointer;font-size:12px;font-weight:600;transition:background 0.15s;" onmouseover="this.style.background='rgba(255,255,255,0.1)'" onmouseout="this.style.background='rgba(255,255,255,0.05)'">${label}</button>`;

  const ecoPills = [
    hasLodging     ? ecoPill('🌿 Lodging')        : '',
    hasVeganFood   ? ecoPill('🌱 Vegan')           : '',
    hasShorePower  ? ecoPill('⚡ Shore Power',  '#f1c40f') : '',
    hasMerch100    ? ecoPill('💯 Merch',         '#3498db') : '',
    isNearTransit  ? ecoPill('🚌 Near Transit', '#9b59b6') : '',
    isEcoConscious ? ecoPill('🌍 Eco Venue',    '#1abc9c') : '',
  ].filter(Boolean).join(' ');

  const anyEco = hasLodging || hasVeganFood || hasShorePower || hasMerch100 || isNearTransit || isEcoConscious;
  const ecoAccent = anyEco
    ? `<div style="height:3px;background:linear-gradient(90deg,#2ecc71,#27ae60);border-radius:3px 3px 0 0;"></div>`
    : '';

  const letterGrade = getLetterGrade(grade);
  const gradeHtml = letterGrade
    ? `<span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:6px;background:${gradeColor(letterGrade)}22;border:1.5px solid ${gradeColor(letterGrade)}88;font-size:14px;font-weight:800;color:${gradeColor(letterGrade)};margin-left:8px;flex-shrink:0;vertical-align:middle;">${letterGrade}</span>`
    : '';

  return `
    <div style="min-width:230px;max-width:290px;font-family:system-ui,-apple-system,sans-serif;">
      ${ecoAccent}
      <div style="padding:14px 16px 12px;">
        ${locationLine ? `<p style="margin:0 0 3px 0;font-size:10px;font-weight:700;letter-spacing:0.9px;text-transform:uppercase;color:rgba(255,255,255,0.38);">${locationLine}</p>` : ''}
        <div style="display:flex;align-items:flex-start;gap:0;margin-bottom:10px;">
          <h3 style="margin:0;font-size:16px;font-weight:700;color:rgba(255,255,255,0.95);line-height:1.25;flex:1;">${name}</h3>
          ${gradeHtml}
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:${(notes || ecoPills) ? '10px' : '2px'};">
          ${capacity && capacity !== 'N/A' ? pill('Cap: ' + capacity) : ''}
          ${type && type !== 'N/A' ? pill(type) : ''}
          ${ecoPills}
        </div>
        ${notes ? `<details style="margin-top:4px;"><summary style="cursor:pointer;font-size:11px;color:rgba(255,255,255,0.38);font-weight:600;letter-spacing:0.3px;list-style:none;">▸ Notes</summary><div style="margin-top:6px;color:rgba(255,255,255,0.62);font-size:12px;max-height:80px;overflow-y:auto;white-space:pre-wrap;word-break:break-word;line-height:1.5;">${notes}</div></details>` : ''}
        <div id="venue-weather-${id}" style="margin-top:6px;"></div>
        <div id="upcoming-shows-${id}" style="margin-top:8px;"></div>
      </div>
      <div style="display:flex;flex-direction:column;">
        ${btn('Add to Tour', addAct, 'rgba(230,126,34,0.85)', 'rgba(230,126,34,1)')}
        ${!isUserLocation ? btn(`${coneImg}Accessibility Flag`, flagAct, 'rgba(255,255,255,0.10)', 'rgba(255,255,255,0.16)') : ''}
        ${showFocus ? btn('Focus on Map', focusAct, 'rgba(37,99,235,0.75)', 'rgba(37,99,235,0.95)') : ''}
        ${btn('View Details', detailAct, 'rgba(255,255,255,0.08)', 'rgba(255,255,255,0.14)')}
        ${isUserLocation && isOwn ? `<div style="display:flex;border-top:1px solid rgba(255,255,255,0.07);">${ghostBtn('Edit', editAction, 'rgba(52,152,219,0.9)')}${ghostBtn('Delete', deleteAction, 'rgba(231,76,60,0.9)')}</div>` : ''}
      </div>
    </div>
  `;
};

// Add helper functions before the component
const isValidLatitude = (lat) => {
  const parsedLat = parseFloat(lat);
  return !isNaN(parsedLat) && parsedLat >= -90 && parsedLat <= 90;
};

const isValidLongitude = (lng) => {
  const parsedLng = parseFloat(lng);
  return !isNaN(parsedLng) && parsedLng >= -180 && parsedLng <= 180;
};

const DIRECTORY_POSTS_SOURCE = 'directory-posts-source';

function ensureDirectoryPostsMapLayers(mapInstance) {
  if (!mapInstance) return;
  if (!mapInstance.getSource(DIRECTORY_POSTS_SOURCE)) {
    mapInstance.addSource(DIRECTORY_POSTS_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!mapInstance.getLayer('directory-posts-layer')) {
    mapInstance.addLayer({
      id: 'directory-posts-layer',
      type: 'circle',
      source: DIRECTORY_POSTS_SOURCE,
      paint: {
        'circle-color': '#e67e22',
        'circle-radius': 9,
        'circle-stroke-width': 2.5,
        'circle-stroke-color': '#ffffff',
      },
    });
  }
  if (!mapInstance.getLayer('directory-posts-labels')) {
    mapInstance.addLayer({
      id: 'directory-posts-labels',
      type: 'symbol',
      source: DIRECTORY_POSTS_SOURCE,
      minzoom: 9,
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 11,
        'text-offset': [0, 1.2],
        'text-anchor': 'top',
        'text-max-width': 10,
      },
      paint: {
        'text-color': '#1a1a1a',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1.5,
      },
    });
  }
}

// Helper function to convert lat/lng to latitude/longitude format
const convertCoordinateFormat = (coords) => {
  if (!coords || coords.lat === null || coords.lng === null) return null;
  return {
    latitude: coords.lat,
    longitude: coords.lng
  };
};

// Helper function to validate and fix potentially swapped coordinates
// Google Maps and Mapbox both use WGS84, but if lat/lng are swapped in data, fix them
const validateAndFixCoordinates = (lat, lng, venueName = '', city = '', state = '') => {
  let parsedLat = parseFloat(lat);
  let parsedLng = parseFloat(lng);

  // Check if both values are valid numbers
  if (isNaN(parsedLat) || isNaN(parsedLng)) {
    return null;
  }

  // If latitude is out of range (-90 to 90) but longitude would be valid for latitude,
  // they might be swapped
  if (Math.abs(parsedLat) > 90 && Math.abs(parsedLng) <= 90) {
    console.log(`[${venueName}] Coordinates appear swapped: lat=${parsedLat}, lng=${parsedLng}. Swapping them.`);
    const temp = parsedLat;
    parsedLat = parsedLng;
    parsedLng = temp;
  }

  // Final validation - just check if coordinates are in valid range
  // Don't try to "fix" coordinates as it often makes things worse
  if (Math.abs(parsedLat) <= 90 && Math.abs(parsedLng) <= 180) {
    return {
      latitude: parsedLat,
      longitude: parsedLng
    };
  }

  // If we get here, coordinates are invalid
  return null;
};

/**
 * Mapbox symbol `text-field` cannot render emoji (glyphs come from Mapbox fonts, not system emoji).
 * Drawing on a 2D canvas uses the browser/OS emoji fonts, then we pass pixels via `addImage` — that works.
 * @param {{ emoji?: string, letter?: string }} glyph — prefer `emoji` pictogram; else `letter` on the badge.
 */
function createPoiCategoryImageData(color, glyph = {}, size = 56) {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const r = size / 2 - 3;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, r, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 4;
  ctx.stroke();

  const emoji = glyph.emoji && String(glyph.emoji).trim();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  if (emoji) {
    // Color emoji on canvas (platform-dependent but works in Chromium/WebKit/Firefox for most glyphs).
    const emojiPx = Math.floor(size * 0.5);
    ctx.font = `${emojiPx}px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", "Twemoji Mozilla", "EmojiOne Color", sans-serif`;
    ctx.fillText(emoji, size / 2, size / 2 + 1);
  } else {
    const text = String(glyph.letter || '?').slice(0, 2).toUpperCase();
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${Math.floor(size * (text.length > 1 ? 0.28 : 0.42))}px system-ui, -apple-system, "Segoe UI", sans-serif`;
    ctx.fillText(text, size / 2, size / 2 + 1);
  }

  return ctx.getImageData(0, 0, size, size);
}

/** Stops from the venues list / DB — excludes map POIs and custom user locations. */
function isVenuesDbTourStop(venue) {
  if (!venue) return false;
  if (venue.isTransitStop || String(venue.id || '').startsWith('transit-')) return false;
  if (venue.isUserLocation || String(venue.id || '').startsWith('userloc-')) return false;
  if (venue.venueType === 'POI' || String(venue.id || '').startsWith('poi-')) return false;
  return true;
}

/** Subtitle line under the stop name in the tour day list (user loc / POI / venue). */
function formatTourDayVenueLocationLine(venue) {
  if (!venue) return '—';
  if (venue.isTransitStop || String(venue.id || '').startsWith('transit-')) {
    if (venue.transitType === 'airport') return 'Airport';
    if (venue.transitType === 'rail') return 'Train Station';
    return 'Bus Stop';
  }
  if (venue.isUserLocation || String(venue.id || '').startsWith('userloc-')) {
    return (venue.address && String(venue.address).trim()) || 'Custom location';
  }
  const isPoi = venue.venueType === 'POI' || String(venue.id || '').startsWith('poi-');
  if (isPoi) {
    const tagCity = venue.tags?.['addr:city'];
    const tagState = venue.tags?.['addr:state'];
    const fromTags = [tagCity, tagState].filter(Boolean).join(', ');
    const addr = String(venue.address || venue.fullAddress || fromTags || '').trim();
    if (addr) return addr;
    const lat = venue.latitude ?? venue.lat;
    const lng = venue.longitude ?? venue.lng;
    if (Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) {
      return `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;
    }
    const cat = String(venue.poiCategory || '').trim();
    return cat ? `POI · ${cat}` : 'POI';
  }
  const city = String(venue.city || '').trim();
  const state = String(venue.state || '').trim();
  if (city && state) return `${city}, ${state}`;
  if (city || state) return city || state;
  return '—';
}

/** Increment when POI marker pixels change so `addImage` is not skipped by stale `hasImage`. */
const POI_MAP_IMAGE_VERSION = 2;
// Beta/efficiency: scale POIs + adjust query limits by zoom.
const POI_ZOOM_TINY_MAX = 5; // 2-5: show tiny dots
const POI_ZOOM_MID_MAX = 11; // 5-12: show medium dots

// Baked POIs are written one gzipped GeoJSON file PER CATEGORY by the
// `bakePoisGeoJSON` Cloud Function (fixed bucket + path prefix + download
// token). The client fetches only the categories it shows. If a category's
// file hasn't been baked yet the fetch 404s and that category stays empty.
const BAKED_POIS_BUCKET = 'venue-report-card.firebasestorage.app';
const BAKED_POIS_TOKEN = 'pois-baked-v1';
const BAKED_POIS_PREFIX = 'baked/pois/';
const buildBakedPoiUrl = (category) =>
  `https://firebasestorage.googleapis.com/v0/b/${BAKED_POIS_BUCKET}/o/${encodeURIComponent(
    `${BAKED_POIS_PREFIX}${category}.geojson`
  )}?alt=media&token=${BAKED_POIS_TOKEN}`;
const BAKED_POIS_SOURCE_ID = 'baked-pois-source';
const BAKED_POIS_CLUSTER_LAYER = 'baked-pois-clusters';
const BAKED_POIS_CLUSTER_COUNT_LAYER = 'baked-pois-cluster-count';
const BAKED_POIS_POINT_LAYER = 'baked-pois-unclustered';

/** Calendar MAP tab "Layer your route" toggles. VENUES drives showVenues; the rest hide map layers by id prefix. */
const ROUTE_LAYER_BUTTONS = [
  { key: 'venues', label: 'VENUES' },
  { key: 'resources', label: 'RESOURCES' },
  { key: 'travel', label: 'TRAVEL' },
  { key: 'food', label: 'FRIDGES & FARMS' },
  { key: 'directory', label: 'DIRECTORY' },
  { key: 'contacts', label: 'SAVED CONTACTS' },
];
const ROUTE_LAYER_ID_PREFIXES = {
  resources: ['resource-areas-', 'screenprinters-', 'route-preview-resources', 'route-preview-flyers'],
  travel: ['poi-', 'baked-pois-', 'transit-', 'bike-lanes', 'bus-call-', 'route-preview-travel'],
  food: ['farm-corridor-'],
  directory: ['directory-posts-', 'route-preview-directory'],
  contacts: ['user-locations-'],
};
/** Side-rail item id -> search-radius section (Fridges & Farms draws its own corridor). */
const RAIL_ITEM_RADIUS_SECTION = {
  map: 'venues',
  leaf: 'resources',
  plane: 'travel',
  directory: 'directory',
  school: 'learn',
  poster: 'flyers',
};
/** Preview pin colors for each section's "what's along the route" matches. */
const ROUTE_PREVIEW_COLORS = {
  resources: '#3d7fe6',
  travel: '#e67e22',
  directory: '#8e44ad',
  learn: '#27ae60',
  flyers: '#d35400',
};
const ROUTE_RADIUS_CORRIDOR_SOURCE = 'route-radius-corridor';
const ROUTE_RADIUS_CORRIDOR_FILL = 'route-radius-corridor-fill';
const ROUTE_RADIUS_CORRIDOR_LINE = 'route-radius-corridor-line';
const escapePopupHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const routeLayerKeyForMapLayer = (layerId) =>
  Object.keys(ROUTE_LAYER_ID_PREFIXES).find((key) =>
    ROUTE_LAYER_ID_PREFIXES[key].some((prefix) => layerId.startsWith(prefix))
  ) || null;

const getPoiIconSizeByZoom = (zoom) => {
  if (zoom < POI_ZOOM_TINY_MAX) return 0.10;
  if (zoom < POI_ZOOM_MID_MAX) return 0.25;
  return 0.4;
};

const getPoiQueryLimitByZoom = (zoom) => {
  if (zoom < POI_ZOOM_TINY_MAX) return 1000;
  if (zoom < POI_ZOOM_MID_MAX) return 2000;
  return 400; // "limit to a few" for close zooms
};

function ensurePoiCategoryMapImage(mapboxMap, categoryId, color, glyph) {
  if (!mapboxMap || typeof mapboxMap.hasImage !== 'function' || typeof mapboxMap.addImage !== 'function') return;
  const imageId = `vrc-poi-v${POI_MAP_IMAGE_VERSION}-${categoryId}`;
  if (mapboxMap.hasImage(imageId)) return;
  const imageData = createPoiCategoryImageData(color, glyph);
  if (!imageData) return;
  try {
    mapboxMap.addImage(imageId, imageData, { pixelRatio: 1 });
  } catch (e) {
    console.warn('addImage failed for POI category', categoryId, e);
  }
}

const EDUCATION_HAT_IMAGE_ID = 'vrc-education-hat-v2';

/** Transparent graduate-cap glyph — Mapbox text glyphs cannot render 🎓. */
function createEducationHatImageData(size = 96) {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.clearRect(0, 0, size, size);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Fill most of the canvas so the emoji reads as a cap on the pin.
  const emojiPx = Math.floor(size * 0.92);
  ctx.font = `${emojiPx}px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", "Twemoji Mozilla", sans-serif`;
  ctx.fillText('🎓', size / 2, size / 2 + size * 0.04);
  return ctx.getImageData(0, 0, size, size);
}

function ensureEducationHatMapImage(mapboxMap) {
  if (!mapboxMap || typeof mapboxMap.hasImage !== 'function' || typeof mapboxMap.addImage !== 'function') return;
  // Drop older outlined asset if a previous session registered it.
  ['vrc-education-hat', EDUCATION_HAT_IMAGE_ID].forEach((id) => {
    if (id !== EDUCATION_HAT_IMAGE_ID && mapboxMap.hasImage(id)) {
      try { mapboxMap.removeImage(id); } catch (_) { /* ignore */ }
    }
  });
  if (mapboxMap.hasImage(EDUCATION_HAT_IMAGE_ID)) return;
  const imageData = createEducationHatImageData();
  if (!imageData) return;
  try {
    mapboxMap.addImage(EDUCATION_HAT_IMAGE_ID, imageData, { pixelRatio: 2 });
  } catch (e) {
    console.warn('addImage failed for education hat', e);
  }
}

/** Graduate hat sitting on top of venue pins that have an education resource. */
function ensureEducationHatLayer(mapboxMap) {
  if (!mapboxMap || !mapboxMap.getSource?.('venues-source')) return;
  ensureEducationHatMapImage(mapboxMap);
  const filter = ['all', ['!', ['has', 'point_count']], ['==', ['get', 'hasEducation'], 1]];
  const layout = {
    'icon-image': EDUCATION_HAT_IMAGE_ID,
    // Sized to sit as a cap on the circle pin (pin radius ~7–10px).
    'icon-size': [
      'interpolate', ['linear'], ['zoom'],
      5, 0.55,
      8, 0.72,
      11, 0.9,
      14, 1.05,
    ],
    'icon-anchor': 'bottom',
    // Pull down so the brim rests on the pin, not floating above it.
    'icon-offset': [0, -2],
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
  };
  if (mapboxMap.getLayer('education-hat')) {
    try {
      mapboxMap.setFilter('education-hat', filter);
      Object.entries(layout).forEach(([key, value]) => {
        mapboxMap.setLayoutProperty('education-hat', key, value);
      });
      mapboxMap.setLayoutProperty('education-hat', 'visibility', 'visible');
      mapboxMap.moveLayer('education-hat');
    } catch (_) { /* style not ready */ }
    return;
  }
  mapboxMap.addLayer({
    id: 'education-hat',
    type: 'symbol',
    source: 'venues-source',
    filter,
    minzoom: 5,
    layout,
  });
  try {
    // Keep hat above pin circles / eco ring.
    if (mapboxMap.getLayer('unclustered-point')) {
      mapboxMap.moveLayer('education-hat');
    }
  } catch (_) { /* style not ready */ }
}

export function TourPlanner({ navigation, route }) {
  const { user: userData } = useUser();
  const { theme, isDarkMode } = useTheme();
  const { showConfirmation, showPopup } = usePopup();
  const { headerHeight, expenseTrackerOpen } = useAppLayout();
  const calendarEmbed = Boolean(route?.params?.calendarEmbed);
  const calendarDateKey = String(route?.params?.calendarDateKey || '').trim();

  // Refs so map click handlers always read the current theme without needing re-registration
  const isDarkModeRef = useRef(isDarkMode);
  const themeRef = useRef(theme);
  useEffect(() => { isDarkModeRef.current = isDarkMode; }, [isDarkMode]);
  useEffect(() => { themeRef.current = theme; }, [theme]);
  useEffect(() => {
    injectTourPlannerInputStyles(isDarkMode, theme);
  }, [isDarkMode, theme]);

  // Generate themed styles using useMemo to avoid recalculation on every render
  const styles = useMemo(() => getThemedStyles(theme, isDarkMode), [theme, isDarkMode]);

  // Dev-only logging to reduce overhead in production/hot paths
  const devLog = useCallback((...args) => {
    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      console.log(...args);
    }
  }, []);

  const [venues, setVenues] = useState([]);
  const [tourVenues, setTourVenues] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [mapLoaded, setMapLoaded] = useState(false);
  const [tourName, setTourName] = useState(null);
  const [isDragging, setIsDragging] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [clusterVenues, setClusterVenues] = useState([]);
  const mapContainer = useRef(null);
  const mapStageRef = useRef(null); // outer shell: map + rails + tools (for Netflix-style fullscreen)
  const mapFullscreenCssOnlyRef = useRef(false);
  const [mapContainerMounted, setMapContainerMounted] = useState(false); // set by ref callback so map init effect runs when div is in DOM
  const mapInitializedRef = useRef(false); // ensures map init runs exactly once
  const map = useRef(null);
  const draw = useRef(null); // Reference for MapboxDraw instance
  const routeCache = useRef({}); // Cache for route data to avoid redundant API calls
  const venuesRef = useRef([]); // Ref to track current venues for click handlers
  const filteredVenuesRef = useRef([]);
  const hasVenueRevealCriteriaRef = useRef(false);
  const handleVenueSelectRef = useRef(null); // Ref so map click handler always calls latest handleVenueSelect
  const addUserLocationToTourHandlerRef = useRef(null); // Ref for "Add to tour" from user-location map popup (set after userLocationToVenue/addVenueToTour exist)
  const addTransitStopToTourRef = useRef(null); // Ref for "Add to day plan" from transit stop map popup
  const auth = getAuth();
  // Latest map-action gate; map popup globals are bound once, so they read it through a ref.
  const mapGateRef = useRef(() => true);

  // Add POI filter states
  const [showHotels, setShowHotels] = useState(false);
  const [showGasStations, setShowGasStations] = useState(false);
  const TRAVEL_LODGING_POI_CATEGORIES = useMemo(() => ([
    'rv_parking',
    'rest_stop',
    'hotel',
    // airport / train_station / bus_station are handled by the Transit filter, not POI mode
  ]), []);
  const HEALTH_WELLNESS_POI_CATEGORIES = useMemo(() => ([
    'clinic',
    'spa',
    'massage_parlor',
    'gym',
    'rock_climbing_gym',
  ]), []);
  const PARKS_REC_POI_CATEGORIES = useMemo(() => ([
    'fishing_destination',
    'park',
    'golf_course',
  ]), []);
  // Pin-mode groups — selecting modes/chips is OR (union). Venue filters stay AND.
  const POI_PIN_MODE_CATEGORIES = useMemo(() => ({
    travel: TRAVEL_LODGING_POI_CATEGORIES,
    wellness: HEALTH_WELLNESS_POI_CATEGORIES,
    parks: PARKS_REC_POI_CATEGORIES,
  }), [TRAVEL_LODGING_POI_CATEGORIES, HEALTH_WELLNESS_POI_CATEGORIES, PARKS_REC_POI_CATEGORIES]);
  const POI_CATEGORY_META = useMemo(() => ({
    rv_parking: { label: 'RV Parking', color: '#f1c40f', abbr: 'RV', icon: '🛻' },
    rest_stop: { label: 'Rest Stops', color: '#d35400', abbr: 'RS', icon: '🛣️' },
    hotel: { label: 'Hotels', color: '#3498db', abbr: 'H', icon: '🏨' },
    border_checkpoint: { label: 'Border', color: '#e74c3c', abbr: 'X', icon: '🚧' },
    gas_station: { label: 'Gas', color: '#e67e22', abbr: 'G', icon: '⛽' },
    clinic: { label: 'Walk-in Clinics', color: '#2ecc71', abbr: 'CL', icon: '🏥' },
    museum: { label: 'Museums', color: '#9b59b6', abbr: 'M', icon: '🏛️' },
    park: { label: 'Parks', color: '#27ae60', abbr: 'P', icon: '🌳' },
    gym: { label: 'Gyms', color: '#2980b9', abbr: 'Y', icon: '🏋️' },
    spa: { label: 'Spas', color: '#e84393', abbr: 'Z', icon: '🧖' },
    massage_parlor: { label: 'Massage', color: '#f39c12', abbr: 'MS', icon: '💆' },
    barbershop: { label: 'Barbershops', color: '#8e44ad', abbr: 'C', icon: '💈' },
    rock_climbing_gym: { label: 'Climbing', color: '#6d4c41', abbr: 'K', icon: '🧗' },
    fishing_destination: { label: 'Fishing', color: '#00b894', abbr: 'F', icon: '🎣' },
    golf_course: { label: 'Golf Courses', color: '#1abc9c', abbr: 'GC', icon: '⛳' },
  }), []);
  const getTourStopTypeLabel = useCallback((venue) => {
    if (!venue) return 'VENUE';
    if (venue.isTransitStop || String(venue.id || '').startsWith('transit-')) {
      if (venue.transitType === 'rail') return 'TRAIN STATION';
      if (venue.transitType === 'airport') return 'AIRPORT';
      if (venue.transitType === 'ferry') return 'FERRY TERMINAL';
      return 'BUS STOP';
    }
    if (venue.isUserLocation || String(venue.id || '').startsWith('userloc-')) {
      return 'USER MADE LOCATION';
    }
    const isPoi = venue.venueType === 'POI' || String(venue.id || '').startsWith('poi-');
    if (!isPoi) return 'VENUE';
    const cat = String(venue.poiCategory || '').trim();
    if (TRAVEL_LODGING_POI_CATEGORIES.includes(cat)) return 'TRAVEL + LODGING';
    if (HEALTH_WELLNESS_POI_CATEGORIES.includes(cat)) return 'HEALTH + WELLNESS';
    if (PARKS_REC_POI_CATEGORIES.includes(cat)) return 'PARKS + REC';
    const poiLabel = String(POI_CATEGORY_META[cat]?.label || '').trim();
    return poiLabel ? `POI · ${poiLabel.toUpperCase()}` : 'POI';
  }, [
    TRAVEL_LODGING_POI_CATEGORIES,
    HEALTH_WELLNESS_POI_CATEGORIES,
    PARKS_REC_POI_CATEGORIES,
    POI_CATEGORY_META,
  ]);

  const getStopAccentColor = useCallback((venue) => {
    if (!venue) return '#888888';
    if (isVenuesDbTourStop(venue)) return '#4a90d9';
    if (venue.isUserLocation || String(venue.id || '').startsWith('userloc-')) return '#e8a020';
    if (venue.isTransitStop || String(venue.id || '').startsWith('transit-')) {
      if (venue.transitType === 'airport') return '#3b82f6';
      if (venue.transitType === 'rail') return '#8b5cf6';
      return '#10b981';
    }
    const cat = String(venue.poiCategory || '').trim();
    if (TRAVEL_LODGING_POI_CATEGORIES.includes(cat)) return '#f59e0b';
    if (HEALTH_WELLNESS_POI_CATEGORIES.includes(cat)) return '#ec4899';
    if (PARKS_REC_POI_CATEGORIES.includes(cat)) return '#22c55e';
    return '#14b8a6';
  }, [TRAVEL_LODGING_POI_CATEGORIES, HEALTH_WELLNESS_POI_CATEGORIES, PARKS_REC_POI_CATEGORIES]);

  const masterTourUi = useMasterTourCalendarUi();

  const [activePoiCategories, setActivePoiCategories] = useState([]);
  const [poiFeaturesByCategory, setPoiFeaturesByCategory] = useState({});
  const [restrictPoisToTourRoute, setRestrictPoisToTourRoute] = useState(false);
  const [selectedLearnVenueIds, setSelectedLearnVenueIds] = useState([]);
  const restrictPoisToTourRouteRef = useRef(false);
  const tourDaysForPoiRef = useRef([]);
  restrictPoisToTourRouteRef.current = restrictPoisToTourRoute;

  // Add display options state
  const [displayMode, setDisplayMode] = useState('pins'); // 'pins', 'hidden' ('clusters' retired from the UI)
  const [showEcoRing, setShowEcoRing] = useState(true); // green sustainability ring on eco venues
  const [clusterRadius, setClusterRadius] = useState(50);
  const [clusterMaxZoom, setClusterMaxZoom] = useState(12);
  const [mapBounds, setMapBounds] = useState(null);
  const [mapCenter, setMapCenter] = useState(null); // { lng: number, lat: number }
  const [filteredVenues, setFilteredVenues] = useState([]); // applyFilters + coord + sort (no bounds)
  const [venuesInBounds, setVenuesInBounds] = useState([]); // filteredVenues within current map bounds
  const venuesToShowRef = useRef([]); // current list passed to map (for updateVenuesOnMap(null))

  // Location & zoom tracking state
  const [userLocation, setUserLocation] = useState(null);
  const [locationRequested, setLocationRequested] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(3);
  const [isZooming, setIsZooming] = useState(false);
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);

  // Initial loading state
  const [isInitializing, setIsInitializing] = useState(true);

  // When true, left panel and map show only venues in the current tour (tour view mode). When false, show all venues (edit mode).
  const [showOnlyTourVenues, setShowOnlyTourVenues] = useState(false);

  // Panel visibility states — collapsed (toggled down) on load
  const [venueListVisible, setVenueListVisible] = useState(false);
  const [tourStopsVisible, setTourStopsVisible] = useState(false);

  // Add state for selected venue
  const [selectedVenueId, setSelectedVenueId] = useState(null);
  const [showAccessibilityFlagModal, setShowAccessibilityFlagModal] = useState(false);
  const [accessibilityFlagVenue, setAccessibilityFlagVenue] = useState(null);
  const [accessibilityFlagNotes, setAccessibilityFlagNotes] = useState('');
  const [accessibilityFlagSubmitting, setAccessibilityFlagSubmitting] = useState(false);
  const [accessibilityFlagStep, setAccessibilityFlagStep] = useState(1);
  const [accessibilityFlagArea, setAccessibilityFlagArea] = useState(null); // 'outside' | 'inside' | 'both'
  const [accessibilityVenueSearch, setAccessibilityVenueSearch] = useState('');
  const openAccessibilityFlagModalRef = useRef(null);

  // Weather panel state — present but collapsed (toggled down) by default; expand via header tap
  const [showWeatherPanel, setShowWeatherPanel] = useState(false);
  const [weatherExpanded, setWeatherExpanded] = useState(false);
  const [weatherPanelHeight, setWeatherPanelHeight] = useState(52);
  const [radarEnabled, setRadarEnabled] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  const [mobileViewportHeight, setMobileViewportHeight] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 800
  );
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  // Multi-open venue filter sections (Figma: Affiliate / Features / Resources / Food)
  const [openFilterSections, setOpenFilterSections] = useState({
    affiliate: true,
    features: false,
    resources: false,
    food: false,
  });
  const [showFoodBanks, setShowFoodBanks] = useState(false);
  const [showFarms, setShowFarms] = useState(false);
  const [farmSearchRadiusMiles, setFarmSearchRadiusMiles] = useState(DEFAULT_FARM_RADIUS_MILES);
  const [farmCorridorHubs, setFarmCorridorHubs] = useState([]);
  const [farmCorridorLoading, setFarmCorridorLoading] = useState(false);
  const [farmCorridorMessage, setFarmCorridorMessage] = useState('');
  const [farmSearchDayId, setFarmSearchDayId] = useState(null);
  const [farmSearchLegId, setFarmSearchLegId] = useState('all');
  // Map view mode: 'map' = normal basemap + pins, 'concentration' = affiliate choropleth.
  const [mapViewMode, setMapViewMode] = useState('map');
  const mapViewModeRef = useRef('map'); // latest view mode for async retries
  // Which concentration dataset is active (only 'affiliate' for now; room to grow).
  const [concentrationType, setConcentrationType] = useState('affiliate');
  // Whether the concentration "type" settings (e.g. Count affiliates) are expanded.
  const [showConcentrationSettings, setShowConcentrationSettings] = useState(true);
  // Which affiliates the concentration map counts (separate from the pin/affiliate filter).
  const [concentrationBucketsOn, setConcentrationBucketsOn] = useState(() =>
    Object.fromEntries(AFFILIATE_BUCKETS.map((b) => [b.id, false])),
  );
  const zctaFetchTokenRef = useRef(0); // guards against out-of-order ZIP-tier fetches
  const zctaDebounceRef = useRef(null);
  const resourceAreaTokenRef = useRef(0);
  const intlFetchTokenRef = useRef(0); // guards against out-of-order international-tier fetches
  const intlDebounceRef = useRef(null);
  const filterPanelRef = useRef(null);

  // Zoom-out hint for disabled POI mode buttons
  const [showZoomHint, setShowZoomHint] = useState(false);
  const zoomHintTimerRef = useRef(null);

  // Animation values for panel sliding
  const venueListAnim = useRef(new Animated.Value(1)).current;
  const tourStopsAnim = useRef(new Animated.Value(0)).current;

  // Add state for days organization
  const [tourDays, setTourDays] = useState([{
    id: 'day-1',
    name: 'Day 1',
    venues: []
  }]);
  tourDaysForPoiRef.current = tourDays;
  const [draggedVenue, setDraggedVenue] = useState(null);
  const [dragOverDayId, setDragOverDayId] = useState(null);
  const [dragInsertAt, setDragInsertAt] = useState(null); // { dayId, index } for insert-line indicator
  const lastDragOverIdRef = useRef(null); // only update dragInsertAt when over id changes to prevent flicker
  const [activeDragId, setActiveDragId] = useState(null); // dnd-kit: id of item being dragged (for DragOverlay)
  const [routesCalculating, setRoutesCalculating] = useState(false); // true while drawing routes / calculating route info
  const [editingDayId, setEditingDayId] = useState(null);
  const [tourVenueOptionsId, setTourVenueOptionsId] = useState(null); // venue.id when options dropdown is open
  const [newDayName, setNewDayName] = useState('');
  /** Day date picker modal: which tour day id is being edited */
  const [dayDatePickerDayId, setDayDatePickerDayId] = useState(null);
  const [dayDatePickerYear, setDayDatePickerYear] = useState('');
  const [dayDatePickerMonth, setDayDatePickerMonth] = useState(1);
  const [dayDatePickerDay, setDayDatePickerDay] = useState(1);
  const [interDayLegModes, setInterDayLegModes] = useState([]); // 'drive'|'train'|'flight' per gap between days (length = tourDays.length - 1)
  // Bus call: { lat, lng, name, time, date } stored per day. pickingBusCallForDayId = which day is awaiting a map click.
  const [pickingBusCallForDayId, setPickingBusCallForDayId] = useState(null);
  const [busCallFormDayId, setBusCallFormDayId] = useState(null); // day whose bus-call form is open

  const openDayDatePicker = useCallback((day) => {
    const raw = day?.scheduledDate != null ? String(day.scheduledDate).trim() : '';
    if (raw && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split('-').map(Number);
      setDayDatePickerYear(String(y));
      setDayDatePickerMonth(m);
      setDayDatePickerDay(d);
    } else {
      const n = new Date();
      setDayDatePickerYear(String(n.getFullYear()));
      setDayDatePickerMonth(n.getMonth() + 1);
      setDayDatePickerDay(n.getDate());
    }
    setDayDatePickerDayId(day.id);
  }, []);

  const closeDayDatePicker = useCallback(() => {
    setDayDatePickerDayId(null);
  }, []);

  const applyDayDatePicker = useCallback(() => {
    const y = parseInt(String(dayDatePickerYear).trim(), 10);
    if (!Number.isFinite(y) || y < 1900 || y > 2100) {
      Alert.alert('Invalid year', 'Enter a year between 1900 and 2100.');
      return;
    }
    const dim = new Date(y, dayDatePickerMonth, 0).getDate();
    const d = Math.min(dayDatePickerDay, dim);
    const scheduledDate = `${y}-${String(dayDatePickerMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    setTourDays((prev) =>
      prev.map((x) => (x.id === dayDatePickerDayId ? { ...x, scheduledDate } : x))
    );
    setDayDatePickerDayId(null);
  }, [dayDatePickerDayId, dayDatePickerYear, dayDatePickerMonth, dayDatePickerDay]);

  const clearDayDatePicker = useCallback(() => {
    setTourDays((prev) =>
      prev.map((x) => (x.id === dayDatePickerDayId ? { ...x, scheduledDate: '' } : x))
    );
    setDayDatePickerDayId(null);
  }, [dayDatePickerDayId]);

  // Web: clear dragging state on any dragend (card's dragend can miss when drop target handles the drop)
  // Track mobile breakpoint
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setMobileViewportHeight(window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Collapse weather when day planner opens so it sits as a compact bar above the sheet.
  useEffect(() => {
    if (isMobile && tourStopsVisible) {
      setWeatherExpanded(false);
    }
  }, [tourStopsVisible, isMobile]);

  // Inject real CSS for Mapbox glass popups (StyleSheet.create selectors don't affect the DOM)
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    const id = 'vrc-glass-popup-styles';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      .glass-popup .mapboxgl-popup-content,
      .dark-popup .mapboxgl-popup-content,
      .light-popup .mapboxgl-popup-content {
        background: rgba(6,9,22,0.84) !important;
        backdrop-filter: blur(28px) saturate(180%) !important;
        -webkit-backdrop-filter: blur(28px) saturate(180%) !important;
        border: 1px solid rgba(255,255,255,0.12) !important;
        border-radius: 14px !important;
        box-shadow: 0 16px 56px rgba(0,0,0,0.6) !important;
        padding: 0 !important;
        min-width: 230px !important;
        overflow: hidden !important;
        color: rgba(255,255,255,0.9) !important;
      }
      .glass-popup .mapboxgl-popup-tip,
      .dark-popup .mapboxgl-popup-tip,
      .light-popup .mapboxgl-popup-tip {
        border-top-color: rgba(6,9,22,0.84) !important;
        border-bottom-color: rgba(6,9,22,0.84) !important;
      }
      .glass-popup .mapboxgl-popup-close-button,
      .dark-popup .mapboxgl-popup-close-button,
      .light-popup .mapboxgl-popup-close-button {
        color: rgba(255,255,255,0.55) !important;
        font-size: 20px !important;
        padding: 6px 10px !important;
        background: transparent !important;
        border: none !important;
        line-height: 1 !important;
        right: 2px !important;
        top: 2px !important;
      }
      .glass-popup .mapboxgl-popup-close-button:hover,
      .dark-popup .mapboxgl-popup-close-button:hover,
      .light-popup .mapboxgl-popup-close-button:hover {
        background: rgba(255,255,255,0.1) !important;
        border-radius: 6px !important;
      }

      /* Glass-style Mapbox controls */
      .mapboxgl-ctrl-group {
        background: rgba(8,10,20,0.72) !important;
        backdrop-filter: blur(18px) saturate(160%) !important;
        -webkit-backdrop-filter: blur(18px) saturate(160%) !important;
        border: 1px solid rgba(255,255,255,0.12) !important;
        border-radius: 10px !important;
        box-shadow: 0 4px 16px rgba(0,0,0,0.4) !important;
        overflow: hidden;
      }
      .mapboxgl-ctrl-group button {
        background: transparent !important;
        border-bottom: 1px solid rgba(255,255,255,0.08) !important;
        width: 30px !important;
        height: 30px !important;
      }
      .mapboxgl-ctrl-group button:last-child {
        border-bottom: none !important;
      }
      .mapboxgl-ctrl-group button .mapboxgl-ctrl-icon {
        filter: invert(1) opacity(0.65);
      }
      .mapboxgl-ctrl-group button:hover .mapboxgl-ctrl-icon {
        filter: invert(1) opacity(1);
      }
      /* Bottom custom controls stay above map attribution */
      .mapboxgl-ctrl-bottom-right,
      .mapboxgl-ctrl-bottom-left {
        z-index: 4;
      }
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    const handleDocumentDragEnd = () => {
      setDragOverDayId(null);
      setDragInsertAt(null);
      setIsDragging(null);
    };
    document.addEventListener('dragend', handleDocumentDragEnd);
    return () => document.removeEventListener('dragend', handleDocumentDragEnd);
  }, []);

  // Add state for active day
  const [activeDayId, setActiveDayId] = useState('day-1');
  const activeDayIdRef = useRef(activeDayId);
  /** Populated after `addVenueToDay` is defined — avoids TDZ when global POI handlers register earlier in the file. */
  const addVenueToDayRef = useRef(null);
  const assignFoodHubRef = useRef(null);
  const currentTourIdRef = useRef(null);
  const farmSearchTokenRef = useRef(0);
  // Last corridor toast (route + radius + outcome). Day edits re-run the
  // search, so the same warning would otherwise cover every success toast.
  const farmCorridorToastKeyRef = useRef('');
  const farmRadiusDebounceRef = useRef(null);
  useEffect(() => {
    activeDayIdRef.current = activeDayId;
  }, [activeDayId]);

  // Check if a venue was passed via route params to be added to the tour
  const venueFromRoute = route.params?.addVenue;

  // Check user permissions
  const isDevelopment = false
  // const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
  const isAdmin = userData && (userData.userType === 'admin' || userData.isAdmin === true);
  const { flags: tourPlannerFlags, loading: flagsLoading } = useFeatureFlags();
  const userLocationsFilterVisible = useUserLocationsFilterVisible();
  const deployTarget = getDeployTarget();
  const tourPlannerFeatureOn =
    isAdmin ||
    isDevelopment ||
    isTourPlannerFeatureEnabledForDeploy(tourPlannerFlags, deployTarget);
  // Admins/dev always; Map + Calendar subscribers when feature flag allows
  const canUseTourPlanner =
    tourPlannerFeatureOn && (isAdmin || isDevelopment || hasMapAccess(userData));
  // Lauren 2026-10-21: anyone can look at the map, even signed out. Planning tours stays paid.
  const canViewMap = tourPlannerFeatureOn;
  const canPlanTours = canUseTourPlanner;
  const [mapAccessPrompt, setMapAccessPrompt] = useState(null);
  const requireMapAction = useCallback((kind) => {
    // Gate not known yet (plan flags loading, or a signed-in profile still loading):
    // do nothing rather than prompt wrongly. Contributing doesn't depend on flags.
    if (kind === MAP_ACTION.PLAN && flagsLoading && !canPlanTours) return false;
    if (!userData?.uid && isRealUser(auth.currentUser)) return false;
    const gate = getMapActionGate({
      signedIn: !!userData?.uid || isRealUser(auth.currentUser),
      canPlanTours,
      kind,
    });
    if (gate === 'ok') return true;
    setMapAccessPrompt({ gate, kind });
    return false;
  }, [userData?.uid, auth, canPlanTours, flagsLoading]);
  mapGateRef.current = requireMapAction;
  const requireContributor = useCallback(() => mapGateRef.current(MAP_ACTION.CONTRIBUTE), []);

  // Admin-only coordinate source filter: 'all', 'mapEmbed', 'stored'
  const [coordinateSourceFilter, setCoordinateSourceFilter] = useState('all');

  // Visibility toggles (User Locations UI/filter hidden for now — force off)
  const [showVenues, setShowVenues] = useState(true);
  // const [showUserLocations, setShowUserLocations] = useState(() => {
  //   if (typeof localStorage === 'undefined') return true;
  //   const stored = localStorage.getItem('vrc-show-user-created-locations');
  //   return stored === null ? true : stored === 'true';
  // });
  const [showUserLocations, setShowUserLocations] = useState(() => Boolean(route?.params?.calendarEmbed));
  const [showScreenprinters, setShowScreenprinters] = useState(false);
  // "Layer your route" (calendar MAP tab): route layer keys switched off, and the map layers this row hid.
  const [hiddenRouteLayers, setHiddenRouteLayers] = useState(() => new Set());
  const routeLayerHiddenMapIdsRef = useRef(new Set());
  useEffect(() => {
    if (!map.current || !mapLoaded) return undefined;
    const m = map.current;
    if (typeof document !== 'undefined' && !document.getElementById('vrc-route-layer-styles')) {
      const styleEl = document.createElement('style');
      styleEl.id = 'vrc-route-layer-styles';
      styleEl.textContent = '.vrc-hide-food-markers .vrc-food-marker{display:none !important}';
      document.head.appendChild(styleEl);
    }
    // Re-run on styledata: layers are re-created as data loads. Only layers this row hid get restored,
    // so per-category visibility set elsewhere (e.g. disabled POI types) is left alone.
    const apply = () => {
      const hiddenIds = routeLayerHiddenMapIdsRef.current;
      (m.getStyle?.()?.layers || []).forEach(({ id }) => {
        const key = routeLayerKeyForMapLayer(id);
        if (!key) return;
        if (hiddenRouteLayers.has(key)) {
          if (m.getLayoutProperty(id, 'visibility') !== 'none') {
            m.setLayoutProperty(id, 'visibility', 'none');
            hiddenIds.add(id);
          }
        } else if (hiddenIds.has(id)) {
          m.setLayoutProperty(id, 'visibility', 'visible');
          hiddenIds.delete(id);
        }
      });
      m.getContainer?.()?.classList.toggle('vrc-hide-food-markers', hiddenRouteLayers.has('food'));
    };
    apply();
    m.on('styledata', apply);
    return () => m.off('styledata', apply);
  }, [mapLoaded, hiddenRouteLayers]);
  const [showDesktopFilters, setShowDesktopFilters] = useState(false);
  const [rightRailTab, setRightRailTab] = useState(null);
  const [mapSideRailOpenRequest, setMapSideRailOpenRequest] = useState(null);
  const [screenprinters, setScreenprinters] = useState([]);
  const [approvedResourceSubmissions, setApprovedResourceSubmissions] = useState([]);
  const [resourceCategoryCatalog, setResourceCategoryCatalog] = useState([
    ...RESOURCE_CATEGORIES,
    MERCH_RESOURCE_CATEGORY,
  ]);
  const [approvedFoodLocations, setApprovedFoodLocations] = useState([]);
  const [riderFoodAssignments, setRiderFoodAssignments] = useState([]);
  const [selectedScreenprinterIds, setSelectedScreenprinterIds] = useState([]);
  const [selectedResourceSubmissionIds, setSelectedResourceSubmissionIds] = useState([]);
  const [selectedResourceVenueIds, setSelectedResourceVenueIds] = useState([]);
  const [loadingResourceDirectoryIds, setLoadingResourceDirectoryIds] = useState([]);
  const [selectedDirectoryPostPins, setSelectedDirectoryPostPins] = useState([]);
  useEffect(() => {
    return subscribeResourceCategoryCatalog(
      getFirestore(),
      setResourceCategoryCatalog,
      (error) => console.error('Error loading map resource categories:', error),
    );
  }, []);
  // Transit stops fetched from Overpass API (bus stops, rail/Amtrak stations)
  const [transitStops, setTransitStops] = useState([]);
  const [showBusStops, setShowBusStops] = useState(false);
  const [showTrainStops, setShowTrainStops] = useState(false);
  const [showAirports, setShowAirports] = useState(false);
  const [showBikeLanes, setShowBikeLanes] = useState(false);
  const [bikeLanesLoading, setBikeLanesLoading] = useState(false);
  const [bikeLanesSource, setBikeLanesSource] = useState('osm'); // osm | streetlight | osm+streetlight-pending
  const bikeLanesFetchedKeyRef = useRef(null);
  const bikeLanesAbortRef = useRef(null);
  const bikeLanesDebounceRef = useRef(null);
  const showBikeLanesRef = useRef(false);
  const loadBikeLanesForMapRef = useRef(null);
  const [ecoRouteColors, setEcoRouteColors] = useState(false);
  const [transitLoading, setTransitLoading] = useState(false);
  const transitFetchedBoundsRef = useRef(null); // avoid re-fetching for same area

  // User custom locations
  const [userLocations, setUserLocations] = useState([]);
  const [publicUserLocations, setPublicUserLocations] = useState([]);
  const [addLocationMode, setAddLocationMode] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [newLocationData, setNewLocationData] = useState({
    name: '',
    notes: '',
    category: 'other',
    address: '',
    isPublic: false,
    capacity: '',
    affiliate: ''
  });

  // Address search state
  const [addressSearchQuery, setAddressSearchQuery] = useState('');
  const [addressSearchResults, setAddressSearchResults] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [showAddLocationPanel, setShowAddLocationPanel] = useState(false);
  const [pendingLocationCoords, setPendingLocationCoords] = useState(null);
  const [saveLocationLoading, setSaveLocationLoading] = useState(false);
  const [updateLocationLoading, setUpdateLocationLoading] = useState(false);
  const [mapDetailsItem, setMapDetailsItem] = useState(null); // { type: 'venue'|'userLocation', data: object } for View Details modal
  const mapDetailsAccessNotes = useApprovedAccessibilityNotes(
    mapDetailsItem?.type === 'venue' ? mapDetailsItem?.data?.id : null,
  );

  /** Approved user-submitted categories from Firestore (merged with {@link BASE_LOCATION_CATEGORIES}). */
  const [approvedLocationCategories, setApprovedLocationCategories] = useState([]);
  const [suggestCategoryLabel, setSuggestCategoryLabel] = useState('');
  const [suggestCategoryIcon, setSuggestCategoryIcon] = useState('');
  const [categorySuggestionSubmitting, setCategorySuggestionSubmitting] = useState(false);


  useEffect(() => {
    // Wait for flags: defaults have production off, so first-visit guests would bounce.
    if (!flagsLoading && !canViewMap) {
      navigation.navigate('Plans');
    }
  }, [canViewMap, flagsLoading]);

  useEffect(() => {
    const db = getFirestore();
    const q = query(
      collection(db, 'locationCategorySuggestions'),
      where('status', '==', 'approved')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({
          id: d.id,
          label: d.data().label || 'Category',
          icon: (d.data().icon && String(d.data().icon).trim()) || '📌',
        }));
        rows.sort((a, b) => a.label.localeCompare(b.label));
        setApprovedLocationCategories(rows);
      },
      (err) => console.warn('locationCategorySuggestions', err)
    );
    return () => unsub();
  }, []);

  const locationCategoriesAll = useMemo(
    () => [...BASE_LOCATION_CATEGORIES, ...approvedLocationCategories],
    [approvedLocationCategories]
  );

  const submitCategorySuggestion = useCallback(async () => {
    const label = suggestCategoryLabel.trim();
    if (label.length < 2) {
      Alert.alert('Category name', 'Enter a short name (at least 2 characters).');
      return;
    }
    if (!userData?.uid) {
      mapGateRef.current(MAP_ACTION.CONTRIBUTE);
      return;
    }
    setCategorySuggestionSubmitting(true);
    try {
      const db = getFirestore();
      const icon = (suggestCategoryIcon.trim() || '📌').slice(0, 8);
      await addDoc(collection(db, 'locationCategorySuggestions'), {
        label: label.slice(0, 80),
        icon,
        status: 'pending',
        requestedByUid: userData.uid,
        requestedByEmail: userData.email || userData.username || '',
        requestedAt: serverTimestamp(),
      });
      setSuggestCategoryLabel('');
      setSuggestCategoryIcon('');
      Alert.alert(
        'Submitted',
        'Thanks! An admin will review your category. Once approved, it will appear in this list for everyone.'
      );
    } catch (e) {
      console.error(e);
      Alert.alert('Error', e.message || 'Could not submit suggestion.');
    } finally {
      setCategorySuggestionSubmitting(false);
    }
  }, [suggestCategoryLabel, suggestCategoryIcon, userData]);

  // Location type options (affiliate options from VenueList)
  const LOCATION_TYPE_OPTIONS = [
    { label: 'DIY', value: 'DIY' },
    { label: 'Independent', value: 'Independent' },
    { label: 'Record Shop', value: 'Record Shop' },
    { label: 'Skate', value: 'Skate' },
    { label: 'Art Gallery', value: 'Art Gallery' },
    { label: 'Event Space', value: 'Event Space' },
    { label: 'Nonprofit', value: 'Nonprofit' },
    { label: 'Brewery', value: 'Brewery' },
    { label: 'Coffee Shop', value: 'Coffee Shop' },
    { label: 'Tea House', value: 'Tea House' },
    { label: 'Restaurant', value: 'Restaurant' }
  ];

  // Add new state for map style
  const [mapStyle, setMapStyle] = useState(isDarkMode ? 'dark-v11' : 'streets-v11');
  // Track if user manually overrode the map style via the map's own toggle
  const [userOverrodeMapStyle, setUserOverrodeMapStyle] = useState(false);

  // Sync map style with page theme when page theme changes (unless user manually overrode)
  useEffect(() => {
    if (!userOverrodeMapStyle && map.current) {
      const newStyle = isDarkMode ? 'dark-v11' : 'streets-v11';
      if (mapStyle !== newStyle) {
        setMapStyle(newStyle);
        map.current.setStyle(`mapbox://styles/mapbox/${newStyle}`);

        // Re-add custom layers after style change
        map.current.once('style.load', () => {
          const layers = map.current.getStyle().layers;
          const labelLayerId = layers.find(
            (layer) => layer.type === 'symbol' && layer.layout['text-field']
          )?.id;

          if (!map.current.getLayer('3d-buildings')) {
            map.current.addLayer(
              {
                'id': '3d-buildings',
                'source': 'composite',
                'source-layer': 'building',
                'filter': ['==', 'extrude', 'true'],
                'type': 'fill-extrusion',
                'minzoom': 14,
                'paint': {
                  'fill-extrusion-color': isDarkMode
                    ? [
                      'interpolate',
                      ['linear'],
                      ['get', 'height'],
                      0, '#1a1a2e',
                      50, '#16213e',
                      100, '#0f3460',
                      200, '#1a1a2e'
                    ]
                    : [
                      'interpolate',
                      ['linear'],
                      ['get', 'height'],
                      0, '#e8e8e8',
                      50, '#d4d4d4',
                      100, '#c0c0c0',
                      200, '#a8a8a8'
                    ],
                  'fill-extrusion-height': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    14, 0,
                    14.5, ['get', 'height']
                  ],
                  'fill-extrusion-base': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    14, 0,
                    14.5, ['get', 'min_height']
                  ],
                  'fill-extrusion-opacity': 0.85
                }
              },
              labelLayerId
            );
          }
        });
      }
    }
  }, [isDarkMode, userOverrodeMapStyle]);

  // Add new state for hover info
  const [hoverInfo, setHoverInfo] = useState(null);

  // Add filter states (affiliate + features match VenueList.js)
  // REQ-104: same shape as before plus video / fanAccess / artistAccess (utils/venueFilters.js).
  // resources = accessibility | safety | environment | education (resource_submissions)
  const { filters, setFilters } = useVenueFilters('tourPlanner', {
    initial: { venueType: 'all', hasParking: 'all' },
  });
  const venueFilterDrawer = useVenueFilterDraft(filters, setFilters);
  const venueFilterButtonRef = useRef(null);
  const venueFilterCountCacheRef = useRef({ deps: null, count: 0 });
  // REQ-104 phase B: Radio layer (own pins, independent of Pins / Hide, which only hides venues).
  const [radioLayerOn, setRadioLayerOn] = useState(false);
  const [radioStations, setRadioStations] = useState(null);
  const radioPopupRef = useRef(null);
  const addLocationModeRef = useRef(addLocationMode);
  addLocationModeRef.current = addLocationMode;
  useEffect(() => {
    if (!radioLayerOn || radioStations) return undefined;
    let cancelled = false;
    loadRadioStations(RADIO_LAYER_URL)
      .then((data) => { if (!cancelled) setRadioStations(data); })
      .catch((err) => {
        console.warn('[radio] load failed', err);
        if (cancelled) return;
        setRadioLayerOn(false);
        setToast({ message: 'Radio stations could not load. Try again in a moment.', type: 'error' });
      });
    return () => { cancelled = true; };
  }, [radioLayerOn, radioStations]);
  useEffect(() => {
    const m = map.current;
    if (!mapLoaded || !m) return undefined;
    const sync = () => syncRadioLayer(m, {
      visible: radioLayerOn && Boolean(radioStations),
      data: radioStations,
      color: LAYER_COLOR.radio,
      yieldToLayers: ['unclustered-point', 'clusters', 'screenprinters-layer', 'user-locations-layer'],
      onClickFeature: (feature) => {
        if (addLocationModeRef.current) return; // that click places the new location
        if (radioPopupRef.current) { try { radioPopupRef.current.remove(); } catch (_) {} }
        radioPopupRef.current = new mapboxgl.Popup({ closeOnClick: true, className: 'dark-popup', maxWidth: '280px', offset: 10 })
          .setLngLat(feature.geometry.coordinates)
          .setHTML(buildRadioPopupHtml(feature.properties))
          .addTo(m);
      },
    });
    sync();
    if (!radioLayerOn && radioPopupRef.current) { try { radioPopupRef.current.remove(); } catch (_) {} }
    // A style switch (dark mode, satellite) wipes custom sources; put the layer back.
    m.on('styledata', sync);
    return () => m.off('styledata', sync);
  }, [mapLoaded, radioLayerOn, radioStations]);
  const [mapDisplayModeRailBox, setMapDisplayModeRailBox] = useState(null); // { width, height } of Pins / Hide
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  // Approved community resources index (same source as VenueScreen): venue / city / state scoped
  const [venueResourceIndex, setVenueResourceIndex] = useState({
    byVenueId: new Map(),
    byState: new Map(),
    byCityState: new Map(),
  });

  // Affiliate + feature options (match VenueList.js).
  // Live Nation / AEG / Independent / Other carry the concentration-map colors.
  const affiliateOptions = [
    { label: 'Live Nation', value: 'Live Nation' },
    { label: 'AEG', value: 'AEG' },
    { label: 'Independent', value: 'Independent' },
    { label: 'Art Gallery', value: 'Art Gallery' },
    { label: 'Bar', value: 'Bar' },
    { label: 'Brewery', value: 'Brewery' },
    { label: 'City', value: 'City' },
    { label: 'Coffee Shop', value: 'Coffee Shop' },
    { label: 'DIY', value: 'DIY' },
    { label: 'Event Space', value: 'Event Space' },
    { label: 'Nonprofit', value: 'Nonprofit' },
    { label: 'Record Shop', value: 'Record Shop' },
    { label: 'Restaurant', value: 'Restaurant' },
    { label: 'Skate', value: 'Skate' },
    { label: 'Other', value: 'Other' }
  ];

  // Color for an affiliate value, matching the concentration-map buckets.
  const affiliateChipColor = (value) =>
    AFFILIATE_BUCKETS.find((b) => b.id === classifyAffiliateBucket(value))?.color || '#5a8feb';
  const featureOptions = [
    { label: 'Artist Accessibility Known', value: 'hasADA' },
    { label: 'Merch: 100% to Artist', value: 'merch100' },
    { label: 'Stage: Floor', value: 'stageFloor' },
    { label: 'Venues with tech spec links', value: 'hasSpecLink' },
    { label: 'Backstage', value: 'hasBackstage' },
    { label: 'Backline', value: 'hasBackline' },
    { label: 'Parking', value: 'hasParking' },
    { label: 'Laundry', value: 'hasLaundry' },
    { label: 'Lodging', value: 'hasLodging' },
    { label: 'Rehearsal Space', value: 'hasRehearsal' },
    { label: 'Pinball!', value: 'hasPinball' },
    { label: 'Shower', value: 'hasShower' },
    { label: 'Shore Power', value: 'hasShorePower' },
    { label: 'Vegan Food Available', value: 'hasVeganFood' }
  ];

  /** Same visibility rules as VenueScreen approved resources (venue / city / state scope). */
  const venueHasResourceCategory = useCallback((venue, categoryId) => (
    venueHasCategoryInResourceIndex(venueResourceIndex, venue, categoryId)
  ), [venueResourceIndex]);

  // drawClusters is captured by a long-lived debounce — read index via ref to avoid stale hats.
  const venueResourceIndexRef = useRef(venueResourceIndex);
  useEffect(() => { venueResourceIndexRef.current = venueResourceIndex; }, [venueResourceIndex]);

  const venueHasEducationResource = useCallback((venue) => (
    venueHasCategoryInResourceIndex(
      venueResourceIndexRef.current || venueResourceIndex,
      venue,
      'education',
    )
  ), [venueResourceIndex]);

  const resourceSubmissionAppliesToVenue = useCallback((submission, venue) => {
    if (!submission || !venue) return false;
    const venueId = venue.id != null ? String(venue.id) : '';
    const submissionVenueId = submission.venueId != null ? String(submission.venueId) : '';
    const venueState = String(venue.state || '').trim().toLowerCase();
    const submissionState = String(submission.venueState || '').trim().toLowerCase();
    const venueCity = String(venue.city || '').trim().toLowerCase();
    const submissionCity = String(submission.venueCity || '').trim().toLowerCase();

    if (submission.scope === 'state') {
      return Boolean(venueState && submissionState && venueState === submissionState);
    }
    if (submission.scope === 'city') {
      return Boolean(
        venueCity &&
        submissionCity &&
        venueState &&
        submissionState &&
        venueCity === submissionCity &&
        venueState === submissionState
      );
    }
    return Boolean(venueId && submissionVenueId && venueId === submissionVenueId);
  }, []);

  // REQ-104: matching lives in utils/venueFilters.js (shared with the drawer, the rail and VenueList).
  // Every group is AND (venues must match all picks). POI pin categories (hamburger menu) use OR.
  const venueFilterCtx = useMemo(() => ({
    profile: 'map',
    classifyAffiliateBucket,
    resourceMatcher: venueHasResourceCategory,
  }), [venueHasResourceCategory]);
  const applyFilters = useCallback(
    (venuesList) => applyVenueFilters(venuesList, filters, venueFilterCtx),
    [filters, venueFilterCtx],
  );

  // Add state for initial map center
  const [initialCenter, setInitialCenter] = useState([-98.5795, 39.8283]); // Center of US

  // Add new state variables at the top of the component
  const [hotels, setHotels] = useState([]);
  const [gasStations, setGasStations] = useState([]);
  const [loadingPOIs, setLoadingPOIs] = useState(false);
  /** Avoid stale `loadingPOIs` in fetch closure; queue a follow-up fetch after pan/zoom while a request is in flight. */
  const poiFetchInFlightRef = useRef(false);
  const poiFetchQueuedRef = useRef(false);
  const fetchPOIsRef = useRef(null);
  const poiCorridorKeyRef = useRef('');
  const poiPersistTimerRef = useRef(null);
  const lastPoiPersistKeyRef = useRef('');
  // Baked POIs: the `pois` collection pre-exported to slim, gzipped GeoJSON
  // files (one per category) in Storage. We fetch only the categories shown,
  // cache their features, and render them clustered as a low-zoom overview
  // (live per-viewport fetch still supplies fresh detail when zoomed in).
  const bakedPoiCacheRef = useRef(new Map()); // category -> features[]
  const bakedPoiInflightRef = useRef(new Set()); // categories currently fetching
  const bakedPoiHandlersWiredRef = useRef(false);
  const bakedPoisClusterEnabledRef = useRef(true); // tracks current clustering of the baked source
  const handlePoiClickRef = useRef(null); // shared live-POI popup opener, reused for baked dots
  const openPoiBubbleRef = useRef(null); // renders the POI bubble for a given id/coords (live + baked)
  const handleBakedPoiClickRef = useRef(null); // baked dot click → loading bubble + DB fetch
  const poiPopupRef = useRef(null); // currently-open POI popup (dedupe across handlers)

  // Add new state for map modes
  const [mapMode, setMapMode] = useState('venues'); // 'venues', 'travel', 'wellness', 'parks', 'draw', 'data'
  const [drawBoundary, setDrawBoundary] = useState(null);

  const buildPoiSourceId = (category) => `poi-${category}-source`;
  const buildPoiLayerId = (category) => `poi-${category}-layer`;
  const isPoiCategoryEnabled = useCallback((category) => activePoiCategories.includes(category), [activePoiCategories]);

  const ensurePoiLayer = useCallback((category) => {
    if (!map.current) return;
    const sourceId = buildPoiSourceId(category);
    const layerId = buildPoiLayerId(category);
    // If clustering layers from a previous session/hot-reload exist, remove them.
    // We want to run in "no clustering" mode for POIs.
    const legacyClusterLayerId = `poi-${category}-cluster`;
    const legacyClusterCountLayerId = `poi-${category}-cluster-count`;

    const hasLegacyPoiClustering = !!(
      map.current.getLayer(legacyClusterLayerId) ||
      map.current.getLayer(legacyClusterCountLayerId)
    );
    if (hasLegacyPoiClustering) {
      // Remove layers before removing the source.
      if (map.current.getLayer(layerId)) map.current.removeLayer(layerId);
      if (map.current.getLayer(legacyClusterLayerId)) map.current.removeLayer(legacyClusterLayerId);
      if (map.current.getLayer(legacyClusterCountLayerId)) map.current.removeLayer(legacyClusterCountLayerId);
      if (map.current.getSource(sourceId)) map.current.removeSource(sourceId);
    }

    const color = POI_CATEGORY_META[category]?.color || '#e67e22';
    const abbr = POI_CATEGORY_META[category]?.abbr || category.slice(0, 2).toUpperCase();
    const icon = POI_CATEGORY_META[category]?.icon;
    const imageId = `vrc-poi-v${POI_MAP_IMAGE_VERSION}-${category}`;
    ensurePoiCategoryMapImage(map.current, category, color, { emoji: icon, letter: abbr });
    const iconSize = getPoiIconSizeByZoom(map.current.getZoom());

    if (!map.current.getSource(sourceId)) {
      map.current.addSource(sourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    if (!map.current.getLayer(layerId)) {
      const z0 = map.current.getZoom();
      const iconOpacity = z0 >= POI_ZOOM_MID_MAX || isCalendarPoiCorridor() ? 1 : 0;
      map.current.addLayer({
        id: layerId,
        type: 'symbol',
        source: sourceId,
        layout: {
          'icon-image': imageId,
          'icon-size': iconSize,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
        paint: {
          'icon-opacity': iconOpacity,
          'icon-opacity-transition': { duration: 380, delay: 0 },
        },
      });
    } else {
      // Migrate older text/emoji layers (Mapbox fonts lack emoji → tofu squares).
      try {
        map.current.setLayoutProperty(layerId, 'icon-image', imageId);
        map.current.setLayoutProperty(layerId, 'icon-size', iconSize);
        map.current.setLayoutProperty(layerId, 'icon-allow-overlap', true);
        map.current.setLayoutProperty(layerId, 'icon-ignore-placement', true);
        map.current.setLayoutProperty(layerId, 'text-field', '');
        map.current.setPaintProperty(layerId, 'icon-opacity-transition', { duration: 380, delay: 0 });
        map.current.setPaintProperty(layerId, 'icon-opacity', map.current.getZoom() >= POI_ZOOM_MID_MAX || isCalendarPoiCorridor() ? 1 : 0);
      } catch (_) {
        /* ignore if style not ready */
      }
    }
  }, [POI_CATEGORY_META]);

  /** Calendar MAP tab with a day route: travel pins cover the route corridor at every zoom. */
  const isCalendarPoiCorridor = () => calendarEmbed && (calendarRouteLineRef.current?.length || 0) > 0;

  const syncPoiLayerVisibility = useCallback(() => {
    if (!map.current) return;
    const zoomOk = map.current.getZoom() >= POI_ZOOM_MID_MAX || isCalendarPoiCorridor();
    Object.keys(POI_CATEGORY_META).forEach((category) => {
      const layerId = buildPoiLayerId(category);
      if (!map.current.getLayer(layerId)) return;
      const enabled = isPoiCategoryEnabled(category);
      if (!enabled) {
        map.current.setLayoutProperty(layerId, 'visibility', 'none');
        return;
      }
      map.current.setLayoutProperty(layerId, 'visibility', 'visible');
      try {
        map.current.setPaintProperty(layerId, 'icon-opacity-transition', { duration: 380, delay: 0 });
        map.current.setPaintProperty(layerId, 'icon-opacity', zoomOk ? 1 : 0);
      } catch (_) {
        /* style not ready */
      }
    });
    if (map.current.getLayer('poi-debug-layer')) {
      try {
        const showDebug = zoomOk && activePoiCategories.length > 0;
        map.current.setPaintProperty('poi-debug-layer', 'circle-opacity-transition', { duration: 380, delay: 0 });
        map.current.setPaintProperty('poi-debug-layer', 'circle-opacity', showDebug ? 0.95 : 0);
      } catch (_) {
        /* ignore */
      }
    }
  }, [POI_CATEGORY_META, isPoiCategoryEnabled, activePoiCategories]);

  const ensurePoiDebugLayer = useCallback(() => {
    if (!map.current) return;
    const poiLayerIds = Object.keys(POI_CATEGORY_META || {});
    const beforeId = poiLayerIds.length > 0 ? buildPoiLayerId(poiLayerIds[0]) : undefined;
    if (!map.current.getSource('poi-debug-source')) {
      map.current.addSource('poi-debug-source', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }
    if (!map.current.getLayer('poi-debug-layer')) {
      const z0 = map.current.getZoom();
      const dbgOp = z0 >= POI_ZOOM_MID_MAX ? 0.95 : 0;
      map.current.addLayer({
        id: 'poi-debug-layer',
        type: 'circle',
        source: 'poi-debug-source',
        paint: {
          'circle-color': '#ff8c00',
          'circle-radius': 6,
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': dbgOp,
          'circle-opacity-transition': { duration: 380, delay: 0 },
        },
      }, beforeId);
    }
  }, [POI_CATEGORY_META]);

  const clearPoiLayers = useCallback(() => {
    if (!map.current) return;
    const emptyCollection = { type: 'FeatureCollection', features: [] };

    Object.keys(POI_CATEGORY_META).forEach((category) => {
      const source = map.current.getSource(buildPoiSourceId(category));
      if (source) {
        source.setData(emptyCollection);
      }
    });

    const debugSource = map.current.getSource('poi-debug-source');
    if (debugSource) {
      debugSource.setData(emptyCollection);
    }
  }, [POI_CATEGORY_META]);

  // --- Baked POIs (low-zoom clustered overview from a single prebaked file) ---

  const ensureBakedPoiLayers = useCallback(() => {
    if (!map.current) return;
    const m = map.current;

    // Display mode drives clustering: 'clusters' clusters the overview, 'pins'
    // shows every sampled POI as an individual category-colored dot, 'hidden'
    // keeps the layers but turns them off.
    const wantCluster = !masterTourUi && displayMode === 'clusters';

    // Mapbox can't toggle `cluster` on an existing source — rebuild if it changed.
    const existing = m.getSource(BAKED_POIS_SOURCE_ID);
    if (existing && bakedPoisClusterEnabledRef.current !== wantCluster) {
      [BAKED_POIS_CLUSTER_LAYER, BAKED_POIS_CLUSTER_COUNT_LAYER, BAKED_POIS_POINT_LAYER].forEach((id) => {
        if (m.getLayer(id)) m.removeLayer(id);
      });
      if (m.getSource(BAKED_POIS_SOURCE_ID)) m.removeSource(BAKED_POIS_SOURCE_ID);
    }

    if (!m.getSource(BAKED_POIS_SOURCE_ID)) {
      m.addSource(BAKED_POIS_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: wantCluster,
        clusterRadius: 48,
        clusterMaxZoom: POI_ZOOM_MID_MAX - 1,
      });
      bakedPoisClusterEnabledRef.current = wantCluster;
    }

    // Color individual dots by category (`c` property baked into each feature).
    const colorMatch = ['match', ['get', 'c']];
    Object.entries(POI_CATEGORY_META).forEach(([cat, meta]) => {
      colorMatch.push(cat, meta?.color || '#e67e22');
    });
    colorMatch.push('#e67e22');

    if (!m.getLayer(BAKED_POIS_CLUSTER_LAYER)) {
      m.addLayer({
        id: BAKED_POIS_CLUSTER_LAYER,
        type: 'circle',
        source: BAKED_POIS_SOURCE_ID,
        maxzoom: POI_ZOOM_MID_MAX,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': ['step', ['get', 'point_count'], '#4dabf7', 50, '#3b82f6', 250, '#1d4ed8', 1000, '#1e3a8a'],
          'circle-radius': ['step', ['get', 'point_count'], 12, 50, 16, 250, 22, 1000, 30],
          'circle-opacity': 0.85,
          'circle-stroke-width': 1.5,
          'circle-stroke-color': 'rgba(255,255,255,0.6)',
        },
      });
    }

    if (!m.getLayer(BAKED_POIS_CLUSTER_COUNT_LAYER)) {
      m.addLayer({
        id: BAKED_POIS_CLUSTER_COUNT_LAYER,
        type: 'symbol',
        source: BAKED_POIS_SOURCE_ID,
        maxzoom: POI_ZOOM_MID_MAX,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-size': 12,
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-allow-overlap': true,
        },
        paint: { 'text-color': '#ffffff' },
      });
    }

    // Dots grow noticeably as you zoom in (tiny at world view, large near the
    // hand-off to live pins at POI_ZOOM_MID_MAX).
    const bakedDotRadius = ['interpolate', ['linear'], ['zoom'], 3, 2.5, 5, 4, 7, 6.5, 9, 9.5, 11, 14];
    const bakedDotStroke = ['interpolate', ['linear'], ['zoom'], 6, 0, 9, 1.2, 11, 2];
    if (!m.getLayer(BAKED_POIS_POINT_LAYER)) {
      m.addLayer({
        id: BAKED_POIS_POINT_LAYER,
        type: 'circle',
        source: BAKED_POIS_SOURCE_ID,
        maxzoom: POI_ZOOM_MID_MAX,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': colorMatch,
          'circle-radius': bakedDotRadius,
          'circle-opacity': 0.9,
          'circle-stroke-width': bakedDotStroke,
          'circle-stroke-color': 'rgba(255,255,255,0.85)',
        },
      });
    } else {
      try {
        m.setPaintProperty(BAKED_POIS_POINT_LAYER, 'circle-color', colorMatch);
        m.setPaintProperty(BAKED_POIS_POINT_LAYER, 'circle-radius', bakedDotRadius);
        m.setPaintProperty(BAKED_POIS_POINT_LAYER, 'circle-stroke-width', bakedDotStroke);
      } catch (_) { /* style not ready */ }
    }

    if (!bakedPoiHandlersWiredRef.current) {
      bakedPoiHandlersWiredRef.current = true;
      // Click a cluster → zoom to where it breaks apart.
      m.on('click', BAKED_POIS_CLUSTER_LAYER, (e) => {
        const features = m.queryRenderedFeatures(e.point, { layers: [BAKED_POIS_CLUSTER_LAYER] });
        const clusterId = features[0]?.properties?.cluster_id;
        const src = m.getSource(BAKED_POIS_SOURCE_ID);
        if (clusterId == null || !src) return;
        src.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          m.easeTo({ center: features[0].geometry.coordinates, zoom });
        });
      });
      // Click a single baked dot → loading bubble + DB fetch.
      m.on('click', BAKED_POIS_POINT_LAYER, (e) => {
        const f = e.features?.[0];
        if (!f) return;
        if (handleBakedPoiClickRef.current) handleBakedPoiClickRef.current(f);
      });
      ['mouseenter', 'mouseleave'].forEach((evt) => {
        [BAKED_POIS_CLUSTER_LAYER, BAKED_POIS_POINT_LAYER].forEach((lid) => {
          m.on(evt, lid, () => {
            m.getCanvas().style.cursor = evt === 'mouseenter' ? 'pointer' : '';
          });
        });
      });
    }

    // 'hidden' display mode turns the overview off entirely.
    const visibility = displayMode === 'hidden' ? 'none' : 'visible';
    [BAKED_POIS_CLUSTER_LAYER, BAKED_POIS_CLUSTER_COUNT_LAYER, BAKED_POIS_POINT_LAYER].forEach((id) => {
      if (m.getLayer(id)) {
        try { m.setLayoutProperty(id, 'visibility', visibility); } catch (_) { /* style not ready */ }
      }
    });
  }, [POI_CATEGORY_META, displayMode, masterTourUi]);

  /** Rebuild the clustered source from the cached features of active categories. */
  const applyBakedPoiData = useCallback(() => {
    if (!map.current) return;
    const src = map.current.getSource(BAKED_POIS_SOURCE_ID);
    if (!src) return;
    // The calendar's Travel section uses live corridor results only.
    const active = calendarEmbed ? [] : (activePoiCategories || []);
    const features = [];
    active.forEach((cat) => {
      const cached = bakedPoiCacheRef.current.get(cat);
      if (Array.isArray(cached)) features.push(...cached);
    });
    src.setData({ type: 'FeatureCollection', features });
  }, [activePoiCategories]);

  /** Fetch the per-category baked files for any active categories not yet cached. */
  const loadBakedPoiCategories = useCallback(async () => {
    if (!map.current) return;
    const active = calendarEmbed ? [] : (activePoiCategories || []);
    if (active.length === 0) {
      applyBakedPoiData();
      return;
    }
    ensureBakedPoiLayers();
    // Show whatever is already cached immediately.
    applyBakedPoiData();

    const toFetch = active.filter(
      (cat) => !bakedPoiCacheRef.current.has(cat) && !bakedPoiInflightRef.current.has(cat)
    );
    if (toFetch.length === 0) return;

    await Promise.all(
      toFetch.map(async (cat) => {
        bakedPoiInflightRef.current.add(cat);
        try {
          const res = await fetch(buildBakedPoiUrl(cat));
          if (!res.ok) {
            if (res.status === 404) {
              console.info(`Baked POIs for "${cat}" not generated yet.`);
            } else {
              console.warn(`Baked POIs fetch failed for "${cat}":`, res.status);
            }
            bakedPoiCacheRef.current.set(cat, []); // cache the miss to avoid re-fetching
            return;
          }
          const json = await res.json();
          bakedPoiCacheRef.current.set(cat, Array.isArray(json?.features) ? json.features : []);
        } catch (err) {
          console.warn(`Baked POIs load error for "${cat}":`, err);
        } finally {
          bakedPoiInflightRef.current.delete(cat);
        }
      })
    );

    applyBakedPoiData();
  }, [activePoiCategories, ensureBakedPoiLayers, applyBakedPoiData]);

  const setupPoiClickHandlers = useCallback(() => {
    if (!map.current) return;

    // Ensure we always have handlers wired, even if Mapbox layers are recreated
    // (e.g. after style changes or POI clustering refactors).

    const escapeHtml = (value) => {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };

    // Escape for embedding in inline `onclick="..."` JS strings.
    const escapeJsString = (value) => {
      return String(value ?? '')
        .replace(/\\/g, '\\\\')
        .replace(/'/g, '\\\'')
        .replace(/\r?\n/g, ' ')
        .trim();
    };

    const fetchPoiNotes = async (poiId) => {
      try {
        const db = getFirestore();
        const notesCol = collection(db, 'pois', poiId, 'notes');
        const notesQuery = query(notesCol, orderBy('createdAt', 'desc'), limit(10));
        const snap = await getDocs(notesQuery);
        const notes = [];
        snap.forEach((d) => notes.push(d.data()));
        return notes;
      } catch (err) {
        console.error('fetchPoiNotes error:', err);
        return [];
      }
    };

    const fetchPoiRatings = async (poiId) => {
      try {
        const db = getFirestore();
        const uid = getAuth().currentUser?.uid;

        const ratingsCol = collection(db, 'pois', poiId, 'ratings');
        const [allRatingsSnap, myRatingSnap] = await Promise.all([
          getDocs(ratingsCol),
          uid ? getDoc(doc(db, 'pois', poiId, 'ratings', uid)) : Promise.resolve(null),
        ]);

        const ratingValues = [];
        allRatingsSnap.forEach((d) => {
          const v = Number(d.data()?.value);
          if (Number.isFinite(v)) ratingValues.push(v);
        });

        const avgRating = ratingValues.length
          ? ratingValues.reduce((a, b) => a + b, 0) / ratingValues.length
          : null;

        const myRating = myRatingSnap?.exists ? Number(myRatingSnap.data()?.value) : null;

        return { avgRating, myRating };
      } catch (err) {
        console.error('fetchPoiRatings error:', err);
        return { avgRating: null, myRating: null };
      }
    };

    // Loading bubble shown immediately for baked overview dots while we fetch the
    // full POI record from the database (id-only on the slim baked features).
    const buildLoadingPopupHtml = () => `
      <div style="min-width:200px;max-width:280px;text-align:center;font-family:system-ui,-apple-system,sans-serif;padding:22px 16px;">
        <div style="display:flex;justify-content:center;margin-bottom:12px;">
          <div style="width:26px;height:26px;border:3px solid rgba(255,255,255,0.18);border-top-color:#e8a020;border-radius:50%;animation:vrcPoiSpin 0.8s linear infinite;"></div>
        </div>
        <p style="margin:0;color:rgba(255,255,255,0.6);font-size:12px;letter-spacing:0.04em;">Loading POI…</p>
      </div>
      <style>@keyframes vrcPoiSpin{to{transform:rotate(360deg);}}</style>
    `;

    /**
     * Render the full POI bubble for a known id/coords. Used by both live pins
     * (props already complete) and baked overview dots (after a DB fetch). If an
     * existing popup is passed (e.g. the loading bubble), its content is replaced.
     */
    const openPoiBubble = async ({ poiId, poiName, poiCategory, address, coords, popup: existingPopup }) => {
      if (!poiId) return;
      if (!coords || typeof coords[0] !== 'number' || typeof coords[1] !== 'number') return;
      const poiIdEscaped = escapeHtml(poiId);
      poiName = String(poiName || 'Unknown POI');
      poiCategory = String(poiCategory || 'other');
      address = String(address || '');
      const typeLabel = POI_CATEGORY_META[poiCategory]?.label || poiCategory;

      // POI popups always use the dark-popup style to match venue popups (map-overlay convention)
      const popupClassName = 'dark-popup';
      const starEmpty = 'rgba(255,255,255,0.22)';
      const starFilled = '#e8a020';
      const noteCardBg = 'rgba(255,255,255,0.05)';

      // Show the bubble immediately (loading spinner) so a click always gives
      // instant feedback — then enrich it once ratings/notes resolve. Reuses the
      // baked-path loading popup when one was already opened.
      let popup;
      if (existingPopup) {
        popup = existingPopup.setLngLat(coords);
      } else {
        if (poiPopupRef.current) { try { poiPopupRef.current.remove(); } catch (_) {} }
        popup = new mapboxgl.Popup({ closeOnClick: true, className: popupClassName, maxWidth: '320px', offset: 12 })
          .setLngLat(coords)
          .setHTML(buildLoadingPopupHtml())
          .addTo(map.current);
        poiPopupRef.current = popup;
      }

      const { avgRating, myRating } = await fetchPoiRatings(poiId);
      if (!popup.isOpen()) return; // closed while loading
      const ratingValue = Number(myRating || 0);
      const starsContainerId = `poi-stars-${poiIdEscaped}`;
      const ratingStarsHtml = `
        <div style="margin:0 0 16px 0;">
          <p style="margin:0 0 8px 0;color:rgba(255,255,255,0.45);font-size:11px;letter-spacing:0.06em;text-transform:uppercase;">
            Your Rating${avgRating != null ? `&nbsp;&nbsp;<span style="color:rgba(255,255,255,0.6);font-weight:600;">${avgRating.toFixed(1)} / 5 avg</span>` : ''}
          </p>
          <div id="${starsContainerId}" style="display:flex;justify-content:center;gap:6px;">
            ${[1, 2, 3, 4, 5].map((n) => {
              const filled = n <= ratingValue;
              return `<button
                onclick="window.savePoiRating('${poiIdEscaped}', ${n})"
                onmouseover="window.poiStarHover('${starsContainerId}', ${n})"
                onmouseout="window.poiStarOut('${starsContainerId}', ${ratingValue})"
                aria-label="Rate ${n} stars"
                style="background:transparent;border:none;padding:2px;cursor:pointer;color:${filled ? starFilled : starEmpty};font-size:24px;line-height:1;transition:color 0.12s,transform 0.12s;transform:scale(1);">★</button>`;
            }).join('')}
          </div>
        </div>
      `;

      const basePopupHtml = `
        <div style="min-width:268px;max-width:316px;text-align:center;font-family:system-ui,-apple-system,sans-serif;padding:14px 12px 10px;">
          <p style="margin:0 0 4px 0;color:rgba(255,255,255,0.45);font-size:11px;letter-spacing:0.08em;text-transform:uppercase;font-weight:600;">${escapeHtml(typeLabel)}</p>
          <h3 style="margin:0 0 16px 0;color:#ffffff;font-size:18px;font-weight:700;line-height:1.25;">${escapeHtml(poiName)}</h3>
          ${ratingStarsHtml}
          <button
            onclick="window.addPoiToTourDay('${poiIdEscaped}', '${escapeJsString(poiName)}', '${escapeJsString(poiCategory)}', '${escapeJsString(address || '')}', ${Number(coords[0])}, ${Number(coords[1])})"
            style="display:block;width:100%;padding:11px 0;background:#e8a020;color:#fff;border:none;border-radius:8px;cursor:pointer;font-weight:700;font-size:14px;letter-spacing:0.3px;margin-bottom:8px;transition:background 0.15s;"
            onmouseover="this.style.background='#cf8f1a'" onmouseout="this.style.background='#e8a020'"
          >+ Add to Day</button>
          <button
            onclick="window.focusPoiOnMap(${Number(coords[0])}, ${Number(coords[1])})"
            style="display:block;width:100%;padding:8px 0;background:rgba(255,255,255,0.08);color:rgba(255,255,255,0.75);border:1px solid rgba(255,255,255,0.15);border-radius:8px;cursor:pointer;font-weight:600;font-size:13px;letter-spacing:0.2px;margin-bottom:14px;transition:background 0.15s;"
            onmouseover="this.style.background='rgba(255,255,255,0.15)'" onmouseout="this.style.background='rgba(255,255,255,0.08)'"
          >⊙ Focus on Map</button>
          <details style="text-align:left;">
            <summary style="cursor:pointer;color:rgba(255,255,255,0.5);font-size:12px;font-weight:600;letter-spacing:0.03em;list-style:none;padding:6px 0;border-top:1px solid rgba(255,255,255,0.08);">
              &#9432;&nbsp; Notes
            </summary>
            <div id="poi-notes-${poiIdEscaped}" style="margin-top:10px;color:rgba(255,255,255,0.7);font-size:12px;line-height:1.5;">__NOTES__</div>
            <textarea
              id="poi-note-${poiIdEscaped}"
              placeholder="Add a note..."
              style="margin-top:10px;width:100%;min-height:64px;border-radius:7px;border:1px solid rgba(255,255,255,0.15);background:rgba(255,255,255,0.07);padding:9px 10px;font-size:12px;color:#fff;outline:none;resize:vertical;box-sizing:border-box;font-family:inherit;"
            ></textarea>
            <div id="poi-note-feedback-${poiIdEscaped}" aria-live="polite" style="margin-top:5px;min-height:16px;font-size:11px;font-weight:700;color:#e8a020;"></div>
            <button
              type="button"
              onclick="window.savePoiNote('${poiIdEscaped}', document.getElementById('poi-note-${poiIdEscaped}').value)"
              style="margin-top:8px;background:rgba(255,255,255,0.1);color:#fff;border:1px solid rgba(255,255,255,0.18);padding:9px 0;border-radius:7px;cursor:pointer;font-weight:600;font-size:13px;width:100%;transition:background 0.15s;letter-spacing:0.2px;"
              onmouseover="this.style.background='rgba(255,255,255,0.2)'" onmouseout="this.style.background='rgba(255,255,255,0.1)'"
            >Save Note</button>
          </details>
        </div>
      `;

      popup.setHTML(basePopupHtml);

      const notes = await fetchPoiNotes(poiId);
      if (!popup.isOpen()) return; // closed while loading notes
      const notesHtmlInner = notes.length
        ? notes.map((n) => {
            const userName = escapeHtml(n.userName || n.userId || 'Anonymous');
            const content = escapeHtml(n.content || '');
            return `<div style="padding:8px 10px;background:${noteCardBg};border-radius:6px;margin-bottom:6px;border:1px solid rgba(255,255,255,0.08);">
                <div style="font-size:11px;color:rgba(255,255,255,0.45);font-weight:700;margin-bottom:3px;">${userName}</div>
                <div style="font-size:12px;color:rgba(255,255,255,0.8);white-space:pre-wrap;word-break:break-word;">${content}</div>
              </div>`;
          }).join('')
        : `<p style="margin:0;color:rgba(255,255,255,0.35);font-size:12px;font-style:italic;">No notes yet.</p>`;

      popup.setHTML(basePopupHtml.replace('__NOTES__', notesHtmlInner));
    };

    // Live pin click: feature props already carry id/name/category/address.
    const handlePoiClick = async (e) => {
      if (!e.features || e.features.length === 0) return;
      const feature = e.features[0];
      const props = feature.properties || {};
      const poiId = props.id || props.i;
      if (!poiId) return;
      const coords = feature.geometry?.coordinates;
      if (!coords || typeof coords[0] !== 'number' || typeof coords[1] !== 'number') return;
      await openPoiBubble({
        poiId,
        poiName: props.name || props.n,
        poiCategory: props.category || props.c,
        address: props.address || '',
        coords,
        popup: null,
      });
    };

    /**
     * Baked overview dot click: the slim feature only carries an id (+ slim
     * name/category). Open a loading bubble immediately, then fetch the full POI
     * record from Firestore and render the same bubble the live pins use.
     */
    const handleBakedPoiClick = async (feature) => {
      if (!feature) return;
      const props = feature.properties || {};
      const poiId = props.id || props.i;
      const coords = feature.geometry?.coordinates;
      if (!coords || typeof coords[0] !== 'number' || typeof coords[1] !== 'number') return;

      // Stale baked files (generated before the bake function wrote id+name) only
      // carry a category, so we can't fetch the full record. Show what we have and
      // hint that a re-bake is needed for full detail bubbles.
      if (!poiId) {
        console.warn('[POI] baked dot has no id — re-bake POIs (deploy functions, then Admin → Bake) to enable detail bubbles.');
        const cat = String(props.c || props.category || 'other');
        const label = POI_CATEGORY_META[cat]?.label || cat;
        if (poiPopupRef.current) { try { poiPopupRef.current.remove(); } catch (_) {} }
        const p = new mapboxgl.Popup({ closeOnClick: true, className: 'dark-popup', maxWidth: '300px', offset: 12 })
          .setLngLat(coords)
          .setHTML(`
            <div style="min-width:220px;max-width:280px;text-align:center;font-family:system-ui,-apple-system,sans-serif;padding:16px 14px;">
              <p style="margin:0 0 6px 0;color:rgba(255,255,255,0.45);font-size:11px;letter-spacing:0.08em;text-transform:uppercase;font-weight:600;">${escapeHtml(label)}</p>
              <p style="margin:0;color:rgba(255,255,255,0.7);font-size:12px;line-height:1.5;">Full details aren't available for this dot yet. Re-bake POIs to enable detail.</p>
            </div>
          `)
          .addTo(map.current);
        poiPopupRef.current = p;
        return;
      }

      if (poiPopupRef.current) { try { poiPopupRef.current.remove(); } catch (_) {} }
      const popup = new mapboxgl.Popup({ closeOnClick: true, className: 'dark-popup', maxWidth: '320px', offset: 12 })
        .setLngLat(coords)
        .setHTML(buildLoadingPopupHtml())
        .addTo(map.current);
      poiPopupRef.current = popup;

      let full = {};
      try {
        const snap = await getDoc(doc(getFirestore(), 'pois', String(poiId)));
        if (snap.exists()) full = snap.data() || {};
      } catch (err) {
        console.warn('Baked POI fetch failed:', err);
      }
      // Popup may have been closed while we were fetching.
      if (!popup.isOpen()) return;

      await openPoiBubble({
        poiId,
        poiName: full.name || full.title || props.n || props.name || 'Unknown POI',
        poiCategory: full.poiCategory || full.category || props.c || props.category || 'other',
        address: full.address || full.formattedAddress || props.address || '',
        coords,
        popup,
      });
    };

    // Expose so the baked-overview dot handler can open the same bubble.
    openPoiBubbleRef.current = openPoiBubble;
    handlePoiClickRef.current = handlePoiClick;
    handleBakedPoiClickRef.current = handleBakedPoiClick;

    // Attach POI popup handler only for unclustered symbol layers.
    Object.keys(POI_CATEGORY_META).forEach((category) => {
      const layerId = buildPoiLayerId(category);
      if (!map.current.getLayer(layerId)) return;

      // Clear and reattach to avoid stale handlers after style/layer changes.
      map.current.off('click', layerId, handlePoiClick);
      map.current.off('mouseenter', layerId);
      map.current.off('mouseleave', layerId);

      map.current.on('click', layerId, handlePoiClick);
      map.current.on('mouseenter', layerId, () => {
        map.current.getCanvas().style.cursor = 'pointer';
      });
      map.current.on('mouseleave', layerId, () => {
        map.current.getCanvas().style.cursor = '';
      });
    });
  }, [POI_CATEGORY_META, isDarkModeRef, themeRef]);

  // Helper function to convert degrees to radians
  const deg2rad = (deg) => {
    return deg * (Math.PI / 180);
  };

  // Function to calculate distance between two points
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c; // Distance in km
    return d * 0.621371; // Convert to miles
  };

  // Filter venues to those inside current map bounds (Mapbox LngLatBounds)
  const filterVenuesByBounds = (venueList, bounds) => {
    if (!bounds || !venueList || venueList.length === 0) return venueList;
    const w = bounds.getWest();
    const e = bounds.getEast();
    const s = bounds.getSouth();
    const n = bounds.getNorth();
    return venueList.filter((v) => {
      const lat = parseFloat(v.latitude);
      const lng = parseFloat(v.longitude);
      if (isNaN(lat) || isNaN(lng)) return false;
      return lat >= s && lat <= n && lng >= w && lng <= e;
    });
  };

  // ── Affiliate region (choropleth) map helpers ──────────────────────────────
  // Tiers, from zoomed-out to zoomed-in: state → metro (CBSA) → county → pins.
  const REGION_TIERS = [
    { source: 'affiliate-region-states', level: 'state', fc: getStatesFeatureCollection, maxzoom: 5 },
    { source: 'affiliate-region-cbsa', level: 'cbsa', fc: getCbsaFeatureCollection, minzoom: 5, maxzoom: 7 },
    { source: 'affiliate-region-counties', level: 'county', fc: getCountiesFeatureCollection, minzoom: 7, maxzoom: 9 },
  ];
  const REGION_LAYER_IDS = REGION_TIERS.map((t) => `${t.source}-fill`);

  // Insert region fills beneath the basemap's text labels (first symbol layer)
  // so street/place names stay readable through the colors; falls back to the
  // venue layers if no symbol layer is found.
  const getRegionInsertBeforeId = () => {
    if (!map.current) return undefined;
    const style = map.current.getStyle();
    const symbol = style && style.layers && style.layers.find((l) => l.type === 'symbol');
    if (symbol) return symbol.id;
    return ['clusters-shadow', 'clusters', 'unclustered-point-shadow', 'unclustered-point']
      .find((id) => map.current.getLayer(id));
  };

  /** Add region sources + fill/outline layers for each tier. */
  const ensureAffiliateRegionLayers = () => {
    if (!map.current) return;
    if (!getStatesFeatureCollection() || !getCountiesFeatureCollection()) return;

    const beforeId = getRegionInsertBeforeId();

    // Per-feature alpha is baked into the fill *color* (fill-opacity does not
    // support feature-state). fill-opacity stays constant / zoom-driven only.
    // Regions with no venues fall back to a neutral gray so the map reads as a
    // continuous surface instead of disconnected colored patches.
    const fillColor = ['coalesce', ['feature-state', 'fillColor'], 'rgba(128,128,128,0.16)'];

    REGION_TIERS.forEach((tier) => {
      const fc = tier.fc();
      if (!fc || !fc.features.length) return; // skip tiers with no geometry (e.g. CBSA unavailable)

      if (!map.current.getSource(tier.source)) {
        map.current.addSource(tier.source, { type: 'geojson', data: fc, promoteId: 'regionId' });
      }

      const zoomRange = {};
      if (tier.minzoom != null) zoomRange.minzoom = tier.minzoom;
      if (tier.maxzoom != null) zoomRange.maxzoom = tier.maxzoom;

      const fillId = `${tier.source}-fill`;
      if (!map.current.getLayer(fillId)) {
        // Hard switch by zoom (minzoom/maxzoom) — no cross-fade between tiers.
        map.current.addLayer({
          id: fillId,
          type: 'fill',
          source: tier.source,
          ...zoomRange,
          layout: { visibility: 'none' },
          paint: { 'fill-color': fillColor, 'fill-opacity': 1 },
        }, beforeId);
      }
    });
  };

  /** Recolor region fills from the current affiliate-filtered venues. */
  const applyAffiliateRegionColors = (venueList) => {
    if (!map.current) return;
    REGION_TIERS.forEach((tier) => {
      if (!map.current.getSource(tier.source)) return;
      try {
        map.current.removeFeatureState({ source: tier.source });
      } catch (e) { /* no-op if nothing set yet */ }
      aggregateRegions(venueList, tier.level, concentrationBucketsOn).forEach((r) => {
        map.current.setFeatureState(
          { source: tier.source, id: r.id },
          { color: r.color, fillColor: r.fillColor },
        );
      });
    });
  };

  const setAffiliateRegionVisibility = (visible) => {
    if (!map.current) return;
    REGION_LAYER_IDS.forEach((id) => {
      if (map.current.getLayer(id)) {
        map.current.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
      }
    });
    if (!visible && map.current.getLayer('affiliate-region-zcta-fill')) {
      map.current.setLayoutProperty('affiliate-region-zcta-fill', 'visibility', 'none');
    }
    if (map.current.getLayer('affiliate-region-intl-adm0-fill')) {
      map.current.setLayoutProperty('affiliate-region-intl-adm0-fill', 'visibility', visible ? 'visible' : 'none');
    }
    if (!visible && map.current.getLayer('affiliate-region-intl-fill')) {
      map.current.setLayoutProperty('affiliate-region-intl-fill', 'visibility', 'none');
    }
  };

  // Wash out the basemap in Concentration mode so the affiliate colors pop.
  // A neutral full-viewport background layer sits beneath the choropleth fills
  // (but above the basemap's roads/water), muting the basemap without touching
  // the choropleth or labels (which render on top).
  const CONCENTRATION_WASH_ID = 'concentration-basemap-wash';
  const setConcentrationBasemapWash = (visible) => {
    if (!map.current) return;
    if (typeof map.current.isStyleLoaded === 'function' && !map.current.isStyleLoaded()) return;
    if (!visible) {
      if (map.current.getLayer(CONCENTRATION_WASH_ID)) {
        map.current.setLayoutProperty(CONCENTRATION_WASH_ID, 'visibility', 'none');
      }
      return;
    }
    if (!map.current.getLayer(CONCENTRATION_WASH_ID)) {
      // Sit just below the lowest region fill so the choropleth stays on top.
      const beforeId = REGION_LAYER_IDS.find((id) => map.current.getLayer(id))
        || getRegionInsertBeforeId();
      try {
        map.current.addLayer({
          id: CONCENTRATION_WASH_ID,
          type: 'background',
          layout: { visibility: 'visible' },
          paint: {
            'background-color': isDarkMode ? '#0c1116' : '#f4f4f2',
            'background-opacity': isDarkMode ? 0.55 : 0.45,
          },
        }, beforeId);
      } catch (e) { /* style not ready */ }
    } else {
      map.current.setLayoutProperty(CONCENTRATION_WASH_ID, 'visibility', 'visible');
    }
  };

  /** Load geometry (once), build layers, recolor, and reveal the region map. */
  const activateAffiliateRegions = async (venueList) => {
    try {
      await ensureRegionGeo();
    } catch (err) {
      console.warn('Affiliate region map: failed to load boundaries', err);
      return;
    }
    if (!map.current || !mapLoaded) return;
    // On first load the map fires 'load' before the style has fully settled, so
    // isStyleLoaded() can be false here. Rather than silently bail (which left the
    // choropleth blank until something re-triggered the effect), retry once the
    // map goes idle, using the latest venues so the colors actually paint.
    if (typeof map.current.isStyleLoaded === 'function' && !map.current.isStyleLoaded()) {
      map.current.once('idle', () => {
        if (mapViewModeRef.current === 'concentration') {
          const pool = hasVenueRevealCriteriaRef.current ? (filteredVenuesRef.current || []) : [];
          activateAffiliateRegions(pool);
          setConcentrationBasemapWash(true);
        }
      });
      return;
    }
    ensureAffiliateRegionLayers();
    applyAffiliateRegionColors(venueList || []);
    setAffiliateRegionVisibility(true);
  };

  // ── Community resource area highlights (city / state scoped submissions) ──
  const RESOURCE_AREA_STATE_SRC = 'resource-areas-states';
  const RESOURCE_AREA_CITY_SRC = 'resource-areas-cities';
  const RESOURCE_AREA_LAYER_IDS = [
    `${RESOURCE_AREA_STATE_SRC}-fill`,
    `${RESOURCE_AREA_STATE_SRC}-line`,
    `${RESOURCE_AREA_CITY_SRC}-fill`,
    `${RESOURCE_AREA_CITY_SRC}-line`,
  ];

  const emptyResourceFc = () => ({ type: 'FeatureCollection', features: [] });

  const ensureResourceAreaLayers = () => {
    if (!map.current) return;
    const beforeId = getRegionInsertBeforeId();
    const addPair = (sourceId, minzoom, maxzoom) => {
      if (!map.current.getSource(sourceId)) {
        map.current.addSource(sourceId, {
          type: 'geojson',
          data: emptyResourceFc(),
        });
      }
      const fillId = `${sourceId}-fill`;
      const lineId = `${sourceId}-line`;
      const zoomRange = {};
      if (minzoom != null) zoomRange.minzoom = minzoom;
      if (maxzoom != null) zoomRange.maxzoom = maxzoom;
      if (!map.current.getLayer(fillId)) {
        map.current.addLayer({
          id: fillId,
          type: 'fill',
          source: sourceId,
          ...zoomRange,
          layout: { visibility: 'none' },
          paint: {
            'fill-color': ['coalesce', ['get', 'fillColor'], 'rgba(230,126,34,0.28)'],
            'fill-opacity': 1,
          },
        }, beforeId);
      }
      if (!map.current.getLayer(lineId)) {
        map.current.addLayer({
          id: lineId,
          type: 'line',
          source: sourceId,
          ...zoomRange,
          layout: { visibility: 'none' },
          paint: {
            'line-color': ['coalesce', ['get', 'color'], '#e67e22'],
            'line-width': 1.4,
            'line-opacity': 0.9,
          },
        }, beforeId);
      }
    };
    // States when zoomed out; city polygons stay on at every zoom (on top).
    addPair(RESOURCE_AREA_STATE_SRC, undefined, 8);
    addPair(RESOURCE_AREA_CITY_SRC, undefined, undefined);
  };

  const setResourceAreaVisibility = (visible) => {
    if (!map.current) return;
    RESOURCE_AREA_LAYER_IDS.forEach((id) => {
      if (map.current.getLayer(id)) {
        map.current.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
      }
    });
  };

  const paintResourceAreaSource = (sourceId, features) => {
    const src = map.current?.getSource(sourceId);
    if (!src) return;
    src.setData({ type: 'FeatureCollection', features: features || [] });
  };

  const updateResourceAreaHighlights = async () => {
    const categories = filters.resources || [];
    const selectedAreaSubmissions = (approvedResourceSubmissions || []).filter(
      (submission) =>
        selectedResourceSubmissionIds.includes(submission.id) &&
        (submission.scope === 'city' || submission.scope === 'state')
    );
    const areaDirectoryIds = selectedAreaSubmissions.map((submission) => `resource:${submission.id}`);
    const clearAreaLoading = () => {
      if (areaDirectoryIds.length === 0) return;
      setLoadingResourceDirectoryIds((prev) =>
        prev.filter((id) => !areaDirectoryIds.includes(id))
      );
    };
    const token = ++resourceAreaTokenRef.current;
    if (!map.current || !mapLoaded || (categories.length === 0 && selectedAreaSubmissions.length === 0)) {
      if (map.current) {
        paintResourceAreaSource(RESOURCE_AREA_STATE_SRC, []);
        paintResourceAreaSource(RESOURCE_AREA_CITY_SRC, []);
        setResourceAreaVisibility(false);
      }
      if (token === resourceAreaTokenRef.current) clearAreaLoading();
      return;
    }
    const coverage = collectResourceCoverage(venueResourceIndex, categories);
    const stateKeys = new Set(coverage.states);
    const cityEntries = new Map(coverage.cities.map((entry) => [entry.key, entry]));
    selectedAreaSubmissions.forEach((submission) => {
      const state = String(submission.venueState || '').trim().toLowerCase();
      const city = String(submission.venueCity || '').trim().toLowerCase();
      if (submission.scope === 'state' && state) {
        stateKeys.add(state);
      } else if (submission.scope === 'city' && city && state) {
        const key = `${city}|${state}`;
        cityEntries.set(key, { key, city, state });
      }
    });
    coverage.states = [...stateKeys];
    coverage.cities = [...cityEntries.values()];
    const style = resourceAreaStyle('#e67e22');

    try {
      await ensureRegionGeo();
    } catch (err) {
      console.warn('Resource area map: state boundaries failed', err);
    }
    if (token !== resourceAreaTokenRef.current || !map.current) return;
    if (typeof map.current.isStyleLoaded === 'function' && !map.current.isStyleLoaded()) {
      map.current.once('idle', () => {
        if (resourceAreaTokenRef.current === token) updateResourceAreaHighlights();
      });
      return;
    }

    ensureResourceAreaLayers();

    const statesFc = getStatesFeatureCollection();
    const stateFeatures = coverage.states.map((stateKey) => {
      const base = findStateFeature(statesFc, stateKey);
      if (!base?.geometry) return null;
      return {
        type: 'Feature',
        properties: {
          regionId: `state:${stateKey}`,
          scope: 'state',
          label: stateKey,
          ...style,
        },
        geometry: base.geometry,
      };
    }).filter(Boolean);

    let placeMap = new Map();
    try {
      placeMap = await fetchCityPlacePolygons(coverage.cities);
    } catch (err) {
      console.warn('Resource area map: city polygons failed', err);
    }
    if (token !== resourceAreaTokenRef.current || !map.current) return;

    const cityFeatures = coverage.cities.map((c) => {
      const census = placeMap.get(c.key);
      const geomFeat = census || hullForCityVenues(venues || [], c.city, c.state);
      if (!geomFeat?.geometry) return null;
      return {
        type: 'Feature',
        properties: {
          regionId: `city:${c.key}`,
          scope: 'city',
          label: c.city,
          ...style,
        },
        geometry: geomFeat.geometry,
      };
    }).filter(Boolean);

    paintResourceAreaSource(RESOURCE_AREA_STATE_SRC, stateFeatures);
    paintResourceAreaSource(RESOURCE_AREA_CITY_SRC, cityFeatures);
    setResourceAreaVisibility(stateFeatures.length > 0 || cityFeatures.length > 0);
    clearAreaLoading();
    console.log(
      `Resource area highlights: ${stateFeatures.length} states, ${cityFeatures.length} cities`
    );
  };

  // ── ZIP / ZCTA tier (viewport-loaded, high zoom only) ──────────────────────
  const ZCTA_MIN_ZOOM = 9;

  /** Add the (initially empty) ZIP source + fill layer once. */
  const ensureAffiliateZctaLayer = () => {
    if (!map.current || map.current.getLayer('affiliate-region-zcta-fill')) return;
    if (!map.current.getSource('affiliate-region-zcta')) {
      map.current.addSource('affiliate-region-zcta', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        promoteId: 'regionId',
      });
    }
    const beforeId = getRegionInsertBeforeId();
    map.current.addLayer({
      id: 'affiliate-region-zcta-fill',
      type: 'fill',
      source: 'affiliate-region-zcta',
      minzoom: ZCTA_MIN_ZOOM,
      maxzoom: 13,
      layout: { visibility: 'none' },
      paint: {
        'fill-color': ['coalesce', ['feature-state', 'fillColor'], 'rgba(128,128,128,0.16)'],
        'fill-opacity': 1, // hard switch — no fade
      },
    }, beforeId);
  };

  /** Fetch + color ZIP areas for the current viewport (debounced, latest-wins). */
  const updateAffiliateZctaTier = () => {
    if (!map.current || !mapLoaded) return;
    const active = mapViewMode === 'concentration';
    const zoom = map.current.getZoom();
    if (!active || zoom < ZCTA_MIN_ZOOM) {
      if (map.current.getLayer('affiliate-region-zcta-fill')) {
        map.current.setLayoutProperty('affiliate-region-zcta-fill', 'visibility', 'none');
      }
      return;
    }
    if (zctaDebounceRef.current) clearTimeout(zctaDebounceRef.current);
    zctaDebounceRef.current = setTimeout(async () => {
      if (!map.current) return;
      const token = ++zctaFetchTokenRef.current;
      const b = map.current.getBounds();
      const bounds = { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() };
      const offset = map.current.getZoom() >= 11 ? 0.0008 : 0.003;
      let data;
      try {
        data = await fetchZctaForBounds(bounds, offset);
      } catch (err) {
        console.warn('Affiliate region map: ZIP tier fetch failed', err);
        return;
      }
      if (token !== zctaFetchTokenRef.current || !map.current) return; // superseded / unmounted
      ensureAffiliateZctaLayer();
      const src = map.current.getSource('affiliate-region-zcta');
      if (!src) return;
      src.setData(data.fc);
      try {
        map.current.removeFeatureState({ source: 'affiliate-region-zcta' });
      } catch (e) { /* no-op */ }
      const pool = hasVenueRevealCriteriaRef.current ? (filteredVenuesRef.current || []) : [];
      const venuesInView = filterVenuesByBounds(pool, b);
      aggregateAgainstEntries(venuesInView, data.entries, concentrationBucketsOn).forEach((r) => {
        map.current.setFeatureState(
          { source: 'affiliate-region-zcta', id: r.id },
          { color: r.color, fillColor: r.fillColor },
        );
      });
      map.current.setLayoutProperty('affiliate-region-zcta-fill', 'visibility', 'visible');
    }, 300);
  };

  // ── International tier (non-US countries: country → region → city) ──────────
  // Two-part design so the zoomed-out view is instant:
  //   • ADM0 (country): one small world bundle, loaded once + recolored locally
  //     — NO per-country API calls.
  //   • ADM1/ADM2 (region/city): geoBoundaries geometry fetched on demand, only
  //     for the non-US countries with venues in view, only once zoomed in.
  // The US keeps its dedicated state/county/ZIP tiers above.
  const INTL_ADM1_MIN_ZOOM = 4.5; // country → region (ADM1)
  const INTL_ADM2_MIN_ZOOM = 6.5; // region → city / municipality (ADM2)

  const intlLevelForZoom = (zoom) => {
    if (zoom >= INTL_ADM2_MIN_ZOOM) return 'ADM2';
    if (zoom >= INTL_ADM1_MIN_ZOOM) return 'ADM1';
    return 'ADM0';
  };

  // ── ADM0 (country) static tier — preloaded once, recolored locally ─────────
  /** Add the world-country source + fill layer once (data already decoded). */
  const ensureAffiliateIntlAdm0Layer = () => {
    if (!map.current) return;
    const geo = getWorldCountriesGeo();
    if (!geo || !geo.fc.features.length) return;
    if (!map.current.getSource('affiliate-region-intl-adm0')) {
      map.current.addSource('affiliate-region-intl-adm0', {
        type: 'geojson', data: geo.fc, promoteId: 'regionId',
      });
    }
    if (map.current.getLayer('affiliate-region-intl-adm0-fill')) return;
    const beforeId = getRegionInsertBeforeId();
    map.current.addLayer({
      id: 'affiliate-region-intl-adm0-fill',
      type: 'fill',
      source: 'affiliate-region-intl-adm0',
      maxzoom: INTL_ADM1_MIN_ZOOM, // hand off to the on-demand ADM1/ADM2 layer
      layout: { visibility: 'none' },
      paint: {
        // Countries without venues stay fully transparent (no gray wash of the
        // whole globe); only covered countries pick up a color.
        'fill-color': ['coalesce', ['feature-state', 'fillColor'], 'rgba(0,0,0,0)'],
        'fill-opacity': 1,
      },
    }, beforeId);
  };

  /** Recolor the ADM0 country tier from all affiliate-filtered venues. */
  const applyAffiliateIntlAdm0Colors = (venueList) => {
    if (!map.current || !map.current.getSource('affiliate-region-intl-adm0')) return;
    const geo = getWorldCountriesGeo();
    if (!geo) return;
    try {
      map.current.removeFeatureState({ source: 'affiliate-region-intl-adm0' });
    } catch (e) { /* no-op */ }
    aggregateAgainstEntries(venueList || [], geo.entries, concentrationBucketsOn).forEach((r) => {
      map.current.setFeatureState(
        { source: 'affiliate-region-intl-adm0', id: r.id },
        { color: r.color, fillColor: r.fillColor },
      );
    });
  };

  /** Load (once) + build + color the ADM0 country tier. */
  const activateAffiliateIntlAdm0 = async (venueList) => {
    try {
      await ensureWorldCountriesGeo();
    } catch (err) {
      console.warn('International region map: world country bundle failed', err);
      return;
    }
    if (!map.current || mapViewModeRef.current !== 'concentration') return;
    if (typeof map.current.isStyleLoaded === 'function' && !map.current.isStyleLoaded()) {
      map.current.once('idle', () => {
        if (mapViewModeRef.current === 'concentration') {
          activateAffiliateIntlAdm0(venuesRef.current || []);
        }
      });
      return;
    }
    ensureAffiliateIntlAdm0Layer();
    applyAffiliateIntlAdm0Colors(venueList || []);
    if (map.current.getLayer('affiliate-region-intl-adm0-fill')) {
      map.current.setLayoutProperty('affiliate-region-intl-adm0-fill', 'visibility', 'visible');
    }
  };

  /** Add the (initially empty) international source + fill layer once. */
  const ensureAffiliateIntlLayer = () => {
    if (!map.current || map.current.getLayer('affiliate-region-intl-fill')) return;
    if (!map.current.getSource('affiliate-region-intl')) {
      map.current.addSource('affiliate-region-intl', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        promoteId: 'regionId',
      });
    }
    const beforeId = getRegionInsertBeforeId();
    map.current.addLayer({
      id: 'affiliate-region-intl-fill',
      type: 'fill',
      source: 'affiliate-region-intl',
      layout: { visibility: 'none' },
      paint: {
        'fill-color': ['coalesce', ['feature-state', 'fillColor'], 'rgba(128,128,128,0.16)'],
        'fill-opacity': 1, // hard switch — no fade
      },
    }, beforeId);
  };

  const hideAffiliateIntlTier = () => {
    if (map.current && map.current.getLayer('affiliate-region-intl-fill')) {
      map.current.setLayoutProperty('affiliate-region-intl-fill', 'visibility', 'none');
    }
  };

  /** Fetch + color ADM1/ADM2 regions for the current viewport (debounced, latest-wins). */
  const updateAffiliateIntlTier = () => {
    if (!map.current || !mapLoaded) return;
    if (mapViewMode !== 'concentration') { hideAffiliateIntlTier(); return; }
    // ADM0 (country) is served by the preloaded static tier — no API needed.
    if (intlLevelForZoom(map.current.getZoom()) === 'ADM0') { hideAffiliateIntlTier(); return; }
    if (intlDebounceRef.current) clearTimeout(intlDebounceRef.current);
    intlDebounceRef.current = setTimeout(async () => {
      if (!map.current) return;
      const token = ++intlFetchTokenRef.current;
      const b = map.current.getBounds();
      const level = intlLevelForZoom(map.current.getZoom());
      const pool = hasVenueRevealCriteriaRef.current ? (filteredVenuesRef.current || []) : [];
      const venuesInView = filterVenuesByBounds(pool, b);
      const countries = collectVenueCountries(venuesInView);
      if (!countries.length) { hideAffiliateIntlTier(); return; }

      let loaded;
      try {
        loaded = await Promise.all(countries.map((iso) => ensureCountryRegions(iso, level)));
      } catch (err) {
        console.warn('International region map: geometry load failed', err);
        return;
      }
      if (token !== intlFetchTokenRef.current || !map.current) return; // superseded / unmounted

      const features = [];
      const entries = [];
      loaded.forEach((res) => {
        if (!res) return;
        features.push(...res.fc.features);
        entries.push(...res.entries);
      });
      if (!features.length) { hideAffiliateIntlTier(); return; }

      ensureAffiliateIntlLayer();
      const src = map.current.getSource('affiliate-region-intl');
      if (!src) return;
      src.setData({ type: 'FeatureCollection', features });
      try {
        map.current.removeFeatureState({ source: 'affiliate-region-intl' });
      } catch (e) { /* no-op */ }
      aggregateAgainstEntries(venuesInView, entries, concentrationBucketsOn).forEach((r) => {
        map.current.setFeatureState(
          { source: 'affiliate-region-intl', id: r.id },
          { color: r.color, fillColor: r.fillColor },
        );
      });
      map.current.setLayoutProperty('affiliate-region-intl-fill', 'visibility', 'visible');
    }, 300);
  };

  // Enhance coordinate validation with more strict checks
  const isValidCoordinate = (venue) => {
    if (!venue.latitude || !venue.longitude) return false;

    const lat = parseFloat(venue.latitude);
    const lng = parseFloat(venue.longitude);

    // Basic range checks
    if (!isValidLatitude(lat) || !isValidLongitude(lng)) return false;

    // Check for common invalid coordinates
    if (lat === 0 && lng === 0) return false; // Null Island
    if (Math.abs(lat) < 0.1 && Math.abs(lng) < 0.1) return false; // Too close to 0,0

    // Check if coordinates are in the ocean (basic check)
    // This is a simple check that can be enhanced with more sophisticated ocean detection
    const isLikelyOcean = [
      { lat: 0, lng: 0, radius: 10 }, // Null Island
      { lat: -48.876667, lng: -123.393333, radius: 20 }, // Point Nemo
      // Add more known ocean points if needed
    ].some(point => {
      const distance = calculateDistance(lat, lng, point.lat, point.lng);
      return distance < point.radius;
    });

    if (isLikelyOcean) return false;

    // For US venues, ensure they're roughly within continental US bounds
    if (venue.country === 'US' || venue.country === 'USA') {
      return lat >= 24.396308 && lat <= 49.384358 && // Latitude bounds for continental US
        lng >= -125.000000 && lng <= -66.934570; // Longitude bounds for continental US
    }

    return true;
  };

  // Toggle panel visibility with animation
  const toggleVenueList = () => {
    if (venueListVisible) {
      Animated.timing(venueListAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start(() => setVenueListVisible(false));
    } else {
      setVenueListVisible(true);
      Animated.timing(venueListAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
    // Resize map after animation completes
    setTimeout(() => {
      if (map.current) {
        map.current.resize();
      }
    }, 350);
  };

  const toggleTourStops = () => {
    if (tourStopsVisible) {
      Animated.timing(tourStopsAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start(() => setTourStopsVisible(false));
    } else {
      openTourPlanner();
    }
    // Resize map after animation completes
    setTimeout(() => {
      if (map.current) {
        map.current.resize();
      }
    }, 350);
  };

  const openTourPlanner = () => {
    setRightRailTab(null);
    if (isMobile) setWeatherExpanded(false);
    if (!tourStopsVisible) {
      setTourStopsVisible(true);
      Animated.timing(tourStopsAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start();
    } else {
      tourStopsAnim.setValue(1);
    }
    setTimeout(() => {
      if (map.current) {
        map.current.resize();
      }
    }, 350);
  };

  // Function to sort venues by distance from user location
  const sortVenuesByDistance = () => {
    if (!userLocation || !venues || venues.length === 0) return;

    console.log('Sorting venues by distance from user location...');

    // Calculate and store distances to avoid recalculating during sort
    const venuesWithDistance = venues.map(venue => {
      if (!venue.latitude || !venue.longitude) {
        return { ...venue, distance: Infinity };
      }

      const distance = calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        venue.latitude,
        venue.longitude
      );

      return { ...venue, distance };
    });

    // Sort by distance and take only the top 100
    const sortedVenues = venuesWithDistance
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 100)
      .map(v => {
        // Remove the distance property before setting state
        const { distance, ...venueWithoutDistance } = v;
        return venueWithoutDistance;
      });

    console.log(`Showing ${sortedVenues.length} closest venues`);
    // List + map are driven by filteredVenues (sorted by distance when userLocation is set) and mapBounds
  };

  // Request user's geolocation
  const requestUserLocation = () => {
    if (navigator && navigator.geolocation) {
      setLocationRequested(true);

      try {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setUserLocation({ latitude, longitude });

            // Add user location marker to map
            if (map.current) {
              // Remove existing user location marker if it exists
              const existingMarker = document.querySelector('.user-location-marker');
              if (existingMarker) {
                existingMarker.remove();
              }

              // Create and add new marker
              const marker = new mapboxgl.Marker({
                color: '#2c7be5',
                scale: 0.8,
                className: 'user-location-marker'
              })
                .setLngLat([longitude, latitude])
                .addTo(map.current);

              // Center map on user location
              map.current.flyTo({
                center: [longitude, latitude],
                zoom: 10,
                essential: true
              });
            }

            // filteredVenues effect will re-run with new userLocation and sort by distance
          },
          (error) => {
            console.error('Error getting location:', error);
            setLocationPermissionDenied(true);
          },
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
        );
      } catch (error) {
        console.error('Geolocation error:', error);
        setLocationPermissionDenied(true);
      }
    } else {
      setLocationPermissionDenied(true);
    }
  };

  // Update fetchVenues function
  const fetchVenues = async () => {
    try {
      setIsInitializing(true);
      console.log('Fetching venues...');
      const db = getFirestore();
      const venuesRef = collection(db, 'venues');
      const venuesSnapshot = await getDocs(venuesRef);

      console.log(`Retrieved ${venuesSnapshot.docs.length} venues`);

      const resolvedDocIds = [];
      const cleanupDocIds = []; // Venues with embedResolvedAt but not resolved by Google — remove those fields
      const venuesData = await Promise.all(venuesSnapshot.docs.map(async (docSnap) => {
        const data = docSnap.data();
        let coordinates = null;
        let coordinateSource = 'none';
        const venueName = data.name || docSnap.id;
        const venueCity = data.city || '';
        const venueState = data.state || '';

        // Use stored venue lat/lng. Google lookup happens on add/approve via Cloud Functions.
        if (data.mapEmbedLink) {
          let coords;
          const viewCenter = extractCoordinatesFromMapEmbed(data.mapEmbedLink);
          const placeNameInEmbed = extractPlaceNameFromMapEmbed(data.mapEmbedLink);
          let hasGoogleResolvedCoords = data.embedResolvedBy === 'google' && data.latitude != null && data.longitude != null;
          if (hasGoogleResolvedCoords && placeNameInEmbed && viewCenter.lat != null && viewCenter.lng != null) {
            const sameAsViewCenter = Math.abs(data.latitude - viewCenter.lat) < 1e-5 && Math.abs(data.longitude - viewCenter.lng) < 1e-5;
            if (sameAsViewCenter) {
              hasGoogleResolvedCoords = false;
              cleanupDocIds.push(docSnap.id);
            }
          }
          if (hasGoogleResolvedCoords) {
            coords = { lat: data.latitude, lng: data.longitude };
            coordinates = validateAndFixCoordinates(coords.lat, coords.lng, venueName, venueCity, venueState);
            if (coordinates) coordinateSource = 'stored';
          } else if (data.embedResolvedAt != null && data.embedResolvedBy !== 'google') {
            cleanupDocIds.push(docSnap.id);
          }
        }

        // Fallback to stored coordinates only if no mapEmbedLink coordinates
        if (!coordinates && data.latitude && data.longitude) {
          coordinates = validateAndFixCoordinates(data.latitude, data.longitude, venueName, venueCity, venueState);
          if (coordinates) {
            coordinateSource = 'stored';
          }
        }

        const venue = {
          id: docSnap.id,
          ...data,
          ...(coordinates || {}), // Spread validated coordinates if they exist
          _coordinateSource: coordinateSource // Track where coords came from for debugging
        };

        return venue;
      }));

      // Filter out venues without valid coordinates
      const validVenues = venuesData.filter(venue => {
        const hasCoords = venue.latitude !== undefined && venue.longitude !== undefined;
        const isValid = hasCoords && isValidLatitude(venue.latitude) && isValidLongitude(venue.longitude);
        // if (!isValid) {
        //   console.log(`Invalid/missing coordinates for venue: ${venue.name || venue.id}`);
        // }
        return isValid;
      });

      // Log coordinate source summary
      const fromEmbed = validVenues.filter(v => v._coordinateSource === 'mapEmbed').length;
      const fromStored = validVenues.filter(v => v._coordinateSource === 'stored').length;
      console.log(`Valid venues processed: ${validVenues.length}`);
      console.log(`  - From Google Maps embed: ${fromEmbed}`);
      console.log(`  - From stored lat/lng: ${fromStored}`);

      setVenues(validVenues);
      venuesRef.current = validVenues; // Keep ref in sync for click handlers

      // Persist newly resolved lat/lng to Firestore (Google only) so we don't re-geocode next time.
      // Guests can view the map but the rules deny their writes, so only signed-in viewers persist.
      if (resolvedDocIds.length > 0 && auth.currentUser) {
        Promise.all(resolvedDocIds.map((id) => {
          const v = validVenues.find((x) => x.id === id);
          if (!v || v.latitude == null || v.longitude == null) return Promise.resolve();
          const venueRef = doc(db, 'venues', id);
          return updateDoc(venueRef, {
            latitude: v.latitude,
            longitude: v.longitude,
            googlePlaceId: v._resolvedGooglePlaceId || v.googlePlaceId || null,
            googleCid: v._resolvedGoogleCid || v.googleCid || null,
            embedResolvedAt: serverTimestamp(),
            embedResolvedBy: 'google',
            embedResolvedMethod: v._resolvedEmbedMethod || null,
          });
        })).catch((err) => console.warn('Failed to persist resolved embed coordinates:', err));
      }

      // Remove embedResolvedAt / latitude / longitude from venues that were NOT resolved by Google
      if (cleanupDocIds.length > 0 && auth.currentUser) {
        Promise.all(cleanupDocIds.map((id) => {
          const venueRef = doc(db, 'venues', id);
          return updateDoc(venueRef, {
            embedResolvedAt: deleteField(),
            embedResolvedBy: deleteField(),
            latitude: deleteField(),
            longitude: deleteField()
          });
        })).catch((err) => console.warn('Failed to clear non-Google embed resolution fields:', err));
      }

      // If we have user location, sort venues by distance
      if (userLocation) {
        console.log(`User location is available, sorting venues by distance`);
        const venuesWithDistance = validVenues.map(venue => {
          if (!venue.latitude || !venue.longitude) return { ...venue, distance: Infinity };
          const distance = calculateDistance(userLocation.latitude, userLocation.longitude, venue.latitude, venue.longitude);
          return { ...venue, distance };
        });
        const sortedVenues = venuesWithDistance
          .sort((a, b) => a.distance - b.distance)
          .slice(0, 100)
          .map(v => {
            const { distance, ...venueWithoutDistance } = v;
            return venueWithoutDistance;
          });
        setVenues(sortedVenues);
      }

      // Display + map update are handled by the filter effect (affiliate/features/coord/sort)
      // Note: requestUserLocation() is triggered by the useEffect when venues load

      setIsInitializing(false);
    } catch (error) {
      console.error('Error fetching venues:', error);
      Alert.alert('Error', 'Failed to load venues. Please try again.');
      setIsInitializing(false);
    }
  };

  // Fetch screenprinters / merch-pickup locations from Firebase
  // Gracefully handles the case where the collection doesn't exist yet.
  const fetchScreenprinters = async () => {
    try {
      const db = getFirestore();
      const ref = collection(db, 'screenprinters');
      const snapshot = await getDocs(ref);
      const data = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      setScreenprinters(data);
      console.log(`Fetched ${data.length} screenprinter/merch locations`);
    } catch (err) {
      console.log('Screenprinters collection not yet available:', err.message);
      setScreenprinters([]);
    }
  };

  // Approved community resources (VenueScreen resource_submissions) → category index for map filters
  const fetchApprovedResourceIndex = async () => {
    try {
      const db = getFirestore();
      const snap = await getDocs(
        query(collection(db, 'resource_submissions'), where('status', '==', 'approved'))
      );
      const submissions = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      const index = buildVenueResourceIndex(submissions);
      setVenueResourceIndex(index);
      setApprovedResourceSubmissions(submissions);
      console.log(
        `Resource index: ${index.byVenueId.size} venues, ${index.byState.size} states, ${index.byCityState.size} cities`,
      );
    } catch (err) {
      console.log('resource_submissions not available for map filters:', err.message);
      setVenueResourceIndex({ byVenueId: new Map(), byState: new Map(), byCityState: new Map() });
      setApprovedResourceSubmissions([]);
    }
  };

  const fetchApprovedFoodLocations = async () => {
    try {
      const db = getFirestore();
      const snap = await getDocs(
        query(collection(db, 'resource_submissions'), where('status', '==', 'approved'))
      );
      const rows = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      const uid = auth.currentUser?.uid;
      if (uid) {
        try {
          const pendingSnap = await getDocs(
            query(
              collection(db, 'resource_submissions'),
              where('status', '==', 'pending'),
              where('userId', '==', uid)
            )
          );
          pendingSnap.docs.forEach((docSnap) => {
            rows.push({ id: docSnap.id, ...docSnap.data() });
          });
        } catch (pendingErr) {
          console.log('Pending food submissions not available:', pendingErr.message);
        }
      }
      const seen = new Set();
      setApprovedFoodLocations(
        rows
          .filter((item) => {
            if (!isFoodDirectoryItem(item) || seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
          })
          .map((item) => ({
            ...item,
            name: item.name || item.resourceName || 'Food location',
          }))
      );
    } catch (err) {
      console.log('Approved food submissions not available yet:', err.message);
      setApprovedFoodLocations([]);
    }
  };

  // Haversine distance in meters between two lat/lng points
  const haversineMeters = (lat1, lng1, lat2, lng2) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // Fetch transit stops (bus, rail, Amtrak) from Overpass for the current map bounds.
  // Skips refetch if bounds haven't changed significantly.
  // (transit markers use circle layers + letter labels — no custom images needed)

  // ── Upcoming shows: inject into an open Mapbox popup via Ticketmaster Discovery API ──
  const fetchUpcomingShowsForPopup = async (venueId, venueName, lat, lng) => {
    const apiKey = process.env.REACT_APP_TICKETMASTER_API_KEY;
    const el = document.getElementById(`upcoming-shows-${venueId}`);
    if (!el) return;

    el.innerHTML = `<p style="font-size:11px;color:rgba(255,255,255,0.35);margin:0;padding-top:8px;">Loading shows…</p>`;

    // ── Step 1: Community posts with calendar items at this venue city ──
    const communityRows = [];
    try {
      const db = getFirestore();
      const venueCity = venueName; // We search by venue name keyword in location field
      const postsSnap = await getDocs(
        query(collection(db, 'communityPosts'), orderBy('createdAt', 'desc'), limit(200))
      );
      const now = new Date();
      postsSnap.forEach(docSnap => {
        const post = docSnap.data();
        const items = post.items || [];
        const calItems = items.filter(it => it.type === 'calendar' && it.events?.length);
        calItems.forEach(cal => {
          (cal.events || []).forEach(ev => {
            if (!ev.date) return;
            const evDate = new Date(ev.date);
            if (evDate < now) return;
            const loc = (post.location || '').toLowerCase();
            const postContent = (post.postContent || '').toLowerCase();
            const venueNameLower = venueName.toLowerCase();
            // Match if post location or content mentions the venue name or city
            const cityFromVenue = (venueName.split(',')[0] || '').toLowerCase().trim();
            if (!loc.includes(cityFromVenue) && !postContent.includes(cityFromVenue) &&
                !loc.includes(venueNameLower) && !postContent.includes(venueNameLower)) return;
            const artistName = post.username || [post.firstName, post.lastName].filter(Boolean).join(' ') || 'VRC Artist';
            const dateLabel = evDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
            communityRows.push({
              artist: artistName,
              title: ev.title || ev.name || artistName,
              dateLabel,
              isCommunity: true,
              url: null,
            });
          });
        });
      });
    } catch (_) {}

    if (!document.getElementById(`upcoming-shows-${venueId}`)) return;

    // ── Step 2: Ticketmaster (fill remaining slots up to 3 total) ──
    const tmEvents = [];
    const remainingSlots = Math.max(0, 3 - communityRows.length);
    if (remainingSlots > 0 && apiKey) {
      try {
        const venueSearchUrl = `https://app.ticketmaster.com/discovery/v2/venues.json?apikey=${apiKey}&keyword=${encodeURIComponent(venueName)}&latlong=${lat},${lng}&radius=5&unit=miles&size=1`;
        const venueRes = await fetch(venueSearchUrl);
        const venueJson = await venueRes.json();
        const tmVenue = venueJson?._embedded?.venues?.[0];
        if (tmVenue?.id) {
          const eventsUrl = `https://app.ticketmaster.com/discovery/v2/events.json?apikey=${apiKey}&venueId=${tmVenue.id}&size=${remainingSlots}&sort=date,asc&classificationName=music`;
          const eventsRes = await fetch(eventsUrl);
          const eventsJson = await eventsRes.json();
          (eventsJson?._embedded?.events || []).forEach(ev => {
            const rawDate = ev.dates?.start?.localDate || '';
            const rawTime = ev.dates?.start?.localTime || '';
            let dateLabel = rawDate;
            try {
              const d = new Date(`${rawDate}T12:00:00`);
              dateLabel = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
              if (rawTime) {
                const [h, m] = rawTime.split(':');
                const hr = parseInt(h, 10);
                dateLabel += ` · ${hr > 12 ? hr - 12 : hr || 12}:${m}${hr >= 12 ? 'pm' : 'am'}`;
              }
            } catch (_) {}
            const priceRange = ev.priceRanges?.[0];
            const priceStr = priceRange
              ? `$${Math.round(priceRange.min)}${priceRange.max !== priceRange.min ? '–$' + Math.round(priceRange.max) : ''}`
              : '';
            tmEvents.push({
              artist: ev._embedded?.attractions?.[0]?.name || ev.name || 'Untitled',
              title: ev.name || '',
              dateLabel,
              priceStr,
              url: ev.url || '#',
              isCommunity: false,
            });
          });
        }
        // Fallback: broader keyword search if venue ID lookup yielded nothing
        if (tmEvents.length === 0) {
          const fbUrl = `https://app.ticketmaster.com/discovery/v2/events.json?apikey=${apiKey}&keyword=${encodeURIComponent(venueName)}&latlong=${lat},${lng}&radius=2&unit=miles&size=${remainingSlots}&sort=date,asc&classificationName=music`;
          const fbRes = await fetch(fbUrl);
          const fbJson = await fbRes.json();
          (fbJson?._embedded?.events || []).forEach(ev => {
            const rawDate = ev.dates?.start?.localDate || '';
            let dateLabel = rawDate;
            try {
              const d = new Date(`${rawDate}T12:00:00`);
              dateLabel = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
            } catch (_) {}
            const priceRange = ev.priceRanges?.[0];
            tmEvents.push({
              artist: ev._embedded?.attractions?.[0]?.name || ev.name || 'Untitled',
              title: ev.name || '',
              dateLabel,
              priceStr: priceRange ? `$${Math.round(priceRange.min)}` : '',
              url: ev.url || '#',
              isCommunity: false,
            });
          });
        }
      } catch (_) {}
    }

    if (!document.getElementById(`upcoming-shows-${venueId}`)) return;

    const allShows = [...communityRows, ...tmEvents];
    if (allShows.length === 0) {
      el.innerHTML = `<p style="font-size:11px;color:rgba(255,255,255,0.25);margin:0;padding-top:8px;">No upcoming shows found</p>`;
      return;
    }

    const rows = allShows.map(show => {
      if (show.isCommunity) {
        return `<div style="display:flex;justify-content:space-between;align-items:flex-start;padding:7px 0;border-bottom:1px solid rgba(255,255,255,0.07);gap:8px;">
          <div style="flex:1;overflow:hidden;">
            <div style="font-size:12px;font-weight:600;color:rgba(255,255,255,0.9);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${show.artist}</div>
            <div style="font-size:10px;color:#2ecc71;font-weight:600;margin-top:1px;">🎸 VRC Community</div>
          </div>
          <div style="text-align:right;flex-shrink:0;">
            <div style="font-size:10px;color:#a29bfe;font-weight:600;white-space:nowrap;">${show.dateLabel}</div>
          </div>
        </div>`;
      }
      const clickUrl = show.url && show.url !== '#' ? `onclick="window.open('${show.url}','_blank');event.stopPropagation();"` : '';
      return `<div ${clickUrl} style="display:flex;justify-content:space-between;align-items:flex-start;padding:7px 0;border-bottom:1px solid rgba(255,255,255,0.07);${show.url && show.url !== '#' ? 'cursor:pointer;' : ''}gap:8px;" onmouseover="this.style.background='rgba(255,255,255,0.04)'" onmouseout="this.style.background='transparent'">
        <div style="flex:1;overflow:hidden;">
          <div style="font-size:12px;font-weight:600;color:rgba(255,255,255,0.9);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${show.artist}</div>
          ${show.title && show.title !== show.artist ? `<div style="font-size:10px;color:rgba(255,255,255,0.4);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${show.title}</div>` : ''}
        </div>
        <div style="text-align:right;flex-shrink:0;">
          <div style="font-size:10px;color:#a29bfe;font-weight:600;white-space:nowrap;">${show.dateLabel}</div>
          ${show.priceStr ? `<div style="font-size:10px;color:rgba(255,255,255,0.35);margin-top:1px;">${show.priceStr}</div>` : ''}
        </div>
      </div>`;
    }).join('');

    const hasCommunity = communityRows.length > 0;
    el.innerHTML = `
      <div style="margin-top:10px;border-top:1px solid rgba(255,255,255,0.12);padding-top:10px;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;">
          <span style="font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:#a29bfe;">🎟 Upcoming Concerts</span>
          ${hasCommunity ? '<span style="font-size:9px;color:#2ecc71;font-weight:600;">✦ VRC Artists</span>' : '<span style="font-size:9px;color:rgba(255,255,255,0.3);">tap · buy tickets</span>'}
        </div>
        ${rows}
      </div>`;
  };

  // ── Weather fetch for venue popup ─────────────────────────────────────────────
  const WMO_LABELS = {
    0:'Clear',1:'Mainly clear',2:'Partly cloudy',3:'Overcast',
    45:'Foggy',48:'Icy fog',51:'Light drizzle',53:'Drizzle',55:'Heavy drizzle',
    61:'Light rain',63:'Rain',65:'Heavy rain',71:'Light snow',73:'Snow',75:'Heavy snow',
    80:'Showers',81:'Showers',82:'Violent showers',
    95:'Thunderstorm',96:'Thunderstorm+hail',99:'Thunderstorm+hail',
  };
  const wmoLabel = (code) => WMO_LABELS[code] || 'Unknown';
  const wmoEmoji = (code) => {
    if (code === 0) return '☀️';
    if (code <= 2) return '🌤';
    if (code <= 3) return '☁️';
    if (code <= 48) return '🌫';
    if (code <= 55) return '🌦';
    if (code <= 65) return '🌧';
    if (code <= 77) return '❄️';
    if (code <= 82) return '🌧';
    return '⛈';
  };

  const fetchVenueWeatherForPopup = async (venueId, lat, lng) => {
    const el = document.getElementById(`venue-weather-${venueId}`);
    if (!el) return;
    try {
      const { weather } = await fetchWeather(lat, lng);
      if (!document.getElementById(`venue-weather-${venueId}`)) return;
      const cur = weather?.current;
      if (!cur) return;
      const temp = Math.round(cur.temperature_2m);
      const feels = Math.round(cur.apparent_temperature);
      const code = cur.weather_code ?? 0;
      el.innerHTML = `<div style="display:flex;align-items:center;gap:8px;padding:5px 0;border-top:1px solid rgba(255,255,255,0.08);margin-top:4px;">
        <span style="font-size:18px;line-height:1;">${wmoEmoji(code)}</span>
        <div>
          <span style="font-size:13px;font-weight:700;color:rgba(255,255,255,0.9);">${temp}°F</span>
          <span style="font-size:11px;color:rgba(255,255,255,0.45);margin-left:5px;">Feels ${feels}°</span>
          <span style="font-size:11px;color:rgba(255,255,255,0.45);margin-left:5px;">· ${wmoLabel(code)}</span>
        </div>
      </div>`;
    } catch (_) {}
  };

  const fetchTransitStops = async (bounds) => {
    const s = bounds.getSouth(), w = bounds.getWest(), n = bounds.getNorth(), e = bounds.getEast();
    const prev = transitFetchedBoundsRef.current;
    if (prev && Math.abs(prev.s - s) < 0.2 && Math.abs(prev.n - n) < 0.2 &&
        Math.abs(prev.w - w) < 0.2 && Math.abs(prev.e - e) < 0.2) return;
    setTransitLoading(true);
    transitFetchedBoundsRef.current = { s, w, n, e };

    // Overpass QL: bus stops, train/Amtrak, airports
    const query = `
      [out:json][timeout:25];
      (
        node["amenity"="bus_station"](${s},${w},${n},${e});
        node["public_transport"="stop_position"]["bus"="yes"](${s},${w},${n},${e});
        node["highway"="bus_stop"](${s},${w},${n},${e});
        node["railway"="station"](${s},${w},${n},${e});
        node["railway"="halt"](${s},${w},${n},${e});
        node["aeroway"="aerodrome"]["iata"](${s},${w},${n},${e});
        node["aeroway"="terminal"](${s},${w},${n},${e});
      );
      out body;
    `;
    try {
      const res = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        body: query,
      });
      const json = await res.json();
      const stops = (json.elements || []).map(el => ({
        id: el.id,
        lat: el.lat,
        lng: el.lon,
        name: el.tags?.name || el.tags?.iata || el.tags?.operator || 'Transit Stop',
        type: (el.tags?.aeroway === 'aerodrome' || el.tags?.aeroway === 'terminal') ? 'airport' :
              el.tags?.railway === 'station' || el.tags?.railway === 'halt' ? 'rail' : 'bus',
      }));
      setTransitStops(stops);
    } catch (err) {
      console.log('Transit fetch error:', err.message);
    } finally {
      setTransitLoading(false);
    }
  };

  // Fetch user's custom locations (own = all for this user; public = others' public locations)
  const fetchUserLocations = async () => {
    try {
      const db = getFirestore();

      // Fetch user's own locations (userId in Firestore is auth uid)
      const userLocationsRef = collection(db, 'userLocations');
      const uid = auth.currentUser?.uid;
      const locations = [];
      if (uid) {
        const ownSnapshot = await getDocs(query(userLocationsRef, where('userId', '==', uid)));
        ownSnapshot.docs.forEach((row) => {
          locations.push({ id: row.id, ...row.data(), isOwn: true });
        });
      }
      setUserLocations(locations);
      console.log(`Fetched ${locations.length} user locations`);

      // Fetch public locations from other users only (exclude current user's)
      const publicQuery = query(
        userLocationsRef,
        where('isPublic', '==', true),
      );
      const publicSnapshot = await getDocs(publicQuery);
      const ownIds = new Set(locations.map((loc) => loc.id));

      const publicLocs = publicSnapshot.docs
        .map((row) => ({
          id: row.id,
          ...row.data(),
          isOwn: false,
        }))
        .filter((loc) => !ownIds.has(loc.id));
      setPublicUserLocations(publicLocs);
      console.log(`Fetched ${publicLocs.length} public user locations`);

    } catch (error) {
      console.error('Error fetching user locations:', error);
    }
  };

  // Search for addresses using Mapbox Geocoding API (worldwide, normalized addresses)
  const searchAddress = async (query) => {
    if (!query || query.trim().length < 2) {
      setAddressSearchResults([]);
      return;
    }

    setIsSearchingAddress(true);

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query.trim())}.json?` +
        `access_token=${MAPBOX_ACCESS_TOKEN}&` +
        `types=address,poi,place,locality,neighborhood,region&` +
        `limit=8&` +
        `language=en`
      );

      if (!response.ok) throw new Error('Geocoding request failed');

      const data = await response.json();

      const results = (data.features || []).map(feature => ({
        id: feature.id,
        name: feature.text || feature.place_name || 'Unnamed',
        fullAddress: feature.place_name,
        coordinates: {
          lng: feature.center[0],
          lat: feature.center[1]
        },
        type: (feature.place_type && feature.place_type[0]) || 'place',
        context: feature.context || []
      }));

      setAddressSearchResults(results);
    } catch (error) {
      console.error('Error searching address:', error);
      setAddressSearchResults([]);
    } finally {
      setIsSearchingAddress(false);
    }
  };

  // Debounced address search
  const debouncedAddressSearch = useRef(
    _.debounce((query) => searchAddress(query), 300)
  ).current;

  // Handle address search input
  const handleAddressSearchChange = (text) => {
    setAddressSearchQuery(text);
    debouncedAddressSearch(text);
  };

  // Select an address from search results
  const selectAddressResult = (result) => {
    setNewLocationData(prev => ({
      ...prev,
      name: prev.name || result.name,
      address: result.fullAddress
    }));
    setPendingLocationCoords(result.coordinates);
    setAddressSearchResults([]);
    setAddressSearchQuery(result.fullAddress);

    // Open the form panel to complete location details
    setShowAddLocationPanel(true);

    // Fly to the selected location
    if (map.current) {
      map.current.flyTo({
        center: [result.coordinates.lng, result.coordinates.lat],
        zoom: 16,
        essential: true
      });
    }
  };

  // Open add location panel (from search or map click)
  const openAddLocationPanel = (coords = null) => {
    if (!mapGateRef.current(MAP_ACTION.CONTRIBUTE)) return;
    setPendingLocationCoords(coords);
    setShowAddLocationPanel(true);
    setNewLocationData({
      name: '',
      notes: '',
      category: 'other',
      address: '',
      isPublic: false,
      capacity: '',
      affiliate: ''
    });
    setAddressSearchQuery('');
    setAddressSearchResults([]);
  };

  // Close add location panel
  const closeAddLocationPanel = () => {
    setShowAddLocationPanel(false);
    setPendingLocationCoords(null);
    setAddLocationMode(false);
    setNewLocationData({
      name: '',
      notes: '',
      category: 'other',
      address: '',
      isPublic: false,
      capacity: '',
      affiliate: ''
    });
    setAddressSearchQuery('');
    setAddressSearchResults([]);
    setSuggestCategoryLabel('');
    setSuggestCategoryIcon('');
  };

  // Add a new custom location
  const addUserLocation = async (lng, lat) => {
    if (!userData?.uid) {
      mapGateRef.current(MAP_ACTION.CONTRIBUTE);
      return;
    }

    if (!newLocationData.name.trim()) {
      Alert.alert('Name Required', 'Please enter a name for your location.');
      return;
    }

    try {
      const db = getFirestore();
      const cap = newLocationData.capacity !== '' && newLocationData.capacity != null ? Number(String(newLocationData.capacity).replace(/\D/g, '')) || null : null;
      const locationData = {
        userId: userData.uid,
        userName: userData.name || userData.username || userData.email || 'Anonymous',
        name: newLocationData.name.trim(),
        notes: newLocationData.notes.trim(),
        category: newLocationData.category || 'other',
        address: newLocationData.address.trim(),
        latitude: lat,
        longitude: lng,
        isPublic: newLocationData.isPublic,
        ...(cap != null && !Number.isNaN(cap) && { capacity: cap }),
        ...(newLocationData.affiliate?.trim() && { affiliate: newLocationData.affiliate.trim() }),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'userLocations'), locationData);
      console.log('Added user location:', docRef.id);

      // Add to local state
      setUserLocations(prev => [...prev, { id: docRef.id, ...locationData, isOwn: true }]);

      // Reset form and close panel
      closeAddLocationPanel();

      // Close any popup
      const popups = document.getElementsByClassName('mapboxgl-popup');
      while (popups.length > 0) {
        popups[0].remove();
      }

      Alert.alert('Success', 'Location added successfully!');

      // Update map to show new location
      updateMapWithUserLocations();

    } catch (error) {
      console.error('Error adding user location:', error);
      Alert.alert('Error', 'Failed to add location. Please try again.');
    }
  };

  // Update a custom location
  const updateUserLocation = async (locationId, updates) => {
    if (!userData?.uid) return;

    try {
      const db = getFirestore();
      const locationRef = doc(db, 'userLocations', locationId);

      await updateDoc(locationRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setUserLocations(prev => prev.map(loc =>
        loc.id === locationId ? { ...loc, ...updates } : loc
      ));

      setEditingLocation(null);
      console.log('Updated user location:', locationId);

      // Update map
      updateMapWithUserLocations();

    } catch (error) {
      console.error('Error updating user location:', error);
      Alert.alert('Error', 'Failed to update location. Please try again.');
    }
  };

  // Delete a custom location
  const deleteUserLocation = (locationId) => {
    if (!userData?.id) return;

    showConfirmation(
      'Delete Location',
      'Are you sure you want to delete this location?',
      async () => {
        try {
          const db = getFirestore();
          await deleteDoc(doc(db, 'userLocations', locationId));

          // Remove from local state
          setUserLocations(prev => prev.filter(loc => loc.id !== locationId));
          console.log('Deleted user location:', locationId);

          // Update map
          updateMapWithUserLocations();
        } catch (error) {
          console.error('Error deleting user location:', error);
          showPopup('Failed to delete location. Please try again.', 'error');
        }
      }
    );
  };

  // Toggle location public/private
  const toggleLocationVisibility = async (locationId, currentIsPublic) => {
    await updateUserLocation(locationId, { isPublic: !currentIsPublic });
    Alert.alert('Updated', `Location is now ${!currentIsPublic ? 'public' : 'private'}.`);
  };

  // ============================================================================
  // MAP DRAWING FUNCTIONS - Separated for better organization and reusability
  // ============================================================================

  // Route colors for different tour days
  const ROUTE_COLORS = [
    '#2c7be5', // Light blue
    '#9b59b6', // Purple
    '#27ae60', // Green 
    '#3498db', // Darker blue
    '#8e44ad', // Dark purple
    '#16a085', // Sea green
    '#2980b9', // Navy blue
    '#2ecc71'  // Emerald
  ];

  // CO2 / mode-based route colors: green (low) → orange → red (high)
  const MODE_CO2_COLORS = {
    walk:   '#2ecc71', // bright green — zero emissions
    train:  '#27ae60', // green — very low
    bus:    '#f1c40f', // yellow — low-medium
    drive:  '#e67e22', // orange — medium
    flight: '#e74c3c', // red — high
  };

  /**
   * Draw venues/clusters on the map
   * @param {Array} venuesToShow - Array of venues to display
   * @param {Array} tourDaysData - Array of tour days (for identifying tour venues)
   * @param {string} coordSourceFilter - Filter by coordinate source ('all', 'mapEmbed', 'stored')
   */
  const drawClusters = (venuesToShow, tourDaysData = [], coordSourceFilter = 'all') => {
    if (!map.current || !map.current.getSource('venues-source')) return;

    // Handle case where venues are hidden (showVenues = false)
    if (!venuesToShow) {
      venuesToShow = [];
    }

    // If no venues to show (venues hidden or empty), clear the map
    if (venuesToShow.length === 0) {
      map.current.getSource('venues-source').setData({
        type: 'FeatureCollection',
        features: []
      });
      return;
    }

    let venuesToDisplay = venuesToShow;

    // Apply coordinate source filter (admin feature)
    if (coordSourceFilter && coordSourceFilter !== 'all') {
      venuesToDisplay = venuesToDisplay.filter(venue => venue._coordinateSource === coordSourceFilter);
    }

    // Get all venues from all days FIRST (needed for isTourVenue check)
    const allTourVenues = tourDaysData ? tourDaysData.flatMap(day => day.venues) : [];

    // Cap at 1000 venues for map/clustering performance; cluster counts reflect this set
    const visibleVenues = venuesToDisplay.length > 1000
      ? venuesToDisplay.slice(0, 3000)
      : venuesToDisplay;


    // Create features with special property to identify tour venues
    const features = visibleVenues.map(venue => {
      // Check if this venue is part of the tour
      const isTourVenue = allTourVenues.some(tv => tv.id === venue.id);

      const lat = parseFloat(venue.latitude);
      const lng = parseFloat(venue.longitude);

      // Sustainability / eco flags
      const backstageLower = (venue.backstage || '').toLowerCase();
      const notesLower = (venue.notes || '').toLowerCase();
      const hasLodging   = backstageLower.includes('lodging') || backstageLower.includes('hotel') || backstageLower.includes('bed');
      const hasVeganFood = (venue.name || '').toLowerCase().includes('vegan') ||
        (venue.name || '').toLowerCase().includes('plant-based') ||
        notesLower.includes('vegan') ||
        (venue.affiliate || '').toLowerCase().includes('vegan');
      const hasShorePower = (venue.shorePower || '').toLowerCase().includes('yes') ||
        backstageLower.includes('shore power') || notesLower.includes('shore power');
      const hasMerch100   = (venue.merch || '').toLowerCase().includes('100%');
      // isNearTransit set later once transit stops are fetched; initially false
      const isNearTransit = (venue._isNearTransit === true);
      const isEcoConscious = venue.isEcoConscious === true;
      const isEco = hasLodging || hasVeganFood || hasShorePower || hasMerch100 || isNearTransit || isEcoConscious;
      const hasEducation = venueHasEducationResource(venue);

      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [lng, lat]
        },
        properties: {
          id: venue.id,
          name: venue.name,
          city: venue.city,
          state: venue.state,
          capacity: venue.capacity || 0,
          venueType: venue.venueType || 'Venue',
          affiliate: venue.affiliate,
          affiliateColor: affiliateChipColor(venue.affiliate),
          isTourVenue: isTourVenue,
          coordinateSource: venue._coordinateSource || 'stored',
          hasMapEmbedLink: !!(venue.mapEmbedLink && venue.mapEmbedLink.trim()),
          hasLodging: hasLodging,
          hasVeganFood: hasVeganFood,
          hasShorePower: hasShorePower,
          hasMerch100: hasMerch100,
          isNearTransit: isNearTransit,
          isEcoConscious: isEcoConscious,
          isEco: isEco,
          // Use 1/0 — Mapbox GeoJSON filters are more reliable than boolean true.
          hasEducation: hasEducation ? 1 : 0,
        }
      };
    });

    const validFeatures = features.filter(f => {
      const lng = f.geometry.coordinates[0];
      const lat = f.geometry.coordinates[1];
      return !isNaN(lng) && !isNaN(lat) &&
        Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
    });

    console.log(`Setting ${validFeatures.length} valid features on map`);

    map.current.getSource('venues-source').setData({
      type: 'FeatureCollection',
      features: validFeatures
    });
    ensureEducationHatLayer(map.current);
  };

  // Tracks whether the live venues-source currently has clustering enabled, so
  // we only tear down / rebuild when the chosen display mode actually changes.
  const venuesClusterEnabledRef = useRef(true);

  const VENUE_LAYER_IDS = [
    'clusters-shadow', 'clusters', 'cluster-count',
    'unclustered-point-shadow', 'unclustered-point', 'venue-labels', 'eco-ring', 'education-hat',
  ];

  /** Show/hide every venue layer (used by the "Hide" display option). */
  const setVenueLayersVisibility = (visible) => {
    if (!map.current) return;
    VENUE_LAYER_IDS.forEach((id) => {
      if (map.current.getLayer(id)) {
        map.current.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
      }
    });
  };

  /** Recolor venue pins — affiliate fill always; stroke tweaks in concentration mode. */
  const applyVenuePinColors = () => {
    if (!map.current || !map.current.getLayer('unclustered-point')) return;
    const on = mapViewMode === 'concentration';
    map.current.setPaintProperty('unclustered-point', 'circle-color', VENUE_PIN_AFFILIATE_COLOR_EXPR);
    map.current.setPaintProperty('unclustered-point', 'circle-stroke-color',
      on ? ['case', ['==', ['get', 'isTourVenue'], true], '#27ae60', '#ffffff'] : '#ffffff');
    // Fade the white outline out when zoomed far out so small pins read as clean dots.
    map.current.setPaintProperty('unclustered-point', 'circle-stroke-width',
      on
        ? ['interpolate', ['linear'], ['zoom'],
            5, 0,
            8, ['case', ['==', ['get', 'isTourVenue'], true], 2, 1],
            11, ['case', ['==', ['get', 'isTourVenue'], true], 3.5, 2]]
        : ['interpolate', ['linear'], ['zoom'], 5, 0, 8, 1.2, 11, 2.5]);
    // Drop the drop-shadow too when zoomed far out (clean dots, no halo).
    if (map.current.getLayer('unclustered-point-shadow')) {
      map.current.setPaintProperty('unclustered-point-shadow', 'circle-opacity',
        ['interpolate', ['linear'], ['zoom'], 5, 0, 8, 1]);
    }
  };

  /**
   * Apply the chosen venue display mode:
   *  - 'hidden'   → hide all venue layers (just the map / concentration base)
   *  - 'clusters' → clustered groups
   *  - 'pins'     → individual pins (clustering off), scaled down at low zoom
   * Mapbox can't toggle a GeoJSON source's `cluster` flag in place, so switching
   * between clusters/pins rebuilds the venue layers + source.
   */
  const applyVenueDisplayMode = () => {
    if (!map.current || !mapLoaded) return;
    if (typeof map.current.isStyleLoaded === 'function' && !map.current.isStyleLoaded()) return;
    if (!map.current.getSource('venues-source')) return;

    if (displayMode === 'hidden') {
      setVenueLayersVisibility(false);
      return;
    }
    setVenueLayersVisibility(true);

    const enableCluster = !masterTourUi && displayMode === 'clusters';
    if (venuesClusterEnabledRef.current === enableCluster) {
      applyVenuePinColors();
      return; // clustering already correct — just (re)color
    }

    VENUE_LAYER_IDS.forEach((id) => {
      if (map.current.getLayer(id)) map.current.removeLayer(id);
    });
    if (map.current.getSource('venues-source')) map.current.removeSource('venues-source');

    map.current.addSource('venues-source', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      cluster: enableCluster,
      clusterMaxZoom: clusterMaxZoom,
      clusterRadius: clusterRadius,
      clusterProperties: { sum: ['+', ['coalesce', ['get', 'capacity'], 0]] },
    });

    // Pins mode: scale individual markers by zoom (smaller when zoomed out).
    const pointRadius = enableCluster
      ? ['case', ['==', ['get', 'isTourVenue'], true], 10, 7]
      : ['interpolate', ['linear'], ['zoom'],
          3, ['case', ['==', ['get', 'isTourVenue'], true], 3.5, 2.5],
          6, ['case', ['==', ['get', 'isTourVenue'], true], 6, 4],
          10, ['case', ['==', ['get', 'isTourVenue'], true], 10, 7]];
    const shadowRadius = enableCluster
      ? ['case', ['==', ['get', 'isTourVenue'], true], 12, 9]
      : ['interpolate', ['linear'], ['zoom'],
          3, ['case', ['==', ['get', 'isTourVenue'], true], 4.5, 3.5],
          6, ['case', ['==', ['get', 'isTourVenue'], true], 7.5, 5],
          10, ['case', ['==', ['get', 'isTourVenue'], true], 12, 9]];

    map.current.addLayer({
      id: 'clusters-shadow', type: 'circle', source: 'venues-source', filter: ['has', 'point_count'],
      paint: {
        'circle-color': 'rgba(0, 0, 0, 0.2)',
        'circle-radius': ['step', ['get', 'point_count'], 22, 100, 32, 750, 42],
        'circle-translate': [2, 2], 'circle-blur': 0.5,
      },
    });
    map.current.addLayer({
      id: 'clusters', type: 'circle', source: 'venues-source', filter: ['has', 'point_count'],
      paint: {
        'circle-color': ['step', ['get', 'point_count'], theme.markers.cluster, 50, theme.markers.clusterMedium, 200, theme.markers.clusterLarge],
        'circle-radius': ['step', ['get', 'point_count'], 18, 50, 24, 200, 32],
        'circle-stroke-width': 3, 'circle-stroke-color': '#EFEFEF',
      },
    });
    map.current.addLayer({
      id: 'cluster-count', type: 'symbol', source: 'venues-source', filter: ['has', 'point_count'],
      layout: { 'text-field': '{point_count_abbreviated}', 'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'], 'text-size': 13 },
      paint: { 'text-color': '#ffffff' },
    });
    map.current.addLayer({
      id: 'unclustered-point-shadow', type: 'circle', source: 'venues-source', filter: ['!', ['has', 'point_count']],
      paint: { 'circle-color': 'rgba(0, 0, 0, 0.25)', 'circle-radius': shadowRadius, 'circle-translate': [1, 1], 'circle-blur': 0.4 },
    });
    map.current.addLayer({
      id: 'unclustered-point', type: 'circle', source: 'venues-source', filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': VENUE_PIN_AFFILIATE_COLOR_EXPR,
        'circle-radius': pointRadius,
        'circle-stroke-width': 2.5, 'circle-stroke-color': '#ffffff',
      },
    });
    map.current.addLayer({
      id: 'venue-labels', type: 'symbol', source: 'venues-source', filter: ['!', ['has', 'point_count']], minzoom: 12,
      layout: { 'text-field': ['get', 'name'], 'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'], 'text-size': 11, 'text-offset': [0, 1.2], 'text-anchor': 'top', 'text-max-width': 10 },
      paint: { 'text-color': '#1a1a1a', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5, 'text-halo-blur': 0.5 },
    });
    map.current.addLayer({
      id: 'eco-ring', type: 'circle', source: 'venues-source', filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'isEco'], true]],
      paint: { 'circle-color': 'rgba(0,0,0,0)', 'circle-radius': ['case', ['==', ['get', 'isTourVenue'], true], 15, 12], 'circle-stroke-width': 2.5, 'circle-stroke-color': '#2ecc71', 'circle-stroke-opacity': 0.9 },
    });
    ensureEducationHatLayer(map.current);

    venuesClusterEnabledRef.current = enableCluster;
    applyVenuePinColors();
    // Repopulate the freshly rebuilt source with the current venues.
    const toShow = showVenues ? (venuesToShowRef.current || []) : [];
    drawClusters(toShow, tourDays, coordinateSourceFilter);
  };

  /**
   * Draw routes between tour venues (within-day and inter-day).
   * @param {Array} tourDaysData - Array of tour days with venues
   * @param {string} activeDayIdValue - ID of the active day
   * @param {Array} interDayLegModesOverride - Optional; when provided (e.g. from debounced callback) use this for inter-day modes so re-render uses current value
   */
  const drawRoutes = async (tourDaysData = [], activeDayIdValue = null, interDayLegModesOverride = undefined) => {
    if (!map.current || !mapLoaded) return;

    // Always clear existing route layers and sources first (so removing a venue removes its line)
    try {
      const existingLayers = map.current.getStyle().layers || [];
      existingLayers.forEach(layer => {
        if (layer.id.startsWith('route-') && map.current.getLayer(layer.id)) {
          map.current.removeLayer(layer.id);
        }
      });
      const existingSources = Object.keys(map.current.getStyle().sources || {});
      existingSources.forEach(source => {
        if (source.startsWith('route-') && map.current.getSource(source)) {
          map.current.removeSource(source);
        }
      });
    } catch (error) {
      console.error('Error clearing existing routes:', error);
    }

    const allTourVenues = tourDaysData ? tourDaysData.flatMap(day => day.venues) : [];
    const isTourActive = allTourVenues.length > 0;
    if (!isTourActive) {
      return;
    }

    // console.log(`Drawing routes for tour with ${allTourVenues.length} total venues`);

    // Prioritize active day but draw all days if we don't have too many venues
    const totalVenues = tourDaysData.reduce((count, day) => count + day.venues.length, 0);
    const shouldDrawAllRoutes = totalVenues <= 15;
    
    // Get days to process - either all days or just the active day
    const daysToProcess = shouldDrawAllRoutes ? 
      tourDaysData : 
      [tourDaysData.find(day => day.id === activeDayIdValue)].filter(Boolean);

    // Draw routes between venues for each day
    for (let dayIndex = 0; dayIndex < daysToProcess.length; dayIndex++) {
      const day = daysToProcess[dayIndex];
      if (!day) continue;
      
      const dayVenues = day.venues;
      const originalDayIndex = tourDaysData.findIndex(d => d.id === day.id);

      // Only draw routes if we have at least 2 venues
      if (dayVenues.length >= 2) {
        const dayLegModes = day.legModes || [];
        const maxPairs = dayVenues.length - 1;

        for (let venueIndex = 0; venueIndex < maxPairs; venueIndex++) {
          const start = dayVenues[venueIndex];
          const end = dayVenues[venueIndex + 1];
          const legMode = dayLegModes[venueIndex] || 'drive';

          if (start && end &&
              start.longitude && start.latitude &&
              end.longitude && end.latitude &&
              !isNaN(parseFloat(start.longitude)) &&
              !isNaN(parseFloat(start.latitude)) &&
              !isNaN(parseFloat(end.longitude)) &&
              !isNaN(parseFloat(end.latitude))) {

            try {
              console.log(`Drawing route from ${start.name} to ${end.name} (${legMode})`);

              const cacheKey = `${start.id}-${end.id}-${legMode}`;
              let routeData = routeCache.current[cacheKey];

              if (!routeData) {
                devLog(`Fetching new route data for ${start.name} to ${end.name} mode=${legMode}`);
                const startCoords = [parseFloat(start.longitude), parseFloat(start.latitude)];
                const endCoords = [parseFloat(end.longitude), parseFloat(end.latitude)];
                if (legMode === 'train') {
                  routeData = getTrainLegGeometry(startCoords, endCoords);
                } else if (legMode === 'flight') {
                  routeData = getFlightLegGeometry(startCoords, endCoords);
                } else {
                  routeData = await getDirections(startCoords, endCoords, { forceDrive: true });
                }
                if (routeData && routeData.geometry) {
                  routeCache.current[cacheKey] = routeData;
                }
              } else {
                devLog(`Using cached route data for ${start.name} to ${end.name}`);
              }

              if (routeData && routeData.geometry) {
                const sourceId = `route-${originalDayIndex}-${venueIndex}`;
                const layerId = `route-layer-${originalDayIndex}-${venueIndex}`;
                const isFlight = routeData.requiresFlight === true;

                if (!map.current.getSource(sourceId)) {
                  map.current.addSource(sourceId, {
                    type: 'geojson',
                    data: {
                      type: 'Feature',
                      geometry: routeData.geometry
                    }
                  });
                } else {
                  map.current.getSource(sourceId).setData({
                    type: 'Feature',
                    geometry: routeData.geometry
                  });
                }

                if (!map.current.getLayer(layerId)) {
                  map.current.addLayer({
                    id: layerId,
                    type: 'line',
                    source: sourceId,
                    layout: {
                      'line-join': 'round',
                      'line-cap': 'round'
                    },
                    paint: {
                      'line-color': ecoRouteColors
                        ? (MODE_CO2_COLORS[legMode] || MODE_CO2_COLORS.drive)
                        : ROUTE_COLORS[originalDayIndex % ROUTE_COLORS.length],
                      'line-width': isFlight ? 2 : 3,
                      'line-opacity': 0.8,
                      ...(isFlight ? { 'line-dasharray': [2, 1] } : {})
                    }
                  });
                }

                if (isFlight && routeData.geometry.coordinates && routeData.geometry.coordinates.length > 0) {
                  const midIdx = Math.floor(routeData.geometry.coordinates.length / 2);
                  const midpoint = routeData.geometry.coordinates[midIdx];
                  const flightMarkerSourceId = `route-flight-${originalDayIndex}-${venueIndex}`;
                  const flightMarkerLayerId = `route-flight-layer-${originalDayIndex}-${venueIndex}`;
                  if (!map.current.getSource(flightMarkerSourceId)) {
                    map.current.addSource(flightMarkerSourceId, {
                      type: 'geojson',
                      data: {
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates: midpoint }
                      }
                    });
                  } else {
                    map.current.getSource(flightMarkerSourceId).setData({
                      type: 'Feature',
                      geometry: { type: 'Point', coordinates: midpoint }
                    });
                  }
                  if (!map.current.getLayer(flightMarkerLayerId)) {
                    map.current.addLayer({
                      id: flightMarkerLayerId,
                      type: 'symbol',
                      source: flightMarkerSourceId,
                      layout: {
                        'text-field': '\u2708',
                        'text-size': 18,
                        'text-allow-overlap': true,
                        'icon-allow-overlap': true
                      },
                      paint: {
                        'text-color': ROUTE_COLORS[originalDayIndex % ROUTE_COLORS.length],
                        'text-halo-color': '#fff',
                        'text-halo-width': 1.5
                      }
                    });
                  }
                }
              }
            } catch (error) {
              console.error('Error drawing route:', error);
            }
          }
        }
      }
    }

    // Draw inter-day routes (last venue of day N to first venue of day N+1)
    const interModes = interDayLegModesOverride || [];
    for (let gapIndex = 0; gapIndex < tourDaysData.length - 1; gapIndex++) {
      const dayFrom = tourDaysData[gapIndex];
      const dayTo = tourDaysData[gapIndex + 1];
      if (!dayFrom?.venues?.length || !dayTo?.venues?.length) continue;
      const lastVenue = dayFrom.venues[dayFrom.venues.length - 1];
      const firstVenue = dayTo.venues[0];
      if (!lastVenue?.longitude || !lastVenue?.latitude || !firstVenue?.longitude || !firstVenue?.latitude) continue;
      if (isNaN(parseFloat(lastVenue.longitude)) || isNaN(parseFloat(lastVenue.latitude)) ||
          isNaN(parseFloat(firstVenue.longitude)) || isNaN(parseFloat(firstVenue.latitude))) continue;

      const mode = interModes[gapIndex] || 'drive';
      try {
        const cacheKey = `inter-${lastVenue.id}-${firstVenue.id}-${mode}`;
        let routeData = routeCache.current[cacheKey];
        if (!routeData) {
          const startCoords = [parseFloat(lastVenue.longitude), parseFloat(lastVenue.latitude)];
          const endCoords = [parseFloat(firstVenue.longitude), parseFloat(firstVenue.latitude)];
          if (mode === 'train') {
            routeData = getTrainLegGeometry(startCoords, endCoords);
          } else if (mode === 'flight') {
            routeData = getFlightLegGeometry(startCoords, endCoords);
          } else {
            routeData = await getDirections(startCoords, endCoords, { forceDrive: true });
          }
          if (routeData?.geometry) routeCache.current[cacheKey] = routeData;
        }

        if (routeData?.geometry) {
          const sourceId = `route-inter-${gapIndex}`;
          const layerId = `route-layer-inter-${gapIndex}`;
          const isFlight = routeData.requiresFlight === true;
          const routeColor = ecoRouteColors
            ? (MODE_CO2_COLORS[mode] || MODE_CO2_COLORS.drive)
            : ROUTE_COLORS[(gapIndex + 1) % ROUTE_COLORS.length];

          if (!map.current.getSource(sourceId)) {
            map.current.addSource(sourceId, {
              type: 'geojson',
              data: { type: 'Feature', geometry: routeData.geometry }
            });
          } else {
            map.current.getSource(sourceId).setData({ type: 'Feature', geometry: routeData.geometry });
          }
          if (!map.current.getLayer(layerId)) {
            map.current.addLayer({
              id: layerId,
              type: 'line',
              source: sourceId,
              layout: { 'line-join': 'round', 'line-cap': 'round' },
              paint: {
                'line-color': routeColor,
                'line-width': isFlight ? 2 : 3,
                'line-opacity': 0.8,
                ...(isFlight ? { 'line-dasharray': [2, 1] } : {})
              }
            });
          }

          if (isFlight && routeData.geometry.coordinates?.length > 0) {
            const midIdx = Math.floor(routeData.geometry.coordinates.length / 2);
            const midpoint = routeData.geometry.coordinates[midIdx];
            const flightSourceId = `route-flight-inter-${gapIndex}`;
            const flightLayerId = `route-flight-layer-inter-${gapIndex}`;
            if (!map.current.getSource(flightSourceId)) {
              map.current.addSource(flightSourceId, {
                type: 'geojson',
                data: { type: 'Feature', geometry: { type: 'Point', coordinates: midpoint } }
              });
            } else {
              map.current.getSource(flightSourceId).setData({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: midpoint }
              });
            }
            if (!map.current.getLayer(flightLayerId)) {
              map.current.addLayer({
                id: flightLayerId,
                type: 'symbol',
                source: flightSourceId,
                layout: {
                  'text-field': '\u2708',
                  'text-size': 18,
                  'text-allow-overlap': true,
                  'icon-allow-overlap': true
                },
                paint: {
                  'text-color': routeColor,
                  'text-halo-color': '#fff',
                  'text-halo-width': 1.5
                }
              });
            }
          }
        }
      } catch (error) {
        console.error('Error drawing inter-day route:', error);
      }
    }
  };

  /**
   * Draw user custom locations on the map
   */
  const drawUserLocations = () => {
    if (!map.current || !mapLoaded) return;

    const tourStops = stopsFromTour({ days: tourDays });
    const pool = calendarEmbed
      ? userLocations
      : (showUserLocations ? [...userLocations, ...publicUserLocations] : []);
    const allUserLocs = calendarEmbed
      ? filterContactsNearStops(pool, tourStops, SAVED_CONTACT_RADIUS_MILES)
      : pool;

    // Create GeoJSON for user locations
    const userLocationFeatures = allUserLocs.map(loc => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [parseFloat(loc.longitude), parseFloat(loc.latitude)]
      },
      properties: {
        id: loc.id,
        name: loc.name,
        notes: loc.notes || '',
        userName: loc.userName,
        isOwn: loc.isOwn,
        isPublic: loc.isPublic,
        category: loc.category || 'other',
        address: loc.address || '',
        capacity: loc.capacity != null ? String(loc.capacity) : '',
        affiliate: loc.affiliate || '',
        type: 'userLocation'
      }
    }));

    // Add or update user locations source
    if (map.current.getSource('user-locations-source')) {
      map.current.getSource('user-locations-source').setData({
        type: 'FeatureCollection',
        features: userLocationFeatures
      });
    } else {
      // Add source and layer if they don't exist
      map.current.addSource('user-locations-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: userLocationFeatures
        }
      });

      // Add layer for user-created locations (distinct from venues/landmarks: purple/teal)
      map.current.addLayer({
        id: 'user-locations-layer',
        type: 'circle',
        source: 'user-locations-source',
        paint: {
          'circle-radius': 11,
          'circle-color': [
            'case',
            ['==', ['get', 'isOwn'], true],
            '#9b59b6', // Purple for own user-created locations
            '#1abc9c'  // Teal for others' public user-created locations
          ],
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff'
        }
      });

      // Add labels for user locations
      map.current.addLayer({
        id: 'user-locations-labels',
        type: 'symbol',
        source: 'user-locations-source',
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 11,
          'text-offset': [0, 1.5],
          'text-anchor': 'top',
          'text-max-width': 10
        },
        paint: {
          'text-color': isDarkMode ? '#ffffff' : '#1a1a1a',
          'text-halo-color': isDarkMode ? '#1a1a1a' : '#ffffff',
          'text-halo-width': 1.5
        }
      });

      // Add click handler for user locations
      map.current.on('click', 'user-locations-layer', (e) => {
        if (!e.features || e.features.length === 0) return;

        const feature = e.features[0];
        const props = feature.properties;
        const coords = feature.geometry.coordinates;

        // Match venue popup layout: name, location/capacity/type, description, coords, buttons
        const locCapacity = props.capacity != null && props.capacity !== '' ? props.capacity : 'N/A';
        const locType = props.affiliate && String(props.affiliate).trim() ? String(props.affiliate).replace(/</g, '&lt;').replace(/>/g, '&gt;') : 'N/A';
        const notesRaw = props.notes != null ? String(props.notes) : '';
        const locDesc = notesRaw.trim() ? notesRaw.replace(/</g, '&lt;').replace(/>/g, '&gt;') : '';
        const locName = String(props.name || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const locAddr = (props.address && String(props.address).trim()) ? String(props.address).replace(/</g, '&lt;').replace(/>/g, '&gt;').substring(0, 80) : '';

        const popupContent = buildVenuePopupHTML({
          id: props.id, name: locName,
          city: locAddr, state: '',
          capacity: locCapacity, type: locType,
          notes: locDesc,
          showFocus: false, isUserLocation: true,
          isOwn: props.isOwn === 'true' || props.isOwn === true,
          addAction: `window.addUserLocationToTourFromMap('${props.id}')`,
          detailAction: `window.openMapDetailsUserLocation('${props.id}')`,
          editAction: `window.editUserLocation('${props.id}')`,
          deleteAction: `window.deleteUserLocation('${props.id}')`,
        });

        new mapboxgl.Popup({ closeOnClick: true, className: 'light-popup' })
          .setLngLat(coords)
          .setHTML(popupContent)
          .addTo(map.current);
      });

      // Change cursor on hover
      map.current.on('mouseenter', 'user-locations-layer', () => {
        map.current.getCanvas().style.cursor = 'pointer';
      });
      map.current.on('mouseleave', 'user-locations-layer', () => {
        map.current.getCanvas().style.cursor = '';
      });
    }
  };

  /**
   * Draw/highlight selected location on the map
   * @param {Object} venue - The venue to highlight (or null to clear selection)
   */
  const drawSelectedLocation = (venue) => {
    if (!map.current || !mapLoaded) return;

    // Remove existing selected location marker if it exists
    if (map.current.getSource('selected-location-source')) {
      map.current.removeLayer('selected-location-layer');
      map.current.removeSource('selected-location-source');
    }

    // If no venue provided, just clear the selection
    if (!venue || !venue.latitude || !venue.longitude) {
      return;
    }

    // Create a source for the selected location
    map.current.addSource('selected-location-source', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [parseFloat(venue.longitude), parseFloat(venue.latitude)]
        },
        properties: {
          id: venue.id,
          name: venue.name
        }
      }
    });

    // Add a highlighted marker layer for the selected location
    map.current.addLayer({
      id: 'selected-location-layer',
      type: 'circle',
      source: 'selected-location-source',
      paint: {
        'circle-radius': 14,
        'circle-color': '#e74c3c', // Red highlight
        'circle-stroke-width': 4,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': 0.9
      }
    });
  };

  /**
   * Draw or clear the "pending location" indicator when user clicks the map in Add Location mode
   * @param {Object|null} coords - { lng, lat } or null to clear
   */
  const drawPendingLocationIndicator = useCallback((coords) => {
    if (!map.current || !mapLoaded) return;

    const sourceId = 'pending-location-source';
    const layerId = 'pending-location-layer';

    if (map.current.getSource(sourceId)) {
      if (map.current.getLayer(layerId)) map.current.removeLayer(layerId);
      map.current.removeSource(sourceId);
    }

    if (!coords || coords.lng == null || coords.lat == null) return;

    map.current.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [parseFloat(coords.lng), parseFloat(coords.lat)]
        },
        properties: { type: 'pending-location' }
      }
    });

    map.current.addLayer({
      id: layerId,
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-radius': 12,
        'circle-color': '#27ae60',
        'circle-stroke-width': 4,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': 0.95
      }
    });
  }, [mapLoaded]);

  const drawEmbedPreviewIndicator = useCallback((coords) => {
    if (!map.current || !mapLoaded) return;

    const sourceId = 'embed-preview-source';
    const layerId = 'embed-preview-layer';

    if (map.current.getSource(sourceId)) {
      if (map.current.getLayer(layerId)) map.current.removeLayer(layerId);
      map.current.removeSource(sourceId);
    }

    if (!coords || coords.lng == null || coords.lat == null) return;

    map.current.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [parseFloat(coords.lng), parseFloat(coords.lat)],
        },
        properties: { type: 'embed-preview' },
      },
    });

    map.current.addLayer({
      id: layerId,
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-radius': 10,
        'circle-color': '#e67e22',
        'circle-stroke-width': 3,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': 0.95,
      },
    });
  }, [mapLoaded]);

  const previewEmbedLocation = useCallback((location) => {
    if (location?.latitude == null || location?.longitude == null) return;
    const coords = {
      lat: Number(location.latitude),
      lng: Number(location.longitude),
    };
    drawEmbedPreviewIndicator(coords);
    if (map.current?.flyTo) {
      map.current.flyTo({
        center: [coords.lng, coords.lat],
        zoom: Math.max(map.current.getZoom?.() || 0, 14),
        duration: 1200,
      });
    }
  }, [drawEmbedPreviewIndicator]);

  const clearEmbedLocationPreview = useCallback(() => {
    drawEmbedPreviewIndicator(null);
  }, [drawEmbedPreviewIndicator]);

  // Keep updateMapWithUserLocations as an alias for backwards compatibility
  const updateMapWithUserLocations = drawUserLocations;

  // Sync edit form when editingLocation is set (from list or map popup)
  useEffect(() => {
    if (editingLocation) {
      setNewLocationData({
        name: editingLocation.name || '',
        notes: editingLocation.notes || '',
        category: editingLocation.category || 'other',
        address: editingLocation.address || '',
        isPublic: !!editingLocation.isPublic,
        capacity: editingLocation.capacity != null && editingLocation.capacity !== '' ? String(editingLocation.capacity) : '',
        affiliate: editingLocation.affiliate || ''
      });
    }
  }, [editingLocation]);

  // Set up global functions for popup buttons
  useEffect(() => {
    window.editUserLocation = (id) => {
      const location = userLocations.find(loc => loc.id === id);
      if (location) {
        setEditingLocation(location);
      }
    };

    window.deleteUserLocation = (id) => {
      deleteUserLocation(id);
    };

    window.addUserLocationToTourFromMap = (locationId) => {
      if (!mapGateRef.current(MAP_ACTION.PLAN)) return;
      addUserLocationToTourHandlerRef.current?.(locationId);
    };

    window.addTransitStopToTourFromMap = (stopId, stopName, stopType, lat, lng) => {
      if (!mapGateRef.current(MAP_ACTION.PLAN)) return;
      addTransitStopToTourRef.current?.({ stopId, stopName, stopType, lat, lng });
    };

    window.focusPoiOnMap = (lng, lat) => {
      if (!map.current) return;
      map.current.flyTo({ center: [lng, lat], zoom: 15, essential: true });
      if (typeof document !== 'undefined') {
        const popups = document.getElementsByClassName('mapboxgl-popup');
        Array.from(popups).forEach(p => p.remove());
      }
    };

    window.openMapDetailsUserLocation = (locationId) => {
      const allLocs = [...userLocations, ...publicUserLocations];
      const loc = allLocs.find(l => l.id === locationId);
      if (loc) {
        setMapDetailsItem({ type: 'userLocation', data: loc });
        if (typeof document !== 'undefined') {
          const popups = document.getElementsByClassName('mapboxgl-popup');
          if (popups.length) popups[0].remove();
        }
      }
    };

    // Progressive star fill on hover — updates all stars up to the hovered index
    window.poiStarHover = (containerId, hoverN) => {
      const wrap = typeof document !== 'undefined' ? document.getElementById(containerId) : null;
      if (!wrap) return;
      Array.from(wrap.children).forEach((btn, i) => {
        btn.style.color = i < hoverN ? '#e8a020' : 'rgba(255,255,255,0.22)';
        btn.style.transform = i < hoverN ? 'scale(1.18)' : 'scale(1)';
      });
    };
    window.poiStarOut = (containerId, savedN) => {
      const wrap = typeof document !== 'undefined' ? document.getElementById(containerId) : null;
      if (!wrap) return;
      Array.from(wrap.children).forEach((btn, i) => {
        btn.style.color = i < savedN ? '#e8a020' : 'rgba(255,255,255,0.22)';
        btn.style.transform = 'scale(1)';
      });
    };

    // Save a short note on a POI (stored in pois/{poiId}/notes)
    window.savePoiNote = async (poiId, content) => {
      const escapeHtmlForNote = (value) =>
        String(value ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');

      const btnId = `poi-note-save-${poiId}`;
      const feedbackId = `poi-note-feedback-${poiId}`;
      const notesElId = `poi-notes-${poiId}`;
      const taId = `poi-note-${poiId}`;

      const btn = typeof document !== 'undefined' ? document.getElementById(btnId) : null;
      const feedbackEl = typeof document !== 'undefined' ? document.getElementById(feedbackId) : null;

      const setLoading = (loading) => {
        if (!btn) return;
        btn.disabled = loading;
        btn.setAttribute('aria-busy', loading ? 'true' : 'false');
        btn.style.opacity = loading ? '0.65' : '1';
        btn.style.cursor = loading ? 'not-allowed' : 'pointer';
        btn.textContent = loading ? 'Saving…' : 'Save Note';
      };

      const setFeedback = (text, kind) => {
        if (!feedbackEl) return;
        feedbackEl.textContent = text || '';
        const tx = theme.text;
        if (!text) {
          feedbackEl.style.color = 'transparent';
          return;
        }
        if (kind === 'success') {
          feedbackEl.style.color = theme.status.success;
        } else if (kind === 'error') {
          feedbackEl.style.color = theme.status.error;
        } else if (kind === 'pending') {
          feedbackEl.style.color = tx.muted;
        } else if (kind === 'muted') {
          feedbackEl.style.color = tx.muted;
        } else {
          feedbackEl.style.color = tx.tertiary;
        }
      };

      try {
        const trimmed = String(content ?? '').trim();
        if (!poiId || !trimmed) {
          setToast({ message: 'Note cannot be empty.', type: 'warning' });
          setFeedback('Add some text first.', 'muted');
          return;
        }
        if (!userData?.uid) {
          mapGateRef.current(MAP_ACTION.CONTRIBUTE);
          return;
        }

        setLoading(true);
        setFeedback('Saving…', 'pending');

        const db = getFirestore();
        await addDoc(collection(db, 'pois', poiId, 'notes'), {
          userId: userData.uid,
          userName: userData.name || userData.email || 'Anonymous',
          content: trimmed,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        const notesEl = typeof document !== 'undefined' ? document.getElementById(notesElId) : null;
        const textarea = typeof document !== 'undefined' ? document.getElementById(taId) : null;
        if (textarea) textarea.value = '';

        const isDark =
          typeof document !== 'undefined' && !!document.querySelector('.mapboxgl-popup.dark-popup');
        const noteCardBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
        const userName = escapeHtmlForNote(userData.name || userData.email || 'Anonymous');
        const contentHtml = escapeHtmlForNote(trimmed);

        if (notesEl) {
          const emptyP = notesEl.querySelector('p');
          if (emptyP && /No notes yet/i.test(String(emptyP.textContent || '').trim())) {
            emptyP.remove();
          }
          const cardEl = document.createElement('div');
          cardEl.style.cssText = `padding: 8px 10px; background: ${noteCardBg}; border-radius: 6px; margin-bottom: 8px; opacity: 0; transform: translateY(-6px); transition: opacity 0.28s ease, transform 0.28s ease;`;
          cardEl.innerHTML = `
            <div style="font-size: 11px; color:${theme.text.tertiary}; font-weight: 700; margin-bottom: 4px;">${userName}</div>
            <div style="font-size: 12px; color:${theme.text.secondary}; white-space: pre-wrap; word-break: break-word;">${contentHtml}</div>
          `;
          notesEl.insertBefore(cardEl, notesEl.firstChild);
          requestAnimationFrame(() => {
            cardEl.style.opacity = '1';
            cardEl.style.transform = 'translateY(0)';
          });
        }

        setLoading(false);
        setFeedback('✓ Note saved', 'success');
        setToast({ message: 'Note saved.', type: 'success' });

        setTimeout(() => {
          setFeedback('', '');
        }, 2800);
      } catch (err) {
        console.error('savePoiNote error:', err);
        setLoading(false);
        setFeedback('Couldn’t save. Try again.', 'error');
        setToast({ message: 'Failed to save note.', type: 'error' });
      }
    };

    window.savePoiRating = async (poiId, rating) => {
      try {
        const trimmedPoiId = String(poiId ?? '').trim();
        const r = Number(rating);

        if (!trimmedPoiId || !Number.isFinite(r) || r < 1 || r > 5) {
          setToast({ message: 'Invalid rating.', type: 'warning' });
          return;
        }
        if (!userData?.uid) {
          mapGateRef.current(MAP_ACTION.CONTRIBUTE);
          return;
        }

        const db = getFirestore();
        const uid = userData.uid;
        await setDoc(doc(db, 'pois', trimmedPoiId, 'ratings', uid), {
          userId: uid,
          userName: userData.name || userData.email || 'Anonymous',
          value: r,
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
        }, { merge: true });

        setToast({ message: 'Rating saved.', type: 'success' });

        // Reflect the newly saved rating in the popup star row (if still open)
        if (typeof document !== 'undefined') {
          const starsId = `poi-stars-${trimmedPoiId}`;
          const wrap = document.getElementById(starsId);
          if (wrap) {
            wrap.innerHTML = [1, 2, 3, 4, 5].map((n) => {
              const filled = n <= r;
              return `<button
                onclick="window.savePoiRating('${trimmedPoiId}', ${n})"
                onmouseover="window.poiStarHover('${starsId}', ${n})"
                onmouseout="window.poiStarOut('${starsId}', ${r})"
                aria-label="Rate ${n} stars"
                style="background:transparent;border:none;padding:2px;cursor:pointer;color:${filled ? '#e8a020' : 'rgba(255,255,255,0.22)'};font-size:24px;line-height:1;transition:color 0.12s,transform 0.12s;transform:scale(1);">★</button>`;
            }).join('');
          }
        }
      } catch (err) {
        console.error('savePoiRating error:', err);
        setToast({ message: 'Failed to save rating.', type: 'error' });
      }
    };

    window.addPoiToTourDay = (poiId, name, category, address, lng, lat) => {
      if (!mapGateRef.current(MAP_ACTION.PLAN)) return;
      try {
        const trimmedPoiId = String(poiId ?? '').trim();
        const poiName = String(name ?? '').trim();
        const poiCategory = String(category ?? '').trim();
        const poiAddress = String(address ?? '').trim();

        const poiLng = Number(lng);
        const poiLat = Number(lat);

        if (!trimmedPoiId || !poiName || !Number.isFinite(poiLng) || !Number.isFinite(poiLat)) {
          setToast({ message: 'Cannot add POI (missing fields).', type: 'warning' });
          return;
        }

        // Convert POI into a "venue-like" stop so it can be used in the tour/day + route drawing.
        const poiAsVenue = {
          id: `poi-${trimmedPoiId}`,
          name: poiName,
          latitude: poiLat,
          longitude: poiLng,
          address: poiAddress,
          fullAddress: poiAddress,
          city: '',
          state: '',
          venueType: 'POI',
          poiCategory: poiCategory,
        };

        addVenueToDayRef.current?.(poiAsVenue, activeDayIdRef.current);
        setToast({ message: 'POI added to day.', type: 'success' });
      } catch (err) {
        console.error('addPoiToTourDay error:', err);
        setToast({ message: 'Failed to add POI.', type: 'error' });
      }
    };

    return () => {
      delete window.editUserLocation;
      delete window.deleteUserLocation;
      delete window.addUserLocationToTourFromMap;
      delete window.addTransitStopToTourFromMap;
      delete window.openMapDetailsUserLocation;
      delete window.poiStarHover;
      delete window.poiStarOut;
      delete window.savePoiNote;
      delete window.savePoiRating;
      delete window.addPoiToTourDay;
      delete window.focusPoiOnMap;
    };
  }, [userLocations, publicUserLocations, userData?.uid, userData?.name, userData?.email, theme]);

  // Update map when user locations or visibility changes
  useEffect(() => {
    if (map.current && mapLoaded) {
      updateMapWithUserLocations();
    }
  }, [userLocations, publicUserLocations, showUserLocations, mapLoaded, isDarkMode, calendarEmbed, tourDays]);

  // Sync screenprinter/merch-pickup data to the map source
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const source = map.current.getSource('screenprinters-source');
    if (!source) return;

    if (!showScreenprinters && selectedScreenprinterIds.length === 0) {
      source.setData({ type: 'FeatureCollection', features: [] });
      return;
    }

    const visibleScreenprinters = selectedScreenprinterIds.length > 0
      ? screenprinters.filter((sp) => selectedScreenprinterIds.includes(sp.id))
      : screenprinters;
    const features = visibleScreenprinters
      .filter(sp => sp.latitude != null && sp.longitude != null)
      .map(sp => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [parseFloat(sp.longitude), parseFloat(sp.latitude)] },
        properties: {
          id: sp.id,
          name: sp.name || 'Merch',
          city: sp.city || '',
          state: sp.state || '',
          website: sp.website || '',
          services: sp.services || '',
          notes: sp.notes || '',
          phone: sp.phone || '',
          email: sp.email || '',
          eco: Boolean(sp.eco),
        }
      }));

    source.setData({ type: 'FeatureCollection', features });
  }, [screenprinters, showScreenprinters, selectedScreenprinterIds, mapLoaded, mapStyle]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    ensureDirectoryPostsMapLayers(map.current);
    const source = map.current.getSource(DIRECTORY_POSTS_SOURCE);
    if (!source) return;
    const features = selectedDirectoryPostPins
      .filter((pin) => Number.isFinite(Number(pin.latitude)) && Number.isFinite(Number(pin.longitude)))
      .map((pin) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [Number(pin.longitude), Number(pin.latitude)],
        },
        properties: {
          id: pin.id,
          name: pin.name || 'Post',
        },
      }));
    source.setData({ type: 'FeatureCollection', features });
  }, [selectedDirectoryPostPins, mapLoaded, mapStyle]);

  // Sync transit stops to the map source
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const source = map.current.getSource('transit-source');
    if (!source) return;
    const features = transitStops.map(stop => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [stop.lng, stop.lat] },
      properties: { id: stop.id, name: stop.name, type: stop.type },
    }));
    source.setData({ type: 'FeatureCollection', features });
  }, [transitStops, mapLoaded]);

  // Show/hide individual transit layers based on per-type toggles
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const setVis = (layerId, visible) => {
      try { map.current.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none'); } catch (_) {}
    };
    setVis('transit-bus-symbol', showBusStops);
    setVis('transit-train-symbol', showTrainStops);
    setVis('transit-airport-symbol', showAirports);
    // Per-type letter labels — each follows its own circle layer toggle
    setVis('transit-bus-labels', showBusStops);
    setVis('transit-train-labels', showTrainStops);
    setVis('transit-airport-labels', showAirports);
    // Name label layer: filter to only active types so hidden types don't show names
    const anyTransit = showBusStops || showTrainStops || showAirports;
    setVis('transit-labels', anyTransit);
    if (anyTransit && map.current.getLayer('transit-labels')) {
      const activeTypes = [
        showBusStops && 'bus',
        showTrainStops && 'rail',
        showAirports && 'airport',
      ].filter(Boolean);
      try {
        map.current.setFilter('transit-labels', ['in', ['get', 'type'], ['literal', activeTypes]]);
      } catch (_) {}
    }
    // Fetch stops on first activation if none loaded yet
    if ((showBusStops || showTrainStops || showAirports) && transitStops.length === 0 && map.current) {
      fetchTransitStops(map.current.getBounds());
    }
  }, [showBusStops, showTrainStops, showAirports, mapLoaded]);

  // Show/hide bike lanes (purple = available, green = commonly used) and load OSM / StreetLight GeoJSON
  const boundsKeyQuick = useCallback((bounds) => {
    if (!bounds) return '';
    const r = (n) => Number(n).toFixed(2);
    return `${r(bounds.getWest())},${r(bounds.getSouth())},${r(bounds.getEast())},${r(bounds.getNorth())}`;
  }, []);

  const loadBikeLanesForMap = useCallback(async () => {
    if (!map.current || !mapLoaded || !showBikeLanes) return;
    const zoom = map.current.getZoom();
    if (zoom < 11) return;
    const bounds = map.current.getBounds();
    const key = boundsKeyQuick(bounds);
    if (key && key === bikeLanesFetchedKeyRef.current && map.current.getSource('bike-lanes-available-src')) {
      setBikeLaneVisibility(map.current, true);
      return;
    }

    ensureBikeLaneLayers(map.current);
    setBikeLaneVisibility(map.current, true);

    try {
      if (bikeLanesAbortRef.current) bikeLanesAbortRef.current.abort();
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      bikeLanesAbortRef.current = controller;
      setBikeLanesLoading(true);
      const result = await fetchBikeLaneGeoJSON(bounds, { signal: controller?.signal });
      if (!map.current || !showBikeLanes) return;
      bikeLanesFetchedKeyRef.current = result.key;
      setBikeLaneData(map.current, result);
      setBikeLanesSource(result.source);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.warn('Bike lanes fetch failed', e);
    } finally {
      setBikeLanesLoading(false);
    }
  }, [mapLoaded, showBikeLanes, boundsKeyQuick]);

  useEffect(() => { showBikeLanesRef.current = showBikeLanes; }, [showBikeLanes]);
  useEffect(() => { loadBikeLanesForMapRef.current = loadBikeLanesForMap; }, [loadBikeLanesForMap]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    ensureBikeLaneLayers(map.current);
    setBikeLaneVisibility(map.current, showBikeLanes);
    if (!showBikeLanes) {
      if (bikeLanesAbortRef.current) bikeLanesAbortRef.current.abort();
      bikeLanesFetchedKeyRef.current = null;
      setBikeLanesLoading(false);
      return;
    }
    loadBikeLanesForMap();
  }, [showBikeLanes, mapLoaded, loadBikeLanesForMap]);

  useEffect(() => {
    if (!mapLoaded || !map.current) return;
    const onMoveEnd = () => {
      if (!showBikeLanes) return;
      if (map.current.getZoom() < 11) return;
      const key = boundsKeyQuick(map.current.getBounds());
      if (key && key === bikeLanesFetchedKeyRef.current) return;
      if (bikeLanesDebounceRef.current) clearTimeout(bikeLanesDebounceRef.current);
      bikeLanesDebounceRef.current = setTimeout(() => { loadBikeLanesForMap(); }, 550);
    };
    map.current.on('moveend', onMoveEnd);
    return () => {
      try { map.current?.off('moveend', onMoveEnd); } catch (_) {}
      if (bikeLanesDebounceRef.current) clearTimeout(bikeLanesDebounceRef.current);
    };
  }, [mapLoaded, showBikeLanes, loadBikeLanesForMap, boundsKeyQuick]);

  // Repaint all route-layer-* lines when eco color mode toggles
  useEffect(() => {
    if (!map.current || !mapLoaded || !tourDays?.length) return;
    try {
      const style = map.current.getStyle();
      if (!style?.layers) return;
      style.layers.forEach(layer => {
        if (!layer.id.startsWith('route-layer-')) return;
        // Derive mode from the layer id or default to drive
        const sourceId = layer.id.replace('route-layer-', 'route-');
        const src = map.current.getSource(sourceId);
        // Parse day/venue index from id to get original day index for ROUTE_COLORS fallback
        const parts = layer.id.replace('route-layer-', '').split('-');
        const isInter = parts[0] === 'inter';
        if (ecoRouteColors) {
          // Determine mode: inter-day layers used mode stored in layer metadata; approximate from existing paint
          // We stored mode as paint line-color; instead re-read from routeCache
          // Best approach: for intra-day use legModes from tourDays, for inter from interDayLegModes
          let mode = 'drive';
          if (!isInter) {
            const dayIdx = parseInt(parts[0], 10);
            const venueIdx = parseInt(parts[1], 10);
            const day = tourDays[dayIdx];
            if (day) mode = (day.legModes || [])[venueIdx] || 'drive';
          } else {
            const gapIdx = parseInt(parts[1], 10);
            mode = (interDayLegModes || [])[gapIdx] || 'drive';
          }
          map.current.setPaintProperty(layer.id, 'line-color', MODE_CO2_COLORS[mode] || MODE_CO2_COLORS.drive);
        } else {
          const dayIdx = isInter ? (parseInt(parts[1], 10) + 1) : parseInt(parts[0], 10);
          map.current.setPaintProperty(layer.id, 'line-color', ROUTE_COLORS[dayIdx % ROUTE_COLORS.length]);
        }
      });
    } catch (_) {}
  }, [ecoRouteColors, mapLoaded]);

  // Fetch transit stops when map moves (only when any transit layer is on)
  useEffect(() => {
    if (!mapLoaded || !map.current) return;
    const onMoveEnd = () => {
      if (!showBusStops && !showTrainStops && !showAirports) return;
      const zoom = map.current.getZoom();
      if (zoom >= 9) fetchTransitStops(map.current.getBounds());
    };
    map.current.on('moveend', onMoveEnd);
    return () => { try { map.current?.off('moveend', onMoveEnd); } catch (_) {} };
  }, [mapLoaded, showBusStops, showTrainStops, showAirports]);

  // Re-run updateVenuesOnMap whenever transitStops change so _isNearTransit is reflected
  useEffect(() => {
    if (!mapLoaded || transitStops.length === 0) return;
    // Tag each venue with _isNearTransit (1 mile = 1609 m)
    venues.forEach(venue => {
      const vLat = parseFloat(venue.latitude);
      const vLng = parseFloat(venue.longitude);
      if (isNaN(vLat) || isNaN(vLng)) return;
      venue._isNearTransit = transitStops.some(
        stop => haversineMeters(vLat, vLng, stop.lat, stop.lng) <= 1609
      );
    });
    // Refresh map features to pick up new isNearTransit values
    updateVenuesOnMap(null);
  }, [transitStops, mapLoaded]);

  // Close open filter sections when clicking outside modeControls — but NOT when clicking the map
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const anyOpen = Object.values(openFilterSections).some(Boolean);
    if (!anyOpen) return;
    const handleDocClick = (e) => {
      const inFilterPanel = filterPanelRef.current?.contains(e.target);
      const inMapCanvas = mapContainer.current?.contains(e.target);
      if (!inFilterPanel && !inMapCanvas) {
        setOpenFilterSections({ affiliate: false, features: false, resources: false, food: false });
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, [openFilterSections]);

  // Handle map click for adding new location
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    const handleMapClick = (e) => {
      if (!addLocationMode) {
        // POI click fallback: per-layer click delegation can be unreliable for
        // symbol/circle layers, so hit-test all POI layers here and open the
        // bubble. (mouseenter shows a pointer but the layer click may not fire.)
        if (!map.current) return;
        const liveLayers = Object.keys(POI_CATEGORY_META)
          .map((c) => buildPoiLayerId(c))
          .filter((id) => map.current.getLayer(id));
        const poiLayers = [...liveLayers];
        if (map.current.getLayer(BAKED_POIS_POINT_LAYER)) poiLayers.push(BAKED_POIS_POINT_LAYER);
        if (poiLayers.length === 0) return;
        // Hit-test a small box around the click so tiny dots are easy to tap.
        const pad = 8;
        const box = [
          [e.point.x - pad, e.point.y - pad],
          [e.point.x + pad, e.point.y + pad],
        ];
        let feats = [];
        try {
          feats = map.current.queryRenderedFeatures(box, { layers: poiLayers });
          if (!feats || feats.length === 0) {
            feats = map.current.queryRenderedFeatures(e.point, { layers: poiLayers });
          }
        } catch (_) { feats = []; }
        if (!feats || feats.length === 0) return;
        // Prefer the feature nearest the actual click point.
        let f = feats[0];
        try {
          let best = Infinity;
          feats.forEach((cand) => {
            const c = cand.geometry?.coordinates;
            if (!c) return;
            const p = map.current.project(c);
            const d = (p.x - e.point.x) ** 2 + (p.y - e.point.y) ** 2;
            if (d < best) { best = d; f = cand; }
          });
        } catch (_) { /* keep first */ }
        if (f.layer?.id === BAKED_POIS_POINT_LAYER) {
          handleBakedPoiClickRef.current?.(f);
        } else {
          handlePoiClickRef.current?.({ features: [f] });
        }
        return;
      }

      const { lng, lat } = e.lngLat;

      // Zoom to the clicked location so user sees where they selected
      map.current.flyTo({
        center: [lng, lat],
        zoom: 16,
        duration: 500,
        essential: true
      });

      // Set the coordinates and open the add location panel
      setPendingLocationCoords({ lng, lat });
      setShowAddLocationPanel(true);

      // Try to reverse geocode to get address
      fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?` +
        `access_token=${MAPBOX_ACCESS_TOKEN}&` +
        `types=address,poi,place&` +
        `limit=1`
      )
        .then(res => res.json())
        .then(data => {
          if (data.features && data.features.length > 0) {
            const feature = data.features[0];
            setNewLocationData(prev => ({
              ...prev,
              address: feature.place_name || ''
            }));
            setAddressSearchQuery(feature.place_name || '');
          }
        })
        .catch(err => console.error('Reverse geocode error:', err));
    };

    map.current.on('click', handleMapClick);

    return () => {
      if (map.current) {
        map.current.off('click', handleMapClick);
      }
    };
  }, [addLocationMode, mapLoaded, isDarkMode, userData]);

  // Show/hide pending location indicator when coords or add-location state changes
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const coords = (addLocationMode || showAddLocationPanel) ? pendingLocationCoords : null;
    drawPendingLocationIndicator(coords);
  }, [pendingLocationCoords, addLocationMode, showAddLocationPanel, mapLoaded, drawPendingLocationIndicator]);

  // Direct add location function (called from panel)
  const addUserLocationDirect = async (lng, lat, name, notes, category, address, isPublic, capacity, affiliate) => {
    if (!userData?.uid) {
      mapGateRef.current(MAP_ACTION.CONTRIBUTE);
      return;
    }

    try {
      const db = getFirestore();
      const cap = capacity !== '' && capacity != null ? Number(String(capacity).replace(/\D/g, '')) || null : null;
      const locationData = {
        userId: userData.uid,
        userName: userData.name || userData.email || 'Anonymous',
        name: name,
        notes: notes || '',
        category: category || 'other',
        address: address || '',
        latitude: lat,
        longitude: lng,
        isPublic: isPublic,
        ...(cap != null && !Number.isNaN(cap) && { capacity: cap }),
        ...(affiliate?.trim() && { affiliate: affiliate.trim() }),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'userLocations'), locationData);

      // Add to local state
      setUserLocations(prev => [...prev, { id: docRef.id, ...locationData, isOwn: true }]);
      closeAddLocationPanel();

      Alert.alert('Success', 'Location added successfully!');

    } catch (error) {
      console.error('Error adding user location:', error);
      Alert.alert('Error', 'Failed to add location. Please try again.');
    } finally {
      setSaveLocationLoading(false);
    }
  };

  // Save location from the add location panel
  const saveNewLocation = async () => {
    if (!newLocationData.name.trim()) {
      Alert.alert('Name Required', 'Please enter a name for your location.');
      return;
    }

    if (!pendingLocationCoords) {
      Alert.alert('Location Required', 'Please search for an address or click on the map to set a location.');
      return;
    }

    setSaveLocationLoading(true);
    try {
      await addUserLocationDirect(
        pendingLocationCoords.lng,
        pendingLocationCoords.lat,
        newLocationData.name.trim(),
        newLocationData.notes.trim(),
        newLocationData.category,
        newLocationData.address.trim(),
        newLocationData.isPublic,
        newLocationData.capacity,
        newLocationData.affiliate
      );
    } finally {
      setSaveLocationLoading(false);
    }
  };

  // Add recenter map function
  const recenterMap = () => {
    if (!map.current) return;

    if (venues.length > 0) {
      // If we have venues, fit to all venues
      const bounds = new mapboxgl.LngLatBounds();
      venues.forEach(venue => {
        if (venue.latitude && venue.longitude) {
          bounds.extend([venue.longitude, venue.latitude]);
        }
      });

      if (!bounds.isEmpty()) {
        map.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 12
        });
        return;
      }
    }

    // If no venues or bounds are empty, go to default center
    map.current.flyTo({
      center: initialCenter,
      zoom: 3,
      essential: true
    });
  };

  useEffect(() => {
    // Redirect if user doesn't have permissions
    if (userData && !flagsLoading && !canViewMap) {
      Alert.alert(
        "Access Restricted",
        "The map is not available in this environment right now.",
        [{ text: "Go Back", onPress: () => navigation.goBack() }]
      );
      return;
    }

    // Fetch venues
    fetchVenues();
    fetchScreenprinters();
    fetchApprovedResourceIndex();
    fetchApprovedFoodLocations();
  }, [userData, navigation, canViewMap, flagsLoading]);

  // Add the venue from route params to tour if provided (duplicates allowed)
  useEffect(() => {
    if (venueFromRoute && venueFromRoute.id) {
      // Wait until flags and the profile load, or a member's venue is dropped.
      if (flagsLoading || (!userData?.uid && isRealUser(auth.currentUser))) return;
      navigation.setParams({ addVenue: null });
      if (!mapGateRef.current(MAP_ACTION.PLAN)) return;
      setTourVenues(prev => [...prev, venueFromRoute]);
      addVenueToDayById(venueFromRoute, activeDayId);
    }
  }, [venueFromRoute, flagsLoading, userData?.uid]);

  // Add this effect to update tour stats whenever tour venues or tour days change
  useEffect(() => {
    if (mapLoaded && map.current) {

      // Don't call updateVenuesOnMap here - the other useEffect handles that
      // This effect focuses on route calculations and stats
      const allTourVenues = tourDays.flatMap(day => day.venues);

      // Calculate and update route information if we have multiple venues
      const activeDay = tourDays.find(day => day.id === activeDayId);
      const activeDayHasMultipleVenues = activeDay && activeDay.venues.length >= 2;

      if (activeDayHasMultipleVenues || allTourVenues.length >= 2) {
        calculateTourDistances().then(info => {
          if (info) {
            setRouteInfo(info);
          }
        });
      }
    }
  }, [tourVenues, tourDays, interDayLegModes, mapLoaded, activeDayId, displayMode]);

  useEffect(() => {
    // Run exactly once when the container div is mounted; mapInitializedRef prevents re-run on any later deps change
    if (Platform.OS !== 'web' || !mapContainerMounted || !mapContainer.current || map.current || mapInitializedRef.current) return;
    (function initMap() {
      console.log('Initializing map...');

      if (!MAPBOX_ACCESS_TOKEN) {
        console.error('Map configuration error');
        return;
      }

      try {
        // Make sure mapboxgl is properly initialized
        mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: isDarkMode ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/streets-v11',
          center: initialCenter,
          zoom: 3,
          minZoom: 3,   // Prevent excessive zoom-out (globe view)
          maxZoom: 18,  // Cap zoom-in for stable, usable detail level
          trackResize: true,
          maxPitch: 85,
          renderWorldCopies: true,
          localIdeographFontFamily: "'Noto Sans', 'Noto Sans CJK SC', sans-serif",
          preserveDrawingBuffer: true,
          customAttribution: 'VRC Tour Planner'
        });
        mapInitializedRef.current = true;

        // Set up event handlers
        map.current.on('load', () => {
          console.log('Map loaded, initializing components...');
          setMapLoaded(true);

          // Add 3D buildings layer
          const layers = map.current.getStyle().layers;
          const labelLayerId = layers.find(
            (layer) => layer.type === 'symbol' && layer.layout['text-field']
          )?.id;

          if (!map.current.getLayer('3d-buildings')) {
            map.current.addLayer(
              {
                'id': '3d-buildings',
                'source': 'composite',
                'source-layer': 'building',
                'filter': ['==', 'extrude', 'true'],
                'type': 'fill-extrusion',
                'minzoom': 14,
                'paint': {
                  'fill-extrusion-color': isDarkMode
                    ? [
                      'interpolate',
                      ['linear'],
                      ['get', 'height'],
                      0, '#1a1a2e',
                      50, '#16213e',
                      100, '#0f3460',
                      200, '#1a1a2e'
                    ]
                    : [
                      'interpolate',
                      ['linear'],
                      ['get', 'height'],
                      0, '#e8e8e8',
                      50, '#d4d4d4',
                      100, '#c0c0c0',
                      200, '#a8a8a8'
                    ],
                  'fill-extrusion-height': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    14, 0,
                    14.5, ['get', 'height']
                  ],
                  'fill-extrusion-base': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    14, 0,
                    14.5, ['get', 'min_height']
                  ],
                  'fill-extrusion-opacity': 0.85
                }
              },
              labelLayerId
            );
          }

          // Add source for venues
          if (!map.current.getSource('venues-source')) {
            map.current.addSource('venues-source', {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: []
              },
              cluster: true,
              clusterMaxZoom: clusterMaxZoom,
              clusterRadius: clusterRadius,
              clusterProperties: {
                sum: ['+', ['coalesce', ['get', 'capacity'], 0]]
              }
            });
          }

          // Source + layer for cluster hover radius (debug: development only)
          if (CLUSTER_HOVER_RADIUS_DEBUG) {
            if (!map.current.getSource('cluster-hover-radius')) {
              map.current.addSource('cluster-hover-radius', {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: [] }
              });
            }
            if (!map.current.getLayer('cluster-hover-radius-fill')) {
              map.current.addLayer({
                id: 'cluster-hover-radius-fill',
                type: 'fill',
                source: 'cluster-hover-radius',
                paint: {
                  'fill-color': '#e67e22',
                  'fill-opacity': 0.12,
                  'fill-outline-color': '#e67e22'
                }
              });
            }
            if (!map.current.getLayer('cluster-hover-radius-line')) {
              map.current.addLayer({
                id: 'cluster-hover-radius-line',
                type: 'line',
                source: 'cluster-hover-radius',
                paint: {
                  'line-color': '#e67e22',
                  'line-width': 2,
                  'line-opacity': 0.6
                }
              });
            }
          }

          // Cluster shadow layer for depth
          if (!map.current.getLayer('clusters-shadow')) {
            map.current.addLayer({
              id: 'clusters-shadow',
              type: 'circle',
              source: 'venues-source',
              filter: ['has', 'point_count'],
              paint: {
                'circle-color': 'rgba(0, 0, 0, 0.2)',
                'circle-radius': [
                  'step',
                  ['get', 'point_count'],
                  22, 100,
                  32, 750,
                  42
                ],
                'circle-translate': [2, 2],
                'circle-blur': 0.5
              }
            });
          }

          if (!map.current.getLayer('clusters')) {
            map.current.addLayer({
              id: 'clusters',
              type: 'circle',
              source: 'venues-source',
              filter: ['has', 'point_count'],
              paint: {
                'circle-color': [
                  'step',
                  ['get', 'point_count'],
                  theme.markers.cluster,  // Orange for small clusters
                  50, theme.markers.clusterMedium,  // Darker orange for medium
                  200, theme.markers.clusterLarge  // Red for large clusters
                ],
                'circle-radius': [
                  'step',
                  ['get', 'point_count'],
                  18, 50,
                  24, 200,
                  32
                ],
                'circle-stroke-width': 3,
                'circle-stroke-color': '#EFEFEF',
              }
            });
          }

          if (!map.current.getLayer('cluster-count')) {
            map.current.addLayer({
              id: 'cluster-count',
              type: 'symbol',
              source: 'venues-source',
              filter: ['has', 'point_count'],
              layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
                'text-size': 13
              },
              paint: {
                'text-color': '#ffffff'
              }
            });
          }

          // Venue marker shadow for depth
          if (!map.current.getLayer('unclustered-point-shadow')) {
            map.current.addLayer({
              id: 'unclustered-point-shadow',
              type: 'circle',
              source: 'venues-source',
              filter: ['!', ['has', 'point_count']],
              paint: {
                'circle-color': 'rgba(0, 0, 0, 0.25)',
                'circle-radius': [
                  'case',
                  ['==', ['get', 'isTourVenue'], true],
                  12,
                  9
                ],
                'circle-translate': [1, 1],
                'circle-blur': 0.4
              }
            });
          }

          // Add the unclustered points layer
          if (!map.current.getLayer('unclustered-point')) {
            map.current.addLayer({
              id: 'unclustered-point',
              type: 'circle',
              source: 'venues-source',
              filter: ['!', ['has', 'point_count']],
              paint: {
                'circle-color': VENUE_PIN_AFFILIATE_COLOR_EXPR,
                'circle-radius': [
                  'case',
                  ['==', ['get', 'isTourVenue'], true],
                  10,  // Larger for tour venues
                  7    // Regular size for other venues
                ],
                'circle-stroke-width': 2.5,
                'circle-stroke-color': '#ffffff'
              }
            });
          }

          // Add the venue labels layer
          if (!map.current.getLayer('venue-labels')) {
            map.current.addLayer({
              id: 'venue-labels',
              type: 'symbol',
              source: 'venues-source',
              filter: ['!', ['has', 'point_count']],
              minzoom: 12, // Only show labels when zoomed in
              layout: {
                'text-field': ['get', 'name'],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 11,
                'text-offset': [0, 1.2],
                'text-anchor': 'top',
                'text-max-width': 10
              },
              paint: {
                'text-color': '#1a1a1a',
                'text-halo-color': '#ffffff',
                'text-halo-width': 1.5,
                'text-halo-blur': 0.5
              }
            });
          }

          ensureDirectoryPostsMapLayers(map.current);

          // Eco sustainability ring — green outer ring on venues with lodging or vegan food
          if (!map.current.getLayer('eco-ring')) {
            map.current.addLayer({
              id: 'eco-ring',
              type: 'circle',
              source: 'venues-source',
              filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'isEco'], true]],
              paint: {
                'circle-color': 'rgba(0,0,0,0)',
                'circle-radius': [
                  'case',
                  ['==', ['get', 'isTourVenue'], true],
                  15,
                  12
                ],
                'circle-stroke-width': 2.5,
                'circle-stroke-color': '#2ecc71',
                'circle-stroke-opacity': 0.9
              }
            });
          }
          ensureEducationHatLayer(map.current);

          // Screenprinters / merch source (initially empty; populated by fetchScreenprinters)
          if (!map.current.getSource('screenprinters-source')) {
            map.current.addSource('screenprinters-source', {
              type: 'geojson',
              data: { type: 'FeatureCollection', features: [] }
            });
          }

          if (!map.current.getLayer('screenprinters-layer')) {
            map.current.addLayer({
              id: 'screenprinters-layer',
              type: 'circle',
              source: 'screenprinters-source',
              paint: {
                'circle-color': '#f39c12',
                'circle-radius': 8,
                'circle-stroke-width': 2.5,
                'circle-stroke-color': '#ffffff'
              }
            });
          }

          if (!map.current.getLayer('screenprinters-labels')) {
            map.current.addLayer({
              id: 'screenprinters-labels',
              type: 'symbol',
              source: 'screenprinters-source',
              minzoom: 10,
              layout: {
                'text-field': ['get', 'name'],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 11,
                'text-offset': [0, 1.2],
                'text-anchor': 'top',
                'text-max-width': 10
              },
              paint: {
                'text-color': '#1a1a1a',
                'text-halo-color': '#ffffff',
                'text-halo-width': 1.5,
                'text-halo-blur': 0.5
              }
            });
          }

          // Transit stops source + layers (bus=purple, rail=blue)
          if (!map.current.getSource('transit-source')) {
            map.current.addSource('transit-source', {
              type: 'geojson',
              data: { type: 'FeatureCollection', features: [] }
            });
          }
          // ── Bus stops — purple circles, zoom 11+ ──
          if (!map.current.getLayer('transit-bus-symbol')) {
            map.current.addLayer({
              id: 'transit-bus-symbol',
              type: 'circle',
              source: 'transit-source',
              minzoom: 11,
              filter: ['==', ['get', 'type'], 'bus'],
              layout: { visibility: 'none' },
              paint: {
                'circle-color': '#8b5cf6',
                'circle-radius': 9,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff',
                'circle-opacity': 0.92,
              },
            });
          }
          // ── Train / Amtrak — red circles, zoom 9+ ──
          if (!map.current.getLayer('transit-train-symbol')) {
            map.current.addLayer({
              id: 'transit-train-symbol',
              type: 'circle',
              source: 'transit-source',
              minzoom: 9,
              filter: ['==', ['get', 'type'], 'rail'],
              layout: { visibility: 'none' },
              paint: {
                'circle-color': '#dc2626',
                'circle-radius': 11,
                'circle-stroke-width': 2.5,
                'circle-stroke-color': '#ffffff',
                'circle-opacity': 0.95,
              },
            });
          }
          // ── Airports — pink circles, zoom 6+ ──
          if (!map.current.getLayer('transit-airport-symbol')) {
            map.current.addLayer({
              id: 'transit-airport-symbol',
              type: 'circle',
              source: 'transit-source',
              minzoom: 6,
              filter: ['==', ['get', 'type'], 'airport'],
              layout: { visibility: 'none' },
              paint: {
                'circle-color': '#ec4899',
                'circle-radius': 12,
                'circle-stroke-width': 2.5,
                'circle-stroke-color': '#ffffff',
                'circle-opacity': 0.95,
              },
            });
          }
          // ── Letter labels — one layer per type, minzoom matches its circle layer ──
          if (!map.current.getLayer('transit-bus-labels')) {
            map.current.addLayer({
              id: 'transit-bus-labels',
              type: 'symbol',
              source: 'transit-source',
              minzoom: 11, // matches transit-bus-symbol
              filter: ['==', ['get', 'type'], 'bus'],
              layout: {
                visibility: 'none',
                'text-field': 'B',
                'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
                'text-size': 10,
                'text-anchor': 'center',
                'text-allow-overlap': true,
                'text-ignore-placement': true,
              },
              paint: { 'text-color': '#ffffff' },
            });
          }
          if (!map.current.getLayer('transit-train-labels')) {
            map.current.addLayer({
              id: 'transit-train-labels',
              type: 'symbol',
              source: 'transit-source',
              minzoom: 9, // matches transit-train-symbol
              filter: ['==', ['get', 'type'], 'rail'],
              layout: {
                visibility: 'none',
                'text-field': 'T',
                'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
                'text-size': 10,
                'text-anchor': 'center',
                'text-allow-overlap': true,
                'text-ignore-placement': true,
              },
              paint: { 'text-color': '#ffffff' },
            });
          }
          if (!map.current.getLayer('transit-airport-labels')) {
            map.current.addLayer({
              id: 'transit-airport-labels',
              type: 'symbol',
              source: 'transit-source',
              minzoom: 6, // matches transit-airport-symbol
              filter: ['==', ['get', 'type'], 'airport'],
              layout: {
                visibility: 'none',
                'text-field': 'A',
                'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
                'text-size': 10,
                'text-anchor': 'center',
                'text-allow-overlap': true,
                'text-ignore-placement': true,
              },
              paint: { 'text-color': '#ffffff' },
            });
          }
          // ── Name labels — only at close zoom ──
          if (!map.current.getLayer('transit-labels')) {
            map.current.addLayer({
              id: 'transit-labels',
              type: 'symbol',
              source: 'transit-source',
              minzoom: 12,
              layout: {
                visibility: 'none',
                'text-field': ['get', 'name'],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 10,
                'text-offset': [0, 1.6],
                'text-anchor': 'top',
                'text-max-width': 8,
              },
              paint: {
                'text-color': '#1a1a1a',
                'text-halo-color': '#ffffff',
                'text-halo-width': 1.5,
              }
            });
          }

          ensureDirectoryPostsMapLayers(map.current);

          // ── Bike lanes: purple = available, green = commonly used ──
          try {
            ensureBikeLaneLayers(map.current);
            bikeLanesFetchedKeyRef.current = null;
            setBikeLaneVisibility(map.current, showBikeLanesRef.current);
            if (showBikeLanesRef.current) loadBikeLanesForMapRef.current?.();
          } catch (_) {}
          // for travel + day-off categories
          Object.keys(POI_CATEGORY_META).forEach((category) => ensurePoiLayer(category));
          ensurePoiDebugLayer();
          syncPoiLayerVisibility();
          setupPoiClickHandlers();

          // Set initial bounds so left panel + map show venues in view (effect will run when filteredVenues + mapBounds are set)
          setMapBounds(map.current.getBounds());
          if (venues.length > 0) {
            setIsInitializing(false);
          }

          // Set up click handlers
          setupVenueClickHandlers();
        });

        // Add navigation controls
        // NavigationControl removed — zoom handled via custom bottom-left controls

        // Initialize map center
        const initialCenterObj = map.current.getCenter();
        setMapCenter({ lng: initialCenterObj.lng, lat: initialCenterObj.lat });

        // Handle map movement
        map.current.on('moveend', () => {
          const bounds = map.current.getBounds();
          setMapBounds(bounds);
          const center = map.current.getCenter();
          setMapCenter({ lng: center.lng, lat: center.lat });
          setIsZooming(false);
        });

        // Handle zoom changes
        map.current.on('zoom', () => {
          setCurrentZoom(map.current.getZoom());
        });
        
        // Track zoom start/end to prevent updates during zoom
        map.current.on('zoomstart', () => {
          setIsZooming(true);
        });
        
        map.current.on('zoomend', () => {
          setIsZooming(false);
          console.log('zoomend', map.current.getZoom());
        });

        map.current.on('movestart', () => {
          setIsZooming(true);
        });

        // Track zoom start/end to prevent updates during zoom
        map.current.on('zoomstart', () => {
          setIsZooming(true);
        });

        map.current.on('zoomend', () => {
          setIsZooming(false);
        });

        map.current.on('movestart', () => {
          setIsZooming(true);
        });

        // Reduce scroll zoom sensitivity. Mapbox defaults: setZoomRate=1/100, setWheelZoomRate=1/450.
        // Previous values of 0.5 were 50–200x TOO FAST. These feel natural and deliberate.
        try {
          const scrollZoom = map.current.scrollZoom;
          if (scrollZoom && typeof scrollZoom.setZoomRate === 'function') {
            scrollZoom.setZoomRate(1 / 400); // mouse wheel — ~4x slower than default
          }
          if (scrollZoom && typeof scrollZoom.setWheelZoomRate === 'function') {
            scrollZoom.setWheelZoomRate(1 / 1200); // trackpad pinch — ~2.7x slower than default
          }
        } catch (_) { /* ignore if API not available */ }

      } catch (error) {
        console.error('Error initializing map:', error);
        Alert.alert('Error', 'Failed to initialize map. Please try again.');
      }
    })();
  }, [mapContainerMounted, MAPBOX_ACCESS_TOKEN]);

  // Resize map when sidebar visibility changes (single delayed resize + cleanup)
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const t = setTimeout(() => {
      if (map.current) map.current.resize();
    }, 150);
    return () => clearTimeout(t);
  }, [venueListVisible, tourStopsVisible, mapLoaded, isMobile, headerHeight, showWeatherPanel]);

  // Separate useEffect for handling permissions and data loading
  useEffect(() => {
    const initializeData = async () => {
      try {
        // Fetch venues regardless of permissions
        // The fetchVenues function will also handle requesting user location if needed
        await fetchUserLocations();
        await fetchVenues();
      } catch (error) {
        console.error('Error initializing data:', error);
        // Display is driven by filteredVenues + mapBounds effect
      }
    };

    initializeData();
  }, []);

  // Keep venuesRef in sync with venues state for click handlers
  useEffect(() => {
    venuesRef.current = venues;
    console.log(`venuesRef updated: ${venues.length} venues`);
  }, [venues]);

  // Apply affiliate + features filters (VenueList behavior), then coord source, sort → filteredVenues only
  // (Display + map are driven by venuesInBounds effect below)
  useEffect(() => {
    if (!venues || venues.length === 0) {
      setFilteredVenues([]);
      return;
    }
    let base = applyFilters(venues);
    const selectedVenueSubmissions = approvedResourceSubmissions.filter((submission) =>
      selectedResourceSubmissionIds.includes(submission.id) &&
      submission.scope !== 'city' &&
      submission.scope !== 'state'
    );
    if (selectedVenueSubmissions.length > 0 || selectedResourceVenueIds.length > 0) {
      // Venue-scoped resource records and direct resource locations are a union.
      // City/state records are represented by area highlights instead of venue pins.
      base = base.filter((venue) =>
        selectedResourceVenueIds.includes(String(venue.id)) ||
        selectedVenueSubmissions.some((submission) =>
          resourceSubmissionAppliesToVenue(submission, venue)
        )
      );
    }
    if (coordinateSourceFilter !== 'all') {
      base = base.filter(v => v._coordinateSource === coordinateSourceFilter);
    }
    let sorted = base;
    if (userLocation && base.length > 0) {
      sorted = [...base].sort((a, b) => {
        const distA = calculateDistance(userLocation.latitude, userLocation.longitude, a.latitude, a.longitude);
        const distB = calculateDistance(userLocation.latitude, userLocation.longitude, b.latitude, b.longitude);
        return distA - distB;
      });
    }
    setFilteredVenues(sorted);
  }, [
    venues,
    filters.affiliate,
    filters.features,
    coordinateSourceFilter,
    userLocation,
    applyFilters,
    selectedResourceSubmissionIds,
    selectedResourceVenueIds,
    approvedResourceSubmissions,
    resourceSubmissionAppliesToVenue,
  ]);

  const hasSelectedVenueResource = useMemo(() =>
    approvedResourceSubmissions.some((submission) =>
      selectedResourceSubmissionIds.includes(submission.id) &&
      submission.scope !== 'city' &&
      submission.scope !== 'state'
    ),
  [approvedResourceSubmissions, selectedResourceSubmissionIds]);

  // Calendar MAP tab: every left-rail section can limit results to a radius around the
  // calendar's selected day's route (day + tour come from the calendar, so no picker here).
  const [routeRadiusMiles, setRouteRadiusMiles] = useState({
    venues: DEFAULT_FARM_RADIUS_MILES,
    resources: DEFAULT_FARM_RADIUS_MILES,
    travel: DEFAULT_FARM_RADIUS_MILES,
    directory: DEFAULT_FARM_RADIUS_MILES,
    learn: DEFAULT_FARM_RADIUS_MILES,
    flyers: DEFAULT_FARM_RADIUS_MILES,
  });
  const calendarDayRouteLine = useMemo(() => {
    if (!calendarEmbed) return [];
    const dayId = resolveFarmSearchDayId(tourDays, activeDayId);
    const line = collectRouteLineCoordinates(
      collectTourRouteGeometries(tourDays, interDayLegModes, routeCache.current, { dayId, legId: 'all' })
    );
    if (line.length) return line;
    // One-stop day: measure from the stop itself.
    const day = (tourDays || []).find((d) => d.id === dayId);
    return (day?.venues || [])
      .map(readHubCoords)
      .filter(Boolean)
      .map((c) => [c.longitude, c.latitude]);
    // routesCalculating flips when driving routes land in routeCache, so the line picks them up.
  }, [calendarEmbed, tourDays, interDayLegModes, activeDayId, routesCalculating]);
  const withinCalendarRouteRadius = (list, section) => {
    if (!calendarEmbed || !calendarDayRouteLine.length) return list;
    const miles = routeRadiusMiles[section];
    return (list || []).filter((item) => {
      const coords = readHubCoords(item);
      return coords ? isPointWithinRouteRadius(coords, calendarDayRouteLine, miles) : false;
    });
  };
  const calendarRouteLineRef = useRef([]);
  calendarRouteLineRef.current = calendarDayRouteLine;
  const travelRouteRadiusRef = useRef(DEFAULT_FARM_RADIUS_MILES);
  travelRouteRadiusRef.current = routeRadiusMiles.travel;
  // Open side-rail section (its radius is drawn) and each section's preview pins.
  const [activeRailItemId, setActiveRailItemId] = useState(null);
  const [routePreviewPins, setRoutePreviewPins] = useState({});
  const publishRoutePreviewPins = useCallback((section, pins) => {
    setRoutePreviewPins((prev) => {
      const next = Array.isArray(pins) ? pins : [];
      if ((prev[section]?.length || 0) === 0 && next.length === 0) return prev;
      return { ...prev, [section]: next };
    });
  }, []);

  // Draw the open section's search radius as a corridor around the day's route.
  useEffect(() => {
    if (!map.current || !mapLoaded || !calendarEmbed) return;
    const m = map.current;
    const section = RAIL_ITEM_RADIUS_SECTION[activeRailItemId] || null;
    const clear = () => {
      [ROUTE_RADIUS_CORRIDOR_FILL, ROUTE_RADIUS_CORRIDOR_LINE].forEach((id) => {
        if (m.getLayer(id)) m.removeLayer(id);
      });
      if (m.getSource(ROUTE_RADIUS_CORRIDOR_SOURCE)) m.removeSource(ROUTE_RADIUS_CORRIDOR_SOURCE);
    };
    if (!section || !calendarDayRouteLine.length) {
      clear();
      return;
    }
    try {
      const miles = clampFarmRadiusMiles(routeRadiusMiles[section]);
      const shape = calendarDayRouteLine.length === 1
        ? turf.point(calendarDayRouteLine[0])
        : turf.simplify(turf.lineString(calendarDayRouteLine), { tolerance: 0.02, highQuality: false, mutate: false });
      const buffered = turf.buffer(shape, miles, { units: 'miles' });
      if (!buffered) {
        clear();
        return;
      }
      const data = { type: 'FeatureCollection', features: [buffered] };
      const color = route?.params?.routeLayerAccent || '#3d7fe6';
      const existing = m.getSource(ROUTE_RADIUS_CORRIDOR_SOURCE);
      if (existing) {
        existing.setData(data);
        m.setPaintProperty(ROUTE_RADIUS_CORRIDOR_FILL, 'fill-color', color);
        m.setPaintProperty(ROUTE_RADIUS_CORRIDOR_LINE, 'line-color', color);
      } else {
        // Keep the corridor under the pins.
        const beforeId = ['clusters-shadow', 'unclustered-point-shadow', 'unclustered-point']
          .find((id) => m.getLayer(id));
        m.addSource(ROUTE_RADIUS_CORRIDOR_SOURCE, { type: 'geojson', data });
        m.addLayer({
          id: ROUTE_RADIUS_CORRIDOR_FILL,
          type: 'fill',
          source: ROUTE_RADIUS_CORRIDOR_SOURCE,
          paint: { 'fill-color': color, 'fill-opacity': 0.1 },
        }, beforeId);
        m.addLayer({
          id: ROUTE_RADIUS_CORRIDOR_LINE,
          type: 'line',
          source: ROUTE_RADIUS_CORRIDOR_SOURCE,
          paint: { 'line-color': color, 'line-width': 1.5, 'line-opacity': 0.6, 'line-dasharray': [2, 1] },
        }, beforeId);
      }
    } catch (err) {
      console.warn('route radius corridor', err);
    }
  }, [mapLoaded, calendarEmbed, activeRailItemId, calendarDayRouteLine, routeRadiusMiles, route?.params?.routeLayerAccent]);

  // Preview pins: each section's current matches along the route (clicking shows the name).
  const routePreviewWiredRef = useRef(new Set());
  useEffect(() => {
    if (!map.current || !mapLoaded || !calendarEmbed) return;
    const m = map.current;
    Object.keys(ROUTE_PREVIEW_COLORS).forEach((section) => {
      const sourceId = `route-preview-${section}`;
      const pinLayer = `route-preview-${section}-pins`;
      const labelLayer = `route-preview-${section}-labels`;
      const data = {
        type: 'FeatureCollection',
        features: (routePreviewPins[section] || []).map((pin) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [Number(pin.longitude), Number(pin.latitude)] },
          properties: { id: pin.id, name: pin.name || '' },
        })),
      };
      const source = m.getSource(sourceId);
      if (source) {
        source.setData(data);
        return;
      }
      if (data.features.length === 0) return;
      m.addSource(sourceId, { type: 'geojson', data });
      m.addLayer({
        id: pinLayer,
        type: 'circle',
        source: sourceId,
        paint: {
          'circle-radius': 6,
          'circle-color': ROUTE_PREVIEW_COLORS[section],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });
      m.addLayer({
        id: labelLayer,
        type: 'symbol',
        source: sourceId,
        minzoom: 10,
        layout: { 'text-field': ['get', 'name'], 'text-size': 11, 'text-anchor': 'top', 'text-offset': [0, 0.9] },
        paint: { 'text-color': ROUTE_PREVIEW_COLORS[section], 'text-halo-color': '#ffffff', 'text-halo-width': 1.4 },
      });
      if (!routePreviewWiredRef.current.has(pinLayer)) {
        routePreviewWiredRef.current.add(pinLayer);
        m.on('click', pinLayer, (event) => {
          const feature = event.features?.[0];
          if (!feature) return;
          new mapboxgl.Popup({ offset: 10, closeButton: false })
            .setLngLat(feature.geometry.coordinates)
            .setHTML(`<strong style="font-size:13px">${escapePopupHtml(feature.properties?.name)}</strong>`)
            .addTo(m);
        });
        m.on('mouseenter', pinLayer, () => { m.getCanvas().style.cursor = 'pointer'; });
        m.on('mouseleave', pinLayer, () => { m.getCanvas().style.cursor = ''; });
      }
    });
  }, [mapLoaded, calendarEmbed, routePreviewPins]);

  const routeRadiusProp = useMemo(() => (calendarEmbed ? {
    lineCoords: calendarDayRouteLine,
    radii: routeRadiusMiles,
    onChangeRadius: (section, miles) => setRouteRadiusMiles((prev) => ({ ...prev, [section]: clampFarmRadiusMiles(miles) })),
    onPreviewPins: publishRoutePreviewPins,
  } : null), [calendarEmbed, calendarDayRouteLine, routeRadiusMiles, publishRoutePreviewPins]);
  // Fridges & Farms searches the calendar's selected day (its own day picker is hidden here).
  useEffect(() => {
    if (!calendarEmbed || !activeDayId) return;
    setFarmSearchDayId(activeDayId);
    setFarmSearchLegId('all');
  }, [calendarEmbed, activeDayId]);

  // Calendar MAP tab: keep the selected day's route lines as they are, fade other days' lines to 30%.
  useEffect(() => {
    if (!map.current || !mapLoaded || !calendarEmbed) return undefined;
    const m = map.current;
    const activeIndex = (tourDays || []).findIndex((day) => day.id === activeDayId);
    const applyOpacity = () => {
      (m.getStyle?.()?.layers || []).forEach(({ id, type }) => {
        let dayIndex = null;
        let match = id.match(/^route-layer-inter-(\d+)$/) || id.match(/^route-flight-layer-inter-(\d+)$/);
        if (match) {
          // Overnight hop out of a day counts as that day's route.
          dayIndex = Number(match[1]);
        } else {
          match = id.match(/^route-layer-(\d+)-\d+$/) || id.match(/^route-flight-layer-(\d+)-\d+$/);
          if (match) dayIndex = Number(match[1]);
        }
        if (dayIndex == null) return;
        const faded = activeIndex >= 0 && dayIndex !== activeIndex;
        try {
          if (type === 'line') {
            const want = faded ? 0.3 : 0.8;
            if (m.getPaintProperty(id, 'line-opacity') !== want) m.setPaintProperty(id, 'line-opacity', want);
          } else if (type === 'circle') {
            const want = faded ? 0.3 : 1;
            if (m.getPaintProperty(id, 'circle-opacity') !== want) m.setPaintProperty(id, 'circle-opacity', want);
          } else if (type === 'symbol') {
            const want = faded ? 0.3 : 1;
            if (m.getPaintProperty(id, 'text-opacity') !== want) m.setPaintProperty(id, 'text-opacity', want);
            if (m.getPaintProperty(id, 'icon-opacity') !== want) m.setPaintProperty(id, 'icon-opacity', want);
          }
        } catch (_) {
          /* layer may be mid-update */
        }
      });
    };
    applyOpacity();
    // Route layers are re-created as routes recalculate; re-apply when the style changes.
    m.on('styledata', applyOpacity);
    return () => m.off('styledata', applyOpacity);
  }, [mapLoaded, calendarEmbed, activeDayId, tourDays]);

  // Travel pins are filtered when POIs are fetched, so refetch when the radius or day route changes.
  useEffect(() => {
    if (!calendarEmbed) return undefined;
    const t = setTimeout(() => fetchPOIsRef.current?.(), 400);
    return () => clearTimeout(t);
  }, [calendarEmbed, routeRadiusMiles.travel, calendarDayRouteLine]);

  // Client request: show no venue pins/list until the user searches or applies a filter.
  const hasVenueRevealCriteria = useMemo(() => (
    Boolean(searchQuery.trim()) ||
    countActiveFilters(filters) > 0 || // REQ-104: includes Video and accessibility picks
    hasSelectedVenueResource ||
    (selectedResourceVenueIds.length > 0)
  ), [
    searchQuery,
    filters,
    hasSelectedVenueResource,
    selectedResourceVenueIds,
  ]);

  useEffect(() => { filteredVenuesRef.current = filteredVenues; }, [filteredVenues]);
  useEffect(() => { hasVenueRevealCriteriaRef.current = hasVenueRevealCriteria; }, [hasVenueRevealCriteria]);

  // Derive venuesInBounds from filteredVenues + mapBounds; update map and keep ref for route/zoom updates
  // When showOnlyTourVenues (tour view mode), map shows only tour venues instead of all in bounds
  useEffect(() => {
    // Calendar MAP tab: a search or venue criteria shows every match within the venue search radius
    // of the day's route (not just the tour's own stops, and not limited to the visible map area).
    if (calendarEmbed && calendarDayRouteLine.length && hasVenueRevealCriteria && showVenues) {
      const nearRoute = withinCalendarRouteRadius(filteredVenues || [], 'venues');
      const matches = filterVenuesBySearch(nearRoute, searchQuery);
      const matchIds = new Set(matches.map((venue) => String(venue.id)));
      const toShow = [...matches, ...tourVenues.filter((venue) => !matchIds.has(String(venue.id)))];
      setVenuesInBounds(nearRoute);
      venuesToShowRef.current = toShow;
      if (map.current && mapLoaded && map.current.getSource('venues-source')) {
        updateVenuesOnMap(toShow);
      }
      return;
    }
    if (showOnlyTourVenues && tourVenues.length > 0) {
      const toShow = filterVenuesBySearch(tourVenues, searchQuery);
      venuesToShowRef.current = toShow;
      if (map.current && mapLoaded && map.current.getSource('venues-source')) {
        updateVenuesOnMap(toShow);
      }
      return;
    }
    if (showOnlyTourVenues && tourVenues.length === 0) {
      venuesToShowRef.current = [];
      if (map.current && mapLoaded && map.current.getSource('venues-source')) {
        updateVenuesOnMap([]);
      }
      return;
    }
    // Default empty map: no pins until search or filter
    if (!hasVenueRevealCriteria) {
      setVenuesInBounds([]);
      venuesToShowRef.current = [];
      if (map.current && mapLoaded && map.current.getSource('venues-source')) {
        updateVenuesOnMap([]);
      }
      return;
    }
    if (!filteredVenues || filteredVenues.length === 0) {
      setVenuesInBounds([]);
      venuesToShowRef.current = [];
      if (map.current && mapLoaded && map.current.getSource('venues-source')) {
        updateVenuesOnMap([]);
      }
      return;
    }
    if (!showVenues) {
      setVenuesInBounds([]);
      venuesToShowRef.current = [];
      if (map.current && mapLoaded && map.current.getSource('venues-source')) {
        updateVenuesOnMap([]);
      }
      return;
    }
    const inBounds = withinCalendarRouteRadius(
      mapBounds
        ? filterVenuesByBounds(filteredVenues, mapBounds)
        : filteredVenues.slice(0, 1000),
      'venues',
    );
    setVenuesInBounds(inBounds);
    const toShow = filterVenuesBySearch(inBounds, searchQuery);
    venuesToShowRef.current = toShow;
    if (map.current && mapLoaded && map.current.getSource('venues-source')) {
      updateVenuesOnMap(toShow);
    }
  }, [filteredVenues, mapBounds, showVenues, mapLoaded, searchQuery, showOnlyTourVenues, tourVenues, hasVenueRevealCriteria, venueResourceIndex, calendarDayRouteLine, routeRadiusMiles.venues]);

  // Affiliate region (choropleth) base layer. Independent of which pins are shown
  // (venues or POIs) — concentration is always venue-affiliate based and can sit
  // beneath any pin overlay. Stay blank until the user searches/filters (same rule as pins).
  useEffect(() => {
    mapViewModeRef.current = mapViewMode;
    if (!map.current || !mapLoaded) return;
    const visible = mapViewMode === 'concentration';
    if (visible) {
      const concentrationVenues = hasVenueRevealCriteria ? (filteredVenues || []) : [];
      activateAffiliateRegions(concentrationVenues);
      activateAffiliateIntlAdm0(concentrationVenues);
    } else {
      setAffiliateRegionVisibility(false);
    }
    setConcentrationBasemapWash(visible);
  }, [mapViewMode, mapMode, venues, filteredVenues, hasVenueRevealCriteria, concentrationBucketsOn, mapLoaded]);

  // Highlight city/state polygons for category filters and selected area-scoped resources.
  useEffect(() => {
    updateResourceAreaHighlights();
  }, [
    filters.resources,
    venueResourceIndex,
    venues,
    mapLoaded,
    mapStyle,
    selectedResourceSubmissionIds,
    approvedResourceSubmissions,
  ]);

  // ZIP/ZCTA (US) and international (non-US) tiers react to viewport changes
  // (mapBounds updates on moveend/zoom).
  useEffect(() => {
    updateAffiliateZctaTier();
    updateAffiliateIntlTier();
  }, [mapBounds, mapViewMode, mapMode, venues, filteredVenues, hasVenueRevealCriteria, concentrationBucketsOn, mapLoaded]);

  // Rebuild / recolor the venue layer when toggling Clusters ↔ Pins ↔ Hide,
  // or when concentration mode changes pin stroke styling.
  useEffect(() => {
    applyVenueDisplayMode();
  }, [displayMode, mapViewMode, mapLoaded]);

  // Show/hide the green eco sustainability ring (stays hidden if all pins are hidden).
  // Also auto-off when zoomed far out (< 5) so the world view stays clean.
  useEffect(() => {
    if (!map.current || !mapLoaded || !map.current.getLayer('eco-ring')) return;
    const visible = showEcoRing && displayMode !== 'hidden';
    map.current.setLayoutProperty('eco-ring', 'visibility', visible ? 'visible' : 'none');
    try { map.current.setLayerZoomRange('eco-ring', 5, 24); } catch (e) { /* layer not ready */ }
  }, [showEcoRing, displayMode, mapLoaded]);

  // ── Persist map settings per user (map type, eco ring, pin display) ─────────
  const mapPrefsLoadedRef = useRef(false);
  const mapPrefsUidRef = useRef(null);
  const mapPrefsKey = (uid) => `vrc-map-prefs-${uid || 'guest'}`;

  // Load saved preferences whenever the signed-in user changes.
  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(mapPrefsKey(userData?.uid));
      if (raw) {
        const p = JSON.parse(raw);
        if (p.mapViewMode === 'map' || p.mapViewMode === 'concentration') setMapViewMode(p.mapViewMode);
        if (typeof p.showEcoRing === 'boolean') setShowEcoRing(p.showEcoRing);
        if (['clusters', 'pins', 'hidden'].includes(p.displayMode)) {
          setDisplayMode(p.displayMode);
        }
      }
    } catch (e) { /* ignore malformed prefs */ }
    mapPrefsLoadedRef.current = true;
  }, [userData?.uid]);

  // Save preferences when they change (skip the first pass right after a user switch).
  useEffect(() => {
    if (typeof localStorage === 'undefined' || !mapPrefsLoadedRef.current) return;
    const uid = userData?.uid || 'guest';
    if (mapPrefsUidRef.current !== uid) { mapPrefsUidRef.current = uid; return; }
    try {
      localStorage.setItem(mapPrefsKey(uid), JSON.stringify({ mapViewMode, showEcoRing, displayMode }));
    } catch (e) { /* storage full / unavailable */ }
  }, [mapViewMode, showEcoRing, displayMode, userData?.uid]);

  // Request user location after venues are loaded (separate from fetchVenues to avoid race condition)
  useEffect(() => {
    if (venues.length > 0 && !locationRequested && !userLocation) {
      console.log('Venues loaded, requesting user location');
      requestUserLocation();
    }
  }, [venues.length, locationRequested, userLocation]);

  // Create a dedicated function for setting up click handlers
  const setupVenueClickHandlers = () => {
    if (!map.current) return;

    // Click handler for individual venues
    map.current.on('click', 'unclustered-point', (e) => {
      const coordinates = e.features[0].geometry.coordinates.slice();
      const properties = e.features[0].properties;

      // Use venuesRef.current instead of venues state to get latest data
      const venue = venuesRef.current.find(v => v.id === properties.id);

      // Also use properties directly as fallback if venue not found in ref
      const venueName = venue?.name || properties.name || 'Unknown Venue';
      const venueCity = venue?.city || properties.city || '';
      const venueState = venue?.state || properties.state || '';
      const venueCapacity = (venue && venue.capacity !== undefined && venue.capacity !== null && venue.capacity !== '') ? String(venue.capacity) : (properties.capacity !== undefined && properties.capacity !== null && properties.capacity !== '') ? String(properties.capacity) : 'N/A';
      const venueTypeDisplay = venue?.affiliate || properties.affiliate || venue?.venueType || properties.venueType || 'N/A';
      const venueId = venue?.id || properties.id;
      const venueNotesRaw = venue?.notes != null ? String(venue.notes) : (properties.notes != null ? String(properties.notes) : '');
      const venueNotesEscaped = venueNotesRaw.trim() ? venueNotesRaw.replace(/</g, '&lt;').replace(/>/g, '&gt;') : '';

      if (!venue && !properties.name) {
        console.error('Venue not found:', properties.id, 'Available venues:', venuesRef.current.length);
        return;
      }
      
      console.log('Venue clicked:', venueName);


      // Show unified glass popup
      const lng = coordinates[0];
      const lat = coordinates[1];

      // Derive eco flags from venue data
      const backstageLowerP = (venue?.backstage || '').toLowerCase();
      const notesLowerP = (venue?.notes || '').toLowerCase();
      const venueLodging    = backstageLowerP.includes('lodging') || backstageLowerP.includes('hotel') || backstageLowerP.includes('bed');
      const venueVegan      = (venue?.name || venueName).toLowerCase().includes('vegan') ||
        (venue?.name || venueName).toLowerCase().includes('plant-based') ||
        notesLowerP.includes('vegan') || (venue?.affiliate || '').toLowerCase().includes('vegan');
      const venueShorePower = (venue?.shorePower || '').toLowerCase().includes('yes') || backstageLowerP.includes('shore power') || notesLowerP.includes('shore power');
      const venueMerch100   = (venue?.merch || '').toLowerCase().includes('100%');
      const venueNearTransit = (venue?._isNearTransit === true);
      const venueEcoConscious = venue?.isEcoConscious === true;

      const popupContent = buildVenuePopupHTML({
        id: venueId, name: venueName,
        city: venueCity, state: venueState,
        capacity: venueCapacity, type: venueTypeDisplay,
        notes: venueNotesEscaped,
        hasLodging: venueLodging, hasVeganFood: venueVegan,
        hasShorePower: venueShorePower, hasMerch100: venueMerch100,
        isNearTransit: venueNearTransit,
        isEcoConscious: venueEcoConscious,
        grade: venue?.approved?.overallGrade ?? null,
        showFocus: true, isUserLocation: false,
      });

      new mapboxgl.Popup({ className: 'dark-popup' })
        .setLngLat(coordinates)
        .setHTML(popupContent)
        .addTo(map.current);

      // Async: inject upcoming shows into the popup after it renders
      requestAnimationFrame(() => fetchUpcomingShowsForPopup(venueId, venueName, lat, lng));
      requestAnimationFrame(() => fetchVenueWeatherForPopup(venueId, lat, lng));

      setSelectedVenueId(venueId);
    });

    // Click handler for clusters
    map.current.on('click', 'clusters', (e) => {
      const features = map.current.queryRenderedFeatures(e.point, {
        layers: ['clusters']
      });
      const clusterId = features[0].properties.cluster_id;

      map.current.getSource('venues-source').getClusterExpansionZoom(
        clusterId,
        (err, zoom) => {
          if (err) return;

          map.current.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom
          });
        }
      );
    });

    // Change cursor on hover
    map.current.on('mouseenter', 'unclustered-point', () => {
      map.current.getCanvas().style.cursor = 'pointer';
    });

    map.current.on('mouseleave', 'unclustered-point', () => {
      map.current.getCanvas().style.cursor = '';
    });

    map.current.on('mouseenter', 'clusters', (e) => {
      map.current.getCanvas().style.cursor = 'pointer';
      const features = map.current.queryRenderedFeatures(e.point, { layers: ['clusters'] });
      if (features.length === 0) return;
      const clusterId = features[0].properties.cluster_id;
      const center = features[0].geometry.coordinates;
      const source = map.current.getSource('venues-source');
      if (!source || !source.getClusterLeaves) return;
      source.getClusterLeaves(clusterId, 500, 0, (err, leaves) => {
        if (err || !leaves || leaves.length === 0) return;
        const centerPt = turf.point(center);
        let maxKm = 0;
        leaves.forEach((leaf) => {
          const coords = leaf.geometry?.coordinates;
          if (coords) {
            const d = turf.distance(centerPt, turf.point(coords), { units: 'kilometers' });
            if (d > maxKm) maxKm = d;
          }
        });
        const radiusKm = Math.max(maxKm * 1.05, 0.05);
        const circle = turf.circle(center, radiusKm, { units: 'kilometers', steps: 64 });
        const hoverSource = map.current.getSource('cluster-hover-radius');
        if (hoverSource) hoverSource.setData({ type: 'FeatureCollection', features: [circle] });
      });
    });

    map.current.on('mouseleave', 'clusters', () => {
      map.current.getCanvas().style.cursor = '';
      const hoverSource = map.current.getSource('cluster-hover-radius');
      if (hoverSource) hoverSource.setData({ type: 'FeatureCollection', features: [] });
    });

    // Click handler for screenprinter / merch-pickup points
    map.current.on('click', 'screenprinters-layer', (e) => {
      if (!e.features || e.features.length === 0) return;
      const coordinates = e.features[0].geometry.coordinates.slice();
      const props = e.features[0].properties;
      const locationLine = [props.city, props.state].filter(Boolean).join(', ');
      const servicesPill = props.services
        ? `<span style="font-size:11px;color:rgba(255,195,0,0.85);background:rgba(255,195,0,0.12);padding:3px 9px;border-radius:6px;border:1px solid rgba(255,195,0,0.3);white-space:nowrap;">🖨 ${props.services}</span>`
        : '';
      const ecoPill = props.eco === true || props.eco === 'true'
        ? `<span style="font-size:11px;color:#8ee0b0;background:rgba(39,174,96,0.16);padding:3px 9px;border-radius:6px;border:1px solid rgba(39,174,96,0.35);white-space:nowrap;">Eco</span>`
        : '';
      const websiteLink = props.website
        ? `<a href="${props.website}" target="_blank" rel="noopener" style="display:block;margin-top:8px;font-size:12px;color:rgba(243,156,18,0.9);text-decoration:underline;word-break:break-all;">${props.website}</a>`
        : '';
      const contactLine = [props.phone, props.email].filter(Boolean).join(' · ');
      const contactHtml = contactLine
        ? `<p style="margin:8px 0 0 0;font-size:12px;color:rgba(255,255,255,0.62);">${contactLine}</p>`
        : '';
      const notesHtml = props.notes
        ? `<details style="margin-top:6px;"><summary style="cursor:pointer;font-size:11px;color:rgba(255,255,255,0.38);font-weight:600;list-style:none;">▸ Notes</summary><div style="margin-top:4px;color:rgba(255,255,255,0.62);font-size:12px;white-space:pre-wrap;">${props.notes}</div></details>`
        : '';
      const popupHtml = `
        <div style="min-width:230px;max-width:280px;font-family:system-ui,-apple-system,sans-serif;">
          <div style="padding:14px 16px 12px;">
            ${locationLine ? `<p style="margin:0 0 3px 0;font-size:10px;font-weight:700;letter-spacing:0.9px;text-transform:uppercase;color:rgba(255,255,255,0.38);">${locationLine}</p>` : ''}
            <h3 style="margin:0 0 10px 0;font-size:16px;font-weight:700;color:rgba(255,255,255,0.95);line-height:1.25;">👕 ${props.name || 'Merch'}</h3>
            <div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:6px;">${servicesPill}${ecoPill}</div>
            ${websiteLink}
            ${contactHtml}
            ${notesHtml}
          </div>
        </div>`;
      new mapboxgl.Popup({ className: 'dark-popup' })
        .setLngLat(coordinates)
        .setHTML(popupHtml)
        .addTo(map.current);
    });

    map.current.on('mouseenter', 'screenprinters-layer', () => {
      map.current.getCanvas().style.cursor = 'pointer';
    });
    map.current.on('mouseleave', 'screenprinters-layer', () => {
      map.current.getCanvas().style.cursor = '';
    });

    // Click handlers for transit stops (bus, rail, airport)
    ['transit-bus-symbol', 'transit-train-symbol', 'transit-airport-symbol'].forEach((layerId) => {
      map.current.on('click', layerId, (e) => {
        if (!e.features || e.features.length === 0) return;
        const coords = e.features[0].geometry.coordinates.slice();
        const props = e.features[0].properties;
        const typeLabel = props.type === 'rail' ? 'Train / Amtrak Station' :
                          props.type === 'airport' ? 'Airport' : 'Bus Stop';
        const accentColor = props.type === 'rail' ? '#dc2626' :
                            props.type === 'airport' ? '#ec4899' : '#8b5cf6';
        const safeId = String(props.id || '').replace(/'/g, "\\'");
        const safeName = String(props.name || typeLabel).replace(/'/g, "\\'");
        const safeLat = coords[1];
        const safeLng = coords[0];
        const popupHtml = `
          <div style="min-width:220px;max-width:270px;font-family:system-ui,-apple-system,sans-serif;">
            <div style="height:3px;background:${accentColor};border-radius:3px 3px 0 0;"></div>
            <div style="padding:14px 16px 0;">
              <p style="margin:0 0 3px 0;font-size:10px;font-weight:700;letter-spacing:0.9px;text-transform:uppercase;color:rgba(255,255,255,0.4);">${typeLabel}</p>
              <h3 style="margin:0 0 10px 0;font-size:15px;font-weight:700;color:rgba(255,255,255,0.95);line-height:1.3;">${props.name || typeLabel}</h3>
            </div>
            <button
              onclick="window.addTransitStopToTourFromMap('${safeId}','${safeName}','${props.type}','${safeLat}','${safeLng}')"
              style="width:100%;padding:11px 0;background:${accentColor};color:#fff;border:none;cursor:pointer;font-size:13px;font-weight:700;letter-spacing:0.2px;transition:opacity 0.15s;"
              onmouseover="this.style.opacity='0.85'"
              onmouseout="this.style.opacity='1'"
            >+ Add to Day Planner</button>
          </div>`;
        new mapboxgl.Popup({ className: 'dark-popup' })
          .setLngLat(coords)
          .setHTML(popupHtml)
          .addTo(map.current);
      });
      map.current.on('mouseenter', layerId, () => { map.current.getCanvas().style.cursor = 'pointer'; });
      map.current.on('mouseleave', layerId, () => { map.current.getCanvas().style.cursor = ''; });
    });
  };

  // Convert a user location (from userLocations or publicUserLocations) to venue shape for adding to tour
  const userLocationToVenue = (location) => {
    if (!location || location.latitude == null || location.longitude == null) return null;
    return {
      id: `userloc-${location.id}`,
      name: location.name || 'Custom location',
      latitude: location.latitude,
      longitude: location.longitude,
      address: location.address || '',
      fullAddress: location.address || '',
      city: location.city || '',
      state: location.state || '',
      isUserLocation: true,
      userLocationId: location.id
    };
  };

  // Add venue to a specific day (used by addVenueToTour and by drop target). insertIndex = add at position (default end).
  const addVenueToDay = (venue, dayId, insertIndex = undefined) => {
    if (!mapGateRef.current(MAP_ACTION.PLAN)) return;
    if (routesCalculating) return;
    if (!venue || !venue.latitude || !venue.longitude) {
      Alert.alert('Cannot Add Venue', 'This venue does not have location coordinates.');
      return;
    }
    // Allow duplicate venues: same venue can appear multiple times in a tour
    const updatedTourVenues = [...tourVenues, venue];
    setTourVenues(updatedTourVenues);
    const targetDay = tourDays.find(d => d.id === dayId);
    const willHaveMultipleVenues = targetDay && targetDay.venues.length >= 1;
    setTourDays(prevDays => {
      const newTourDays = prevDays.map(day => {
        if (day.id !== dayId) return day;
        const idx = insertIndex != null ? Math.min(Math.max(0, insertIndex), day.venues.length) : day.venues.length;
        const next = [...day.venues.slice(0, idx), venue, ...day.venues.slice(idx)];
        const prevLegModes = day.legModes || [];
        // Smart default: if this transit stop adjoins a same-type stop, inherit that mode
        const transitTypeToMode = (t) => t === 'rail' ? 'train' : t === 'airport' ? 'flight' : t === 'bus' ? 'bus' : 'drive';
        let defaultMode = 'drive';
        if (venue.isTransitStop) {
          const beforeStop = next[idx - 1];
          const afterStop  = next[idx + 1];
          if (beforeStop?.isTransitStop && beforeStop.transitType === venue.transitType) {
            defaultMode = transitTypeToMode(venue.transitType);
          } else if (afterStop?.isTransitStop && afterStop.transitType === venue.transitType) {
            defaultMode = transitTypeToMode(venue.transitType);
          } else {
            defaultMode = transitTypeToMode(venue.transitType);
          }
        }
        const newLegModes = [...prevLegModes.slice(0, idx), defaultMode, ...prevLegModes.slice(idx)];
        // Also update the leg BEFORE this stop if its neighbor is now same-type
        if (venue.isTransitStop && idx > 0) {
          const neighbor = next[idx - 1];
          if (neighbor?.isTransitStop && neighbor.transitType === venue.transitType) {
            newLegModes[idx - 1] = transitTypeToMode(venue.transitType);
          }
        }
        return { ...day, venues: next, legModes: newLegModes };
      });
      if (willHaveMultipleVenues && map.current && mapLoaded) {
        setTimeout(() => {
          const allVenues = newTourDays.flatMap(day => day.venues);
          directUpdateMapWithRoutes(allVenues, newTourDays, activeDayId);
          setTimeout(() => {
            directUpdateMapWithRoutes(allVenues, newTourDays, activeDayId);
            setTimeout(() => directUpdateMapWithRoutes(allVenues, newTourDays, activeDayId), 1000);
          }, 500);
        }, 50);
      }
      return newTourDays;
    });

    console.log("venues:", venues)
  };
  addVenueToDayRef.current = addVenueToDay;

  // Move venue slot from one day/position to another (reorder within day or between days). Uses slot (dayId, index) so duplicates work.
  const moveVenueInTour = (fromDayId, fromIndex, toDayId, toIndex) => {
    if (routesCalculating) return;
    setTourDays(prev => {
      const sourceDay = prev.find(d => d.id === fromDayId);
      const venue = sourceDay?.venues?.[fromIndex];
      if (!venue) return prev;
      const withoutSource = prev.map(d => {
        if (d.id !== fromDayId) return d;
        const newVenues = [...d.venues.slice(0, fromIndex), ...d.venues.slice(fromIndex + 1)];
        const legModes = d.legModes || [];
        const newLegModes = [...legModes.slice(0, fromIndex), ...legModes.slice(fromIndex + 1)];
        return { ...d, venues: newVenues, legModes: newLegModes.length ? newLegModes : undefined };
      });
      const newDays = withoutSource.map(d => {
        if (d.id !== toDayId) return d;
        const insertAt = fromDayId === toDayId && fromIndex < toIndex ? toIndex - 1 : toIndex;
        const list = d.venues;
        const prevLegModes = d.legModes || [];
        const nextLegModes = [...prevLegModes.slice(0, insertAt), 'drive', ...prevLegModes.slice(insertAt)];
        return { ...d, venues: [...list.slice(0, insertAt), venue, ...list.slice(insertAt)], legModes: nextLegModes };
      });
      setTourVenues(newDays.flatMap(d => d.venues));
      if (map.current && mapLoaded) {
        setTimeout(() => {
          directUpdateMapWithRoutes(newDays.flatMap(d => d.venues), newDays, activeDayId, interDayLegModes);
        }, 50);
      }
      return newDays;
    });
  };

  // Split one day into two: venues 0..splitAfterIndex stay; venues (splitAfterIndex+1)..end move to a new day inserted after this one.
  const splitDayAfter = (dayId, splitAfterIndex) => {
    if (routesCalculating) return;
    const dayIndex = tourDays.findIndex(d => d.id === dayId);
    const day = tourDays[dayIndex];
    if (!day || day.venues.length < 2 || splitAfterIndex < 0 || splitAfterIndex >= day.venues.length - 1) return;
    const nextInterModes = [...(interDayLegModes || []).slice(0, dayIndex), 'drive', ...(interDayLegModes || []).slice(dayIndex)];
    setTourDays(prev => {
      const idx = prev.findIndex(d => d.id === dayId);
      const currentDay = prev[idx];
      if (!currentDay || currentDay.venues.length < 2 || splitAfterIndex < 0 || splitAfterIndex >= currentDay.venues.length - 1) return prev;
      const keptVenues = currentDay.venues.slice(0, splitAfterIndex + 1);
      const movedVenues = currentDay.venues.slice(splitAfterIndex + 1);
      const keptLegModes = (currentDay.legModes || []).slice(0, splitAfterIndex);
      const movedLegModes = (currentDay.legModes || []).slice(splitAfterIndex + 1);
      const newDayId = `day-${Date.now()}`;
      const newDayName = `Day ${idx + 2}`;
      const newDay = { id: newDayId, name: newDayName, venues: movedVenues, legModes: movedLegModes.length ? movedLegModes : undefined };
      const updatedCurrent = { ...currentDay, venues: keptVenues, legModes: keptLegModes.length ? keptLegModes : undefined };
      const next = [...prev.slice(0, idx), updatedCurrent, newDay, ...prev.slice(idx + 1)];
      setTourVenues(next.flatMap(days => days.venues));
      setInterDayLegModes(prevModes => [...prevModes.slice(0, idx), 'drive', ...prevModes.slice(idx)]);
      if (map.current && mapLoaded) {
        setTimeout(() => directUpdateMapWithRoutes(next.flatMap(days => days.venues), next, activeDayId, nextInterModes), 50);
      }
      return next;
    });
  };

  // New direct update function that bypasses debouncing for immediate route drawing
  const directUpdateMapWithRoutes = async (allVenues, currentTourDays, currentActiveDayId, interDayModesOverride = undefined) => {
    if (!map.current || !mapLoaded) return;

    console.log('DIRECT route update running - bypassing debounce');

    // This function ONLY draws routes - DO NOT update venue source here
    // Venues are managed separately by debouncedUpdateRef to avoid race conditions
    // Use the separated drawRoutes function (pass override so inter-day mode changes apply immediately)
    await drawRoutes(currentTourDays, currentActiveDayId, interDayModesOverride);
  };

  const addVenueToTour = (venue) => {
    addVenueToDay(venue, activeDayId);
  };

  addUserLocationToTourHandlerRef.current = (locationId) => {
    const loc = [...userLocations, ...publicUserLocations].find(l => l.id === locationId);
    const venue = userLocationToVenue(loc);
    if (venue) addVenueToTour(venue);
  };

  addTransitStopToTourRef.current = ({ stopId, stopName, stopType, lat, lng }) => {
    const transitVenue = {
      id: `transit-${stopId}`,
      name: stopName || (stopType === 'airport' ? 'Airport' : stopType === 'rail' ? 'Train Station' : 'Bus Stop'),
      latitude: parseFloat(lat),
      longitude: parseFloat(lng),
      address: '',
      fullAddress: '',
      city: '',
      state: '',
      venueType: stopType === 'rail' ? 'Train Station' : stopType === 'airport' ? 'Airport' : stopType === 'ferry' ? 'Ferry Terminal' : 'Bus Stop',
      isTransitStop: true,
      transitType: stopType,
    };
    addVenueToTour(transitVenue);
    setToast({ message: `${transitVenue.name} added to day plan `, type: 'success' });
  };

  const setLegModeForDay = (dayId, legIndex, mode) => {
    setTourDays(prev =>
      prev.map(d => {
        if (d.id !== dayId) return d;
        const len = d.venues.length - 1;
        if (legIndex < 0 || legIndex >= len) return d;
        const next = (d.legModes ? [...d.legModes] : Array(len).fill(null));
        next[legIndex] = mode;
        return { ...d, legModes: next };
      })
    );
  };

  const setInterDayLegMode = (dayIndex, mode) => {
    setInterDayLegModes(prev => {
      const next = [...prev];
      while (next.length <= dayIndex) next.push('drive');
      next[dayIndex] = mode;
      return next;
    });
  };

  useEffect(() => {
    const n = Math.max(0, tourDays.length - 1);
    setInterDayLegModes(prev => {
      if (prev.length === n) return prev;
      if (prev.length > n) return prev.slice(0, n);
      return [...prev, ...Array(n - prev.length).fill('drive')];
    });
  }, [tourDays.length]);

  // Zoom to venue on map (explicit user action only; no auto zoom on select)
  const zoomToVenue = (venue) => {
    if (!venue?.latitude || !venue?.longitude || !map.current) return;
    map.current.flyTo({
      center: [venue.longitude, venue.latitude],
      essential: true
    });
  };

  const handleVenueSelect = (venue) => {
    if (!venue.latitude || !venue.longitude || !map.current) return;

    const isUserLocation = venue.isUserLocation || (venue.id && String(venue.id).startsWith('userloc-'));
    const locationId = isUserLocation ? (venue.userLocationId || String(venue.id).replace(/^userloc-/, '')) : null;
    const loc = locationId ? (userLocations.find(l => l.id === locationId) || publicUserLocations.find(l => l.id === locationId)) : null;

    setSelectedVenueId(venue.id);
    drawSelectedLocation(venue);

    // Fly to venue only if it's outside the current view
    const lng = Number(venue.longitude);
    const lat = Number(venue.latitude);
    const bounds = map.current.getBounds();
    if (!bounds.contains([lng, lat])) {
      map.current.flyTo({
        center: [lng, lat],
        essential: true
      });
    }

    // User location: show user-location popup (Add to Tour, View Details, Edit/Delete if own)
    if (isUserLocation) {
      if (!loc) return; // location no longer in list
      const locCapacity = loc.capacity != null && loc.capacity !== '' ? loc.capacity : 'N/A';
      const locType = (loc.affiliate && String(loc.affiliate).trim()) ? String(loc.affiliate).replace(/</g, '&lt;').replace(/>/g, '&gt;') : 'N/A';
      const notesRaw = loc.notes != null ? String(loc.notes) : '';
      const locDesc = notesRaw.trim() ? notesRaw.replace(/</g, '&lt;').replace(/>/g, '&gt;') : '';
      const locName = String(loc.name || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const locAddr = (loc.address && String(loc.address).trim()) ? String(loc.address).replace(/</g, '&lt;').replace(/>/g, '&gt;').substring(0, 80) : '';
      const popupContent = buildVenuePopupHTML({
        id: loc.id, name: locName,
        city: locAddr, state: '',
        capacity: locCapacity, type: locType,
        notes: locDesc,
        showFocus: false, isUserLocation: true,
        isOwn: !!loc.isOwn,
        addAction: `window.addUserLocationToTourFromMap('${loc.id}')`,
        detailAction: `window.openMapDetailsUserLocation('${loc.id}')`,
        editAction: `window.editUserLocation('${loc.id}')`,
        deleteAction: `window.deleteUserLocation('${loc.id}')`,
      });
      new mapboxgl.Popup({ closeOnClick: false, closeButton: true, className: 'light-popup' })
        .setLngLat([venue.longitude, venue.latitude])
        .setHTML(popupContent)
        .addTo(map.current);
      return;
    }

    // Venue: show unified glass popup
    const venueTypeVal = venue.affiliate || venue.venueType || 'N/A';
    const venueCapVal = venue.capacity != null && venue.capacity !== '' ? String(venue.capacity) : 'N/A';
    const venueCity = venue.city || '';
    const venueState = venue.state || '';
    const venueNotesR = venue.notes != null ? String(venue.notes).trim() : '';
    const venueNotesE = venueNotesR ? venueNotesR.replace(/</g,'&lt;').replace(/>/g,'&gt;') : '';
    const bs2 = (venue.backstage || '').toLowerCase();
    const notes2 = (venue.notes || '').toLowerCase();
    const selectLodging    = bs2.includes('lodging') || bs2.includes('hotel') || bs2.includes('bed');
    const selectVegan      = (venue.name || '').toLowerCase().includes('vegan') ||
      (venue.name || '').toLowerCase().includes('plant-based') ||
      notes2.includes('vegan') || (venue.affiliate || '').toLowerCase().includes('vegan');
    const selectShorePower = (venue.shorePower || '').toLowerCase().includes('yes') || bs2.includes('shore power') || notes2.includes('shore power');
    const selectMerch100   = (venue.merch || '').toLowerCase().includes('100%');
    const selectNearTransit = (venue._isNearTransit === true);
    const selectEcoConscious = venue.isEcoConscious === true;
    new mapboxgl.Popup({ closeOnClick: false, closeButton: true, className: 'dark-popup' })
      .setLngLat([venue.longitude, venue.latitude])
      .setHTML(buildVenuePopupHTML({
        id: venue.id, name: venue.name,
        city: venueCity, state: venueState,
        capacity: venueCapVal, type: venueTypeVal,
        notes: venueNotesE,
        hasLodging: selectLodging, hasVeganFood: selectVegan,
        hasShorePower: selectShorePower, hasMerch100: selectMerch100,
        isNearTransit: selectNearTransit,
        isEcoConscious: selectEcoConscious,
        grade: venue.approved?.overallGrade ?? null,
        showFocus: true, isUserLocation: false,
      }))
      .addTo(map.current);

    // Async: inject upcoming shows
    requestAnimationFrame(() => fetchUpcomingShowsForPopup(
      venue.id, venue.name,
      parseFloat(venue.latitude), parseFloat(venue.longitude)
    ));
    requestAnimationFrame(() => fetchVenueWeatherForPopup(
      venue.id,
      parseFloat(venue.latitude), parseFloat(venue.longitude)
    ));
  };
  handleVenueSelectRef.current = handleVenueSelect;

  // Add function to handle adding venue from map popup
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Define the handler for popup buttons
      window.handleMapPopupAction = (action, venueId) => {
        const venue = venues.find(v => v.id === venueId);
        if (!venue) return;

        switch (action) {
          case 'addToTour':
            addVenueToTour(venue);
            break;
          case 'viewDetails':
            setMapDetailsItem({ type: 'venue', data: venue });
            break;
          case 'zoomToVenue':
            zoomToVenue(venue);
            break;
          case 'accessibilityFlag':
            openAccessibilityFlagModalRef.current?.(venue);
            break;
        }
      };

      // Keep backwards compatibility
      window.addVenueToTourFromMap = (venueId) => {
        const venue = venues.find(v => v.id === venueId);
        if (venue) {
          addVenueToTour(venue);
        }
      };
    }

    // Clean up function when component unmounts
    return () => {
      if (typeof window !== 'undefined') {
        window.handleMapPopupAction = null;
        window.addVenueToTourFromMap = null;
      }
    };
  }, [venues]);

  // Toggle display mode between clusters, all venues, and tour venues
  const toggleDisplayMode = (mode) => {
    if (!map.current) return;

    switch (mode) {
      case 'satellite':
        setUserOverrodeMapStyle(true); // User manually changed map style
        const currentStyle = map.current.getStyle().name.toLowerCase();
        const newStyle = currentStyle.includes('satellite') ? 'mapbox://styles/mapbox/streets-v12' : 'mapbox://styles/mapbox/satellite-streets-v12';
        map.current.setStyle(newStyle);
        // Re-add sources and layers after style change
        map.current.once('style.load', () => {
          // Add the venues source
          if (!map.current.getSource('venues-source')) {
            map.current.addSource('venues-source', {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: []
              },
              cluster: true,
              clusterMaxZoom: clusterMaxZoom,
              clusterRadius: clusterRadius
            });
          }
          if (CLUSTER_HOVER_RADIUS_DEBUG) {
            if (!map.current.getSource('cluster-hover-radius')) {
              map.current.addSource('cluster-hover-radius', {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: [] }
              });
            }
            if (!map.current.getLayer('cluster-hover-radius-fill')) {
              map.current.addLayer({
                id: 'cluster-hover-radius-fill',
                type: 'fill',
                source: 'cluster-hover-radius',
                paint: {
                  'fill-color': '#e67e22',
                  'fill-opacity': 0.12,
                  'fill-outline-color': '#e67e22'
                }
              });
            }
            if (!map.current.getLayer('cluster-hover-radius-line')) {
              map.current.addLayer({
                id: 'cluster-hover-radius-line',
                type: 'line',
                source: 'cluster-hover-radius',
                paint: {
                  'line-color': '#e67e22',
                  'line-width': 2,
                  'line-opacity': 0.6
                }
              });
            }
          }

          // Add the clusters layer
          if (!map.current.getLayer('clusters')) {
            map.current.addLayer({
              id: 'clusters',
              type: 'circle',
              source: 'venues-source',
              filter: ['has', 'point_count'],
              paint: {
                'circle-color': '#e67e22',
                'circle-radius': [
                  'step',
                  ['get', 'point_count'],
                  20, 100,
                  30, 750,
                  40
                ]
              }
            });
          }

          // Add the cluster count layer
          if (!map.current.getLayer('cluster-count')) {
            map.current.addLayer({
              id: 'cluster-count',
              type: 'symbol',
              source: 'venues-source',
              filter: ['has', 'point_count'],
              layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12
              },
              paint: {
                'text-color': '#ffffff'
              }
            });
          }

          // Add the unclustered points layer
          if (!map.current.getLayer('unclustered-point')) {
            map.current.addLayer({
              id: 'unclustered-point',
              type: 'circle',
              source: 'venues-source',
              filter: ['!', ['has', 'point_count']],
              paint: {
                'circle-color': VENUE_PIN_AFFILIATE_COLOR_EXPR,
                'circle-radius': 8,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff'
              }
            });
          }

          // Eco sustainability ring
          if (!map.current.getLayer('eco-ring')) {
            map.current.addLayer({
              id: 'eco-ring',
              type: 'circle',
              source: 'venues-source',
              filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'isEco'], true]],
              paint: {
                'circle-color': 'rgba(0,0,0,0)',
                'circle-radius': ['case', ['==', ['get', 'isTourVenue'], true], 15, 12],
                'circle-stroke-width': 2.5,
                'circle-stroke-color': '#2ecc71',
                'circle-stroke-opacity': 0.9
              }
            });
          }
          ensureEducationHatLayer(map.current);

          // Screenprinters source + layer
          if (!map.current.getSource('screenprinters-source')) {
            map.current.addSource('screenprinters-source', {
              type: 'geojson',
              data: { type: 'FeatureCollection', features: [] }
            });
          }
          if (!map.current.getLayer('screenprinters-layer')) {
            map.current.addLayer({
              id: 'screenprinters-layer',
              type: 'circle',
              source: 'screenprinters-source',
              paint: { 'circle-color': '#f39c12', 'circle-radius': 8, 'circle-stroke-width': 2.5, 'circle-stroke-color': '#ffffff' }
            });
          }
          if (!map.current.getLayer('screenprinters-labels')) {
            map.current.addLayer({
              id: 'screenprinters-labels',
              type: 'symbol',
              source: 'screenprinters-source',
              minzoom: 10,
              layout: { 'text-field': ['get', 'name'], 'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'], 'text-size': 11, 'text-offset': [0, 1.2], 'text-anchor': 'top' },
              paint: { 'text-color': '#1a1a1a', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 }
            });
          }

          ensureDirectoryPostsMapLayers(map.current);

          // ── Bike lanes: purple = available, green = commonly used ──
          try {
            ensureBikeLaneLayers(map.current);
            bikeLanesFetchedKeyRef.current = null;
            setBikeLaneVisibility(map.current, showBikeLanesRef.current);
            if (showBikeLanesRef.current) loadBikeLanesForMapRef.current?.();
          } catch (_) {}

          // Update venues on map
          updateVenuesOnMap();
        });
        break;

      case 'dark':
        setUserOverrodeMapStyle(true); // User manually changed map style
        const isDark = map.current.getStyle().name.toLowerCase().includes('dark');
        const newStyleIsDark = !isDark; // We're toggling, so new style is opposite
        map.current.setStyle(isDark ? 'mapbox://styles/mapbox/streets-v12' : 'mapbox://styles/mapbox/dark-v11');
        // Re-add sources and layers after style change
        map.current.once('style.load', () => {
          // Add 3D buildings layer
          const layers = map.current.getStyle().layers;
          const labelLayerId = layers.find(
            (layer) => layer.type === 'symbol' && layer.layout['text-field']
          )?.id;

          if (!map.current.getLayer('3d-buildings')) {
            map.current.addLayer(
              {
                'id': '3d-buildings',
                'source': 'composite',
                'source-layer': 'building',
                'filter': ['==', 'extrude', 'true'],
                'type': 'fill-extrusion',
                'minzoom': 14,
                'paint': {
                  'fill-extrusion-color': newStyleIsDark
                    ? [
                      'interpolate',
                      ['linear'],
                      ['get', 'height'],
                      0, '#1a1a2e',
                      50, '#16213e',
                      100, '#0f3460',
                      200, '#1a1a2e'
                    ]
                    : [
                      'interpolate',
                      ['linear'],
                      ['get', 'height'],
                      0, '#e8e8e8',
                      50, '#d4d4d4',
                      100, '#c0c0c0',
                      200, '#a8a8a8'
                    ],
                  'fill-extrusion-height': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    14, 0,
                    14.5, ['get', 'height']
                  ],
                  'fill-extrusion-base': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    14, 0,
                    14.5, ['get', 'min_height']
                  ],
                  'fill-extrusion-opacity': 0.85
                }
              },
              labelLayerId
            );
          }

          // Add the venues source
          if (!map.current.getSource('venues-source')) {
            map.current.addSource('venues-source', {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: []
              },
              cluster: true,
              clusterMaxZoom: clusterMaxZoom,
              clusterRadius: clusterRadius
            });
          }
          if (CLUSTER_HOVER_RADIUS_DEBUG) {
            if (!map.current.getSource('cluster-hover-radius')) {
              map.current.addSource('cluster-hover-radius', {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: [] }
              });
            }
            if (!map.current.getLayer('cluster-hover-radius-fill')) {
              map.current.addLayer({
                id: 'cluster-hover-radius-fill',
                type: 'fill',
                source: 'cluster-hover-radius',
                paint: {
                  'fill-color': '#e67e22',
                  'fill-opacity': 0.12,
                  'fill-outline-color': '#e67e22'
                }
              });
            }
            if (!map.current.getLayer('cluster-hover-radius-line')) {
              map.current.addLayer({
                id: 'cluster-hover-radius-line',
                type: 'line',
                source: 'cluster-hover-radius',
                paint: {
                  'line-color': '#e67e22',
                  'line-width': 2,
                  'line-opacity': 0.6
                }
              });
            }
          }

          // Add the clusters layer
          if (!map.current.getLayer('clusters')) {
            map.current.addLayer({
              id: 'clusters',
              type: 'circle',
              source: 'venues-source',
              filter: ['has', 'point_count'],
              paint: {
                'circle-color': '#e67e22',
                'circle-radius': [
                  'step',
                  ['get', 'point_count'],
                  20, 100,
                  30, 750,
                  40
                ]
              }
            });
          }

          // Add the cluster count layer
          if (!map.current.getLayer('cluster-count')) {
            map.current.addLayer({
              id: 'cluster-count',
              type: 'symbol',
              source: 'venues-source',
              filter: ['has', 'point_count'],
              layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12
              },
              paint: {
                'text-color': '#ffffff'
              }
            });
          }

          // Add the unclustered points layer
          if (!map.current.getLayer('unclustered-point')) {
            map.current.addLayer({
              id: 'unclustered-point',
              type: 'circle',
              source: 'venues-source',
              filter: ['!', ['has', 'point_count']],
              paint: {
                'circle-color': VENUE_PIN_AFFILIATE_COLOR_EXPR,
                'circle-radius': 8,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff'
              }
            });
          }

          // Eco sustainability ring
          if (!map.current.getLayer('eco-ring')) {
            map.current.addLayer({
              id: 'eco-ring',
              type: 'circle',
              source: 'venues-source',
              filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'isEco'], true]],
              paint: {
                'circle-color': 'rgba(0,0,0,0)',
                'circle-radius': ['case', ['==', ['get', 'isTourVenue'], true], 15, 12],
                'circle-stroke-width': 2.5,
                'circle-stroke-color': '#2ecc71',
                'circle-stroke-opacity': 0.9
              }
            });
          }
          ensureEducationHatLayer(map.current);

          // Screenprinters source + layer
          if (!map.current.getSource('screenprinters-source')) {
            map.current.addSource('screenprinters-source', {
              type: 'geojson',
              data: { type: 'FeatureCollection', features: [] }
            });
          }
          if (!map.current.getLayer('screenprinters-layer')) {
            map.current.addLayer({
              id: 'screenprinters-layer',
              type: 'circle',
              source: 'screenprinters-source',
              paint: { 'circle-color': '#f39c12', 'circle-radius': 8, 'circle-stroke-width': 2.5, 'circle-stroke-color': '#ffffff' }
            });
          }
          if (!map.current.getLayer('screenprinters-labels')) {
            map.current.addLayer({
              id: 'screenprinters-labels',
              type: 'symbol',
              source: 'screenprinters-source',
              minzoom: 10,
              layout: { 'text-field': ['get', 'name'], 'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'], 'text-size': 11, 'text-offset': [0, 1.2], 'text-anchor': 'top' },
              paint: { 'text-color': '#1a1a1a', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 }
            });
          }

          ensureDirectoryPostsMapLayers(map.current);

          // ── Bike lanes: purple = available, green = commonly used ──
          try {
            ensureBikeLaneLayers(map.current);
            bikeLanesFetchedKeyRef.current = null;
            setBikeLaneVisibility(map.current, showBikeLanesRef.current);
            if (showBikeLanesRef.current) loadBikeLanesForMapRef.current?.();
          } catch (_) {}

          // Update venues on map
          updateVenuesOnMap();
        });
        break;

      case 'cluster':
        const clusterVisibility = map.current.getLayoutProperty('clusters', 'visibility');
        const newVisibility = clusterVisibility === 'visible' ? 'none' : 'visible';
        map.current.setLayoutProperty('clusters', 'visibility', newVisibility);
        map.current.setLayoutProperty('cluster-count', 'visibility', newVisibility);
        // Always show lone-venue dots; only clusters are toggled
        map.current.setLayoutProperty('unclustered-point', 'visibility', 'visible');
        if (map.current.getLayer('unclustered-point-shadow')) {
          map.current.setLayoutProperty('unclustered-point-shadow', 'visibility', 'visible');
        }
        break;

      case '3d':
        const pitch = map.current.getPitch();
        map.current.easeTo({
          pitch: pitch === 0 ? 45 : 0,
          duration: 1000
        });
        break;
    }
  };

  // Simple debounced update - directly call the ref function with current state values
  const debouncedMapUpdateTimeoutRef = useRef(null);

  // Update venues and routes on map when venues or tour items change
  useEffect(() => {
    // Skip updates if user is actively zooming or still initializing
    if (isZooming || isInitializing) {
      return;
    }

    const hasVenues = venues.length > 0;
    const hasTourVenues = tourVenues.length > 0;
    const shouldUpdate = map.current && mapLoaded && (hasVenues || hasTourVenues);

    if (shouldUpdate) {
      if (debouncedMapUpdateTimeoutRef.current) {
        clearTimeout(debouncedMapUpdateTimeoutRef.current);
      }

      debouncedMapUpdateTimeoutRef.current = setTimeout(() => {
        if (debouncedUpdateRef.current) {
          const venuesToShow = showVenues ? (venuesToShowRef.current || []) : [];
          debouncedUpdateRef.current(venuesToShow, tourDays, activeDayId, coordinateSourceFilter, interDayLegModes);
        }
      }, 300);
    }

    return () => {
      if (debouncedMapUpdateTimeoutRef.current) {
        clearTimeout(debouncedMapUpdateTimeoutRef.current);
      }
    };
  }, [venues, tourVenues, tourDays, interDayLegModes, mapLoaded, displayMode, coordinateSourceFilter, activeDayId, showVenues, selectedVenueId, currentZoom, mapCenter]);

  // ROUTE_COLORS moved to top with drawing functions

  // Add debounce utility function
  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  // Create a React ref for the debounced function
  const debouncedUpdateRef = useRef(null);

  // Initialize the debounced function in useEffect to avoid redeclaration
  useEffect(() => {
    debouncedUpdateRef.current = debounce(async (venuesToShow, tourDaysData, activeDayIdValue, coordSourceFilter = 'all', interDayLegModesData = []) => {
      if (!map.current) return;
      const toShow = venuesToShow ?? venuesToShowRef.current ?? [];

      if (map.current.getSource('venues-source')) {
        drawClusters(toShow, tourDaysData, coordSourceFilter);
      }
      await drawRoutes(tourDaysData, activeDayIdValue, interDayLegModesData);
    }, 100);
  }, []);

  // Wrapper function that calls the debounced version
  const updateVenuesOnMap = (venuesToShow = null) => {
    if (debouncedUpdateRef.current) {
      debouncedUpdateRef.current(venuesToShow, tourDays, activeDayId, coordinateSourceFilter);
    }
  };

  const calculateDayRouteInfo = async (venues, legModes = []) => {
    if (!venues || venues.length < 2) {
      console.log('Not enough venues to calculate route info');
      return {
        totalDistance: 0,
        totalDuration: 0,
        fuelEstimate: 0,
        fuelCost: 0,
        legs: []
      };
    }

    console.log(`Calculating route info for ${venues.length} venues`);

    let totalDistance = 0;
    let totalDuration = 0;
    let totalFuelEstimate = 0;
    let totalFuelCost = 0;
    let totalCo2Kg = 0;
    let legs = [];

    const avgFuelEfficiency = 25; // miles per gallon
    const avgGasPrice = 3.50; // dollars per gallon
    // kg CO2 per mile by mode (EPA/DEFRA averages)
    const CO2_PER_MILE = { drive: 0.21, flight: 0.255, train: 0.041, bus: 0.089, walk: 0 };

    try {
      for (let i = 0; i < venues.length - 1; i++) {
        const start = venues[i];
        const end = venues[i + 1];
        const mode = legModes[i] || 'drive';

        if (isValidCoordinate(start) && isValidCoordinate(end)) {
          console.log(`Getting directions from ${start.name} to ${end.name} (${mode})`);

          const cacheKey = `${start.id}-${end.id}-${mode}`;
          let route;

          if (routeCache.current[cacheKey]) {
            console.log('Using cached route data');
            route = routeCache.current[cacheKey];
          } else {
            const startCoords = [parseFloat(start.longitude), parseFloat(start.latitude)];
            const endCoords = [parseFloat(end.longitude), parseFloat(end.latitude)];
            if (mode === 'train') {
              route = getTrainLegGeometry(startCoords, endCoords);
            } else if (mode === 'flight') {
              route = getFlightLegGeometry(startCoords, endCoords);
            } else {
              route = await getDirections(startCoords, endCoords, { forceDrive: true });
            }
            if (route) {
              routeCache.current[cacheKey] = route;
            }
          }

          if (route) {
            const routeDistance = route.distance || 0;
            const routeDuration = route.duration || 0;
            const distanceInMiles = routeDistance * 0.621371;
            const isFlight = route.requiresFlight === true;
            const isTrain = route.isTrain === true;

            if (isFlight || mode === 'flight') {
              const co2Kg = Math.round(distanceInMiles * CO2_PER_MILE.flight * 10) / 10;
              legs.push({ from: start.name, to: end.name, mode: 'flight',
                distance: Math.round(distanceInMiles * 10) / 10,
                duration: Math.round(routeDuration),
                isFlight: true, isTrain: false, fuelUsed: 0, fuelCost: 0, co2Kg,
                fallbackStraightLine: route.fallbackStraightLine });
              totalDistance += routeDistance; totalDuration += routeDuration; totalCo2Kg += co2Kg;
            } else if (isTrain || mode === 'train') {
              const co2Kg = Math.round(distanceInMiles * CO2_PER_MILE.train * 10) / 10;
              legs.push({ from: start.name, to: end.name, mode: 'train',
                distance: Math.round(distanceInMiles * 10) / 10,
                duration: Math.round(routeDuration),
                isFlight: false, isTrain: true, fuelUsed: 0, fuelCost: 0, co2Kg });
              totalDistance += routeDistance; totalDuration += routeDuration; totalCo2Kg += co2Kg;
            } else if (mode === 'bus') {
              const co2Kg = Math.round(distanceInMiles * CO2_PER_MILE.bus * 10) / 10;
              legs.push({ from: start.name, to: end.name, mode: 'bus',
                distance: Math.round(distanceInMiles * 10) / 10,
                duration: Math.round(routeDuration),
                isBus: true, fuelUsed: 0, fuelCost: 0, co2Kg });
              totalDistance += routeDistance; totalDuration += routeDuration; totalCo2Kg += co2Kg;
            } else if (mode === 'walk') {
              legs.push({ from: start.name, to: end.name, mode: 'walk',
                distance: Math.round(distanceInMiles * 10) / 10,
                duration: Math.round(routeDuration),
                isWalk: true, fuelUsed: 0, fuelCost: 0, co2Kg: 0 });
              totalDistance += routeDistance; totalDuration += routeDuration;
            } else {
              // drive — calculate fuel
              const fuelUsed = distanceInMiles / avgFuelEfficiency;
              const fuelCost = fuelUsed * avgGasPrice;
              const co2Kg = Math.round(distanceInMiles * CO2_PER_MILE.drive * 10) / 10;
              legs.push({ from: start.name, to: end.name, mode: 'drive',
                distance: Math.round(distanceInMiles * 10) / 10,
                duration: Math.round(routeDuration),
                fuelUsed: Math.round(fuelUsed * 10) / 10,
                fuelCost: Math.round(fuelCost * 100) / 100,
                co2Kg,
                fallbackStraightLine: route.fallbackStraightLine });
              totalDistance += routeDistance; totalDuration += routeDuration;
              totalFuelEstimate += fuelUsed; totalFuelCost += fuelCost; totalCo2Kg += co2Kg;
            }
          } else {
            console.log(`No route found between ${start.name} and ${end.name}`);
          }
        } else {
          console.log(`Invalid coordinates for ${start.name} or ${end.name}`);
        }
      }

      // Fix the output result to maintain distance in kilometers but also include miles
      const result = {
        totalDistance: Math.round(totalDistance), // In kilometers
        totalDistanceMiles: Math.round(totalDistance * 0.621371), // Miles conversion
        totalDuration: Math.round(totalDuration / 60), // Convert to minutes
        fuelEstimate: Math.round(totalFuelEstimate * 10) / 10, // Round to 1 decimal
        fuelCost: Math.round(totalFuelCost * 100) / 100, // Round to cents
        totalCo2Kg: Math.round(totalCo2Kg * 10) / 10,
        legs: legs
      };

      console.log('Route info calculated:', result);
      return result;
    } catch (error) {
      console.error('Error calculating route info:', error);
      return {
        totalDistance: 0,
        totalDistanceMiles: 0,
        totalDuration: 0,
        fuelEstimate: 0,
        fuelCost: 0,
        legs: []
      };
    }
  };

  // State to store route info for each day (keyed by day.id)
  const [dayRouteInfos, setDayRouteInfos] = useState({});

  // Calculate route info for all days when tourDays changes
  useEffect(() => {
    const calculateAllDayRouteInfos = async () => {
      const newRouteInfos = {};

      for (const day of tourDays) {
        if (!day || !day.venues || !Array.isArray(day.venues) || day.venues.length < 2) {
          continue;
        }

        try {
          const routeInfo = await calculateDayRouteInfo(day.venues, day.legModes || []);
          if (routeInfo && routeInfo.legs && Array.isArray(routeInfo.legs)) {
            newRouteInfos[day.id] = routeInfo;
          }
        } catch (error) {
          console.error(`Error calculating route info for day ${day.id}:`, error);
        }
      }

      setDayRouteInfos(newRouteInfos);
    };

    let cancelled = false;
    setRoutesCalculating(true);
    (async () => {
      try {
        if (tourDays.length > 0) {
          await calculateAllDayRouteInfos();
          if (cancelled) return;
          await directUpdateMapWithRoutes(tourVenues, tourDays, activeDayId, interDayLegModes);
        } else {
          setDayRouteInfos({});
          if (cancelled) return;
          await directUpdateMapWithRoutes(tourVenues, tourDays, activeDayId, interDayLegModes);
        }
      } finally {
        if (!cancelled) setRoutesCalculating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tourDays, tourVenues, activeDayId, interDayLegModes]);

  // Bus call: map-click picking mode
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    if (!pickingBusCallForDayId) {
      map.current.getCanvas().style.cursor = '';
      return;
    }
    map.current.getCanvas().style.cursor = 'crosshair';
    const handleClick = async (e) => {
      const { lng, lat } = e.lngLat;
      let placeName = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      try {
        const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
        if (token) {
          const r = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${token}&limit=1&types=address,poi,place`);
          if (r.ok) {
            const data = await r.json();
            if (data.features?.[0]?.place_name) placeName = data.features[0].place_name;
          }
        }
      } catch (_) {}
      const dayId = pickingBusCallForDayId;
      setTourDays(prev => prev.map(d => {
        if (d.id !== dayId) return d;
        const existing = d.busCall || {};
        return { ...d, busCall: { time: '', date: '', ...existing, lat, lng, name: placeName } };
      }));
      setPickingBusCallForDayId(null);
    };
    map.current.once('click', handleClick);
    const onKeyDown = (ev) => {
      if (ev.key === 'Escape') {
        map.current?.off('click', handleClick);
        setPickingBusCallForDayId(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      map.current?.off('click', handleClick);
      window.removeEventListener('keydown', onKeyDown);
      if (map.current) map.current.getCanvas().style.cursor = '';
    };
  }, [pickingBusCallForDayId, mapLoaded]);

  // Bus call: draw markers on the map whenever tourDays changes
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const BUS_CALL_SOURCE = 'bus-call-source';
    const BUS_CALL_LAYER = 'bus-call-layer';
    const BUS_CALL_LABEL_LAYER = 'bus-call-label-layer';

    const features = tourDays
      .filter(d => d.busCall?.lat && d.busCall?.lng)
      .map(d => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [d.busCall.lng, d.busCall.lat] },
        properties: { name: d.busCall.name || 'Bus Call', time: d.busCall.time || '', dayId: d.id },
      }));

    if (!map.current.getSource(BUS_CALL_SOURCE)) {
      map.current.addSource(BUS_CALL_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features } });
      map.current.addLayer({ id: BUS_CALL_LAYER, type: 'circle', source: BUS_CALL_SOURCE, paint: { 'circle-radius': 10, 'circle-color': '#f43f5e', 'circle-stroke-width': 2, 'circle-stroke-color': '#ffffff', 'circle-opacity': 0.92 } });
      map.current.addLayer({ id: BUS_CALL_LABEL_LAYER, type: 'symbol', source: BUS_CALL_SOURCE, layout: { 'text-field': ['concat', '🚌 ', ['get', 'name']], 'text-size': 11, 'text-anchor': 'top', 'text-offset': [0, 1.2] }, paint: { 'text-color': '#f43f5e', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 } });
    } else {
      map.current.getSource(BUS_CALL_SOURCE).setData({ type: 'FeatureCollection', features });
    }
  }, [tourDays, mapLoaded]);

  // Estimate straight-line travel time/distance between two stops for the inter-day connector
  const estimateInterDayTravel = (fromVenue, toVenue, mode = 'drive') => {
    const lat1 = Number(fromVenue?.latitude ?? fromVenue?.lat);
    const lng1 = Number(fromVenue?.longitude ?? fromVenue?.lng);
    const lat2 = Number(toVenue?.latitude ?? toVenue?.lat);
    const lng2 = Number(toVenue?.longitude ?? toVenue?.lng);
    if (!lat1 || !lng1 || !lat2 || !lng2) return null;
    if (Math.abs(lat1 - lat2) < 0.0001 && Math.abs(lng1 - lng2) < 0.0001) return { same: true, miles: 0, label: 'Same location' };
    const meters = haversineMeters(lat1, lng1, lat2, lng2);
    const milesNum = meters / 1609.344;
    const miles = milesNum.toFixed(1);
    const speedMph = { drive: 55, bus: 45, walk: 3, train: 70, flight: 480 }[mode] || 55;
    const hrs = milesNum / speedMph;
    const h = Math.floor(hrs);
    const m = Math.round((hrs - h) * 60);
    const timeLabel = h > 0 ? `~${h}h ${m}m` : `~${m}m`;
    const modeLabel = { drive: '🚗 Drive', bus: '🚌 Bus', walk: '🚶 Walk', train: '🚂 Train', flight: '✈ Flight' }[mode] || mode;
    // Fuel estimate for driving only (assumes 25 mpg, $3.50/gal)
    const MPG = 25;
    const FUEL_PRICE = 3.50;
    const fuelGal = mode === 'drive' ? (milesNum / MPG) : null;
    const fuelLabel = fuelGal != null ? `${fuelGal.toFixed(1)} gal ($${(fuelGal * FUEL_PRICE).toFixed(2)})` : null;
    return { miles, timeLabel, modeLabel, fuelLabel, same: false };
  };

  const renderDayRouteInfo = (day, index) => {
    if (!day || !day.venues || !Array.isArray(day.venues) || day.venues.length < 2) {
      return null;
    }

    // Get route info from state instead of calling async function
    const routeInfo = dayRouteInfos[day.id];

    // If route info is still being calculated, show loading state
    if (!routeInfo) {
      return (
        <View style={styles.dayRouteInfo}>
          <Text style={styles.dayRouteText}>Calculating route information...</Text>
        </View>
      );
    }

    if (!routeInfo.legs || !Array.isArray(routeInfo.legs) || routeInfo.legs.length === 0) {
      return null;
    }

    const color = ROUTE_COLORS[index % ROUTE_COLORS.length];

    const modeIcon = (leg) => {
      if (leg.isFlight || leg.mode === 'flight') return '✈ ';
      if (leg.isTrain || leg.mode === 'train')   return '🚂 ';
      if (leg.isBus   || leg.mode === 'bus')     return '🚌 ';
      if (leg.isWalk  || leg.mode === 'walk')    return '🚶 ';
      return '🚗 ';
    };
    const legLabel = (leg) => {
      const dist = leg.distance;
      const hrs  = Math.floor((leg.duration || 0) / 60);
      const mins = (leg.duration || 0) % 60;
      const time = hrs > 0 ? `~${hrs}h ${mins}m` : `~${mins}m`;
      const co2  = leg.co2Kg > 0 ? ` · ${leg.co2Kg} kg CO₂` : '';
      if (leg.isFlight || leg.mode === 'flight') return `Flight · ${dist} mi · ${time}${co2}${leg.fallbackStraightLine ? ' (straight-line)' : ''}`;
      if (leg.isTrain  || leg.mode === 'train')  return `Train · ${dist} mi · ${time}${co2}`;
      if (leg.isBus    || leg.mode === 'bus')    return `Bus · ${dist} mi · ${time}${co2}`;
      if (leg.isWalk   || leg.mode === 'walk') {
        const ft = Math.round(dist * 5280);
        return dist < 0.25 ? `Walk · ${ft} ft · ${time}` : `Walk · ${dist} mi · ${time}`;
      }
      return `Drive · ${dist} mi${leg.fuelUsed > 0 ? ` · ${leg.fuelUsed} gal ($${leg.fuelCost})` : ''}${co2}${leg.fallbackStraightLine ? ' (straight-line)' : ''}`;
    };
    return (
      <View style={styles.dayRouteInfo}>
        {routeInfo.legs.map((leg, i) => (
          <View key={i} style={styles.dayRouteLeg}>
            <Text style={styles.dayRouteText} numberOfLines={1}>
              {modeIcon(leg)}{leg.from} → {leg.to}
            </Text>
            <Text style={styles.dayRouteMileage}>{legLabel(leg)}</Text>
          </View>
        ))}
        <Text style={styles.totalMileage}>
          Total distance: {routeInfo.totalDistanceMiles} mi ({routeInfo.totalDistance} km)
        </Text>
        {routeInfo.fuelEstimate > 0 && (
          <Text style={styles.fuelEstimateText}>
            Driving fuel: {routeInfo.fuelEstimate} gal (${routeInfo.fuelCost})
          </Text>
        )}
        {routeInfo.totalCo2Kg > 0 && (
          <Text style={[styles.fuelEstimateText, { color: routeInfo.totalCo2Kg < 50 ? '#2ecc71' : routeInfo.totalCo2Kg < 200 ? '#f39c12' : '#e74c3c' }]}>
            🌱 Est. CO₂: {routeInfo.totalCo2Kg} kg
          </Text>
        )}
      </View>
    );
  };

  // Update tour route on map
  const updateTourRoute = () => {
    if (!map.current || tourVenues.length < 2) return;

    // Draw the route line
    calculateRoute();

    // Fit map to tour venues
    const bounds = new mapboxgl.LngLatBounds();
    tourVenues.forEach(venue => {
      if (venue.longitude && venue.latitude) {
        bounds.extend([venue.longitude, venue.latitude]);
      }
    });

    if (!bounds.isEmpty()) {
      map.current.fitBounds(bounds, {
        padding: 70,
        maxZoom: 9
      });
    }
  };

  // Shared map control buttons (locate, satellite, dark, 3D)
  const renderMapControlButtons = (iconSize = 15) => {
    const iconColor = isDarkMode ? 'rgba(255,255,255,0.80)' : 'rgba(0,0,0,0.65)';
    let styleName = '';
    try { styleName = map.current?.getStyle?.()?.name?.toLowerCase?.() ?? ''; } catch (_) {}
    const isSatellite = mapLoaded && styleName.includes('satellite');
    const isDarkMap   = mapLoaded && styleName.includes('dark');
    const is3D        = mapLoaded && (map.current?.getPitch?.() ?? 0) > 0;
    return (
      <>
        <TouchableOpacity
          style={[styles.controlButton, userLocation && styles.controlButtonActive]}
          onPress={requestUserLocation}
          accessibilityLabel="My location"
        >
          <Ionicons name={userLocation ? 'locate' : 'locate-outline'} size={iconSize} color={iconColor} />
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.controlButton, isSatellite && styles.controlButtonActive]}
          onPress={() => toggleDisplayMode('satellite')}
          accessibilityLabel="Toggle satellite view"
        >
          <Ionicons name={isSatellite ? 'earth' : 'earth-outline'} size={iconSize} color={iconColor} />
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.controlButton, isDarkMap && styles.controlButtonActive]}
          onPress={() => toggleDisplayMode('dark')}
          accessibilityLabel="Toggle dark map"
        >
          <Ionicons name={isDarkMap ? 'moon' : 'moon-outline'} size={iconSize} color={iconColor} />
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.controlButton, is3D && styles.controlButtonActive]}
          onPress={() => toggleDisplayMode('3d')}
          accessibilityLabel="Toggle 3D view"
        >
          <Ionicons name={is3D ? 'cube' : 'cube-outline'} size={iconSize} color={iconColor} />
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.controlButton}
          onPress={recenterMap}
          accessibilityLabel="Recenter map"
        >
          <Ionicons name="add-circle-outline" size={iconSize} color={iconColor} />
        </TouchableOpacity>
      </>
    );
  };

  // Map controls now live top-right (left of the ☰ overlay menu).
  const renderBottomControls = () => null;

  const findVenueForAccessibilityFlag = useCallback((venueId) => {
    if (!venueId) return null;
    return (
      venues.find((v) => v.id === venueId) ||
      (venuesRef.current || []).find((v) => v.id === venueId) ||
      tourVenues.find((v) => v.id === venueId) ||
      null
    );
  }, [venues, tourVenues]);

  const isVenueEligibleForAccessibilityFlag = useCallback((venue) => {
    if (!venue?.id) return false;
    if (
      venue.isUserLocation ||
      venue.venueType === 'POI' ||
      String(venue.id).startsWith('userloc-') ||
      String(venue.id).startsWith('poi-') ||
      String(venue.id).startsWith('transit-')
    ) {
      return false;
    }
    return true;
  }, []);

  const openAccessibilityFlagModal = useCallback((venueOrId) => {
    const auth = getAuth();
    if (!isRealUser(auth.currentUser)) {
      mapGateRef.current(MAP_ACTION.CONTRIBUTE);
      return;
    }

    let venue = null;
    if (venueOrId != null && venueOrId !== '') {
      venue = typeof venueOrId === 'string' || typeof venueOrId === 'number'
        ? findVenueForAccessibilityFlag(venueOrId)
        : venueOrId;
      if (venue && !isVenueEligibleForAccessibilityFlag(venue)) {
        showPopup?.('Accessibility flags are for venues only.', 'warning');
        venue = null;
      }
    } else if (selectedVenueId) {
      const selected = findVenueForAccessibilityFlag(selectedVenueId);
      if (selected && isVenueEligibleForAccessibilityFlag(selected)) {
        venue = selected;
      }
    }

    setAccessibilityFlagVenue(venue || null);
    setAccessibilityVenueSearch('');
    setAccessibilityFlagNotes('');
    setAccessibilityFlagArea(null);
    setAccessibilityFlagStep(1);
    setShowAccessibilityFlagModal(true);
  }, [findVenueForAccessibilityFlag, isVenueEligibleForAccessibilityFlag, selectedVenueId, showPopup]);

  openAccessibilityFlagModalRef.current = openAccessibilityFlagModal;

  const closeAccessibilityFlagModal = useCallback(() => {
    setShowAccessibilityFlagModal(false);
    setAccessibilityFlagNotes('');
    setAccessibilityFlagVenue(null);
    setAccessibilityVenueSearch('');
    setAccessibilityFlagArea(null);
    setAccessibilityFlagStep(1);
  }, []);

  const handleAccessibilityFlagSubmit = useCallback(async () => {
    const notes = accessibilityFlagNotes.trim();
    if (!notes) {
      showPopup?.('Please add a note for this venue.', 'warning');
      return;
    }
    if (!accessibilityFlagArea) {
      showPopup?.('Please select where this note applies.', 'warning');
      return;
    }
    const auth = getAuth();
    const contributor = auth.currentUser;
    if (!isRealUser(contributor)) {
      mapGateRef.current(MAP_ACTION.CONTRIBUTE);
      return;
    }
    if (!accessibilityFlagVenue?.id) {
      showPopup?.('Please select a venue first.', 'warning');
      return;
    }
    try {
      setAccessibilityFlagSubmitting(true);
      const db = getFirestore();
      const areaLabel = {
        outside: 'Outside venue',
        inside: 'Inside venue',
        both: 'Both',
      }[accessibilityFlagArea] || accessibilityFlagArea;
      await addDoc(collection(db, 'venue_spec_flags'), {
        venueId: accessibilityFlagVenue.id,
        venueName: accessibilityFlagVenue.name || '',
        category: 'accessibility',
        categoryLabel: getResourceCategoryLabel('accessibility') || 'Accessibility',
        area: accessibilityFlagArea,
        areaLabel,
        notes,
        userId: contributor.uid,
        userEmail: contributor.email || '',
        userName: userData?.displayName || userData?.username || 'Anonymous',
        status: 'pending',
        createdAt: new Date(),
      });
      closeAccessibilityFlagModal();
      showPopup?.('Thanks — your accessibility note was submitted for review.', 'success');
    } catch (error) {
      console.error('Error submitting accessibility flag:', error);
      showPopup?.('Failed to add note. Please try again.', 'error');
    } finally {
      setAccessibilityFlagSubmitting(false);
    }
  }, [
    accessibilityFlagArea,
    accessibilityFlagNotes,
    accessibilityFlagVenue,
    closeAccessibilityFlagModal,
    userData?.displayName,
    userData?.username,
    showPopup,
  ]);

  const accessibilityVenueSearchResults = useMemo(() => {
    if (accessibilityFlagVenue) return [];
    const q = accessibilityVenueSearch.trim();
    if (q.length < 2) return [];
    return searchVenuesByQuery(venues, q, { limit: 8 }).filter(isVenueEligibleForAccessibilityFlag);
  }, [accessibilityFlagVenue, accessibilityVenueSearch, venues, isVenueEligibleForAccessibilityFlag]);

  const renderAccessibilityFlagModal = () => {
    if (!showAccessibilityFlagModal) return null;

    const venueName = accessibilityFlagVenue?.name || '';
    const venueLocation = [accessibilityFlagVenue?.city, accessibilityFlagVenue?.state].filter(Boolean).join(', ');
    const areaOptions = [
      { key: 'outside', label: 'OUTSIDE VENUE' },
      { key: 'inside', label: 'INSIDE VENUE' },
      { key: 'both', label: 'BOTH' },
    ];
    const canGoNext = Boolean(accessibilityFlagVenue?.id && accessibilityFlagArea);
    const placeholderColor = isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.38)';

    return (
      <FullscreenSafeModal
        visible={showAccessibilityFlagModal}
        transparent
        animationType="fade"
        onRequestClose={closeAccessibilityFlagModal}
        hostRef={mapStageRef}
      >
      <View style={styles.a11yModalBackdrop}>
          <View style={styles.a11yModalCard}>
            {accessibilityFlagStep === 1 ? (
              <>
                <View style={styles.a11yModalHeader}>
                  <Text style={styles.a11yModalTitle}>Accessibility Note</Text>
                  <TouchableOpacity onPress={closeAccessibilityFlagModal} accessibilityLabel="Close form">
                    <Text style={styles.a11yModalClose}>✕</Text>
                  </TouchableOpacity>
                </View>

                <View style={styles.a11yCategoryBanner}>
                  <Image source={TRAFFIC_CONE_IMG} style={styles.a11yConeHero} resizeMode="contain" />
                  <View style={{ flex: 1 }}>
                    <Text style={styles.a11yCategoryLabel}>Submission type</Text>
                    <Text style={styles.a11yCategoryValue}>Accessibility Flag</Text>
                  </View>
                </View>

                <Text style={styles.a11yHelpText}>
                  Choose a venue and where the note applies. Admins will review before it appears.
                </Text>

                <Text style={styles.a11yFieldLabel}>Venue *</Text>
                {accessibilityFlagVenue ? (
                  <View style={styles.a11yVenueSelected}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.a11yVenueSelectedName} numberOfLines={1}>{venueName}</Text>
                      {venueLocation ? (
                        <Text style={styles.a11yVenueSelectedLoc} numberOfLines={1}>{venueLocation}</Text>
                      ) : null}
                    </View>
                    <TouchableOpacity
                      onPress={() => {
                        setAccessibilityFlagVenue(null);
                        setAccessibilityVenueSearch('');
                      }}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                      accessibilityLabel="Clear selected venue"
                    >
                      <Ionicons
                        name="close-circle"
                        size={20}
                        color={isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)'}
                      />
                    </TouchableOpacity>
                  </View>
                ) : (
                  <View style={styles.a11yVenueSearchWrap}>
                    <TextInput
                      value={accessibilityVenueSearch}
                      onChangeText={setAccessibilityVenueSearch}
                      placeholder="Search venues…"
                      placeholderTextColor={placeholderColor}
                      style={styles.a11yInput}
                    />
                    {accessibilityVenueSearchResults.length > 0 ? (
                      <ScrollView
                        style={styles.a11yVenueDropdown}
                        nestedScrollEnabled
                        keyboardShouldPersistTaps="handled"
                      >
                        {accessibilityVenueSearchResults.map((venue) => (
                          <TouchableOpacity
                            key={venue.id}
                            onPress={() => {
                              setAccessibilityFlagVenue(venue);
                              setAccessibilityVenueSearch('');
                            }}
                            style={styles.a11yVenueResult}
                          >
                            <Text style={styles.a11yVenueResultName} numberOfLines={1}>{venue.name}</Text>
                            <Text style={styles.a11yVenueResultLoc} numberOfLines={1}>
                              {[venue.city, venue.state].filter(Boolean).join(', ')}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    ) : accessibilityVenueSearch.trim().length >= 2 ? (
                      <Text style={styles.a11yEmptyHint}>No matching venues.</Text>
                    ) : null}
                  </View>
                )}

                <Text style={styles.a11yFieldLabel}>Select area *</Text>
                <View style={styles.a11yAreaRow}>
                  {areaOptions.map((opt) => {
                    const active = accessibilityFlagArea === opt.key;
                    return (
                      <TouchableOpacity
                        key={opt.key}
                        onPress={() => setAccessibilityFlagArea(opt.key)}
                        style={[styles.a11yAreaOption, active && styles.a11yAreaOptionActive]}
                      >
                        <Text style={[styles.a11yAreaOptionText, active && styles.a11yAreaOptionTextActive]}>
                          {opt.label}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>

                <View style={styles.a11yActions}>
                  <TouchableOpacity onPress={closeAccessibilityFlagModal} style={styles.a11yCancelBtn}>
                    <Text style={styles.a11yCancelText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => {
                      if (!accessibilityFlagVenue?.id) {
                        showPopup?.('Please select a venue first.', 'warning');
                        return;
                      }
                      if (!accessibilityFlagArea) {
                        showPopup?.('Please select where this note applies.', 'warning');
                        return;
                      }
                      setAccessibilityFlagStep(2);
                    }}
                    disabled={!canGoNext}
                    style={[styles.a11yPrimaryBtn, !canGoNext && styles.a11yPrimaryBtnDisabled]}
                  >
                    <Text style={styles.a11yPrimaryBtnText}>Next</Text>
                  </TouchableOpacity>
                </View>
              </>
            ) : (
              <>
                <View style={styles.a11yModalHeader}>
                  <Text style={styles.a11yModalTitle}>Add Note</Text>
                  <TouchableOpacity onPress={closeAccessibilityFlagModal} accessibilityLabel="Close form">
                    <Text style={styles.a11yModalClose}>✕</Text>
                  </TouchableOpacity>
                </View>

                <View style={styles.a11yCategoryBanner}>
                  <Image source={TRAFFIC_CONE_IMG} style={styles.a11yConeSmall} resizeMode="contain" />
                  <View style={{ flex: 1 }}>
                    <Text style={styles.a11yCategoryLabel}>Accessibility Note for</Text>
                    <Text style={styles.a11yCategoryValue} numberOfLines={2}>
                      {venueName || 'Selected venue'}
                    </Text>
                  </View>
                </View>

                <Text style={styles.a11yHelpText}>
                  Describe the accessibility improvements needed here.
                </Text>

                <Text style={styles.a11yFieldLabel}>Notes *</Text>
                <TextInput
                  value={accessibilityFlagNotes}
                  onChangeText={setAccessibilityFlagNotes}
                  placeholder="What should we know about accessibility here?"
                  placeholderTextColor={placeholderColor}
                  multiline
                  numberOfLines={5}
                  style={[styles.a11yInput, styles.a11yTextArea]}
                />

                <View style={styles.a11yActions}>
                  <TouchableOpacity onPress={() => setAccessibilityFlagStep(1)} style={styles.a11yCancelBtn}>
                    <Text style={styles.a11yCancelText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={handleAccessibilityFlagSubmit}
                    disabled={accessibilityFlagSubmitting}
                    style={[styles.a11yPrimaryBtn, accessibilityFlagSubmitting && styles.a11yPrimaryBtnDisabled]}
                  >
                    {accessibilityFlagSubmitting ? (
                      <ActivityIndicator size="small" color="#ffffff" />
                    ) : (
                      <Text style={styles.a11yPrimaryBtnText}>Add Note</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>
      </View>
      </FullscreenSafeModal>
    );
  };

  const renderAccessibilityMapButton = () => {
    const rightPanelOpen = Boolean(rightRailTab)
      || (tourStopsVisible && rightRailTab !== 'map' && rightRailTab !== 'tours' && rightRailTab !== 'funds');
    // Mobile Map/Tours fills remaining width; keep a11y near the right edge (floats above panel).
    const btnRight = isMobile
      ? 4
      : (rightPanelOpen ? 10 + RIGHT_RAIL_PANEL_WIDTH + 10 : 14);
    return (
      <TouchableOpacity
        style={[
          styles.mapAccessibilityBtn,
          isMobile && styles.mapAccessibilityBtnMobile,
          { right: btnRight },
        ]}
        onPress={() => openAccessibilityFlagModal(selectedVenueId)}
        accessibilityLabel="Add accessibility note"
        activeOpacity={0.85}
      >
        <Image
          source={TRAFFIC_CONE_IMG}
          style={[styles.mapAccessibilityBtnImg, isMobile && styles.mapAccessibilityBtnImgMobile]}
          resizeMode="contain"
        />
      </TouchableOpacity>
    );
  };

  /** Below-map bar: legend · Metro Accessible */
  const renderMapBelowBar = () => {
    if (isMobile) return null;
    const legendItems = [
      { ionicon: 'location', color: '#E67E22', label: 'Venue' },
      { ionicon: 'leaf', color: '#2ECC71', label: 'Eco' },
      { ionicon: 'shirt-outline', color: '#F39C12', label: 'Merch' },
      { ionicon: 'bus', color: '#8B5CF6', label: 'Bus stop' },
      { ionicon: 'train', color: '#DC2626', label: 'Train/Amtrak' },
      { ionicon: 'airplane', color: '#EC4899', label: 'Airport' },
      { ionicon: 'bicycle', color: '#10B981', label: 'Bike lanes' },
      { image: TRAFFIC_CONE_IMG, label: 'Accessibility Flags' },
      ...(radioLayerOn ? [{ mci: 'radio-tower', color: LAYER_COLOR.radio, label: 'Radio station' }] : []),
    ];
    const muted = isDarkMode ? 'rgba(255,255,255,0.50)' : 'rgba(0,0,0,0.45)';
    return (
      <View style={styles.mapBelowBar}>
        <View style={styles.mapBelowBarLegend}>
          {legendItems.map((item) => (
            <View key={item.label} style={styles.mapBelowBarLegendItem}>
              {item.image ? (
                <Image source={item.image} style={{ width: 12, height: 12 }} resizeMode="contain" />
              ) : item.mci ? (
                <MaterialCommunityIcons name={item.mci} size={12} color={item.color} />
              ) : (
                <Ionicons name={item.ionicon} size={12} color={item.color} />
              )}
              <Text style={[styles.mapBelowBarLegendText, { color: muted }]}>{item.label}</Text>
            </View>
          ))}
        </View>

        <TouchableOpacity
          style={styles.mapBelowBarMetroBtn}
          activeOpacity={0.75}
          onPress={() => {
            const metro = ACCESSIBILITY_LINKS.find((l) => l.id === 'metro-accessible') || ACCESSIBILITY_LINKS[0];
            if (metro?.url) openAccessibilityLink(metro.url);
          }}
        >
          <Text style={styles.mapBelowBarMetroBtnText}>Is this Metro Accessible</Text>
        </TouchableOpacity>
      </View>
    );
  };

  // Add renderLocationPrompt function since it's used in the JSX
  const renderLocationPrompt = () => {
    if (!locationRequested && !userLocation && !locationPermissionDenied) {
      return (
        <View style={styles.locationPrompt}>
          <Text style={styles.locationPromptText}>
            Enable location services to see venues near you and get accurate distance calculations
          </Text>
          <TouchableOpacity
            style={styles.locationPromptButton}
            onPress={requestUserLocation}
          >
            <Text style={styles.locationPromptButtonText}>Enable Location</Text>
          </TouchableOpacity>
        </View>
      );
    }
    return null;
  };

  // Add function to add venue to specific day and update routes
  const addVenueToDayById = (venue, dayId) => {
    if (!mapGateRef.current(MAP_ACTION.PLAN)) return;
    console.log(`Adding venue ${venue.name} to day ${dayId}`);

    setTourDays(prevDays => {
      // Find the day we're adding to
      const updatedDay = prevDays.find(d => d.id === dayId);

      if (!updatedDay) {
        console.error(`Day with ID ${dayId} not found`);
        return prevDays;
      }

      // Check if there will be at least 2 venues after adding the new venue
      const willHaveMultipleVenues = updatedDay.venues.length >= 1;

      // Update the days with the new venue
      const newDays = prevDays.map(day =>
        day.id === dayId
          ? { ...day, venues: [...day.venues, venue] }
          : day
      );

      // Tour venues are highlighted with green markers
      // The useEffect watching tourDays will handle updating the map
      // Just return the new days - React will trigger the appropriate updates

      return newDays;
    });
  };

  // Memoize distances for in-view venues (avoids N recalculations per render; displayedVenues is a subset)
  const venueDistancesMiles = useMemo(() => {
    if (!userLocation || !venuesInBounds.length) return {};
    const map = {};
    for (const venue of venuesInBounds) {
      if (venue.id && venue.latitude != null && venue.longitude != null) {
        const km = calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          parseFloat(venue.latitude),
          parseFloat(venue.longitude)
        );
        map[venue.id] = km * 0.621371; // miles
      }
    }
    return map;
  }, [userLocation, venuesInBounds]);

  // Update the venue card distance text (uses memoized distances)
  const renderVenueDistance = useCallback((venue) => {
    if (!venue?.id) return null;
    const distance = venueDistancesMiles[venue.id];
    if (distance == null) return null;
    return (
      <Text style={styles.mileageText}>
        {distance.toFixed(1)} miles away
      </Text>
    );
  }, [venueDistancesMiles]);

  // Web-only: dnd-kit draggable for venue cards (replaces native HTML5 drag). No transform in list — DragOverlay shows the moving copy.
  function DndDraggableVenueCard({ venueId, style, onSelect, routesCalculating, children }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
      id: `add-venue-${venueId}`,
      data: { type: 'add', venueId },
      disabled: routesCalculating,
    });
    return (
      <TouchableOpacity
        ref={setNodeRef}
        style={[style, isDragging && { opacity: 0 }]}
        onPress={onSelect}
        activeOpacity={0.8}
        {...listeners}
        {...attributes}
      >
        {children}
      </TouchableOpacity>
    );
  }

  // Web-only: dnd-kit draggable for user location cards. No transform in list — DragOverlay shows the moving copy.
  function DndDraggableUserLocationCard({ locationId, style, routesCalculating, children }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
      id: `add-userloc-${locationId}`,
      data: { type: 'add', venueId: `userloc-${locationId}` },
      disabled: routesCalculating,
    });
    return (
      <View
        ref={setNodeRef}
        style={[style, isDragging && { opacity: 0 }]}
        {...listeners}
        {...attributes}
      >
        {children}
      </View>
    );
  }

  // Web-only: dnd-kit droppable for tour day/row (used alongside native drop for "move" reorder)
  function TourDroppable({ id, dayId, insertIndex, style, styleIsOver, children }) {
    const { setNodeRef, isOver } = useDroppable({ id, data: { dayId, insertIndex } });
    return (
      <View ref={setNodeRef} style={[style, isOver && styleIsOver]}>
        {children}
      </View>
    );
  }

  // Pick dropEffect to match drag source so cursor isn't "not-allowed" (getData not readable in dragover, so use types).
  function getTourDropEffect(dataTransfer, routesCalculating) {
    if (!dataTransfer || routesCalculating) return 'none';
    const t = dataTransfer.types || [];
    if (t.includes('application/x-tour-move')) return 'move';
    if (t.includes('application/x-tour-copy')) return 'copy';
    return 'copy';
  }

  // Web-only: draggable wrapper for user location cards (payload add:userloc-<id> for drop handler)
  function WebDraggableUserLocationCard({ locationId, style, onDragEnd, routesCalculating, children }) {
    const ref = useRef(null);
    useEffect(() => {
      if (Platform.OS !== 'web' || !ref.current) return;
      const el = ref.current;
      el.setAttribute('draggable', routesCalculating ? 'false' : 'true');
      const handleDragStart = (e) => {
        if (routesCalculating) { e.preventDefault(); return; }
        if (e.dataTransfer) {
          e.dataTransfer.setData('text/plain', `add:userloc-${locationId}`);
          e.dataTransfer.setData('application/x-tour-copy', '1');
          e.dataTransfer.effectAllowed = 'copyMove';
        }
      };
      el.addEventListener('dragstart', handleDragStart);
      el.addEventListener('dragend', onDragEnd);
      return () => {
        el.removeAttribute('draggable');
        el.removeEventListener('dragstart', handleDragStart);
        el.removeEventListener('dragend', onDragEnd);
      };
    }, [locationId, onDragEnd, routesCalculating]);
    return <View ref={ref} style={style}>{children}</View>;
  }

  // Pick dropEffect to match drag source so cursor isn't "not-allowed" (getData not readable in dragover, so use types).
  function getTourDropEffect(dataTransfer, routesCalculating) {
    if (!dataTransfer || routesCalculating) return 'none';
    const t = dataTransfer.types || [];
    if (t.includes('application/x-tour-move')) return 'move';
    if (t.includes('application/x-tour-copy')) return 'copy';
    return 'copy';
  }

  // Web-only: RNW View doesn't forward onDragOver/onDragLeave/onDrop, so attach to DOM node via ref
  function WebDropTargetDay({ dayId, style, setDragOverDayId, setDragInsertAt, insertIndexAtEnd, onDropWithPayload, routesCalculating, dragInsertAt, children }) {
    const ref = useRef(null);
    useEffect(() => {
      if (Platform.OS !== 'web' || !ref.current) return;
      const el = ref.current;
      const handleDragEnter = (e) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = getTourDropEffect(e.dataTransfer, routesCalculating);
      };
      const handleDragOver = (e) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = getTourDropEffect(e.dataTransfer, routesCalculating);
        if (!routesCalculating) {
          setDragOverDayId(dayId);
          if (setDragInsertAt != null && insertIndexAtEnd != null) setDragInsertAt({ dayId, index: insertIndexAtEnd });
        }
      };
      const handleDragLeave = (e) => {
        // Don't clear when pointer moved into a child (e.g. a row) — we're still in this day
        if (e.relatedTarget && el.contains(e.relatedTarget)) return;
        setDragOverDayId(null);
        if (setDragInsertAt) setDragInsertAt(null);
      };
      const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverDayId(null);
        if (setDragInsertAt) setDragInsertAt(null);
        // When an insert line is active, only that insert target should handle the drop; day-level drop must not run
        if (!routesCalculating && !dragInsertAt) {
          const payload = e.dataTransfer?.getData?.('text/plain');
          if (payload) onDropWithPayload(payload);
        }
      };
      el.addEventListener('dragenter', handleDragEnter);
      el.addEventListener('dragover', handleDragOver);
      el.addEventListener('dragleave', handleDragLeave);
      el.addEventListener('drop', handleDrop);
      return () => {
        el.removeEventListener('dragenter', handleDragEnter);
        el.removeEventListener('dragover', handleDragOver);
        el.removeEventListener('dragleave', handleDragLeave);
        el.removeEventListener('drop', handleDrop);
      };
    }, [dayId, setDragOverDayId, setDragInsertAt, insertIndexAtEnd, onDropWithPayload, routesCalculating, dragInsertAt]);
    return <View ref={ref} style={style}>{children}</View>;
  }

  // Web-only: drop target for inserting before a specific venue in a day (reorder / move between days).
  // Insert indicator turns ON only on dragover over this row's element (the line shows above this venue).
  function WebDropTargetVenueRow({ style, dayId, index, setDragInsertAt, onDropWithPayload, routesCalculating, dragInsertAt, children }) {
    const ref = useRef(null);
    useEffect(() => {
      if (Platform.OS !== 'web' || !ref.current) return;
      const el = ref.current;
      const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer) e.dataTransfer.dropEffect = getTourDropEffect(e.dataTransfer, routesCalculating);
      };
      const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer) e.dataTransfer.dropEffect = getTourDropEffect(e.dataTransfer, routesCalculating);
        if (!routesCalculating && setDragInsertAt){
           setDragInsertAt({ dayId, index });
        }
      };
      const handleDragLeave = (e) => {
        e.stopPropagation();
        // Don't clear when pointer moves into a child (e.g. the card) — we're still in this row
        if (e.relatedTarget && el.contains(e.relatedTarget)) return;
        if (setDragInsertAt) setDragInsertAt(null);
      };
      const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (setDragInsertAt) setDragInsertAt(null);
        // Only run drop when this row is the active insert line
        const isActiveInsert = dragInsertAt && dragInsertAt.dayId === dayId && dragInsertAt.index === index;
        if (!routesCalculating && isActiveInsert && onDropWithPayload){
          const payload = e.dataTransfer?.getData?.('text/plain');
          if (payload) onDropWithPayload(payload);
        }
      };
      el.addEventListener('dragenter', handleDragEnter, true);
      el.addEventListener('dragover', handleDragOver, true);
      el.addEventListener('dragleave', handleDragLeave, true);
      el.addEventListener('drop', handleDrop, true);
      return () => {
        el.removeEventListener('dragenter', handleDragEnter, true);
        el.removeEventListener('dragover', handleDragOver, true);
        el.removeEventListener('dragleave', handleDragLeave, true);
        el.removeEventListener('drop', handleDrop, true);
      };
    }, [dayId, index, setDragInsertAt, onDropWithPayload, routesCalculating, dragInsertAt]);
    return <View ref={ref} style={style}>{children}</View>;
  }

  // Web-only: draggable tour venue card (inside a day) for reorder / move between days. Uses slot (dayId, slotIndex) so duplicates work. Pass draggable={false} in tour view mode.
  function WebDraggableTourVenueCard({ venueId, dayId, slotIndex, style, onDragStart, onDragEnd, routesCalculating, draggable = true, children }) {
    const ref = useRef(null);
    useEffect(() => {
      if (Platform.OS !== 'web' || !ref.current) return;
      const el = (ref.current && typeof ref.current.getNativeRef === 'function')
        ? ref.current.getNativeRef()
        : ref.current;
      if (!el || typeof el.setAttribute !== 'function') return;
      const canDrag = draggable && !routesCalculating;
      el.setAttribute('draggable', canDrag ? 'true' : 'false');
      if (!canDrag) return;
      const handleDragStart = (e) => {
        if (routesCalculating) { e.preventDefault(); return; }
        if (e.dataTransfer) {
          e.dataTransfer.setData('text/plain', `move:${dayId}:${slotIndex}`);
          e.dataTransfer.setData('application/x-tour-move', '1');
          e.dataTransfer.effectAllowed = 'copyMove';
        }
        onDragStart?.();
      };
      const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer) e.dataTransfer.dropEffect = routesCalculating ? 'none' : 'move';
      };
      el.addEventListener('dragstart', handleDragStart);
      el.addEventListener('dragend', onDragEnd);
      el.addEventListener('dragover', handleDragOver);
      return () => {
        el.removeAttribute('draggable');
        el.removeEventListener('dragstart', handleDragStart);
        el.removeEventListener('dragend', onDragEnd);
        el.removeEventListener('dragover', handleDragOver);
      };
    }, [venueId, dayId, slotIndex, onDragStart, onDragEnd, routesCalculating, draggable]);
    return <View ref={ref} style={style}>{children}</View>;
  }

  // Web-only: sortable tour venue row (drag to reorder; also accepts drop from left panel at this index).
  const SORTABLE_ROW_GAP = 12;
  function SortableTourVenueRow({ dayId, index, style, children, legRowContent, tourVenueOptionsId, slotKey }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: `tour-${dayId}-${index}`,
      data: { dayId, index, type: 'tour-slot', insertIndex: index },
    });
    const sortableStyle = {
      opacity: isDragging ? 0.5 : 1,
      transform: CSS.Transform.toString(transform),
      transition,
      marginBottom: SORTABLE_ROW_GAP,
      marginTop: index === 0 ? SORTABLE_ROW_GAP : 0,
    };
    return (
      <View ref={setNodeRef} style={[style, sortableStyle]} {...listeners} {...attributes}>
        <View style={tourVenueOptionsId === slotKey ? { zIndex: 200 } : { zIndex: 10 }}>
          {children}
        </View>
        {legRowContent}
      </View>
    );
  }

  // Single venue card for FlatList (virtualized list only renders visible items); draggable on web
  const renderVenueCard = useCallback(({ item: venue }) => {
    if (!venue?.id || !venue?.name) return null;
    const distanceEl = renderVenueDistance(venue);
    // Eco badges
    const bs = (venue.backstage || '').toLowerCase();
    const nt = (venue.notes || '').toLowerCase();
    const venueIsEcoConscious = venue.isEcoConscious === true;
    const venueIsNearTransit = venue._isNearTransit === true;
    const venueHasVegan = (venue.name||'').toLowerCase().includes('vegan') || nt.includes('vegan') || (venue.affiliate||'').toLowerCase().includes('vegan');
    const ecoFlags = [
      (bs.includes('lodging') || bs.includes('hotel') || bs.includes('bed')) && { name: 'bed', color: '#2ecc71', title: 'Lodging' },
      venueHasVegan && { name: 'leaf', color: '#27ae60', title: 'Vegan' },
      ((venue.shorePower||'').toLowerCase().includes('yes') || bs.includes('shore power')) && { name: 'flash', color: '#f1c40f', title: 'Shore Power' },
      (venue.merch||'').toLowerCase().includes('100%') && { name: 'ribbon', color: '#9b59b6', title: '100% Merch' },
      venueIsNearTransit && { name: 'bus', color: '#26c6da', title: 'Near Transit' },
      venueIsEcoConscious && { name: 'earth', color: '#1abc9c', title: 'Eco Venue' },
    ].filter(Boolean);
    const venueHasLodging = bs.includes('lodging') || bs.includes('hotel') || bs.includes('bed');
    const isSearchOutOfMapView = Boolean(searchQuery.trim() && venue._searchOutOfMapView);
    const locationLine = [venue.city, venue.state].filter(Boolean).join(', ');
    const detailParts = searchQuery.trim() ? getVenueSearchDetailParts(venue) : [];
    // Green card highlight: near transit, vegan, lodging, or admin-flagged eco
    const isGreenCard = venueIsNearTransit || venueHasVegan || venueIsEcoConscious || venueHasLodging;
    const cardContent = (
      <>
        <View style={styles.venueImagePlaceholder}>
          <Ionicons name="location" size={16} color="#e67e22" />
        </View>
        <View style={styles.venueInfo}>
          {searchQuery.trim() ? (
            <HighlightedSearchText
              text={venue.name}
              query={searchQuery}
              style={styles.venueName}
              numberOfLines={1}
            />
          ) : (
            <Text style={styles.venueName} numberOfLines={1}>{venue.name}</Text>
          )}
          {searchQuery.trim() && locationLine ? (
            <HighlightedSearchText
              text={locationLine}
              query={searchQuery}
              style={styles.venueLocation}
              numberOfLines={1}
            />
          ) : (
            <Text style={styles.venueLocation}>{venue.city}, {venue.state}</Text>
          )}
          {searchQuery.trim() && detailParts.length > 1 ? (
            <HighlightedSearchTextParts
              parts={detailParts.slice(1)}
              query={searchQuery}
              style={[styles.venueMetaText, { marginTop: 1 }]}
              numberOfLines={2}
            />
          ) : searchQuery.trim() && venue.affiliate ? (
            <HighlightedSearchText
              text={venue.affiliate}
              query={searchQuery}
              style={[styles.venueMetaText, { marginTop: 1 }]}
              numberOfLines={1}
            />
          ) : null}
          {distanceEl}
          <View style={styles.venueMetaRow}>
            <Text style={styles.venueMetaText}>{venue.capacity || '—'} cap</Text>
            <Text style={styles.venueMetaDot}>•</Text>
            <Text style={styles.venueMetaText}>{venue.venueType || 'Venue'}</Text>
            {ecoFlags.length > 0 && (
              <Text style={styles.venueMetaDot}>•</Text>
            )}
            {ecoFlags.map((flag, i) => (
              <Ionicons key={i} name={flag.name} size={11} color={flag.color} />
            ))}
          </View>
        </View>
        <TouchableOpacity
          style={styles.zoomButtonSmall}
          onPress={(e) => { e.stopPropagation(); zoomToVenue(venue); }}
        >
          <Text style={styles.zoomButtonSmallText}>🔍</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.addButtonSmall, routesCalculating && styles.addButtonSmallDisabled]}
          onPress={(e) => { e.stopPropagation(); if (!routesCalculating) addVenueToTour(venue); }}
          disabled={routesCalculating}
        >
          <Text style={styles.addButtonSmallText}>+</Text>
        </TouchableOpacity>
      </>
    );
    const cardStyle = [
      styles.venueCard,
      selectedVenueId === venue.id && styles.selectedVenueCard,
      isGreenCard && styles.venueCardGreen,
      isSearchOutOfMapView && styles.venueCardSearchDimmed,
    ];
    if (Platform.OS === 'web') {
      return (
        <DndDraggableVenueCard
          venueId={venue.id}
          style={cardStyle}
          onSelect={() => handleVenueSelect(venue)}
          routesCalculating={routesCalculating}
        >
          {cardContent}
        </DndDraggableVenueCard>
      );
    }
    return (
      <TouchableOpacity
        style={cardStyle}
        onPress={() => handleVenueSelect(venue)}
      >
        {cardContent}
      </TouchableOpacity>
    );
  }, [selectedVenueId, searchQuery, renderVenueDistance, handleVenueSelect, zoomToVenue, addVenueToTour, routesCalculating, isDarkMode, styles.venueCard, styles.selectedVenueCard, styles.venueCardGreen, styles.venueCardSearchDimmed, styles.venueImagePlaceholder, styles.venueInfo, styles.venueName, styles.venueLocation, styles.venueMetaRow, styles.venueMetaText, styles.venueMetaDot, styles.zoomButtonSmall, styles.zoomButtonSmallText, styles.addButtonSmall, styles.addButtonSmallDisabled, styles.addButtonSmallText]);

  // Overpass selectors per POI category — mirrors the server's POI_CATEGORY_QUERIES
  const POI_OVERPASS_SELECTORS = useMemo(() => ({
    hotel:              ['["tourism"~"hotel|motel|hostel|guest_house|resort"]'],
    gas_station:        ['["amenity"="fuel"]'],
    airport:            ['["aeroway"="aerodrome"]'],
    train_station:      ['["railway"="station"]'],
    bus_station:        ['["amenity"="bus_station"]', '["public_transport"="station"]'],
    rest_stop:          ['["highway"="rest_area"]', '["highway"="services"]'],
    rv_parking:         ['["amenity"="parking"]["parking"="overnight"]', '["tourism"="caravan_site"]'],
    border_checkpoint:  ['["barrier"="border_control"]'],
    museum:             ['["tourism"="museum"]'],
    park:               ['["leisure"="park"]'],
    gym:                ['["leisure"="fitness_centre"]', '["leisure"="sports_centre"]'],
    spa:                ['["leisure"="spa"]'],
    clinic:             ['["amenity"="clinic"]', '["amenity"="doctors"]', '["amenity"="urgent_care"]'],
    massage_parlor:     ['["amenity"="massage"]', '["shop"="massage"]'],
    barbershop:         ['["shop"="hairdresser"]', '["shop"="barber"]'],
    rock_climbing_gym:  ['["sport"="climbing"]'],
    fishing_destination:['["leisure"="fishing"]'],
    golf_course:        ['["leisure"="golf_course"]'],
  }), []);

  /**
   * Calendar MAP tab Travel search: place names (live Overpass) within Travel's search radius of the
   * selected day's route, nearest to the route first.
   */
  const searchTravelPlacesAlongRoute = useCallback(async (text) => {
    const line = calendarRouteLineRef.current;
    if (!line.length) return [];
    const miles = travelRouteRadiusRef.current;
    const sampled = sampleRouteCoordinates(line, { minSpacingMiles: 5, maxPoints: 60 });
    const features = await searchOverpassPoisAlongRoute({
      query: text,
      sampledCoords: sampled,
      radiusMiles: miles,
      selectorsByCategory: POI_OVERPASS_SELECTORS,
    });
    return features
      .map((feature) => {
        const [lng, lat] = feature.geometry.coordinates;
        const category = feature.properties.category;
        return {
          id: feature.properties.id,
          name: feature.properties.name,
          category,
          categoryLabel: POI_CATEGORY_META[category]?.label || category,
          icon: POI_CATEGORY_META[category]?.icon || '•',
          address: feature.properties.address || '',
          latitude: lat,
          longitude: lng,
          milesFromRoute: milesFromRoute({ lat, lng }, line),
        };
      })
      .filter((place) => place.milesFromRoute != null && place.milesFromRoute <= miles + 1e-6)
      .sort((a, b) => a.milesFromRoute - b.milesFromRoute);
  }, [POI_OVERPASS_SELECTORS, POI_CATEGORY_META]);

  const travelPlacePopupRef = useRef(null);
  const focusTravelPlace = useCallback((place) => {
    if (!map.current || !place) return;
    const center = [Number(place.longitude), Number(place.latitude)];
    if (!center.every(Number.isFinite)) return;
    map.current.flyTo({ center, zoom: Math.max(map.current.getZoom?.() || 0, 13), duration: 700 });
    const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;',
    }[ch]));
    travelPlacePopupRef.current?.remove();
    travelPlacePopupRef.current = new mapboxgl.Popup({ offset: 12, closeButton: true })
      .setLngLat(center)
      .setHTML(
        `<div style="font-size:13px;min-width:160px"><strong>${esc(place.name)}</strong><br/>` +
        `<span style="font-size:11px;color:#666">${esc(place.categoryLabel)}` +
        `${place.address ? ` · ${esc(place.address)}` : ''}</span></div>`
      )
      .addTo(map.current);
  }, []);

  // POIs: Firestore cache first, then live Overpass, merge, then persist new OSM rows to Firestore.
  const fetchPOIs = useCallback(async () => {
    if (!map.current) {
      setLoadingPOIs(false);
      poiFetchInFlightRef.current = false;
      poiFetchQueuedRef.current = false;
      return;
    }

    if (!activePoiCategories || activePoiCategories.length === 0) {
      clearPoiLayers();
      setPoiFeaturesByCategory({});
      setHotels([]);
      setGasStations([]);
      setLoadingPOIs(false);
      poiFetchQueuedRef.current = false;
      return;
    }

    const zoom = map.current.getZoom();
    const corridorMode = isCalendarPoiCorridor();
    if (zoom < POI_ZOOM_MID_MAX && !corridorMode) {
      setLoadingPOIs(false);
      poiFetchInFlightRef.current = false;
      poiFetchQueuedRef.current = false;
      return;
    }

    if (poiFetchInFlightRef.current) {
      poiFetchQueuedRef.current = true;
      setLoadingPOIs(true);
      return;
    }

    poiFetchInFlightRef.current = true;
    setLoadingPOIs(true);

    const bounds = map.current.getBounds();
    const bbox = {
      s: bounds.getSouth(),
      w: bounds.getWest(),
      n: bounds.getNorth(),
      e: bounds.getEast(),
    };

    const applyGroupedPoiFeaturesToMap = (groupedFeatures) => {
      let features = groupedFeatures;
      const calendarRouteLine = calendarRouteLineRef.current;
      if (calendarEmbed && calendarRouteLine.length) {
        // Calendar MAP tab: Travel's search radius around the selected day's route.
        const miles = travelRouteRadiusRef.current;
        features = Object.fromEntries(Object.entries(groupedFeatures).map(([category, rows]) => [
          category,
          (rows || []).filter((feature) => {
            const [lng, lat] = feature?.geometry?.coordinates || [];
            return isPointWithinRouteRadius({ lat, lng }, calendarRouteLine, miles);
          }),
        ]));
      } else if (restrictPoisToTourRouteRef.current) {
        const stops = (tourDaysForPoiRef.current || [])
          .flatMap((day) => day.venues || [])
          .map((stop) => ({
            lat: Number(stop.lat ?? stop.latitude),
            lng: Number(stop.lng ?? stop.longitude),
          }))
          .filter((stop) => Number.isFinite(stop.lat) && Number.isFinite(stop.lng));
        const miles = farmSearchRadiusMiles || 25;
        if (stops.length) {
          features = Object.fromEntries(Object.entries(groupedFeatures).map(([category, rows]) => [
            category,
            (rows || []).filter((feature) => {
              const [lng, lat] = feature?.geometry?.coordinates || [];
              return stops.some((stop) => haversineMiles(lat, lng, stop.lat, stop.lng) <= miles);
            }),
          ]));
        }
      }
      Object.keys(POI_CATEGORY_META).forEach((category) => {
        ensurePoiLayer(category);
        const sourceId = buildPoiSourceId(category);
        const source = map.current?.getSource(sourceId);
        if (!source) return;
        source.setData({
          type: 'FeatureCollection',
          features: features[category] || [],
        });
      });

      ensurePoiDebugLayer();
      const poiDebugSource = map.current?.getSource('poi-debug-source');
      if (poiDebugSource) {
        poiDebugSource.setData({
          type: 'FeatureCollection',
          features: Object.values(features).flat(),
        });
      }

      setPoiFeaturesByCategory(features);
      setHotels(features.hotel || []);
      setGasStations(features.gas_station || []);
      syncPoiLayerVisibility();
      setupPoiClickHandlersRef.current?.();
    };

    if (corridorMode) {
      // Calendar MAP tab: one live Overpass query along the day's route. The corridor doesn't change
      // when the map pans, so skip refetching for the same categories, route and radius.
      const line = calendarRouteLineRef.current;
      const miles = travelRouteRadiusRef.current;
      const first = line[0] || [];
      const last = line[line.length - 1] || [];
      const corridorKey = [
        [...activePoiCategories].sort().join(','),
        line.length, first[0], first[1], last[0], last[1], miles,
      ].join('|');
      try {
        if (poiCorridorKeyRef.current !== corridorKey) {
          const sampled = sampleRouteCoordinates(line, { minSpacingMiles: 5, maxPoints: 60 });
          const elements = await fetchOverpassPoiElementsAlongRoute(
            sampled, miles, activePoiCategories, POI_OVERPASS_SELECTORS,
          );
          poiCorridorKeyRef.current = corridorKey;
          applyGroupedPoiFeaturesToMap(groupedFeaturesFromOverpassElements(elements, activePoiCategories));
        }
      } catch (error) {
        console.error('Corridor POI fetch error:', error);
      } finally {
        poiFetchInFlightRef.current = false;
        const runAgain = poiFetchQueuedRef.current;
        poiFetchQueuedRef.current = false;
        if (runAgain) {
          setTimeout(() => { fetchPOIsRef.current?.(); }, 0);
        } else {
          setLoadingPOIs(false);
        }
      }
      return;
    }

    const schedulePoiPersistToFirestore = () => {
      const persistKey = [
        bbox.s.toFixed(3),
        bbox.w.toFixed(3),
        bbox.n.toFixed(3),
        bbox.e.toFixed(3),
        [...activePoiCategories].sort().join(','),
      ].join('|');
      if (lastPoiPersistKeyRef.current === persistKey) return;
      if (poiPersistTimerRef.current) clearTimeout(poiPersistTimerRef.current);
      poiPersistTimerRef.current = setTimeout(async () => {
        try {
          await persistPoisToFirestore(functions, bbox, activePoiCategories);
          lastPoiPersistKeyRef.current = persistKey;
        } catch (error) {
          console.warn('POI Firestore persist failed:', error);
        }
      }, 4000);
    };

    let groupedFeatures = {};
    activePoiCategories.forEach((category) => {
      groupedFeatures[category] = [];
    });

    try {
      // 1) Firestore first — show cached POIs immediately.
      try {
        const firestorePois = await queryPoisFromFirestore(functions, bbox, activePoiCategories);
        groupedFeatures = groupedFeaturesFromPoiRecords(firestorePois, activePoiCategories);
        applyGroupedPoiFeaturesToMap(groupedFeatures);
      } catch (firestoreError) {
        console.warn('Firestore POI query failed, continuing with Overpass:', firestoreError);
      }

      // 2) Live Overpass import for the current viewport.
      const overpassElements = await fetchOverpassPoiElements(bbox, activePoiCategories, POI_OVERPASS_SELECTORS);
      const overpassGrouped = groupedFeaturesFromOverpassElements(overpassElements, activePoiCategories);

      // 3) Aggregate — keep Firestore rows, add any new Overpass-only POIs.
      groupedFeatures = mergeGroupedPoiFeatures(groupedFeatures, overpassGrouped, { preferExisting: true });
      applyGroupedPoiFeaturesToMap(groupedFeatures);

      // 4) Persist new OSM POIs to Firestore in the background (server merges with cache).
      schedulePoiPersistToFirestore();
    } catch (error) {
      console.error('POI fetch error:', error);
    } finally {
      poiFetchInFlightRef.current = false;
      const runAgain = poiFetchQueuedRef.current;
      poiFetchQueuedRef.current = false;
      if (runAgain) {
        setTimeout(() => { fetchPOIsRef.current?.(); }, 0);
      } else {
        setLoadingPOIs(false);
      }
    }
  }, [
    activePoiCategories,
    clearPoiLayers,
    ensurePoiLayer,
    ensurePoiDebugLayer,
    syncPoiLayerVisibility,
    POI_CATEGORY_META,
    POI_OVERPASS_SELECTORS,
  ]);

  fetchPOIsRef.current = fetchPOIs;

  // Keep a ref to the latest setupPoiClickHandlers so fetchPOIs can call it without adding it to its deps
  const setupPoiClickHandlersRef = useRef(setupPoiClickHandlers);
  useEffect(() => { setupPoiClickHandlersRef.current = setupPoiClickHandlers; }, [setupPoiClickHandlers]);

  // Fetch POIs after any map movement (pan/zoom), debounced. `fetchPOIsRef` keeps listener stable so debounce isn't reset every render.
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    const debouncedFetchPOIs = _.debounce(() => {
      fetchPOIsRef.current?.();
    }, 1000);

    const handler = () => debouncedFetchPOIs();
    map.current.on('moveend', handler);

    return () => {
      debouncedFetchPOIs.cancel();
      if (map.current) map.current.off('moveend', handler);
    };
  }, [mapLoaded]);

  useEffect(() => {
    if (!mapLoaded) return;
    fetchPOIs();
  }, [mapLoaded, activePoiCategories, fetchPOIs]);

  // Baked POI overview: fetch the per-category prebaked files for the active
  // categories (cached after first load) and render them clustered. The live
  // per-viewport fetch above still supplies fresh detail past POI_ZOOM_MID_MAX.
  useEffect(() => {
    if (!mapLoaded) return;
    loadBakedPoiCategories();
    // Wire the shared POI popup opener now (not only after a zoomed-in live
    // fetch), so clicking a baked dot while zoomed out opens its bubble.
    setupPoiClickHandlersRef.current?.();
  }, [mapLoaded, activePoiCategories, loadBakedPoiCategories]);

  // Shift Mapbox built-in controls (zoom, attribution) so they aren't hidden by the sidebar / mobile FABs
  useEffect(() => {
    if (!mapLoaded || !map.current) return;
    try {
      if (isMobile) {
        const fabStackBottom = MOBILE_MAP_BOTTOM_GAP + MOBILE_FAB_BOTTOM_OFFSET + MOBILE_FAB_ROW_HEIGHT;
        map.current.setPadding({
          top: 0,
          right: 0,
          bottom: tourStopsVisible ? 0 : fabStackBottom + MOBILE_MAP_ABOVE_CHROME_GAP,
          left: 0,
        });
      } else {
        map.current.setPadding({
          top: 0,
          right: tourStopsVisible ? 356 : 0,
          bottom: 0,
          left: isMobile ? 0 : MAP_SIDE_RAIL_WIDTH,
        });
      }
    } catch (_) {}
  }, [mapLoaded, tourStopsVisible, isMobile, showWeatherPanel]);

  // Empty effect placeholder removed — POI OR selection is driven by activePoiCategories only.

  // Adjust POI marker size by zoom for beta efficiency/clarity.
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const iconSize = getPoiIconSizeByZoom(currentZoom);

    Object.keys(POI_CATEGORY_META).forEach((category) => {
      const layerId = buildPoiLayerId(category);
      if (!map.current.getLayer(layerId)) return;
      try {
        map.current.setLayoutProperty(layerId, 'icon-size', iconSize);
      } catch (_) {
        /* ignore style not ready */
      }
    });
  }, [currentZoom, mapLoaded, POI_CATEGORY_META]);

  // Venues and POIs are independent: venue AND-filters do not hide when POI layers are on.
  // (Previously POI-focused mapMode hid all non-tour venues.)

  useEffect(() => {
    syncPoiLayerVisibility();
  }, [syncPoiLayerVisibility, activePoiCategories, currentZoom]);

  // Add separate effect for handling mode changes
  useEffect(() => {
    const drawControls = document.querySelector('.mapboxgl-ctrl-group');
    if (drawControls) {
      drawControls.style.display = mapMode === 'draw' ? 'block' : 'none';
    }
  }, [mapMode]);

  // Radar layer: add/remove RainViewer raster tiles on Mapbox when radarEnabled changes.
  // Free RainViewer API max zoom is 7 — set source maxzoom so Mapbox overzooms instead of
  // requesting z8+ tiles (which return a "Zoom Level Not Supported" placeholder image).
  useEffect(() => {
    if (!mapLoaded || !map.current) return;
    const RADAR_SOURCE = 'rainviewer-radar';
    const RADAR_LAYER  = 'rainviewer-radar-layer';

    const removeRadar = () => {
      if (!map.current) return;
      try {
        if (map.current.getLayer(RADAR_LAYER))  map.current.removeLayer(RADAR_LAYER);
        if (map.current.getSource(RADAR_SOURCE)) map.current.removeSource(RADAR_SOURCE);
      } catch { /* map torn down */ }
    };

    if (!radarEnabled) {
      removeRadar();
      return;
    }

    let cancelled = false;
    let refreshTimer = null;

    const applyRadarFrame = (frame) => {
      if (cancelled || !frame || !map.current) return;
      removeRadar();
      map.current.addSource(RADAR_SOURCE, {
        type: 'raster',
        tiles: [buildRadarTileUrl(frame)],
        tileSize: 256,
        minzoom: 0,
        maxzoom: RADAR_MAX_ZOOM, // RainViewer free max — Mapbox scales these tiles when zoomed in further
        attribution: '© RainViewer',
      });
      map.current.addLayer({
        id: RADAR_LAYER,
        type: 'raster',
        source: RADAR_SOURCE,
        paint: {
          'raster-opacity': 0.65,
          'raster-resampling': 'linear',
        },
      });
    };

    const loadRadar = () => {
      fetchRadarTimestamp()
        .then((frame) => {
          if (cancelled) return;
          if (!frame) {
            setRadarEnabled(false);
            return;
          }
          applyRadarFrame(frame);
        })
        .catch(() => {
          if (!cancelled) setRadarEnabled(false);
        });
    };

    loadRadar();
    // Refresh frame every ~10 minutes to match RainViewer interval
    refreshTimer = setInterval(loadRadar, 10 * 60 * 1000);

    return () => {
      cancelled = true;
      if (refreshTimer) clearInterval(refreshTimer);
      removeRadar();
    };
  }, [radarEnabled, mapLoaded]);

  // State for 3D mode
  const [is3DMode, setIs3DMode] = useState(false);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);

  const resizeMapSoon = useCallback(() => {
    const run = () => {
      try { map.current?.resize?.(); } catch (_) {}
    };
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        run();
        setTimeout(run, 120);
      });
    } else {
      setTimeout(run, 80);
    }
  }, []);

  const getFullscreenElement = () => {
    if (typeof document === 'undefined') return null;
    return document.fullscreenElement || document.webkitFullscreenElement || null;
  };

  const exitMapFullscreen = useCallback(async () => {
    mapFullscreenCssOnlyRef.current = false;
    const fsEl = getFullscreenElement();
    if (fsEl) {
      try {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      } catch (_) {}
    }
    setIsMapFullscreen(false);
    resizeMapSoon();
  }, [resizeMapSoon]);

  const enterMapFullscreen = useCallback(async () => {
    if (isMobile) return;
    const el = mapStageRef.current;
    if (el && typeof Element !== 'undefined' && el instanceof Element) {
      try {
        if (typeof el.requestFullscreen === 'function') {
          await el.requestFullscreen({ navigationUI: 'hide' });
          mapFullscreenCssOnlyRef.current = false;
          setIsMapFullscreen(true);
          resizeMapSoon();
          return;
        }
        if (typeof el.webkitRequestFullscreen === 'function') {
          el.webkitRequestFullscreen();
          mapFullscreenCssOnlyRef.current = false;
          setIsMapFullscreen(true);
          resizeMapSoon();
          return;
        }
      } catch (_) {
        // Policy / gesture restrictions — use CSS cover fallback below
      }
    }
    mapFullscreenCssOnlyRef.current = true;
    setIsMapFullscreen(true);
    resizeMapSoon();
  }, [isMobile, resizeMapSoon]);

  const toggleMapFullscreen = useCallback(() => {
    if (isMobile) return;
    if (isMapFullscreen || getFullscreenElement()) {
      exitMapFullscreen();
    } else {
      enterMapFullscreen();
    }
  }, [isMobile, isMapFullscreen, enterMapFullscreen, exitMapFullscreen]);

  // Fullscreen is desktop-only — leave it if the viewport becomes mobile
  useEffect(() => {
    if (isMobile && (isMapFullscreen || getFullscreenElement())) {
      exitMapFullscreen();
    }
  }, [isMobile, isMapFullscreen, exitMapFullscreen]);

  // Keep React state in sync with browser fullscreen (Esc, system UI, etc.)
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return undefined;

    const styleId = 'vrc-map-stage-fullscreen-css';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .vrc-map-stage:fullscreen,
        .vrc-map-stage:-webkit-full-screen {
          width: 100vw !important;
          height: 100vh !important;
          height: 100dvh !important;
          max-width: none !important;
          max-height: none !important;
          border-radius: 0 !important;
          background: #0b0f1a !important;
        }
        .vrc-map-stage:fullscreen .mapboxgl-map,
        .vrc-map-stage:-webkit-full-screen .mapboxgl-map {
          width: 100% !important;
          height: 100% !important;
        }
      `;
      document.head.appendChild(style);
    }

    const onFsChange = () => {
      const stage = mapStageRef.current;
      const fsEl = getFullscreenElement();
      if (fsEl && stage && (fsEl === stage || stage.contains?.(fsEl))) {
        mapFullscreenCssOnlyRef.current = false;
        setIsMapFullscreen(true);
      } else if (!fsEl && !mapFullscreenCssOnlyRef.current) {
        setIsMapFullscreen(false);
      }
      resizeMapSoon();
    };

    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, [resizeMapSoon]);

  // CSS fallback cover: lock scroll + Esc; native Fullscreen API handles Esc itself
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return undefined;
    if (!isMapFullscreen) return undefined;

    const prevOverflow = document.body.style.overflow;
    if (mapFullscreenCssOnlyRef.current) {
      document.body.style.overflow = 'hidden';
    }
    const onKey = (e) => {
      if (e.key === 'Escape' && mapFullscreenCssOnlyRef.current) {
        exitMapFullscreen();
      }
    };
    window.addEventListener('keydown', onKey);
    resizeMapSoon();
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
      resizeMapSoon();
    };
  }, [isMapFullscreen, exitMapFullscreen, resizeMapSoon]);

  // Toggle 3D mode
  const toggle3DMode = () => {
    if (!map.current) return;

    const newMode = !is3DMode;
    setIs3DMode(newMode);

    if (newMode) {
      // Enable 3D view with pitch
      map.current.easeTo({
        pitch: 60,
        bearing: map.current.getBearing(),
        duration: 1000
      });
    } else {
      // Reset to flat view
      map.current.easeTo({
        pitch: 0,
        bearing: 0,
        duration: 1000
      });
    }
  };

  // Keep 3D button state in sync if pitch is changed elsewhere
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const syncPitch = () => {
      const pitched = (map.current?.getPitch?.() ?? 0) > 0;
      setIs3DMode((prev) => (prev === pitched ? prev : pitched));
    };
    map.current.on('pitchend', syncPitch);
    return () => {
      map.current?.off?.('pitchend', syncPitch);
    };
  }, [mapLoaded]);

  // Add mode controls to the map container (venue filters — left)
  const renderModeControls = () => {
    const activeFilterCount =
      (filters.affiliate?.length || 0) +
      (filters.features?.length || 0) +
      (filters.resources?.length || 0) +
      ((filters.capacityMin || filters.capacityMax) ? 1 : 0) +
      (showFoodBanks || showFarms ? 1 : 0) +
      (coordinateSourceFilter !== 'all' ? 1 : 0);
    const searchMatches = searchQuery.trim().length > 0
      ? filterVenuesBySearch(filteredVenues, searchQuery).slice(0, 25)
      : [];
    const searchResults = searchMatches.length > 0
      ? sortSearchResultsByMapVicinity(searchMatches, mapBounds)
      : [];

    const toggleAffiliateChip = (val) => {
      setFilters(prev => ({
        ...prev,
        affiliate: prev.affiliate?.includes(val)
          ? prev.affiliate.filter(a => a !== val)
          : [...(prev.affiliate || []), val],
      }));
    };

    // Mobile collapsed state: search button + map controls always visible
    if (isMobile && !showMobileSearch) {
      return (
        <View style={[styles.modeControls, { flexDirection: 'column', padding: 8, gap: 6, width: 'auto', maxWidth: 'calc(100vw - 20px)', right: 10 }]}>
          <TouchableOpacity
            style={[styles.mapSearchBtn, { width: '100%' }]}
            onPress={() => setShowMobileSearch(true)}
          >
            <Text style={[styles.mapSearchBtnText, { textAlign: 'center' }]}>
              Search venues…{activeFilterCount > 0 ? ` · ${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} active` : ''}
            </Text>
          </TouchableOpacity>
          {/* Map controls moved next to top-right ☰ hamburger */}
        </View>
      );
    }

    // Desktop: hug content; cap height so it stays above the weather panel and scrolls if needed
    const leftPanelBottomGap = !isMobile && showWeatherPanel
      ? Math.max(12 + (weatherPanelHeight || 52) + 8, 70)
      : 10;
    const leftPanelMaxHeight = !isMobile
      ? `calc(100% - ${10 + leftPanelBottomGap}px)`
      : 'min(72vh, calc(100% - 24px))';
    // Leave room for the sticky search row (~48px) inside the capped panel
    const filterScrollMaxHeight = !isMobile
      ? `calc(100% - ${10 + leftPanelBottomGap + 52}px)`
      : 'min(64vh, calc(100% - 80px))';

    return (
      <View
        ref={filterPanelRef}
        style={[
          styles.modeControls,
          styles.modeControlsFilled,
          { maxHeight: leftPanelMaxHeight },
          isMobile && { width: 'auto', maxWidth: 'calc(100vw - 20px)', right: 10 },
        ]}
      >

        {/* ── Search bar row (sticky) ── */}
        <View style={[styles.mapSearchRow, { flexShrink: 0 }, isMobile && { flexWrap: 'wrap', gap: 6 }]}>
          <TextInput
            style={[styles.mapSearchInput, !isDarkMode && { color: theme.text.primary }]}
            nativeID="tour-planner-search"
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Search venues..."
            placeholderTextColor={isDarkMode ? "rgba(255,255,255,0.38)" : "rgba(0,0,0,0.55)"}
            returnKeyType="search"
            clearButtonMode="while-editing"
          />
          <TouchableOpacity
            style={[
              styles.mapSearchBtn,
              styles.mapSearchBtnAdd,
              (addLocationMode || showAddLocationPanel || editingLocation) && styles.mapSearchBtnCancel,
            ]}
            onPress={() => {
              if (!userData?.uid) {
                mapGateRef.current(MAP_ACTION.CONTRIBUTE);
                return;
              }
              if (addLocationMode || showAddLocationPanel || editingLocation) {
                setEditingLocation(null);
                closeAddLocationPanel();
              } else {
                setAddLocationMode(true);
                setShowAddLocationPanel(true);
              }
            }}
          >
            <Text style={styles.mapSearchBtnText}>
              {(addLocationMode || showAddLocationPanel || editingLocation) ? 'Cancel' : 'Add a Location'}
            </Text>
          </TouchableOpacity>
          {/* Desktop collapse toggle */}
          {!isMobile && (
            <TouchableOpacity
              onPress={() => setShowDesktopFilters(v => !v)}
              style={{ paddingHorizontal: 6, paddingVertical: 4, borderRadius: 6, justifyContent: 'center', alignItems: 'center' }}
            >
              <Text style={{ fontSize: 13, color: isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)', lineHeight: 16 }}>
                {showDesktopFilters ? '▲' : '▼'}
              </Text>
            </TouchableOpacity>
          )}
          {isMobile && (
            <TouchableOpacity
              style={[styles.mapSearchBtn, { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }]}
              onPress={() => { setShowMobileSearch(false); setSearchQuery(''); }}
            >
              <Text style={[styles.mapSearchBtnText, { color: isDarkMode ? 'rgba(255,255,255,0.55)' : theme.text.secondary }]}>✕</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* ── Search results dropdown ── */}
        {searchResults.length > 0 && (
          <ScrollView
            style={[styles.mapSearchDropdown, { flexShrink: 0, maxHeight: 160, scrollbarWidth: 'thin', scrollbarColor: 'rgba(128,128,128,0.45) transparent' }]}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator
            {...(typeof document !== 'undefined' ? { className: 'vrc-venue-filters-scroll' } : {})}
          >
            {searchResults.map(({ venue, inMapView }) => (
              <React.Fragment key={venue.id || venue.name}>
                {renderVenueSearchResultRow(venue, searchQuery, {
                  rowStyle: styles.mapSearchResultRow,
                  nameStyle: styles.mapSearchResultName,
                  subStyle: styles.mapSearchResultSub,
                  dimmed: !inMapView,
                  onPress: () => {
                    handleVenueSelect(venue);
                    setSearchQuery('');
                  },
                })}
              </React.Fragment>
            ))}
          </ScrollView>
        )}

        {/* ── Expandable filter body (scrolls when panel is tall) ── */}
        {(showDesktopFilters || isMobile) && (
        <ScrollView
          style={[styles.modeControlsScroll, { maxHeight: filterScrollMaxHeight }]}
          contentContainerStyle={styles.modeControlsScrollContent}
          nestedScrollEnabled
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator
          {...(typeof document !== 'undefined' ? { className: 'vrc-venue-filters-scroll' } : {})}
        >

        {/* ── Capacity Min / Max (always visible) ── */}
        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', width: '100%', marginTop: 2 }}>
          <TextInput
            style={[styles.mapSearchInput, { flex: 1, paddingVertical: 6, fontSize: 13 }, !isDarkMode && { color: theme.text.primary }]}
            placeholder="Min"
            placeholderTextColor={isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.4)'}
            keyboardType="numeric"
            value={filters.capacityMin}
            onChangeText={val => setFilters(prev => ({ ...prev, capacityMin: val.replace(/\D/g, '') }))}
          />
          <TextInput
            style={[styles.mapSearchInput, { flex: 1, paddingVertical: 6, fontSize: 13 }, !isDarkMode && { color: theme.text.primary }]}
            placeholder="Max"
            placeholderTextColor={isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.4)'}
            keyboardType="numeric"
            value={filters.capacityMax}
            onChangeText={val => setFilters(prev => ({ ...prev, capacityMax: val.replace(/\D/g, '') }))}
          />
        </View>

        {/* ── Venue filter sections (multi-open) ── */}
        <View style={{ width: '100%', marginTop: 10 }}>
            <Text style={{ fontSize: 11, color: isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.45)', fontWeight: '500', marginBottom: 6, letterSpacing: 0.2 }}>
              {!hasVenueRevealCriteria
                ? 'Search or select filters to show venues'
                : `${displayedVenues.length} of ${venuesInBounds.length} venues in view${searchQuery.trim() ? ' (filtered)' : ''}`}
            </Text>

            <View style={[styles.venueFilterRow, { gap: 5 }]}>
              {[
                { id: 'affiliate', label: 'Affiliate', count: filters.affiliate?.length || 0 },
                { id: 'features', label: 'Features', count: filters.features?.length || 0 },
                { id: 'resources', label: 'Resources', count: filters.resources?.length || 0 },
                { id: 'food', label: 'Food', count: (showFoodBanks ? 1 : 0) + (showFarms ? 1 : 0) },
              ].map((pill) => {
                const open = !!openFilterSections[pill.id];
                return (
                  <TouchableOpacity
                    key={pill.id}
                    style={[
                      styles.categoryPill,
                      open && styles.categoryPillOpen,
                      pill.count > 0 && !open && styles.categoryPillHasFilter,
                    ]}
                    onPress={() => setOpenFilterSections((prev) => ({ ...prev, [pill.id]: !prev[pill.id] }))}
                  >
                    <Text style={[styles.categoryPillText, open && styles.categoryPillTextOpen]}>{pill.label}</Text>
                    {pill.count > 0 && <Text style={styles.categoryPillBadge}>{pill.count}</Text>}
                  </TouchableOpacity>
                );
              })}
              {/* User Locations filter — hidden for now
              {userLocationsFilterVisible && (
                <TouchableOpacity
                  style={[styles.categoryPill, showUserLocations && styles.categoryPillHasFilter]}
                  onPress={() => {
                    const next = !showUserLocations;
                    setShowUserLocations(next);
                    if (typeof localStorage !== 'undefined') localStorage.setItem('vrc-show-user-created-locations', String(next));
                  }}
                >
                  <Text style={styles.categoryPillText}>User Locations</Text>
                  {showUserLocations && <Text style={styles.categoryPillBadge}>✓</Text>}
                </TouchableOpacity>
              )}
              */}
              {activeFilterCount > 0 && (
                <TouchableOpacity
                  style={[styles.venueFilterChip, { borderColor: 'rgba(239,68,68,0.4)', backgroundColor: 'rgba(239,68,68,0.15)' }]}
                  onPress={() => {
                    setFilters({ affiliate: [], features: [], resources: [], capacityMin: '', capacityMax: '', venueType: 'all', hasParking: 'all' });
                    setShowFoodBanks(false);
                    setShowFarms(false);
                    setOpenFilterSections({ affiliate: false, features: false, resources: false, food: false });
                  }}
                >
                  <Text style={[styles.venueFilterChipText, { color: '#fca5a5' }]}>✕ Clear</Text>
                </TouchableOpacity>
              )}
            </View>

            {openFilterSections.affiliate && (
              <View style={[styles.filterSubPanel, { marginTop: 8 }]}>
                <View style={styles.venueFilterRow}>
                  {['Live Nation','AEG','Independent','Bar','Brewery','DIY','Event Space','Nonprofit','Record Shop','Restaurant','Skate','Art Gallery','Coffee Shop','Tea House','Other'].map(val => {
                    const active = filters.affiliate?.includes(val);
                    const color = affiliateChipColor(val);
                    return (
                      <TouchableOpacity
                        key={val}
                        onPress={() => toggleAffiliateChip(val)}
                        style={[
                          styles.venueFilterChip,
                          { flexDirection: 'row', alignItems: 'center', gap: 5 },
                          active && { borderColor: color, backgroundColor: color + '26' },
                        ]}
                      >
                        <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: color }} />
                        <Text style={[styles.venueFilterChipText, active && { color, fontWeight: '700' }]}>{val}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
                <Text style={{ fontSize: 9.5, color: isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', marginTop: 6 }}>
                  AND filter: venue must match every selected affiliate. None checked = all shown.
                </Text>
              </View>
            )}

            {openFilterSections.features && (
              <View style={[styles.filterSubPanel, { marginTop: 8 }]}>
                <View style={styles.venueFilterRow}>
                  {[
                    { label: 'ADA info', value: 'hasADA' },
                    { label: '100% Merch', value: 'merch100' },
                    { label: 'Backstage', value: 'hasBackstage' },
                    { label: 'Backline', value: 'hasBackline' },
                    { label: 'Floor Stage', value: 'stageFloor' },
                    { label: 'Parking', value: 'hasParking' },
                    { label: 'Laundry', value: 'hasLaundry' },
                    { label: 'Rehearsal', value: 'hasRehearsal' },
                    { label: 'Pinball!', value: 'hasPinball' },
                    { label: 'Shower', value: 'hasShower' },
                    { label: 'Shore Power', value: 'hasShorePower' },
                    { label: 'Spec Links', value: 'hasSpecLink' },
                    { label: '🌱 Vegan food', value: 'hasVeganFood' },
                    { label: '🌿 Lodging on-site', value: 'hasLodging' },
                  ].map(opt => {
                    const active = filters.features?.includes(opt.value);
                    return (
                      <TouchableOpacity
                        key={opt.value}
                        style={[styles.venueFilterChip, active && styles.venueFilterChipActive]}
                        onPress={() => setFilters(prev => ({
                          ...prev,
                          features: prev.features?.includes(opt.value)
                            ? prev.features.filter(f => f !== opt.value)
                            : [...(prev.features || []), opt.value],
                        }))}
                      >
                        <Text style={[styles.venueFilterChipText, active && styles.venueFilterChipTextActive]}>{opt.label}</Text>
                      </TouchableOpacity>
                    );
                  })}
                  <TouchableOpacity
                    onPress={() => setShowScreenprinters(v => !v)}
                    style={[styles.venueFilterChip, styles.venueFilterChipLink, showScreenprinters && styles.venueFilterChipActive]}
                  >
                    <Ionicons
                      name="shirt-outline"
                      size={12}
                      color={showScreenprinters ? (isDarkMode ? '#062a38' : '#fff') : (isDarkMode ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)')}
                      style={{ marginRight: 4 }}
                    />
                    <Text style={[styles.venueFilterChipText, showScreenprinters && styles.venueFilterChipTextActive]}>Merch shops</Text>
                  </TouchableOpacity>
                </View>
                <Text style={{ fontSize: 9.5, color: isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', marginTop: 6 }}>
                  AND filter: venue must have every selected feature.
                </Text>
              </View>
            )}

            {openFilterSections.resources && (
              <View style={[styles.filterSubPanel, { marginTop: 8 }]}>
                <View style={styles.venueFilterRow}>
                  {resourceCategoryCatalog.map((opt) => {
                    const active = filters.resources?.includes(opt.id);
                    return (
                      <TouchableOpacity
                        key={opt.id}
                        style={[styles.venueFilterChip, active && styles.venueFilterChipActive]}
                        onPress={() => setFilters((prev) => ({
                          ...prev,
                          resources: prev.resources?.includes(opt.id)
                            ? prev.resources.filter((r) => r !== opt.id)
                            : [...(prev.resources || []), opt.id],
                        }))}
                      >
                        <Text style={[styles.venueFilterChipText, active && styles.venueFilterChipTextActive]}>{opt.label}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
                <Text style={{ fontSize: 9.5, color: isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', marginTop: 6 }}>
                  AND filter: venues with approved community resources in every selected category. City- and state-wide shares highlight those areas in orange.
                </Text>
              </View>
            )}

            {openFilterSections.food && (
              <View style={[styles.filterSubPanel, { marginTop: 8 }]}>
                <View style={styles.venueFilterRow}>
                  <TouchableOpacity
                    style={[styles.venueFilterChip, { flexDirection: 'row', alignItems: 'center', gap: 5 }, showFoodBanks && styles.venueFilterChipActive]}
                    onPress={() => {
                      setShowFoodBanks((v) => {
                        const next = !v;
                        if (next) {
                          setFarmSearchDayId((prev) => resolveFarmSearchDayId(tourDays, prev || activeDayId));
                          setFarmSearchLegId((prev) => prev || 'all');
                        }
                        return next;
                      });
                    }}
                  >
                    <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: '#94780D' }} />
                    <Text style={[styles.venueFilterChipText, showFoodBanks && styles.venueFilterChipTextActive]}>Food Banks</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.venueFilterChip, { flexDirection: 'row', alignItems: 'center', gap: 5 }, showFarms && styles.venueFilterChipActive]}
                    onPress={() => {
                      const next = !showFarms;
                      setShowFarms(next);
                      if (next) {
                        setFarmSearchDayId(resolveFarmSearchDayId(tourDays, activeDayId));
                        setFarmSearchLegId('all');
                        setMapSideRailOpenRequest({ itemId: 'food' });
                      }
                    }}
                  >
                    <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: '#C0FF72' }} />
                    <Text style={[styles.venueFilterChipText, showFarms && styles.venueFilterChipTextActive]}>Farms</Text>
                  </TouchableOpacity>
                </View>
                <Text style={{ fontSize: 9.5, color: isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', marginTop: 6 }}>
                  Click Farms to search OSM farms and food banks along that day’s drive (default 25 miles).
                </Text>
              </View>
            )}

            {/* Left-panel venue legend */}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 12, paddingTop: 10, borderTopWidth: 1, borderTopColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)' }}>
              {[
                { ionicon: 'location', color: '#E67E22', label: 'Venue' },
                { ionicon: 'leaf', color: '#2ECC71', label: 'Eco' },
                { ionicon: 'shirt-outline', color: '#F39C12', label: 'Merch' },
              ].map((item) => (
                <View key={item.label} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  <Ionicons name={item.ionicon} size={12} color={item.color} />
                  <Text style={{ fontSize: 10, color: isDarkMode ? 'rgba(255,255,255,0.50)' : 'rgba(0,0,0,0.50)', fontWeight: '500', letterSpacing: 0.12 }}>
                    {item.label}
                  </Text>
                </View>
              ))}
            </View>

          </View>
        </ScrollView>
        )}
      </View>
    );
  };

  const resourceDirectoryItems = useMemo(() => {
    // Coordinates let the calendar's per-section search radius measure each item.
    const coordsOf = (src) => {
      const c = readHubCoords(src);
      return c ? { latitude: c.latitude, longitude: c.longitude } : {};
    };
    const venueById = new Map((venues || []).map((venue) => [String(venue.id), venue]));
    const merchItems = (screenprinters || []).map((sp) => {
      const location = [sp.city, sp.state].filter(Boolean).join(', ');
      const description = [sp.services, sp.notes, location].filter(Boolean).join(' · ');
      return {
        id: `merch:${sp.id}`,
        sourceId: sp.id,
        type: 'merch',
        categoryId: 'merch',
        name: sp.name || 'Merch location',
        description,
        location,
        imageUrl: sp.imageUrl || sp.photoUrl || sp.logoUrl || sp.photo || null,
        websiteUrl: sp.website || sp.url || null,
        ...coordsOf(sp),
        searchText: [
          'merch',
          'screenprinter',
          'screen printing',
          sp.name,
          sp.services,
          sp.notes,
          sp.city,
          sp.state,
          sp.phone,
          sp.email,
          sp.eco ? 'eco' : '',
        ].filter(Boolean).join(' ').toLowerCase(),
      };
    });

    const resourceItems = (approvedResourceSubmissions || []).map((resource) => {
      const location = resource.scope === 'state'
        ? `All venues in ${resource.venueState || 'this state'}`
        : resource.scope === 'city'
          ? `All venues in ${[resource.venueCity, resource.venueState].filter(Boolean).join(', ')}`
          : [resource.venueName, resource.venueCity, resource.venueState].filter(Boolean).join(' · ');
      const categoryLabel = resource.categoryLabel || getResourceCategoryLabel(resource.category);
      const description = [resource.description, categoryLabel, location].filter(Boolean).join(' · ');
      return {
        id: `resource:${resource.id}`,
        sourceId: resource.id,
        type: 'resource',
        categoryId: resource.category || 'other',
        categoryLabel,
        name: resource.resourceName || categoryLabel || 'Community resource',
        description,
        location,
        scope: resource.scope || 'venue',
        imageUrl: resource.imageUrl || null,
        websiteUrl: resource.url || resource.website || null,
        // City/state-wide resources have no single point; those stay unmeasured.
        ...(resource.scope === 'city' || resource.scope === 'state'
          ? {}
          : coordsOf(readHubCoords(resource) ? resource : venueById.get(String(resource.venueId)))),
        searchText: [
          resource.resourceName,
          resource.description,
          ...(Array.isArray(resource.tags) ? resource.tags : []),
          resource.category,
          categoryLabel,
          resource.venueName,
          resource.venueCity,
          resource.venueState,
          // Common directory vocabulary users may type.
          resource.category === 'accessibility' ? 'accessibility ada' : '',
          resource.category === 'environment' ? 'environment eco' : '',
          resource.category === 'safety' ? 'safety' : '',
          resource.category === 'education' ? 'education learn' : '',
          'resource directory',
        ].filter(Boolean).join(' ').toLowerCase(),
      };
    });

    const venueDerivedItems = (venues || []).flatMap((venue) =>
      venueDerivedResourceCategories(venue).map((category) => {
        const location = [venue.city, venue.state].filter(Boolean).join(', ');
        return {
          id: `venue:${venue.id}`,
          sourceId: String(venue.id),
          type: 'venue',
          categoryId: category.id,
          name: venue.name || `${category.label} location`,
          description: [category.description, location].filter(Boolean).join(' · '),
          location,
          imageUrl:
            venue.imageUrl ||
            venue.photoUrl ||
            venue.photos?.[0]?.url ||
            (typeof venue.photos?.[0] === 'string' ? venue.photos[0] : null) ||
            null,
          websiteUrl: category.linkOf(venue) || venue.website || venue.url || null,
          ...coordsOf(venue),
          searchText: [
            category.id,
            category.description,
            venue.name,
            venue.city,
            venue.state,
            venue.affiliate,
            venue.backstage,
            venue.notes,
          ].filter(Boolean).join(' ').toLowerCase(),
        };
      })
    );

    return [...merchItems, ...resourceItems, ...venueDerivedItems].sort((a, b) =>
      String(a.name).localeCompare(String(b.name))
    );
  }, [screenprinters, approvedResourceSubmissions, venues]);

  const selectedResourceDirectoryIds = useMemo(() => [
    ...selectedScreenprinterIds.map((id) => `merch:${id}`),
    ...selectedResourceSubmissionIds.map((id) => `resource:${id}`),
    ...selectedResourceVenueIds.map((id) => `venue:${id}`),
  ], [selectedScreenprinterIds, selectedResourceSubmissionIds, selectedResourceVenueIds]);

  const uploadResourceSubmissionPhoto = async (file, folder) => {
    if (!file) return null;
    const blob = file instanceof Blob ? file : await (await fetch(file.uri || file)).blob();
    const ext = (file.name || 'image').split('.').pop()?.toLowerCase() || 'jpg';
    const path = `resource-submissions/${folder}/${auth.currentUser.uid}_${Date.now()}.${ext}`;
    const refPath = storageRef(getStorage(), path);
    await uploadBytes(refPath, blob);
    return getDownloadURL(refPath);
  };

  const submitMapResource = async (form) => {
    if (!isRealUser(auth.currentUser)) {
      mapGateRef.current(MAP_ACTION.CONTRIBUTE);
      return;
    }
    try {
      const matchedVenue = venues.find(
        (venue) => String(venue.id) === String(form.venueId)
      ) || null;
      const uploadedImageUrl = await uploadResourceSubmissionPhoto(
        form.photoFile,
        form.venueId || 'tour-planner'
      );
      const embedCoords = form.mapEmbedLink
        ? await resolveEmbedCoordinatesForSave(form.mapEmbedLink, {
            city: form.city || matchedVenue?.city,
            state: form.state || matchedVenue?.state,
            name: form.name,
          })
        : {};
      const latitude = embedCoords.latitude ?? (form.latitude ? Number(form.latitude) : null);
      const longitude = embedCoords.longitude ?? (form.longitude ? Number(form.longitude) : null);
      await addDoc(collection(getFirestore(), 'resource_submissions'), {
        resourceName: form.name,
        url: form.url || null,
        description: form.description || null,
        tags: parseResourceTags(form.tags),
        scope: form.scope || 'venue',
        category: form.category,
        categoryLabel: resourceCategoryCatalog.find((category) => category.id === form.category)?.label
          || getResourceCategoryLabel(form.category),
        mapEmbedLink: form.mapEmbedLink || null,
        imageUrl: uploadedImageUrl || form.imageUrl || null,
        imageWidth: form.photoWidth || null,
        imageHeight: form.photoHeight || null,
        venueId: matchedVenue?.id != null ? String(matchedVenue.id) : form.venueId || null,
        venueName: matchedVenue?.name || form.venueName || '',
        venueCity: form.city || matchedVenue?.city || '',
        venueState: form.state || matchedVenue?.state || '',
        latitude: Number.isFinite(latitude) ? latitude : null,
        longitude: Number.isFinite(longitude) ? longitude : null,
        googlePlaceId: embedCoords.googlePlaceId || null,
        googleCid: embedCoords.googleCid || null,
        embedResolvedBy: embedCoords.embedResolvedBy || null,
        embedResolvedMethod: embedCoords.embedResolvedMethod || null,
        embedResolvedAt: embedCoords.latitude != null ? serverTimestamp() : null,
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email || '',
        userName: userData?.displayName || userData?.username || userData?.name || 'Anonymous',
        status: 'pending',
        createdAt: serverTimestamp(),
        submittedFrom: 'tour_planner',
      });
      setToast({
        message: 'Resource submitted! It will appear after admin approval.',
        type: 'success',
      });
    } catch (error) {
      console.error('Failed to submit map resource:', error);
      setToast({ message: 'Could not submit this resource.', type: 'error' });
    }
  };

  const submitMapMerch = async (form) => {
    if (!isRealUser(auth.currentUser)) {
      mapGateRef.current(MAP_ACTION.CONTRIBUTE);
      return;
    }
    try {
      const uploadedImageUrl = await uploadResourceSubmissionPhoto(form.photoFile, 'merch');
      const embedCoords = form.mapEmbedLink
        ? await resolveEmbedCoordinatesForSave(form.mapEmbedLink, {
            city: form.city,
            state: form.state,
            name: form.name,
          })
        : {};
      const latitude = embedCoords.latitude ?? (form.latitude ? Number(form.latitude) : null);
      const longitude = embedCoords.longitude ?? (form.longitude ? Number(form.longitude) : null);
      await addDoc(collection(getFirestore(), 'resource_submissions'), {
        resourceName: form.name,
        category: 'merch',
        categoryLabel: 'Merch',
        submissionType: 'merch',
        scope: 'venue',
        url: form.url || null,
        mapEmbedLink: form.mapEmbedLink || null,
        imageUrl: uploadedImageUrl || form.imageUrl || null,
        imageWidth: form.photoWidth || null,
        imageHeight: form.photoHeight || null,
        venueName: form.venueName || '',
        venueCity: form.city || '',
        venueState: form.state || '',
        latitude,
        longitude,
        googlePlaceId: embedCoords.googlePlaceId || null,
        googleCid: embedCoords.googleCid || null,
        embedResolvedBy: embedCoords.embedResolvedBy || null,
        embedResolvedMethod: embedCoords.embedResolvedMethod || null,
        embedResolvedAt: embedCoords.latitude != null ? serverTimestamp() : null,
        services: form.services || null,
        notes: form.notes || null,
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email || '',
        userName: userData?.displayName || userData?.username || userData?.name || 'Anonymous',
        status: 'pending',
        createdAt: serverTimestamp(),
        submittedFrom: 'tour_planner',
      });
      setToast({
        message: 'Merch shop submitted! It will appear after admin approval.',
        type: 'success',
      });
    } catch (error) {
      console.error('Failed to submit merch shop:', error);
      setToast({ message: 'Could not submit this merch shop.', type: 'error' });
    }
  };

  const submitFoodLocation = async (form) => {
    if (!isRealUser(auth.currentUser)) {
      mapGateRef.current(MAP_ACTION.CONTRIBUTE);
      return;
    }
    try {
      const coordinates = form.mapEmbedUrl
        ? await resolveEmbedCoordinatesForSave(form.mapEmbedUrl, {
            name: form.name,
          })
        : { latitude: null, longitude: null, googlePlaceId: null, googleCid: null, embedResolvedBy: null, embedResolvedMethod: null };
      const uploadedImageUrl = await uploadResourceSubmissionPhoto(form.photoFile, 'food');
      await addDoc(collection(getFirestore(), 'resource_submissions'), {
        resourceName: form.name,
        category: 'food',
        categoryLabel: form.locationType === 'fridge' ? 'Fridge / Pantry' : 'Farm',
        submissionType: 'food',
        scope: 'venue',
        url: form.website || null,
        mapEmbedUrl: form.mapEmbedUrl || null,
        description: form.description || null,
        pickup: Boolean(form.pickup),
        dropoff: Boolean(form.dropoff),
        locationType: form.locationType || (form.dropoff ? 'fridge' : 'farm'),
        networkName: form.networkName || null,
        address: form.address || null,
        hours: form.hours || null,
        donationRules: form.donationRules || null,
        imageUrl: uploadedImageUrl || null,
        imageWidth: form.photoWidth || null,
        imageHeight: form.photoHeight || null,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        googlePlaceId: coordinates.googlePlaceId,
        googleCid: coordinates.googleCid,
        embedResolvedBy: coordinates.embedResolvedBy,
        embedResolvedMethod: coordinates.embedResolvedMethod,
        embedResolvedAt: coordinates.latitude != null ? serverTimestamp() : null,
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email || '',
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      setToast({
        message: 'Food location submitted! It will appear after admin approval.',
        type: 'success',
      });
    } catch (error) {
      console.error('Failed to submit food location:', error);
      setToast({ message: 'Could not submit this food location.', type: 'error' });
    }
  };

  const submitTravelLocation = async (form) => {
    if (!isRealUser(auth.currentUser)) {
      mapGateRef.current(MAP_ACTION.CONTRIBUTE);
      return;
    }
    try {
      const coordinates = form.mapEmbedLink
        ? await resolveEmbedCoordinatesForSave(form.mapEmbedLink, { name: form.name })
        : { latitude: null, longitude: null };
      const uploadedImageUrl = await uploadResourceSubmissionPhoto(form.photoFile, 'travel');
      await addDoc(collection(getFirestore(), 'resource_submissions'), {
        resourceName: form.name,
        category: 'travel',
        categoryLabel: 'Travel',
        submissionType: 'travel',
        locationType: form.locationType || 'wellness',
        address: form.address || null,
        url: form.website || null,
        mapEmbedLink: form.mapEmbedLink || null,
        description: form.notes || null,
        imageUrl: uploadedImageUrl || null,
        latitude: coordinates.latitude ?? null,
        longitude: coordinates.longitude ?? null,
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email || '',
        status: 'pending',
        createdAt: serverTimestamp(),
        submittedFrom: 'tour_planner',
      });
      setToast({ message: 'Travel location submitted! It will appear after admin approval.', type: 'success' });
    } catch (error) {
      console.error('Failed to submit travel location:', error);
      setToast({ message: 'Could not submit this travel location.', type: 'error' });
    }
  };

  const submitEducationLocation = async (form) => {
    if (!isRealUser(auth.currentUser)) {
      mapGateRef.current(MAP_ACTION.CONTRIBUTE);
      return;
    }
    try {
      const coordinates = form.mapEmbedLink
        ? await resolveEmbedCoordinatesForSave(form.mapEmbedLink, { name: form.name })
        : { latitude: null, longitude: null };
      await addDoc(collection(getFirestore(), 'resource_submissions'), {
        resourceName: form.name,
        category: 'education',
        categoryLabel: 'Educational Location',
        submissionType: 'education',
        locationType: form.locationType || null,
        description: form.resourcesProvided || null,
        url: form.website || null,
        mapEmbedLink: form.mapEmbedLink || null,
        latitude: coordinates.latitude ?? null,
        longitude: coordinates.longitude ?? null,
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email || '',
        status: 'pending',
        createdAt: serverTimestamp(),
        submittedFrom: 'tour_planner',
      });
      setToast({ message: 'Educational location submitted! It will appear after admin approval.', type: 'success' });
    } catch (error) {
      console.error('Failed to submit educational location:', error);
      setToast({ message: 'Could not submit this educational location.', type: 'error' });
    }
  };

  const submitEventFlyer = async (form) => {
    if (!isRealUser(auth.currentUser)) {
      mapGateRef.current(MAP_ACTION.CONTRIBUTE);
      return;
    }
    try {
      await addDoc(collection(getFirestore(), 'resource_submissions'), {
        resourceName: form.title,
        category: 'event_flyer',
        categoryLabel: 'Event Flyer',
        submissionType: 'event_flyer',
        title: form.title,
        eventDate: form.date || null,
        url: form.link || null,
        ticketLink: form.ticketLink || null,
        organizedBy: form.organizedBy || null,
        venueId: form.venueId || null,
        venueName: form.venueName || null,
        description: form.details || null,
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email || '',
        status: 'pending',
        createdAt: serverTimestamp(),
        submittedFrom: 'tour_planner',
      });
      setToast({ message: 'Flyer submitted! It will appear after admin approval.', type: 'success' });
    } catch (error) {
      console.error('Failed to submit event flyer:', error);
      setToast({ message: 'Could not post this flyer.', type: 'error' });
    }
  };

  const submitNewVenue = async (form) => {
    if (!isRealUser(auth.currentUser)) {
      mapGateRef.current(MAP_ACTION.CONTRIBUTE);
      return;
    }
    try {
      const userDoc = await getDoc(doc(getFirestore(), 'users', auth.currentUser.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};
      const embedCoords = form.mapEmbedLink
        ? await resolveEmbedCoordinatesForSave(form.mapEmbedLink, {
            city: form.city,
            state: form.state,
            name: form.name,
          })
        : {};
      await addDoc(collection(getFirestore(), 'new_venues'), {
        ...form,
        ...embedCoords,
        embedResolvedAt: embedCoords.latitude != null ? serverTimestamp() : null,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: auth.currentUser.uid,
        submittedBy: {
          uid: auth.currentUser.uid,
          email: auth.currentUser.email || '',
          username: userData.username || 'Anonymous',
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          photoURL: userData.photoURL || null,
          verificationStatus: userData.verificationStatus || null,
        },
      });
      setToast({ message: 'Venue submitted! An admin will review before it appears on the map.', type: 'success' });
    } catch (error) {
      console.error('Failed to submit new venue:', error);
      setToast({ message: 'Could not submit venue. Please try again.', type: 'error' });
      throw error;
    }
  };

  const foodDirectoryItems = useMemo(() =>
    approvedFoodLocations.map(normalizeFoodDirectoryItem).filter(Boolean),
  [approvedFoodLocations]);

  const farmCorridorActive = showFarms || showFoodBanks;
  const farmRouteDays = useMemo(
    () => farmRouteDaySummaries(tourDays, listTourRouteLegs(tourDays, interDayLegModes)),
    [tourDays, interDayLegModes],
  );
  const visibleFoodHubs = useMemo(() => {
    if (!farmCorridorActive) return foodDirectoryItems;
    return farmCorridorHubs.filter((hub) => hubMatchesFoodFilters(hub, { showFarms, showFoodBanks }));
  }, [farmCorridorActive, farmCorridorHubs, foodDirectoryItems, showFarms, showFoodBanks]);

  const clearFarmCorridorLayer = useCallback(() => {
    if (!map.current) return;
    try {
      if (map.current.getLayer(FARM_CORRIDOR_FILL_LAYER)) map.current.removeLayer(FARM_CORRIDOR_FILL_LAYER);
      if (map.current.getLayer(FARM_CORRIDOR_LINE_LAYER)) map.current.removeLayer(FARM_CORRIDOR_LINE_LAYER);
      if (map.current.getSource(FARM_CORRIDOR_SOURCE)) map.current.removeSource(FARM_CORRIDOR_SOURCE);
    } catch (err) {
      console.warn('clearFarmCorridorLayer', err);
    }
  }, []);

  const drawFarmCorridorLayer = useCallback((lineCoords, radiusMiles) => {
    if (!map.current || Platform.OS !== 'web') return;
    if (!Array.isArray(lineCoords) || lineCoords.length < 2) {
      clearFarmCorridorLayer();
      return;
    }
    try {
      const line = turf.lineString(lineCoords);
      const simplified = turf.simplify(line, { tolerance: 0.02, highQuality: false, mutate: false });
      const buffered = turf.buffer(simplified, clampFarmRadiusMiles(radiusMiles), { units: 'miles' });
      if (!buffered) {
        clearFarmCorridorLayer();
        return;
      }
      const data = { type: 'FeatureCollection', features: [buffered] };
      const existing = map.current.getSource(FARM_CORRIDOR_SOURCE);
      if (existing) {
        existing.setData(data);
      } else {
        map.current.addSource(FARM_CORRIDOR_SOURCE, { type: 'geojson', data });
        map.current.addLayer({
          id: FARM_CORRIDOR_FILL_LAYER,
          type: 'fill',
          source: FARM_CORRIDOR_SOURCE,
          paint: {
            'fill-color': LAYER_COLOR.farm,
            'fill-opacity': 0.12,
          },
        });
        map.current.addLayer({
          id: FARM_CORRIDOR_LINE_LAYER,
          type: 'line',
          source: FARM_CORRIDOR_SOURCE,
          paint: {
            'line-color': LAYER_COLOR.farm,
            'line-width': 1.5,
            'line-opacity': 0.55,
            'line-dasharray': [2, 1],
          },
        });
      }
    } catch (err) {
      console.warn('drawFarmCorridorLayer', err);
    }
  }, [clearFarmCorridorLayer]);

  const persistFoodStopToRider = useCallback(async (hub, role, day) => {
    const snippet = riderFoodSnippetHtml(hub, {
      role,
      milesFromRoute: hub.milesFromRoute,
    });
    const dateKey = String(day?.scheduledDate || '').trim();
    const tourId = currentTourIdRef.current;
    const uid = auth.currentUser?.uid;
    if (!uid || !tourId || !/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return;
    try {
      const db = getFirestore();
      const entryId = v2TabEntryId(tourId, 'RIDER_DAILY', dateKey);
      const riderRef = doc(db, 'users', uid, 'calendarTabEntries', entryId);
      const snap = await getDoc(riderRef);
      const existing = snap.exists() ? getTabEntryDraftText(snap.data()) : '';
      const next = upsertRiderFoodSnippet(existing, snippet, hub.id);
      await setDoc(riderRef, {
        ...buildTabEntryDraftFields({
          tourId,
          tabKey: 'RIDER_DAILY',
          date: dateKey,
          draftText: next,
        }),
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (err) {
      console.warn('persistFoodStopToRider', err);
    }
  }, [auth]);

  const assignFoodHubToDay = useCallback((hub, { route = true, rider = true, role = 'dropoff' } = {}) => {
    if (!hub) return;
    if (!mapGateRef.current(MAP_ACTION.PLAN)) return;
    const foodRole = role === 'pickup' ? 'pickup' : 'dropoff';
    const dayId = activeDayIdRef.current;
    if (route) {
      if (!hasValidMapCoords(hub)) {
        setToast({
          message: 'This food location needs map coordinates before it can be added to the route.',
          type: 'warning',
        });
      } else {
        addVenueToDayRef.current?.(foodStopVenueFromHub(hub, { role: foodRole }), dayId);
      }
    }
    if (rider) {
      const record = foodStopRecordFromHub(hub, { role: foodRole, dayId });
      setRiderFoodAssignments((prev) =>
        prev.some((entry) => entry.id === record.id && entry.foodRole === foodRole)
          ? prev
          : [...prev, record]
      );
      setTourDays((prev) => prev.map((day) => {
        if (day.id !== dayId) return day;
        const existing = Array.isArray(day.foodStops) ? day.foodStops : [];
        if (existing.some((entry) => entry.id === record.id && entry.foodRole === foodRole)) return day;
        return { ...day, foodStops: [...existing, record] };
      }));
      const day = tourDays.find((d) => d.id === dayId);
      persistFoodStopToRider(hub, foodRole, day);
      setToast({
        message: route
          ? `${hub.name} added as ${foodRole === 'pickup' ? 'pickup' : 'food dropoff'} (route + rider).`
          : `${hub.name} added to the rider.`,
        type: 'success',
      });
    } else if (route) {
      setToast({
        message: `${hub.name} added as ${foodRole === 'pickup' ? 'pickup' : 'food dropoff'}.`,
        type: 'success',
      });
    }
  }, [persistFoodStopToRider, tourDays]);
  assignFoodHubRef.current = assignFoodHubToDay;

  useEffect(() => {
    if (!farmCorridorActive) {
      clearFarmCorridorLayer();
      setFarmCorridorHubs([]);
      setFarmCorridorMessage('');
      setFarmCorridorLoading(false);
      return undefined;
    }
    if (!mapLoaded) return undefined;

    const runSearch = async () => {
      const token = (farmSearchTokenRef.current += 1);
      const geometries = collectTourRouteGeometries(tourDays, interDayLegModes, routeCache.current, {
        dayId: resolveFarmSearchDayId(tourDays, farmSearchDayId),
        legId: farmSearchLegId || 'all',
      });
      const lineCoords = collectRouteLineCoordinates(geometries);
      const radius = clampFarmRadiusMiles(farmSearchRadiusMiles);
      drawFarmCorridorLayer(lineCoords, radius);
      const first = lineCoords[0] || [];
      const last = lineCoords[lineCoords.length - 1] || [];
      const toastRouteKey = [lineCoords.length, first[0], first[1], last[0], last[1], radius].join('|');
      const toastOnce = (outcome, toast) => {
        const key = `${toastRouteKey}|${outcome}`;
        if (farmCorridorToastKeyRef.current === key) return;
        farmCorridorToastKeyRef.current = key;
        setToast(toast);
      };

      if (lineCoords.length < 2) {
        setFarmCorridorHubs([]);
        setFarmCorridorLoading(false);
        setFarmCorridorMessage('Add at least two stops so we can search along the route.');
        return;
      }

      setFarmCorridorLoading(true);
      setFarmCorridorMessage('');
      const directoryHubs = filterHubsAlongRoute(
        foodDirectoryItems.map(approvedLocationToFoodHub).filter(Boolean),
        lineCoords,
        radius,
      );
      setFarmCorridorHubs(directoryHubs);
      if (directoryHubs.length) {
        setFarmCorridorMessage('');
      }
      try {
        const callServer = async (payload) => {
          const call = httpsCallable(functions, 'searchFoodCorridor', { timeout: 150000 });
          const res = await call(payload);
          return res?.data;
        };
        const { hubs: osmHubs, partial } = await searchFoodHubsAlongRoute(lineCoords, radius, { callServer });
        if (token !== farmSearchTokenRef.current) return;
        const alongOsm = filterHubsAlongRoute(osmHubs, lineCoords, radius);
        setFarmCorridorHubs(mergeFoodHubs([directoryHubs, alongOsm]));
        setFarmCorridorMessage(alongOsm.length || directoryHubs.length
          ? ''
          : 'No named farms or community fridges on OpenStreetMap in this corridor. Saved hubs still appear if they have map coordinates.');
        if (partial) {
          toastOnce('partial', {
            message: 'Part of the route could not be searched for farms and fridges. Showing what loaded.',
            type: 'warning',
          });
        } else {
          farmCorridorToastKeyRef.current = '';
        }
      } catch (err) {
        if (token !== farmSearchTokenRef.current) return;
        console.warn('Farm corridor search failed', err);
        setFarmCorridorHubs(directoryHubs);
        const failMessage = directoryHubs.length
          ? 'Live farm search failed. Showing saved food hubs on the route.'
          : 'Could not load farms and fridges along the route. Try again in a moment.';
        setFarmCorridorMessage(failMessage);
        toastOnce('failed', { message: failMessage, type: 'error' });
      } finally {
        if (token === farmSearchTokenRef.current) setFarmCorridorLoading(false);
      }
    };

    if (farmRadiusDebounceRef.current) clearTimeout(farmRadiusDebounceRef.current);
    farmRadiusDebounceRef.current = setTimeout(runSearch, 400);
    return () => {
      if (farmRadiusDebounceRef.current) clearTimeout(farmRadiusDebounceRef.current);
    };
  }, [
    farmCorridorActive,
    farmSearchRadiusMiles,
    farmSearchDayId,
    farmSearchLegId,
    tourDays,
    interDayLegModes,
    foodDirectoryItems,
    mapLoaded,
    drawFarmCorridorLayer,
    clearFarmCorridorLayer,
  ]);

  // ── Food hub markers ────────────────────────────────────────────────
  // Farms (pickup) and Food Banks (drop off) render as DOM markers rather than
  // a GeoJSON layer: the set is small, and reusing the marker path leaves the
  // venue source untouched so clustering and venue filters are unaffected.
  useEffect(() => {
    if (!masterTourUi) return undefined;
    if (Platform.OS !== 'web' || !map.current || !mapLoaded) return;

    const markers = [];

    // Pins -> Hide only hides venue pins (its hint says so); farm, fridge, and
    // food-bank pins stay on so the corridor never draws empty.
    visibleFoodHubs.forEach((item) => {
      const lat = Number(item.latitude);
      const lng = Number(item.longitude);
      if (!hasValidMapCoords({ latitude: lat, longitude: lng })) return;

      const pin = (item.kind === 'dropoff' || (item.dropoff && !item.pickup))
        ? FOOD_HUB_PIN.dropoff
        : FOOD_HUB_PIN.pickup;
      const safeName = String(item.name || 'Food hub').replace(/[<>&]/g, '');
      const milesLabel = Number.isFinite(Number(item.milesFromRoute))
        ? `${Number(item.milesFromRoute).toFixed(1)} mi off route`
        : '';

      const el = document.createElement('div');
      el.className = 'vrc-food-marker';
      el.setAttribute('title', safeName + ' \u2014 ' + pin.label);
      el.style.cssText = [
        'width:26px', 'height:26px', 'border-radius:50%',
        'display:flex', 'align-items:center', 'justify-content:center',
        'background:' + pin.color,
        'box-shadow:0 0 0 3px rgba(244,244,242,0.9)',
        'cursor:pointer', 'font-size:15px', 'font-weight:700', 'line-height:1', 'color:#fffffe',
      ].join(';');
      el.textContent = pin.glyph;

      const hubId = String(item.id || '').replace(/\\/g, '\\\\').replace(/'/g, "\\'");
      const popupHtml =
        '<div style="font-family:\'Space Grotesk\',Helvetica,sans-serif;font-size:13px;min-width:180px">' +
        '<strong>' + safeName + '</strong><br/>' +
        '<span style="font-family:\'IBM Plex Mono\',monospace;font-size:10px;' +
        'letter-spacing:0.08em;text-transform:uppercase;color:#7a7a75">' + pin.label +
        (milesLabel ? ' · ' + milesLabel : '') + '</span>' +
        '<div style="display:flex;flex-direction:column;gap:6px;margin-top:10px">' +
        '<button type="button" onclick="window.assignFoodHubFromMap && window.assignFoodHubFromMap(\'' + hubId + '\',\'dropoff\')" ' +
        'style="padding:8px 10px;border:none;border-radius:6px;background:#e67e22;color:#fff;font-weight:700;cursor:pointer;font-size:12px">' +
        'Add as food dropoff</button>' +
        (item.pickup
          ? '<button type="button" onclick="window.assignFoodHubFromMap && window.assignFoodHubFromMap(\'' + hubId + '\',\'pickup\')" ' +
            'style="padding:7px 10px;border:1px solid rgba(0,0,0,0.12);border-radius:6px;background:transparent;color:#333;font-weight:600;cursor:pointer;font-size:12px">' +
            'Add as farm pickup</button>'
          : '') +
        '</div></div>';

      markers.push(
        new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat([lng, lat])
          .setPopup(new mapboxgl.Popup({ offset: 16, closeButton: false }).setHTML(popupHtml))
          .addTo(map.current)
      );
    });

    window.assignFoodHubFromMap = (hubId, role) => {
      const hub = visibleFoodHubs.find((item) => String(item.id) === String(hubId))
        || farmCorridorHubs.find((item) => String(item.id) === String(hubId))
        || foodDirectoryItems.find((item) => String(item.id) === String(hubId) || `dir:${item.id}` === String(hubId));
      if (!hub) {
        setToast({ message: 'Could not find that food location.', type: 'warning' });
        return;
      }
      assignFoodHubRef.current?.(hub, { route: true, rider: true, role });
    };

    return () => {
      markers.forEach((m) => m.remove());
      if (window.assignFoodHubFromMap) delete window.assignFoodHubFromMap;
    };
  }, [visibleFoodHubs, farmCorridorHubs, foodDirectoryItems, mapLoaded, masterTourUi]);

  // Shared by the Resources and Event Flyers sections (flyers are resource submissions).
  const toggleResourceDirectoryItem = (item) => {
    if (item.type === 'merch') {
      setShowScreenprinters(false);
      setSelectedScreenprinterIds((prev) =>
        prev.includes(item.sourceId)
          ? prev.filter((id) => id !== item.sourceId)
          : [...prev, item.sourceId]
      );
      return;
    }
    if (item.type === 'venue') {
      setSelectedResourceVenueIds((prev) =>
        prev.includes(item.sourceId)
          ? prev.filter((id) => id !== item.sourceId)
          : [...prev, item.sourceId]
      );
      return;
    }
    const alreadySelected = selectedResourceSubmissionIds.includes(item.sourceId);
    if (alreadySelected) {
      setSelectedResourceSubmissionIds((prev) =>
        prev.filter((id) => id !== item.sourceId)
      );
      setLoadingResourceDirectoryIds((prev) =>
        prev.filter((id) => id !== item.id)
      );
      return;
    }
    setSelectedResourceSubmissionIds((prev) => [...prev, item.sourceId]);
    if (item.scope === 'city' || item.scope === 'state') {
      setLoadingResourceDirectoryIds((prev) =>
        prev.includes(item.id) ? prev : [...prev, item.id]
      );
    }
  };

  const renderMapSideRail = () => (
    <MapSideRail
      routeRadius={routeRadiusProp}
      onSectionChange={setActiveRailItemId}
      isDarkMode={isDarkMode}
      isMobile={isMobile}
      side="left"
      fullscreenHostRef={mapStageRef}
      openRequest={mapSideRailOpenRequest}
      onOpenRequestHandled={() => setMapSideRailOpenRequest(null)}
      venueCriteria={{
        filters,
        affiliateChipColor,
        searchQuery,
        onSearchChange: setSearchQuery,
        onToggleAffiliate: (val) => {
          setFilters((prev) => ({
            ...prev,
            affiliate: prev.affiliate?.includes(val)
              ? prev.affiliate.filter((a) => a !== val)
              : [...(prev.affiliate || []), val],
          }));
        },
        onToggleFeature: (value) => {
          setFilters((prev) => ({
            ...prev,
            features: prev.features?.includes(value)
              ? prev.features.filter((f) => f !== value)
              : [...(prev.features || []), value],
          }));
        },
        onDeselectAll: () => {
          setFilters((prev) => ({
            ...prev,
            affiliate: [],
            features: [],
          }));
        },
      }}
      resourceDirectory={{
        items: resourceDirectoryItems,
        categories: [
          ...resourceCategoryCatalog,
          ...VENUE_DERIVED_RESOURCE_CATEGORIES.map(({ id, label }) => ({ id, label })),
        ],
        venueOptions: venues,
        selectedIds: selectedResourceDirectoryIds,
        loadingIds: loadingResourceDirectoryIds,
        onToggleItem: toggleResourceDirectoryItem,
        onKeepSelectedIds: (keepIds) => {
          const keep = new Set(keepIds);
          setSelectedScreenprinterIds((prev) =>
            prev.filter((id) => keep.has(`merch:${id}`))
          );
          setSelectedResourceSubmissionIds((prev) =>
            prev.filter((id) => keep.has(`resource:${id}`))
          );
          setSelectedResourceVenueIds((prev) =>
            prev.filter((id) => keep.has(`venue:${id}`))
          );
          setLoadingResourceDirectoryIds((prev) =>
            prev.filter((id) => keep.has(id))
          );
          setShowScreenprinters(false);
        },
        onDeselectAll: () => {
          setSelectedScreenprinterIds([]);
          setSelectedResourceSubmissionIds([]);
          setSelectedResourceVenueIds([]);
          setLoadingResourceDirectoryIds([]);
          setShowScreenprinters(false);
        },
        onSubmitResource: submitMapResource,
        onSubmitMerch: submitMapMerch,
      }}
      travel={{
        // Calendar only: the search box finds place names along the selected day's route.
        onSearchPlaces: calendarEmbed ? searchTravelPlacesAlongRoute : null,
        onSelectPlace: focusTravelPlace,
        pinModeCategories: POI_PIN_MODE_CATEGORIES,
        activePoiCategories,
        poiMeta: POI_CATEGORY_META,
        onTogglePoiCategory: (type) => {
          setActivePoiCategories((prev) => (
            prev.includes(type) ? prev.filter((category) => category !== type) : [...prev, type]
          ));
        },
        onDeselectPoiGroup: (categories) => {
          const categorySet = new Set(categories || []);
          setActivePoiCategories((prev) =>
            prev.filter((category) => !categorySet.has(category))
          );
        },
        loadingPOIs,
        showBusStops,
        setShowBusStops,
        showTrainStops,
        setShowTrainStops,
        showAirports,
        setShowAirports,
        showBikeLanes,
        setShowBikeLanes,
        ecoRouteColors,
        setEcoRouteColors,
        bikeLanesLoading,
        onDeselectTransit: () => {
          setShowBusStops(false);
          setShowTrainStops(false);
          setShowAirports(false);
          setShowBikeLanes(false);
          setEcoRouteColors(false);
        },
        restrictToTourRoute: restrictPoisToTourRoute,
        onToggleRouteOnly: () => {
          setRestrictPoisToTourRoute((prev) => {
            const next = !prev;
            setTimeout(() => fetchPOIsRef.current?.(), 0);
            return next;
          });
        },
        onSubmitTravel: submitTravelLocation,
      }}
      learn={{
        venues,
        selectedIds: selectedLearnVenueIds,
        onToggleVenue: (venue) => {
          const id = String(venue.id);
          setSelectedLearnVenueIds((prev) => (
            prev.includes(id) ? prev.filter((entry) => entry !== id) : [...prev, id]
          ));
          if (
            map.current &&
            Number.isFinite(Number(venue.latitude ?? venue.lat)) &&
            Number.isFinite(Number(venue.longitude ?? venue.lng))
          ) {
            map.current.easeTo({
              center: [Number(venue.longitude ?? venue.lng), Number(venue.latitude ?? venue.lat)],
              zoom: Math.max(map.current.getZoom?.() || 0, 10),
              duration: 650,
            });
          }
        },
        onDeselectAll: () => setSelectedLearnVenueIds([]),
        onSubmitEducation: submitEducationLocation,
      }}
      flyers={{
        venueOptions: venues,
        items: resourceDirectoryItems.filter((item) => item.categoryId === 'event_flyer'),
        selectedIds: selectedResourceDirectoryIds,
        onToggleItem: toggleResourceDirectoryItem,
        onSubmitFlyer: submitEventFlyer,
      }}
      food={{
        items: visibleFoodHubs,
        onSubmitFood: submitFoodLocation,
        onAssignFood: (item, targets) => {
          assignFoodHubToDay(item, {
            route: Boolean(targets.route),
            rider: Boolean(targets.rider),
            role: targets.role === 'pickup' ? 'pickup' : 'dropoff',
          });
        },
        riderAssignedIds: riderFoodAssignments.map((item) => item.id),
        corridorRadiusMiles: farmSearchRadiusMiles,
        onCorridorRadiusChange: setFarmSearchRadiusMiles,
        corridorActive: farmCorridorActive,
        corridorLoading: farmCorridorLoading,
        corridorMessage: farmCorridorMessage,
        corridorCount: visibleFoodHubs.length,
        routeDays: farmRouteDays,
        selectedDayId: resolveFarmSearchDayId(tourDays, farmSearchDayId || activeDayId),
        selectedLegId: farmSearchLegId || 'all',
        onSelectDay: (dayId) => {
          setFarmSearchDayId(dayId);
          setFarmSearchLegId('all');
          setActiveDayId(dayId);
        },
        onSelectLeg: (legId) => {
          setFarmSearchLegId(legId);
          setFarmSearchDayId((prev) => resolveFarmSearchDayId(tourDays, prev || activeDayIdRef.current));
        },
        hideDayPicker: calendarEmbed,
        onFoodModeChange: (tab) => {
          setShowFarms(tab === 'pickup');
          setShowFoodBanks(tab === 'dropoff');
          setFarmSearchDayId((prev) => resolveFarmSearchDayId(tourDays, prev || activeDayIdRef.current));
          setFarmSearchLegId((prev) => prev || 'all');
        },
      }}
      directory={{
        venueOptions: venues,
        selectedIds: selectedDirectoryPostPins.map((pin) => pin.id),
        onToggleItem: (item) => {
          const alreadySelected = selectedDirectoryPostPins.some((pin) => pin.id === item.id);
          if (alreadySelected) {
            setSelectedDirectoryPostPins((prev) => prev.filter((pin) => pin.id !== item.id));
            return;
          }
          setSelectedDirectoryPostPins((prev) => [...prev, item]);
          if (
            map.current &&
            Number.isFinite(Number(item.latitude)) &&
            Number.isFinite(Number(item.longitude))
          ) {
            map.current.easeTo({
              center: [Number(item.longitude), Number(item.latitude)],
              zoom: Math.max(map.current.getZoom?.() || 0, 10),
              duration: 650,
            });
          }
        },
        onKeepSelectedIds: (keepIds) => {
          const keep = new Set(keepIds);
          setSelectedDirectoryPostPins((prev) => prev.filter((pin) => keep.has(pin.id)));
        },
      }}
      onSubmitVenue={submitNewVenue}
      onEmbedLocationPreview={previewEmbedLocation}
      onClearEmbedLocationPreview={clearEmbedLocationPreview}
    />
  );

  // REQ-104: Filter / Video buttons beside Pins / Hide, and the Filter drawer over the map canvas.
  // Drawer picks go to a draft (live count) and reach `filters` on "Show"; Video applies at once.
  const renderVenueFilterControls = () => {
    if (Platform.OS !== 'web') return null;
    const railWidth = isMobile ? MAP_SIDE_RAIL_MOBILE_WIDTH : MAP_SIDE_RAIL_WIDTH;
    if (masterTourUi && !mapDisplayModeRailBox) return null; // wait for Pins / Hide to measure
    // Beside Pins / Hide on desktop; on phones the top-right Map / Tours pill leaves no room, so a second row.
    const besidePill = masterTourUi && !isMobile;
    const left = railWidth + 10 + (besidePill ? mapDisplayModeRailBox.width + 6 : 0);
    const top = masterTourUi && isMobile ? 12 + mapDisplayModeRailBox.height + 6 : 12;
    const layers = visibleMapLayers(MAP_LAYERS);
    const { draft } = venueFilterDrawer;
    let resultCount = 0;
    if (draft) {
      // Same steps as the filteredVenues effect + the calendar radius + search, so "Show N" matches the map.
      const deps = [JSON.stringify(draft), searchQuery, venues, venueFilterCtx, calendarEmbed, calendarDayRouteLine,
        coordinateSourceFilter, selectedResourceSubmissionIds, selectedResourceVenueIds, approvedResourceSubmissions];
      const cached = venueFilterCountCacheRef.current;
      if (!cached.deps || cached.deps.length !== deps.length || deps.some((d, i) => d !== cached.deps[i])) {
        let matches = applyVenueFilters(venues, draft, venueFilterCtx);
        const pickedSubmissions = approvedResourceSubmissions.filter((submission) =>
          selectedResourceSubmissionIds.includes(submission.id) && submission.scope !== 'city' && submission.scope !== 'state');
        if (pickedSubmissions.length > 0 || selectedResourceVenueIds.length > 0) {
          matches = matches.filter((venue) => selectedResourceVenueIds.includes(String(venue.id))
            || pickedSubmissions.some((submission) => resourceSubmissionAppliesToVenue(submission, venue)));
        }
        if (coordinateSourceFilter !== 'all') matches = matches.filter((v) => v._coordinateSource === coordinateSourceFilter);
        if (calendarEmbed && calendarDayRouteLine.length) matches = withinCalendarRouteRadius(matches, 'venues');
        venueFilterCountCacheRef.current = { deps, count: filterVenuesBySearch(matches, searchQuery).length };
      }
      resultCount = venueFilterCountCacheRef.current.count;
    }
    const drawerGroupIds = VENUE_FILTER_GROUPS.map((g) => g.id);
    return (
      <>
        <MapQuickControls
          layers={layers}
          activeLayerIds={activeMapLayerIds(filters, layers, radioLayerOn ? ['radio'] : [])}
          onToggleLayer={(layer) => {
            if (layer.id === 'radio') {
              setRadioLayerOn((on) => !on);
              return;
            }
            if (layer.kind !== 'venueFilter') return;
            const turningOn = !(filters[layer.filter.group] || []).includes(layer.filter.value);
            if (turningOn && displayMode === 'hidden') setDisplayMode('pins');
            setFilters((prev) => toggleFilterValue(prev, layer.filter.group, layer.filter.value));
          }}
          filterCount={countActiveFilters(filters)}
          filterOpen={venueFilterDrawer.isOpen}
          onOpenFilter={venueFilterDrawer.begin}
          filterButtonRef={venueFilterButtonRef}
          isDarkMode={isDarkMode}
          isMobile={isMobile}
          left={left}
          top={top}
        />
        <VenueFilterDrawer
          open={venueFilterDrawer.isOpen}
          onClose={venueFilterDrawer.discard}
          groups={VENUE_FILTER_GROUPS}
          filters={draft}
          onChange={venueFilterDrawer.setDraft}
          onClear={() => venueFilterDrawer.setDraft((prev) => clearVenueFilters(prev, ['capacity', ...drawerGroupIds]))}
          resultCount={resultCount}
          onApply={() => {
            if (displayMode === 'hidden' && draft && countActiveFilters(draft) > 0) setDisplayMode('pins');
            venueFilterDrawer.apply();
          }}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          isDarkMode={isDarkMode}
          isMobile={isMobile}
          left={railWidth}
          returnFocusRef={venueFilterButtonRef}
        />
      </>
    );
  };

  const renderMapSideTools = () => {
    const rightPanelOpen = Boolean(rightRailTab)
      || (tourStopsVisible && rightRailTab !== 'map' && rightRailTab !== 'tours' && rightRailTab !== 'funds');
    // Mobile: hug the map edge. When Map/Tours is open it fills past the left rail;
    // tools stay near the right edge (above the panel via z-index).
    const toolsRight = isMobile
      ? 4
      : (rightPanelOpen ? 10 + RIGHT_RAIL_PANEL_WIDTH + 10 : 12);
    const iconColor = isDarkMode ? 'rgba(255,255,255,0.88)' : 'rgba(0,0,0,0.72)';
    const iconSize = isMobile ? 16 : 20;
    const locateSize = isMobile ? 14 : 18;

    const zoomBy = (delta) => {
      if (!map.current) return;
      map.current.easeTo({
        zoom: (map.current.getZoom?.() ?? 3) + delta,
        duration: 280,
      });
    };

    return (
      <View
        style={[styles.mapSideTools, isMobile && styles.mapSideToolsMobile, { right: toolsRight }]}
        pointerEvents="box-none"
      >
        <View style={[styles.mapSideToolsCluster, isMobile && styles.mapSideToolsClusterMobile]}>
          {!isMobile ? (
            <TouchableOpacity
              style={[
                styles.mapSideToolBtn,
                isMapFullscreen && styles.mapSideToolBtnActive,
              ]}
              onPress={toggleMapFullscreen}
              accessibilityLabel={isMapFullscreen ? 'Exit fullscreen map' : 'Expand map to fullscreen'}
              accessibilityState={{ selected: isMapFullscreen }}
            >
              <Ionicons
                name={isMapFullscreen ? 'contract' : 'expand'}
                size={18}
                color={isMapFullscreen ? '#e67e22' : iconColor}
              />
            </TouchableOpacity>
          ) : null}
          <TouchableOpacity
            style={[
              styles.mapSideToolBtn,
              isMobile && styles.mapSideToolBtnMobile,
              is3DMode && styles.mapSideToolBtnActive,
            ]}
            onPress={toggle3DMode}
            accessibilityLabel="Toggle 3D view"
          >
            <Text
              style={[
                styles.mapSideToolLabel,
                isMobile && styles.mapSideToolLabelMobile,
                is3DMode && styles.mapSideToolLabelActive,
              ]}
            >
              3D
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.mapSideToolBtn, isMobile && styles.mapSideToolBtnMobile]}
            onPress={() => zoomBy(1)}
            accessibilityLabel="Zoom in"
          >
            <Ionicons name="add" size={iconSize} color={iconColor} />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.mapSideToolBtn, isMobile && styles.mapSideToolBtnMobile]}
            onPress={() => zoomBy(-1)}
            accessibilityLabel="Zoom out"
          >
            <Ionicons name="remove" size={iconSize} color={iconColor} />
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.mapSideToolBtn,
              isMobile && styles.mapSideToolBtnMobile,
              userLocation && styles.mapSideToolBtnActive,
            ]}
            onPress={requestUserLocation}
            accessibilityLabel="Go to my location"
          >
            <Ionicons
              name={userLocation ? 'navigate' : 'navigate-outline'}
              size={locateSize}
              color={userLocation ? '#e67e22' : iconColor}
            />
          </TouchableOpacity>
        </View>
        <TouchableOpacity
          style={[
            styles.mapSideEcoBtn,
            isMobile && styles.mapSideEcoBtnMobile,
            showEcoRing && styles.mapSideEcoBtnActive,
          ]}
          onPress={() => setShowEcoRing((v) => !v)}
          accessibilityLabel="Toggle eco ring"
          accessibilityState={{ selected: showEcoRing }}
        >
          <Text
            style={[
              styles.mapSideEcoText,
              isMobile && styles.mapSideEcoTextMobile,
              showEcoRing && styles.mapSideEcoTextActive,
            ]}
          >
            {'ECO\nRING'}
          </Text>
        </TouchableOpacity>
      </View>
    );
  };

  const renderRightMapToursRail = () => {
    let styleName = '';
    try { styleName = map.current?.getStyle?.()?.name?.toLowerCase?.() ?? ''; } catch (_) {}
    const isSatellite = mapLoaded && styleName.includes('satellite');
    const isDarkMap = mapLoaded && styleName.includes('dark');
    const is3D = mapLoaded && (map.current?.getPitch?.() ?? 0) > 0;
    const basemapActive = is3D || is3DMode ? '3d' : isSatellite ? 'satellite' : isDarkMap ? 'dark' : 'streets';

    const handleBasemapChange = (key) => {
      if (!map.current) return;
      if (key === '3d') {
        if (!is3DMode) toggle3DMode();
        return;
      }
      if (key === 'satellite') {
        if (!isSatellite) {
          if (isDarkMap) toggleDisplayMode('dark');
          toggleDisplayMode('satellite');
        }
        return;
      }
      if (key === 'dark') {
        if (!isDarkMap) {
          if (isSatellite) toggleDisplayMode('satellite');
          toggleDisplayMode('dark');
        }
        return;
      }
      if (key === 'streets') {
        if (isSatellite) toggleDisplayMode('satellite');
        else if (isDarkMap) toggleDisplayMode('dark');
        if (is3DMode) toggle3DMode();
      }
    };

    return (
      <RightMapToursRail
        isDarkMode={isDarkMode}
        isMobile={isMobile}
        selectedTab={rightRailTab}
        onSelectTab={setRightRailTab}
        rightOffset={10}
        tourPlannerOpen={tourStopsVisible}
        mapViewMode={mapViewMode}
        onMapViewModeChange={setMapViewMode}
        displayMode={displayMode}
        onDisplayModeChange={setDisplayMode}
        masterTourUi={masterTourUi}
        concentrationType={concentrationType}
        onConcentrationTypeChange={(key) => {
          setConcentrationType(key);
          setShowConcentrationSettings(true);
        }}
        showConcentrationSettings={showConcentrationSettings}
        onToggleConcentrationSettings={() => setShowConcentrationSettings((v) => !v)}
        concentrationBucketsOn={concentrationBucketsOn}
        onConcentrationBucketsChange={setConcentrationBucketsOn}
        basemapActive={basemapActive}
        onBasemapChange={handleBasemapChange}
        onLocate={requestUserLocation}
        onRecenter={recenterMap}
        hasUserLocation={Boolean(userLocation)}
        savedTours={savedTours}
        currentTourId={currentTourId}
        onCreateTour={() => {
          if (!mapGateRef.current(MAP_ACTION.PLAN)) return;
          if (currentTourId || ((tourName && tourName !== 'My Tour') || tourDays[0].venues.length > 0)) {
            setConfirmNewTourVisible(true);
          } else {
            openNewTourModal();
          }
        }}
        onLoadTour={async (tourId) => {
          await loadTour(tourId);
          openTourPlanner();
        }}
      />
    );
  };

  const renderOverlayMenu = () => null;

  // Add draw instructions component
  const renderDrawInstructions = () => {
    if (mapMode !== 'draw') return null;

    return (
      <View style={styles.drawInstructions}>
        <Text style={styles.drawInstructionsText}>
          1. Click the polygon icon in the top right to start drawing{'\n'}
          2. Click on the map to place points{'\n'}
          3. Connect back to the first point to complete{'\n'}
          4. Use the trash icon to clear and start over
        </Text>
      </View>
    );
  };

  // Add state for drawing status
  const [isDrawing, setIsDrawing] = useState(false);

  // Add draw mode controls component
  const renderDrawControls = () => {
    if (mapMode !== 'draw') return null;

    return (
      <View style={styles.drawModeContainer}>
        <Text style={styles.drawModeInstructions}>
          {isDrawing
            ? "Click on the map to place points. Connect back to the first point to complete the boundary."
            : "Click 'Start Drawing' to begin creating a boundary. Only venues within the boundary will be shown."}
        </Text>
        <TouchableOpacity
          style={[styles.drawButton, isDrawing && styles.drawButtonActive]}
          onPress={() => {
            if (draw) {
              if (isDrawing) {
                draw.trash();
                setIsDrawing(false);
              } else {
                draw.changeMode('draw_polygon');
                setIsDrawing(true);
              }
            }
          }}
        >
          <Text style={styles.drawButtonText}>
            {isDrawing ? 'Cancel Drawing' : 'Start Drawing'}
          </Text>
        </TouchableOpacity>
        {drawBoundary && (
          <TouchableOpacity
            style={styles.clearButton}
            onPress={() => {
              if (draw) {
                draw.deleteAll();
                setDrawBoundary(null);
                updateVenuesOnMap(venues);
                setIsDrawing(false);
              }
            }}
          >
            <Text style={styles.clearButtonText}>Clear Boundary</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  // Update map event handlers
  useEffect(() => {
    if (!map.current || !draw) return;

    const handleDrawCreate = (e) => {
      if (mapMode === 'draw') {
        setDrawBoundary(e.features[0]);
        const polygon = turf.polygon(e.features[0].geometry.coordinates);
        const filteredVenues = venues.filter(venue => {
          if (!venue.latitude || !venue.longitude) return false;
          const point = turf.point([venue.longitude, venue.latitude]);
          return turf.booleanPointInPolygon(point, polygon);
        });
        updateVenuesOnMap(filteredVenues);
        setIsDrawing(false);
      }
    };

    const handleDrawCancel = () => {
      setIsDrawing(false);
    };

    map.current.on('draw.create', handleDrawCreate);
    map.current.on('draw.cancel', handleDrawCancel);

    return () => {
      if (map.current) {
        map.current.off('draw.create', handleDrawCreate);
        map.current.off('draw.cancel', handleDrawCancel);
      }
    };
  }, [map.current, draw, mapMode, venues]);

  // Add new state for tour management
  const [savedTours, setSavedTours] = useState([]);
  const [currentTourId, setCurrentTourId] = useState(null);
  currentTourIdRef.current = currentTourId;
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importFoldersList, setImportFoldersList] = useState([]);
  const [importLoading, setImportLoading] = useState(false);

  // Confirm "new tour" popup when there are unsaved changes (replaces Alert)
  const [confirmNewTourVisible, setConfirmNewTourVisible] = useState(false);
  const [openHeaderMenu, setOpenHeaderMenu] = useState(null); // 'folder' | 'envelope' | null
  const [openSidebarMenu, setOpenSidebarMenu] = useState(null); // 'folder' | 'envelope' | null
  const [exportingTourPdf, setExportingTourPdf] = useState(false);

  const { members: tourPartyMembers } = useTourPartyMembers({
    ownerId: userData?.uid || auth.currentUser?.uid,
    ownerName: userData?.name || userData?.username,
    ownerEmail: userData?.email,
    ownerPhotoURL: userData?.photoURL,
    tourId: currentTourId,
  });

  const toggleDayLodgingVenue = useCallback(
    (dayIndex, venueId) => {
      setTourDays((prev) => {
        const day = prev[dayIndex];
        if (!day) return prev;
        const nextId = String(day.lodgingVenueId || '') === String(venueId) ? null : venueId;
        const patch = { lodgingVenueId: nextId };
        const uid = auth.currentUser?.uid;
        if (uid && currentTourId) {
          updateTourDayFields(uid, currentTourId, dayIndex, patch).catch((err) => {
            showPopup(err?.message || 'Could not save lodging.', 'error');
          });
        }
        return prev.map((d, i) => (i === dayIndex ? { ...d, ...patch } : d));
      });
    },
    [currentTourId, showPopup]
  );

  const toggleDayStopParticipation = useCallback(
    (dayIndex, stopKey) => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      setTourDays((prev) => {
        const day = prev[dayIndex];
        if (!day) return prev;
        const map = { ...(day.stopParticipation || {}) };
        const current = getStopParticipants(day, stopKey);
        const joined = current.includes(uid);
        map[stopKey] = joined ? current.filter((id) => id !== uid) : [...current, uid];
        const patch = { stopParticipation: map };
        if (currentTourId) {
          updateTourDayFields(uid, currentTourId, dayIndex, patch).catch((err) => {
            showPopup(err?.message || 'Could not save participation.', 'error');
          });
        }
        return prev.map((d, i) => (i === dayIndex ? { ...d, ...patch } : d));
      });
    },
    [currentTourId, showPopup]
  );

  // Import setup modal: when importing a folder with no tour, user can split venues into days before applying
  const [importSetupModalVisible, setImportSetupModalVisible] = useState(false);
  const [importSetupDays, setImportSetupDays] = useState([]); // array of arrays of venue objects

  // New Tour setup modal: name + start/end locations (venue search) + number of days
  const [newTourModalVisible, setNewTourModalVisible] = useState(false);
  const [newTourName, setNewTourName] = useState('');
  const [newTourStartSearchQuery, setNewTourStartSearchQuery] = useState('');
  const [newTourStartVenue, setNewTourStartVenue] = useState(null);
  const [newTourStartDropdownVisible, setNewTourStartDropdownVisible] = useState(false);
  const [newTourEndSearchQuery, setNewTourEndSearchQuery] = useState('');
  const [newTourEndVenue, setNewTourEndVenue] = useState(null);
  const [newTourEndDropdownVisible, setNewTourEndDropdownVisible] = useState(false);
  const [newTourNumberOfDays, setNewTourNumberOfDays] = useState('3');
  const [newTourStartDate, setNewTourStartDate] = useState('');
  const [newTourEndDate, setNewTourEndDate] = useState('');
  const [newTourLinkedPostId, setNewTourLinkedPostId] = useState(null);
  const [newTourLinkedPost, setNewTourLinkedPost] = useState(null);
  const [userCommunityPosts, setUserCommunityPosts] = useState([]);
  const [loadingUserPosts, setLoadingUserPosts] = useState(false);
  const [tourStartLocation, setTourStartLocation] = useState('');
  const [tourEndLocation, setTourEndLocation] = useState('');
  const [tourEventStartDate, setTourEventStartDate] = useState('');
  const [tourEventEndDate, setTourEventEndDate] = useState('');
  const [tourLinkedPostId, setTourLinkedPostId] = useState(null);
  const [tourLinkedPost, setTourLinkedPost] = useState(null);

  // Add state for toast
  const [toast, setToast] = useState(null);

  // Save tour as a folder: tour = folder with tour data (name, days) stored on the folder
  const saveTour = async () => {
    if (!mapGateRef.current(MAP_ACTION.PLAN)) return;
    const nameToSave = tourName == null ? '' : String(tourName).trim();
    if (!nameToSave) {
      setToast({ message: 'Please enter a tour name', type: 'error' });
      return;
    }

    try {
      setToast({ message: 'Saving tour...', type: 'warning' });
      const db = getFirestore();
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data() || {};
      const existingFolders = userData.folders || {};
      const existingFiles = userData.files || {};
      const folderId = currentTourId || ('t' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8));

      const existing = existingFolders[folderId] || {};
      const nInter = Math.max(0, tourDays.length - 1);
      const VALID_LEG_MODE = (m) => (['train', 'flight', 'bus', 'walk'].includes(m) ? m : 'drive');
      const interModes = Array.from({ length: nInter }, (_, i) =>
        VALID_LEG_MODE((interDayLegModes || [])[i])
      );
      const daysForSave = JSON.parse(JSON.stringify(tourDays));
      daysForSave.forEach((d, i) => {
        const raw = tourDays[i];
        d.legModes = (raw.legModes || []).map((m) => VALID_LEG_MODE(m));
      });
      // Pre-migration: parentId null = library root. Post–tab migration: tour folders use My Tours root.
      const parentIdForTour = getParentIdForSavedTourFolder(userData, existing);
      const parentIdSave = parentIdForTour != null ? parentIdForTour : null;
      const defaultOrder = Object.values(existingFolders).filter((f) => {
        if (!f || f.id === folderId) return false;
        const sameParent =
          parentIdSave == null
            ? f.parentId == null || f.parentId === undefined
            : f.parentId === parentIdSave;
        return sameParent;
      }).length;
      existingFolders[folderId] = {
        ...existing,
        id: folderId,
        name: nameToSave,
        parentId: parentIdSave,
        order: existing.order != null ? existing.order : defaultOrder,
        color: existing.color || '#0bb5df',
        tour: {
          name: nameToSave,
          days: daysForSave,
          interDayLegModes: interModes,
          eventStartDate: tourEventStartDate || null,
          eventEndDate: tourEventEndDate || null,
          linkedPostId: tourLinkedPostId || null,
          linkedPostSummary: tourLinkedPost ? { id: tourLinkedPost.id, postContent: tourLinkedPost.postContent, location: tourLinkedPost.location } : null,
        },
      };

      const tourVenues = tourDays.flatMap(d => d.venues || []);
      const seenIds = new Set();
      const updatedFiles = { ...existingFiles };
      for (const [fileId, file] of Object.entries(updatedFiles)) {
        if (file.parentId === folderId && file.itemType === 'savedVenue') {
          delete updatedFiles[fileId];
        }
      }
      for (const venue of tourVenues) {
        if (!venue || !venue.id || seenIds.has(venue.id)) continue;
        seenIds.add(venue.id);
        const fileId = `${folderId}_${venue.id}`;
        updatedFiles[fileId] = {
          parentId: folderId,
          itemType: 'savedVenue',
          data: {
            id: venue.id,
            name: venue.name || '',
            city: venue.city || '',
            state: venue.state || '',
            latitude: venue.latitude ?? null,
            longitude: venue.longitude ?? null,
            capacity: venue.capacity || '',
            venueType: venue.venueType || '',
          },
        };
      }

      await updateDoc(userRef, { folders: existingFolders, files: updatedFiles });
      if (!currentTourId) setCurrentTourId(folderId);

      // My Calendar does NOT auto-sync here — users add tour days via **Import tour** on My Calendar only.
      setToast({ message: 'Tour saved successfully!', type: 'success' });
      loadSavedTours();
    } catch (error) {
      console.error('Error saving tour:', error);
      setToast({ message: 'Failed to save tour. Please try again.', type: 'error' });
    }
  };

  // Load saved tours from folders that have tour data (tour = folder)
  const loadSavedTours = async () => {
    if (!auth.currentUser) return;

    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      const folders = (userSnap.data() && userSnap.data().folders) || {};
      const tours = Object.entries(folders)
        .filter(([, f]) => f && f.tour)
        .map(([id, f]) => ({ id, name: (f.tour && f.tour.name) || f.name || 'Unnamed Tour' }));
      setSavedTours(tours);
    } catch (error) {
      console.error('Error loading tours:', error);
    }
  };

  // Load a specific tour from folder (tour = folder with .tour data)
  const loadTour = async (tourId) => {
    if (!auth.currentUser) return;
    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      const folders = (userSnap.data() && userSnap.data().folders) || {};
      const folder = folders[tourId];
      if (folder && folder.tour) {
        const { name, days, interDayLegModes: savedInter, eventStartDate, eventEndDate, linkedPostId, linkedPostSummary } = folder.tour;
        const loadedDays = Array.isArray(days) ? days : [{ id: 'day-1', name: 'Day 1', venues: [] }];
        setTourName(name || folder.name || 'My Tour');
        setTourDays(loadedDays);
        setCurrentTourId(tourId);
        const allVenues = (folder.tour.days || []).flatMap(d => d.venues || []);
        setTourVenues(allVenues);
        setRiderFoodAssignments(
          loadedDays.flatMap((day) => (day.foodStops || []).map((stop) => ({ ...stop, dayId: day.id })))
        );
        const nInter = Math.max(0, loadedDays.length - 1);
        const loaded = Array.isArray(savedInter) ? savedInter.slice(0, nInter) : [];
        while (loaded.length < nInter) loaded.push('drive');
        setInterDayLegModes(loaded);
        setTourEventStartDate(eventStartDate || '');
        setTourEventEndDate(eventEndDate || '');
        setTourLinkedPostId(linkedPostId || null);
        setTourLinkedPost(linkedPostSummary ? { id: linkedPostId, ...linkedPostSummary } : null);
        setShowOnlyTourVenues(true); // Tour view mode: show only venues in this tour
        const dateKey = String(route?.params?.calendarDateKey || '').trim();
        const dated = dateKey
          ? loadedDays.find((day) => String(day?.scheduledDate || '').trim() === dateKey)
          : null;
        if (dated?.id) setActiveDayId(dated.id);
        else if (loadedDays[0]?.id) setActiveDayId(loadedDays[0].id);
        if (allVenues.length > 0 && map.current) {
          const bounds = new mapboxgl.LngLatBounds();
          allVenues.forEach(venue => {
            if (venue.longitude && venue.latitude) {
              bounds.extend([venue.longitude, venue.latitude]);
            }
          });
          if (!bounds.isEmpty()) {
            map.current.fitBounds(bounds, { padding: 50, maxZoom: 9 });
          }
        }
      } else {
        Alert.alert('Error', 'Tour not found.');
      }
    } catch (error) {
      console.error('Error loading tour:', error);
      Alert.alert('Error', 'Failed to load tour. Please try again.');
    }
  };

  // Add tour management controls to the header
  const renderTourControls = ({ containerStyle, iconSize = 30 } = {}) => (
    <View style={[styles.tourControls, containerStyle]}>
      <TouchableOpacity
        style={styles.plusTourIconButton}
        onPress={() => {
          if (!mapGateRef.current(MAP_ACTION.PLAN)) return;
          if (currentTourId || ((tourName && tourName !== 'My Tour') || tourDays[0].venues.length > 0)) {
            setConfirmNewTourVisible(true);
          } else {
            openNewTourModal();
          }
        }}
      >
        <Ionicons name="add" size={iconSize} color={theme.brand.secondary} />
      </TouchableOpacity>

      <View style={styles.tourMenuWrap}>
        <TouchableOpacity
          style={styles.folderMenuButton}
          onPress={() => setOpenHeaderMenu((m) => (m === 'folder' ? null : 'folder'))}
        >
          <Ionicons name="folder-open" size={iconSize} color="#bca785" />
        </TouchableOpacity>
        {openHeaderMenu === 'folder' && (
          <View style={styles.tourHeaderDropdown}>
            <TouchableOpacity
              style={styles.tourHeaderDropdownItem}
              onPress={() => {
                setOpenHeaderMenu(null);
                openImportModal();
              }}
            >
              <Text style={styles.tourHeaderDropdownItemText}>Import</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.tourHeaderDropdownItem}
              onPress={() => {
                setOpenHeaderMenu(null);
                saveTour();
              }}
            >
              <Text style={styles.tourHeaderDropdownItemText}>Save Tour</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      <View style={styles.tourMenuWrap}>
        <TouchableOpacity
          style={styles.envelopeMenuButton}
          onPress={() => setOpenHeaderMenu((m) => (m === 'envelope' ? null : 'envelope'))}
        >
          <Ionicons name="mail" size={iconSize} color="#9b59b6" />
        </TouchableOpacity>
        {openHeaderMenu === 'envelope' && (
          <View style={styles.tourHeaderDropdown}>
            <TouchableOpacity
              style={styles.tourHeaderDropdownItem}
              onPress={() => {
                setOpenHeaderMenu(null);
                exportTourAsPdf();
              }}
              disabled={exportingTourPdf}
            >
              <Text style={styles.tourHeaderDropdownItemText}>
                {exportingTourPdf ? 'Exporting…' : 'Export as PDF'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.tourHeaderDropdownItem}
              onPress={() => {
                setOpenHeaderMenu(null);
                shareTour();
              }}
            >
              <Text style={styles.tourHeaderDropdownItemText}>Share Link</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </View>
  );

  // Load saved tours when component mounts
  useEffect(() => {
    loadSavedTours();
  }, [auth.currentUser]);

  // Load tour from nav params (e.g. deep link) — tour is stored as folder
  useEffect(() => {
    if (route.params?.loadTourId && auth.currentUser) {
      loadTour(route.params.loadTourId);
    }
  }, [route.params?.loadTourId, auth.currentUser]);

  useEffect(() => {
    if (!calendarEmbed || !calendarDateKey || !Array.isArray(tourDays) || !tourDays.length) return;
    const match = tourDays.find((day) => String(day?.scheduledDate || '').trim() === calendarDateKey);
    if (match?.id && match.id !== activeDayId) setActiveDayId(match.id);
  }, [calendarEmbed, calendarDateKey, tourDays, activeDayId]);

  // Admin > Resource Submissions "Add resource" opens the rail's resource add flow (REQ-076).
  useEffect(() => {
    if (route.params?.openResourceAdd) {
      setMapSideRailOpenRequest({ itemId: 'leaf', action: 'add' });
      navigation.setParams({ openResourceAdd: undefined });
    }
  }, [route.params?.openResourceAdd]);

  // Open Add a Location flow when navigated from VenueScreen with openAddLocation
  useEffect(() => {
    if (route.params?.openAddLocation) {
      setAddLocationMode(true);
      openAddLocationPanel();
      navigation.setParams({ openAddLocation: false });
    }
  }, [route.params?.openAddLocation]);

  // Add clipboard functionality
  const shareTour = async () => {
    if (!mapGateRef.current(MAP_ACTION.PLAN)) return;
    if (!currentTourId) {
      setToast({ message: 'Please save your tour first', type: 'warning' });
      return;
    }
    try {
      // Write a public snapshot to sharedTours so anyone with the link can view it
      const db = getFirestore();
      const ownerSnap = await getDoc(doc(db, 'users', userData?.uid || ''));
      const ownerData = ownerSnap.exists() ? ownerSnap.data() : {};
      const ownerName = ownerData.username || [ownerData.firstName, ownerData.lastName].filter(Boolean).join(' ') || 'Anonymous';
      await setDoc(doc(db, 'sharedTours', currentTourId), {
        tourId: currentTourId,
        tourName: tourName || 'My Tour',
        days: tourDays,
        ownerId: userData?.uid || null,
        ownerName,
        updatedAt: serverTimestamp(),
        isPublic: true,
      });
    } catch (e) {
      console.warn('shareTour write failed:', e.message);
    }
    const shareUrl = `${window.location.origin}/tour/${currentTourId}`;
    Clipboard.setString(shareUrl);
    setToast({ message: 'Tour link copied to clipboard!', type: 'success' });
  };

  const exportTourAsPdf = async () => {
    try {
      setExportingTourPdf(true);
      let jsPDF;
      try {
        const mod = await import('jspdf');
        jsPDF = mod.jsPDF;
      } catch (e) {
        setToast({ message: 'PDF export unavailable. Install jspdf.', type: 'error' });
        return;
      }

      const cleanName = String(tourName || '').trim() || 'My Tour';
      const docPdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const pageW = docPdf.internal.pageSize.getWidth();
      const pageH = docPdf.internal.pageSize.getHeight();
      const margin = 14;
      const textW = pageW - margin * 2;
      const bottom = pageH - margin;
      let y = 18;

      const addLine = (text, opts = {}) => {
        const lines = docPdf.splitTextToSize(String(text || ''), opts.width || textW);
        lines.forEach((ln) => {
          if (y > bottom - 6) {
            docPdf.addPage();
            y = 18;
          }
          docPdf.text(ln, opts.x || margin, y);
          y += opts.step || 5;
        });
      };

      docPdf.setFont('helvetica', 'bold');
      docPdf.setFontSize(18);
      docPdf.setTextColor(35, 35, 35);
      docPdf.text(cleanName, margin, y);
      y += 8;
      docPdf.setFont('helvetica', 'normal');
      docPdf.setFontSize(10);
      docPdf.setTextColor(95, 95, 95);
      addLine(`Exported ${new Date().toLocaleString()}`);
      addLine(`Days: ${tourDays.length} · Stops: ${tourDays.reduce((n, d) => n + (d.venues?.length || 0), 0)}`);
      if (tourEventStartDate || tourEventEndDate) {
        addLine(`Event dates: ${tourEventStartDate || '—'} to ${tourEventEndDate || '—'}`);
      }
      y += 2;

      tourDays.forEach((day, di) => {
        if (y > bottom - 18) {
          docPdf.addPage();
          y = 18;
        }
        docPdf.setFont('helvetica', 'bold');
        docPdf.setFontSize(12);
        docPdf.setTextColor(50, 80, 110);
        docPdf.text(day.name || `Day ${di + 1}`, margin, y);
        y += 7;
        docPdf.setFont('helvetica', 'normal');
        docPdf.setFontSize(10);
        docPdf.setTextColor(30, 30, 30);
        const dayStops = Array.isArray(day.venues) ? day.venues : [];
        if (!dayStops.length) {
          addLine('• No stops', { x: margin + 2, step: 4.5 });
        } else {
          dayStops.forEach((v, vi) => {
            const loc = formatTourDayVenueLocationLine(v);
            addLine(`${vi + 1}. ${v?.name || 'Untitled stop'}`, { x: margin + 2, step: 4.5 });
            if (loc && loc !== '—') addLine(`   ${loc}`, { x: margin + 2, step: 4.5 });
          });
        }
        y += 2;
      });

      const fileSafe = cleanName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'tour';
      docPdf.save(`${fileSafe}.pdf`);
      setToast({ message: 'Tour exported as PDF.', type: 'success' });
    } catch (e) {
      console.error('exportTourAsPdf', e);
      setToast({ message: 'Failed to export PDF.', type: 'error' });
    } finally {
      setExportingTourPdf(false);
    }
  };

  // Create new tour; optionally prefill from setup (start/end labels, numDays, optional start/end venue objects, optional tour name, event dates, linked community post)
  const createNewTour = (startLocation = '', endLocation = '', numDays = 1, startVenue = null, endVenue = null, tourNameOverride = '', eventStartDate = '', eventEndDate = '', linkedPostId = null, linkedPost = null) => {
    const n = Math.max(1, Math.min(99, parseInt(numDays, 10) || 1));
    const customName = (tourNameOverride || '').trim();
    const name =
      customName
        ? customName
        : startLocation?.trim() && endLocation?.trim()
          ? `${startLocation.trim()} to ${endLocation.trim()}`
          : startLocation?.trim()
            ? `${startLocation.trim()} Tour`
            : endLocation?.trim()
              ? `${endLocation.trim()} Tour`
              : 'My Tour';
    setTourName(name);
    setTourStartLocation(startLocation?.trim() || '');
    setTourEndLocation(endLocation?.trim() || '');

    const hasStart = startVenue && startVenue.latitude != null && startVenue.longitude != null;
    const hasEnd = endVenue && endVenue.latitude != null && endVenue.longitude != null;
    const sameVenue = hasStart && hasEnd && startVenue.id === endVenue.id;

    const days = Array.from({ length: n }, (_, i) => {
      const dayNum = i + 1;
      let dayVenues = [];
      let legModes = undefined;
      if (n === 1) {
        if (hasStart && hasEnd && !sameVenue) {
          dayVenues = [{ ...startVenue }, { ...endVenue }];
          legModes = ['drive'];
        } else if (hasStart) {
          dayVenues = [{ ...startVenue }];
        } else if (hasEnd) {
          dayVenues = [{ ...endVenue }];
        }
      } else {
        if (dayNum === 1 && hasStart) {
          dayVenues = [{ ...startVenue }];
        } else if (dayNum === n && hasEnd) {
          dayVenues = [{ ...endVenue }];
        }
      }
      return {
        id: `day-${dayNum}`,
        name: `Day ${dayNum}`,
        venues: dayVenues,
        legModes
      };
    });

    setTourDays(days);
    setCurrentTourId(null);
    setTourVenues(days.flatMap(d => d.venues));
    setShowOnlyTourVenues(false); // New tour: show all venues so user can add stops
    setTourEventStartDate(eventStartDate || '');
    setTourEventEndDate(eventEndDate || '');
    setTourLinkedPostId(linkedPostId || null);
    setTourLinkedPost(linkedPost || null);

    if (map.current) {
      const allVenues = days.flatMap(d => d.venues);
      if (allVenues.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        allVenues.forEach(v => {
          if (v.longitude != null && v.latitude != null) bounds.extend([v.longitude, v.latitude]);
        });
        if (!bounds.isEmpty()) {
          map.current.fitBounds(bounds, { padding: 80, maxZoom: 9 });
        } else {
          map.current.flyTo({ center: initialCenter, zoom: 3, essential: true });
        }
      } else {
        map.current.flyTo({ center: initialCenter, zoom: 3, essential: true });
      }
    }
    openTourPlanner();
  };

  const openNewTourModal = () => {
    if (!mapGateRef.current(MAP_ACTION.PLAN)) return;
    setNewTourName('');
    setNewTourStartSearchQuery('');
    setNewTourStartVenue(null);
    setNewTourStartDropdownVisible(false);
    setNewTourEndSearchQuery('');
    setNewTourEndVenue(null);
    setNewTourEndDropdownVisible(false);
    setNewTourNumberOfDays('3');
    setNewTourStartDate('');
    setNewTourEndDate('');
    setNewTourLinkedPostId(null);
    setNewTourLinkedPost(null);
    setNewTourModalVisible(true);
    // Fetch user's community posts when modal opens (imperative, not effect)
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setUserCommunityPosts([]);
      setLoadingUserPosts(false);
      return;
    }
    setLoadingUserPosts(true);
    setUserCommunityPosts([]);
    (async () => {
      try {
        const db = getFirestore();
        const q = query(
          collection(db, 'communityPosts'),
          where('userId', '==', uid),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setUserCommunityPosts(list);
      } catch (err) {
        setUserCommunityPosts([]);
      } finally {
        setLoadingUserPosts(false);
      }
    })();
  };

  const startVenueResults = useMemo(() => {
    const q = newTourStartSearchQuery.trim();
    if (q.length < 2) return [];
    return searchVenuesByQuery(venues, q, { limit: 20 });
  }, [venues, newTourStartSearchQuery]);

  const endVenueResults = useMemo(() => {
    const q = newTourEndSearchQuery.trim();
    if (q.length < 2) return [];
    return searchVenuesByQuery(venues, q, { limit: 20 });
  }, [venues, newTourEndSearchQuery]);

  const submitNewTourSetup = () => {
    const name = (newTourName || '').trim();
    if (!name) {
      setToast({ message: 'Please enter a tour name', type: 'error' });
      return;
    }
    const numDays = Math.max(1, Math.min(99, parseInt(newTourNumberOfDays, 10) || 1));
    const startLabel = newTourStartVenue ? `${newTourStartVenue.name}, ${newTourStartVenue.city}, ${newTourStartVenue.state}` : newTourStartSearchQuery.trim();
    const endLabel = newTourEndVenue ? `${newTourEndVenue.name}, ${newTourEndVenue.city}, ${newTourEndVenue.state}` : newTourEndSearchQuery.trim();
    setNewTourModalVisible(false);
    createNewTour(startLabel, endLabel, numDays, newTourStartVenue || null, newTourEndVenue || null, name || undefined, newTourStartDate || '', newTourEndDate || '', newTourLinkedPostId || null, newTourLinkedPost || null);
    if (setToast) {
      setToast({ message: `Tour created with ${numDays} day${numDays !== 1 ? 's' : ''}.`, type: 'success' });
    }
  };

  // Load user folders: show tour folders (have .tour) and plain folders (have venue files) for Import
  const openImportModal = async () => {
    if (!mapGateRef.current(MAP_ACTION.PLAN)) return;
    setImportModalVisible(true);
    setImportLoading(true);
    setImportFoldersList([]);
    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        setImportLoading(false);
        return;
      }
      const userData = userDoc.data();
      const folders = userData.folders || {};
      const files = userData.files || {};
      const list = [];
      Object.entries(folders).forEach(([folderId, folder]) => {
        const venueCount = Object.values(files).filter(
          f => f.parentId === folderId && f.itemType === 'savedVenue'
        ).length;
        const isTour = !!(folder && folder.tour);
        if (venueCount > 0 || isTour) {
          list.push({
            id: folderId,
            name: (folder && (folder.tour && folder.tour.name) || folder.name) || 'Unnamed',
            venueCount: isTour ? (folder.tour.days || []).flatMap(d => d.venues || []).length : venueCount,
            isTour,
            eventStartDate: isTour ? (folder.tour.eventStartDate || '') : '',
            eventEndDate: isTour ? (folder.tour.eventEndDate || '') : '',
            createdAt: folder.createdAt || folder.tour?.createdAt || null,
          });
        }
      });
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setImportFoldersList(list);
    } catch (err) {
      console.error('Error loading folders for import:', err);
      Alert.alert('Import Error', 'Could not load your folders.');
    }
    setImportLoading(false);
  };

  // Import: if folder has tour data, load full tour; else load venue files as day 1
  const importFromFolder = async (folderId, folderName) => {
    if (!mapGateRef.current(MAP_ACTION.PLAN)) return;
    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) return;
      const data = userDoc.data();
      const folders = data.folders || {};
      const folder = folders[folderId];

      setImportModalVisible(false);

      if (folder && folder.tour && Array.isArray(folder.tour.days)) {
        // Tour folder: load full tour (name + days)
        await loadTour(folderId);
        if (setToast) setToast({ message: `Loaded tour "${folder.tour.name || folderName}".`, type: 'success' });
        return;
      }

      // Plain folder: load venue files as single-day tour
      const files = data.files || {};
      const folderFiles = Object.entries(files).filter(
        ([_, f]) => f.parentId === folderId && f.itemType === 'savedVenue'
      );
      const resolvedVenues = [];
      for (const [_, file] of folderFiles) {
        const fileData = file.data || {};
        const venueId = fileData.id;
        const fromApp = venues.find(v => v.id === venueId);
        const venue = fromApp
          ? { ...fromApp }
          : {
            id: venueId,
            name: fileData.name || '',
            city: fileData.city || '',
            state: fileData.state || '',
            latitude: fileData.latitude ?? null,
            longitude: fileData.longitude ?? null,
            capacity: fileData.capacity
          };
        if (venue.latitude != null && venue.longitude != null) {
          resolvedVenues.push(venue);
        }
      }
      createNewTour();
      setCurrentTourId(folderId);
      setTourName(folderName || 'Imported Tour');
      setTourDays([{ id: 'day-1', name: 'Day 1', venues: resolvedVenues }]);
      setTourVenues(resolvedVenues);
      setImportSetupDays([resolvedVenues]); // one day to start; user can split in modal
      setImportSetupModalVisible(true); // show modal to organize into days
      setShowOnlyTourVenues(true);
      if (resolvedVenues.length < folderFiles.length && setToast) {
        const skipped = folderFiles.length - resolvedVenues.length;
        setToast({ message: `Imported ${resolvedVenues.length} venues. ${skipped} skipped (no coordinates).`, type: 'info' });
      } else if (setToast && resolvedVenues.length > 0) {
        setToast({ message: `Imported ${resolvedVenues.length} venues from "${folderName}".`, type: 'success' });
      }
      if (resolvedVenues.length > 0 && map.current) {
        const bounds = new mapboxgl.LngLatBounds();
        resolvedVenues.forEach(v => {
          if (v.longitude != null && v.latitude != null) bounds.extend([v.longitude, v.latitude]);
        });
        if (!bounds.isEmpty()) {
          map.current.fitBounds(bounds, { padding: 60, maxZoom: 9 });
        }
      }
    } catch (err) {
      console.error('Error importing from folder:', err);
      Alert.alert('Import Error', 'Could not import from this folder.');
    }
  };

  // Add this after the other useEffect hooks
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.handleMapPopupAction = (action, venueId) => {
        const venue = venues.find(v => v.id === venueId);
        if (!venue) return;

        switch (action) {
          case 'addToTour':
            addVenueToTour(venue);
            const popups = document.getElementsByClassName('mapboxgl-popup');
            if (popups.length) popups[0].remove();
            break;
          case 'zoomToVenue':
            zoomToVenue(venue);
            break;
          case 'viewDetails':
            setMapDetailsItem({ type: 'venue', data: venue });
            break;
          case 'accessibilityFlag':
            openAccessibilityFlagModalRef.current?.(venue);
            break;
        }
      };
    }

    return () => {
      if (typeof window !== 'undefined') {
        delete window.handleMapPopupAction;
      }
    };
  }, [venues, addVenueToTour]);

  const calculateTourDistances = async () => {
    let totalDistance = 0;
    let totalDuration = 0;
    let totalFuelUsed = 0;
    let totalFuelCost = 0;
    const avgFuelEfficiency = 25;
    const avgGasPrice = 3.50;
    const CO2_MI = { drive: 0.21, flight: 0.255, train: 0.041, bus: 0.089, walk: 0 };
    let totalCo2Kg = 0;

    try {
      // Within-day legs
      for (const day of tourDays) {
        const dayRouteInfo = await calculateDayRouteInfo(day.venues);
        if (dayRouteInfo) {
          totalDistance += dayRouteInfo.totalDistance;
          totalDuration += dayRouteInfo.totalDuration;
          totalFuelUsed += dayRouteInfo.fuelEstimate;
          totalFuelCost += dayRouteInfo.fuelCost;
          totalCo2Kg += dayRouteInfo.totalCo2Kg || 0;
        }
      }

      // Inter-day legs (last venue of day N -> first venue of day N+1)
      const interModes = interDayLegModes || [];
      for (let gapIndex = 0; gapIndex < tourDays.length - 1; gapIndex++) {
        const dayFrom = tourDays[gapIndex];
        const dayTo = tourDays[gapIndex + 1];
        if (!dayFrom?.venues?.length || !dayTo?.venues?.length) continue;
        const lastVenue = dayFrom.venues[dayFrom.venues.length - 1];
        const firstVenue = dayTo.venues[0];
        if (!lastVenue?.longitude || !lastVenue?.latitude || !firstVenue?.longitude || !firstVenue?.latitude) continue;
        const startCoords = [parseFloat(lastVenue.longitude), parseFloat(lastVenue.latitude)];
        const endCoords = [parseFloat(firstVenue.longitude), parseFloat(firstVenue.latitude)];
        const mode = interModes[gapIndex] || 'drive';

        if (mode === 'train') {
          const leg = getTrainLegGeometry(startCoords, endCoords);
          const miles = (leg.distance || 0) * 0.621371;
          totalDistance += leg.distance || 0; totalDuration += leg.duration || 0;
          totalCo2Kg += miles * CO2_MI.train;
        } else if (mode === 'flight') {
          const leg = getFlightLegGeometry(startCoords, endCoords);
          const miles = (leg.distance || 0) * 0.621371;
          totalDistance += leg.distance || 0; totalDuration += leg.duration || 0;
          totalCo2Kg += miles * CO2_MI.flight;
        } else if (mode === 'bus' || mode === 'walk') {
          // Bus follows roads; walk is straight-line — no fuel either way
          const cacheKey = `inter-${lastVenue.id}-${firstVenue.id}-${mode}`;
          let route = routeCache.current[cacheKey];
          if (!route) {
            route = await getDirections(startCoords, endCoords, { forceDrive: true });
            if (route?.geometry) routeCache.current[cacheKey] = route;
          }
          if (route) { totalDistance += route.distance || 0; totalDuration += route.duration || 0;
            const busWalkMiles = (route.distance || 0) * 0.621371;
            totalCo2Kg += busWalkMiles * (CO2_MI[mode] || 0);
          }
        } else {
          // drive — accumulate fuel
          const cacheKey = `inter-${lastVenue.id}-${firstVenue.id}`;
          let route = routeCache.current[cacheKey];
          if (!route) {
            route = await getDirections(startCoords, endCoords, { forceDrive: true });
            if (route?.geometry) routeCache.current[cacheKey] = route;
          }
          if (route) {
            const distKm = route.distance || 0;
            const distMiles = distKm * 0.621371;
            totalDistance += distKm; totalDuration += route.duration || 0;
            if (!route.requiresFlight) {
              totalFuelUsed += distMiles / avgFuelEfficiency;
              totalFuelCost += (distMiles / avgFuelEfficiency) * avgGasPrice;
              totalCo2Kg += distMiles * CO2_MI.drive;
            }
          }
        }
      }

      return {
        totalDistance: Math.round(totalDistance),
        totalDuration: Math.round(totalDuration),
        totalFuelUsed: Math.round(totalFuelUsed * 100) / 100,
        totalFuelCost: Math.round(totalFuelCost * 100) / 100,
        totalCo2Kg: Math.round(totalCo2Kg * 10) / 10,
      };
    } catch (error) {
      console.error('Error calculating tour distances:', error);
      return {
        totalDistance: 0,
        totalDuration: 0,
        totalFuelUsed: 0,
        totalFuelCost: 0
      };
    }
  };

  // Function to filter venues based on search query
  // Filter a venue list by search query (used for left panel; list is venuesInBounds)
  const filterVenuesBySearch = (list, query) => {
    if (!list || list.length === 0) return list;
    if (!query || query.trim() === '') return list;
    return searchVenuesByQuery(list, query, { limit: 500 });
  };

  const renderVenueSearchResultRow = (venue, query, {
    rowStyle,
    nameStyle,
    subStyle,
    dimmed = false,
    onPress,
  }) => {
    const detailParts = getVenueSearchDetailParts(venue);
    return (
      <TouchableOpacity
        style={[rowStyle, dimmed && styles.mapSearchResultRowDimmed]}
        onPress={onPress}
      >
        <HighlightedSearchText
          text={venue.name}
          query={query}
          style={nameStyle}
          numberOfLines={1}
        />
        {detailParts.length > 0 ? (
          <HighlightedSearchTextParts
            parts={detailParts}
            query={query}
            style={subStyle}
            numberOfLines={2}
          />
        ) : null}
      </TouchableOpacity>
    );
  };

  // Left panel list: tour view mode shows only tour venues; edit mode shows venues in map bounds
  const displayedVenues = useMemo(() => {
    if (!searchQuery.trim()) {
      return (showOnlyTourVenues && tourVenues.length > 0) ? tourVenues : venuesInBounds;
    }
    const searchPool = (showOnlyTourVenues && tourVenues.length > 0)
      ? tourVenues
      : filteredVenues;
    const matches = filterVenuesBySearch(searchPool, searchQuery);
    return sortSearchResultsByMapVicinity(matches, mapBounds).map(({ venue, inMapView }) => ({
      ...venue,
      _searchOutOfMapView: !inMapView,
    }));
  }, [showOnlyTourVenues, tourVenues, venuesInBounds, filteredVenues, searchQuery, mapBounds]);

  // dnd-kit: sensors and handlers for venue/user-location drag (web only) — defined after displayedVenues to avoid TDZ
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );
  const handleDndDragStart = useCallback((event) => {
    setActiveDragId(event.active.id);
  }, []);
  const handleDndDragEnd = useCallback((event) => {
    const { active, over } = event;
    lastDragOverIdRef.current = null;
    setActiveDragId(null);
    setDragOverDayId(null);
    setDragInsertAt(null);
    if (routesCalculating || !over) return;
    const dragId = active.id;
    if (typeof dragId !== 'string') return;

    // Reorder / move within or between days: drag source is a tour slot (sortable item).
    if (dragId.startsWith('tour-')) {
      const fromData = active.data?.current;
      const toData = over.data?.current;
      if (fromData?.dayId == null || fromData.index == null || toData?.dayId == null || (toData.insertIndex == null && toData.index == null)) return;
      const fromDayId = fromData.dayId;
      const fromIndex = fromData.index;
      const toDayId = toData.dayId;
      const toIndex = toData.insertIndex != null ? toData.insertIndex : toData.index;
      if (toIndex == null) return;
      if (fromDayId === toDayId && fromIndex === toIndex) return;
      moveVenueInTour(fromDayId, fromIndex, toDayId, toIndex);
      return;
    }

    // Add from left panel: resolve drop target (sortable slot or TourDroppable) and insert index.
    const dropData = over.data?.current;
    const dayId = dropData?.dayId;
    const insertIndex = dropData?.insertIndex ?? dropData?.index;
    if (dayId == null || insertIndex == null) return;
    let venue = null;
    if (dragId.startsWith('add-venue-')) {
      const venueId = dragId.slice('add-venue-'.length);
      venue = displayedVenues.find(v => v.id === venueId) || venues.find(v => v.id === venueId);
    } else if (dragId.startsWith('add-userloc-')) {
      const locationId = dragId.slice('add-userloc-'.length);
      const loc = userLocations.find(l => l.id === locationId) || publicUserLocations.find(l => l.id === locationId);
      if (loc) venue = userLocationToVenue(loc);
    }
    if (venue) addVenueToDay(venue, dayId, insertIndex);
  }, [routesCalculating, displayedVenues, venues, userLocations, publicUserLocations, userLocationToVenue, addVenueToDay, moveVenueInTour]);
  const handleDndDragOver = useCallback((event) => {
    const { over } = event;
    const overId = over?.id ?? null;
  
    if (overId === lastDragOverIdRef.current) return;
    lastDragOverIdRef.current = overId;
  
    const data = over?.data?.current;
  
    if (!data) {
      setDragOverDayId(prev => (prev ? null : prev));
      setDragInsertAt(prev => (prev ? null : prev));
      return;
    }
  
    const nextDayId = data.dayId ?? null;
    const nextInsertIndex =
      data.insertIndex != null ? data.insertIndex : null;
  
    setDragOverDayId(prev =>
      prev === nextDayId ? prev : nextDayId
    );
  
    setDragInsertAt(prev => {
      if (!nextInsertIndex && nextInsertIndex !== 0) {
        return prev ? null : prev;
      }
  
      if (
        prev?.dayId === nextDayId &&
        prev?.index === nextInsertIndex
      ) {
        return prev; // prevent re-render
      }
  
      return { dayId: nextDayId, index: nextInsertIndex };
    });
  
  }, []);
  const handleDndDragCancel = useCallback(() => {
    lastDragOverIdRef.current = null;
    setActiveDragId(null);
    setDragOverDayId(null);
    setDragInsertAt(null);
  }, []);

  // DragOverlay content so the drag preview is rendered outside the FlatList (avoids scroll/clip)
  const renderDndDragOverlay = useCallback(() => {
    if (!activeDragId || typeof activeDragId !== 'string') return null;
    if (activeDragId.startsWith('add-venue-')) {
      const venueId = activeDragId.slice('add-venue-'.length);
      const venue = displayedVenues.find(v => v.id === venueId) || venues.find(v => v.id === venueId);
      if (!venue) return null;
      const distanceEl = renderVenueDistance(venue);
      return (
        <View style={[styles.venueCard, { cursor: 'grabbing' }]}>
          <View style={styles.venueImagePlaceholder}>
            <Ionicons name="location" size={16} color="#e67e22" />
          </View>
          <View style={styles.venueInfo}>
            <Text style={styles.venueName} numberOfLines={1}>{venue.name}</Text>
            <Text style={styles.venueLocation}>{venue.city}, {venue.state}</Text>
            {distanceEl}
            <View style={styles.venueMetaRow}>
              <Text style={styles.venueMetaText}>{venue.capacity || '—'} cap</Text>
              <Text style={styles.venueMetaDot}>•</Text>
              <Text style={styles.venueMetaText}>{venue.venueType || 'Venue'}</Text>
            </View>
          </View>
          <View style={styles.zoomButtonSmall}><Text style={styles.zoomButtonSmallText}>🔍</Text></View>
          <View style={styles.addButtonSmall}><Text style={styles.addButtonSmallText}>+</Text></View>
        </View>
      );
    }
    if (activeDragId.startsWith('add-userloc-')) {
      const locationId = activeDragId.slice('add-userloc-'.length);
      const loc = userLocations.find(l => l.id === locationId) || publicUserLocations.find(l => l.id === locationId);
      if (!loc) return null;
      const category = locationCategoriesAll.find(c => c.id === loc.category) || locationCategoriesAll.find(c => c.id === 'other');
      return (
        <View style={[styles.venueCard, styles.userLocationCardPublic, { cursor: 'grabbing' }]}>
          <View style={styles.venueImagePlaceholder}>
            <Text style={styles.venueImageIcon}>{category?.icon || '📌'}</Text>
          </View>
          <View style={styles.venueInfo}>
            <Text style={styles.venueName} numberOfLines={1}>{loc.name}</Text>
            <Text style={styles.venueLocation} numberOfLines={1}>{loc.address || loc.notes || 'Custom location'}</Text>
            <View style={styles.venueMetaRow}>
              <Text style={styles.venueMetaText}>{category?.label || 'Other'}</Text>
              <Text style={styles.venueMetaDot}>•</Text>
              <Text style={styles.venueMetaText}>{loc.isPublic ? 'Public' : 'Private'}</Text>
            </View>
          </View>
        </View>
      );
    }
    // Dragging a tour stop (reorder): id is tour-{dayId}-{index}, e.g. tour-day-1-0
    if (activeDragId.startsWith('tour-')) {
      const parts = activeDragId.split('-');
      const index = parseInt(parts[parts.length - 1], 10);
      const dayId = parts.slice(1, -1).join('-');
      if (isNaN(index) || !dayId) return null;
      const day = tourDays.find(d => d.id === dayId);
      const venue = day?.venues?.[index];
      if (!venue) return null;
      const isDbVenue = isVenuesDbTourStop(venue);
      return (
        <View style={[styles.tourVenueCard, isDbVenue && styles.tourVenueCardVenuesDb, { cursor: 'grabbing', minWidth: 200 }]}>
          <View style={styles.tourVenueHeaderRow}>
            {/* <Text style={[styles.tourVenueIndex, isDbVenue && styles.tourVenueIndexVenuesDb]}>{index + 1}</Text> */}
            <View style={styles.tourVenueInfo}>
              <Text style={[styles.tourVenueName, isDbVenue && styles.tourVenueNameVenuesDb]} numberOfLines={1}>{venue.name}</Text>
              <Text style={[styles.tourVenueLocation, isDbVenue && styles.tourVenueLocationVenuesDb]} numberOfLines={1}>{formatTourDayVenueLocationLine(venue)}</Text>
            </View>
          </View>
        </View>
      );
    }
    return null;
  }, [activeDragId, displayedVenues, venues, tourDays, userLocations, publicUserLocations, locationCategoriesAll, renderVenueDistance, styles.venueCard, styles.venueImagePlaceholder, styles.venueImageIcon, styles.venueInfo, styles.venueName, styles.venueLocation, styles.venueMetaRow, styles.venueMetaText, styles.venueMetaDot, styles.zoomButtonSmall, styles.zoomButtonSmallText, styles.addButtonSmall, styles.addButtonSmallText, styles.userLocationCardPublic, styles.tourVenueCard, styles.tourVenueCardVenuesDb, styles.tourVenueHeaderRow, styles.tourVenueIndex, styles.tourVenueIndexVenuesDb, styles.tourVenueInfo, styles.tourVenueName, styles.tourVenueNameVenuesDb, styles.tourVenueLocation, styles.tourVenueLocationVenuesDb]);

  const mobileFabBottomPx = MOBILE_MAP_BOTTOM_GAP + MOBILE_FAB_BOTTOM_OFFSET;
  const mobileWeatherBottomPx = mobileFabBottomPx + MOBILE_FAB_ROW_HEIGHT + MOBILE_WEATHER_ABOVE_FAB_GAP;
  const mobileFabStackTopPx = mobileFabBottomPx + MOBILE_FAB_ROW_HEIGHT;
  const mobilePlannerMaxPx = Math.max(
    mobileViewportHeight - headerHeight - MOBILE_MAP_BOTTOM_GAP,
    Math.round(mobileViewportHeight * 0.42),
  );
  const mobilePlannerSheetCollapsedPx = Math.min(
    Math.round(mobileViewportHeight * MOBILE_DAY_PLANNER_SHEET_RATIO),
    mobilePlannerMaxPx,
  );
  const mobilePlannerSheetExpandedPx = Math.max(
    Math.round(mobileViewportHeight * 0.42),
    mobileViewportHeight - headerHeight - MOBILE_WEATHER_EXPANDED_ABOVE_PLANNER - 4,
  );
  const mobilePlannerSheetPx = tourStopsVisible
    ? (showWeatherPanel && weatherExpanded ? mobilePlannerSheetExpandedPx : mobilePlannerSheetCollapsedPx)
    : 0;
  const mobileWeatherLayoutHeight = weatherExpanded
    ? (tourStopsVisible ? MOBILE_WEATHER_EXPANDED_ABOVE_PLANNER : MOBILE_WEATHER_EXPANDED_STANDALONE)
    : MOBILE_WEATHER_COLLAPSED_HEIGHT;
  // Map inset only accounts for the fixed FAB strip — weather floats over the map without resizing it.
  const mobileMapBottomInsetPx = tourStopsVisible
    ? MOBILE_MAP_BOTTOM_GAP
    : mobileFabStackTopPx + MOBILE_MAP_ABOVE_CHROME_GAP;

  const renderMobileWeatherPanel = () => {
    if (!showWeatherPanel || !isMobile || Platform.OS !== 'web' || typeof document === 'undefined') return null;

    if (tourStopsVisible) {
      return createPortal(
        <View
          style={{
            position: 'fixed',
            left: 10,
            right: 10,
            bottom: mobilePlannerSheetPx,
            height: mobileWeatherLayoutHeight,
            zIndex: 2450,
            pointerEvents: 'box-none',
          }}
        >
          <WeatherPanel
            coords={mapCenter}
            isDarkMode={isDarkMode}
            radarEnabled={radarEnabled}
            onToggleRadar={() => setRadarEnabled(v => !v)}
            isMobile={isMobile}
            onClose={() => setShowWeatherPanel(false)}
            expanded={weatherExpanded}
            onExpandedChange={setWeatherExpanded}
            fillContainer
            compactMobile
            panelStyle={{
              position: 'relative',
              left: 0,
              right: 0,
              width: '100%',
              height: '100%',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          />
        </View>,
        document.body,
      );
    }

    return createPortal(
      <WeatherPanel
        coords={mapCenter}
        isDarkMode={isDarkMode}
        radarEnabled={radarEnabled}
        onToggleRadar={() => setRadarEnabled(v => !v)}
        isMobile={isMobile}
        onClose={() => setShowWeatherPanel(false)}
        expanded={weatherExpanded}
        onExpandedChange={setWeatherExpanded}
        fillContainer
        panelStyle={{
          position: 'fixed',
          left: 10,
          right: 10,
          width: 'auto',
          bottom: mobileWeatherBottomPx,
          zIndex: 2450,
          flexDirection: 'column',
          height: mobileWeatherLayoutHeight,
          minHeight: mobileWeatherLayoutHeight,
          maxHeight: mobileWeatherLayoutHeight,
          overflow: 'hidden',
        }}
      />,
      document.body,
    );
  };

  const renderMobileMapBottomChrome = () => {
    if (!isMobile || Platform.OS !== 'web' || typeof document === 'undefined') return null;
    // The Budget rail covers the phone map; the floating Day Planner pill would sit on its Save button.
    if (tourStopsVisible || expenseTrackerOpen) return null;

    return createPortal(
      <>
        <View style={styles.mobileBottomInsetBar} />
        <View
          style={[
            styles.mobileMapFabRow,
            { bottom: mobileFabBottomPx },
          ]}
        >
          {/* Hidden for now — weather FAB
          {!showWeatherPanel ? (
            <TouchableOpacity
              style={styles.dayPlannerFab}
              onPress={() => setShowWeatherPanel(true)}
            >
              <Text style={styles.dayPlannerFabText}>Weather</Text>
            </TouchableOpacity>
          ) : null}
          */}
          {!tourStopsVisible ? (
            <TouchableOpacity
              style={styles.dayPlannerFab}
              onPress={toggleTourStops}
            >
              <Text style={styles.dayPlannerFabText}>Day Planner</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </>,
      document.body,
    );
  };

  const routeLayerAccent = route?.params?.routeLayerAccent || '#1d1d1d';
  const isRouteLayerOn = (key) => (key === 'venues' ? showVenues : !hiddenRouteLayers.has(key));
  const toggleRouteLayer = (key) => {
    if (key === 'venues') {
      setShowVenues((v) => !v);
      return;
    }
    setHiddenRouteLayers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  /** Calendar MAP tab: the selected day's stops and legs, shown under the map. */
  const formatLegMinutes = (minutes) => {
    const total = Math.max(0, Math.round(Number(minutes) || 0));
    const h = Math.floor(total / 60);
    const m = total % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };
  const legModeIcon = (mode) => ({
    flight: 'airplane-outline',
    train: 'train-outline',
    bus: 'bus-outline',
    walk: 'walk-outline',
  }[mode] || 'car-outline');
  const renderCalendarRouteSummary = () => {
    const dayId = resolveFarmSearchDayId(tourDays, activeDayId);
    const day = (tourDays || []).find((d) => d.id === dayId) || null;
    const stops = Array.isArray(day?.venues) ? day.venues : [];
    const info = day ? dayRouteInfos[day.id] : null;
    const legs = Array.isArray(info?.legs) ? info.legs : [];
    // Legs are only recorded when a route was found, so match each one to its stop pair by name.
    // Each row shows the leg that arrives at that stop (drive time to it).
    const legIntoStop = (index) => {
      const from = stops[index - 1];
      const to = stops[index];
      if (!from || !to) return null;
      return legs.find((leg) => leg.from === from.name && leg.to === to.name) || null;
    };
    const driveMinutes = legs
      .filter((leg) => (leg.mode || 'drive') === 'drive')
      .reduce((sum, leg) => sum + (Number(leg.duration) || 0), 0);
    const totalMiles = legs.reduce((sum, leg) => sum + (Number(leg.distance) || 0), 0);
    const gasCost = legs.reduce((sum, leg) => sum + (Number(leg.fuelCost) || 0), 0);
    const co2Kg = Number(info?.totalCo2Kg) || 0;
    const accent = route?.params?.routeLayerAccent || '#5b52db';
    const dateLabel = day?.scheduledDate ? formatScheduledDateShort(day.scheduledDate) : '';
    const totals = [
      { icon: 'car-outline', value: formatLegMinutes(driveMinutes), label: 'Total drive time' },
      { icon: 'cash-outline', value: `$${Math.round(gasCost)}`, label: 'Est. gas cost' },
      { icon: 'navigate-outline', value: `${Math.round(totalMiles)} mi`, label: 'Total distance' },
      { icon: 'leaf-outline', value: `${Math.round(co2Kg)} kg`, label: 'Est. CO₂' },
      { icon: 'flag-outline', value: String(stops.length), label: stops.length === 1 ? 'Stop' : 'Stops' },
    ];

    return (
      <ScrollView
        style={styles.routeSummary}
        contentContainerStyle={styles.routeSummaryContent}
        showsVerticalScrollIndicator
      >
        <Text style={styles.routeSummaryTitle}>YOUR ROUTE</Text>
        {!day || stops.length === 0 ? (
          <Text style={styles.routeSummaryEmpty}>No stops on this day yet. Add stops to see the route.</Text>
        ) : (
          <>
            <View style={styles.routeSummaryTotals}>
              {totals.map((item) => (
                <View key={item.label} style={styles.routeSummaryTotal}>
                  <Ionicons name={item.icon} size={24} color="#787a99" />
                  <View>
                    <Text style={styles.routeSummaryTotalValue}>{item.value}</Text>
                    <Text style={styles.routeSummaryMuted}>{item.label}</Text>
                  </View>
                </View>
              ))}
            </View>
            {stops.length >= 2 && !info && routesCalculating ? (
              <Text style={styles.routeSummaryMuted}>Calculating route…</Text>
            ) : null}
            {/* Scrolls sideways on narrow screens instead of squeezing the columns. */}
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View style={styles.routeTable}>
                <View style={[styles.routeTableRow, styles.routeTableHeaderRow]}>
                  <Text style={[styles.routeTableHeader, styles.routeColNum]}>#</Text>
                  <Text style={[styles.routeTableHeader, styles.routeColStop]}>STOP</Text>
                  <Text style={[styles.routeTableHeader, styles.routeColType]}>TYPE</Text>
                  <Text style={[styles.routeTableHeader, styles.routeColDate]}>DATE</Text>
                  <Text style={[styles.routeTableHeader, styles.routeColTime]}>TIME</Text>
                  <Text style={[styles.routeTableHeader, styles.routeColTravel]}>DRIVE TIME</Text>
                  <Text style={[styles.routeTableHeader, styles.routeColDistance]}>DISTANCE</Text>
                  <Text style={[styles.routeTableHeader, styles.routeColGas]}>GAS</Text>
                </View>
                {stops.map((stop, index) => {
                  const leg = legIntoStop(index);
                  const isFirst = index === 0;
                  const isLodging = isDayLodgingVenue(day, String(stop?.id || ''));
                  const address = String(stop.fullAddress || stop.address || '').trim()
                    || formatTourDayVenueLocationLine(stop);
                  return (
                    <View key={`${stop.id || 'stop'}-${index}`} style={styles.routeTableRow}>
                      <View style={styles.routeColNum}>
                        <View style={[styles.routeSummaryBadge, { backgroundColor: accent }]}>
                          <Text style={styles.routeSummaryBadgeText}>{index + 1}</Text>
                        </View>
                      </View>
                      <View style={styles.routeColStop}>
                        <Text style={styles.routeSummaryStopName} numberOfLines={1}>{stop.name || 'Stop'}</Text>
                        <Text style={styles.routeSummaryStopAddress} numberOfLines={1}>{address}</Text>
                      </View>
                      <Text style={[styles.routeTableType, styles.routeColType]} numberOfLines={2}>
                        {isLodging ? 'LODGING' : getTourStopTypeLabel(stop)}
                      </Text>
                      <Text style={[styles.routeTableCell, styles.routeColDate]}>{dateLabel || '\u2014'}</Text>
                      {/* Stops don't store a time of day yet. */}
                      <Text style={[styles.routeTableCellMuted, styles.routeColTime]}>{'\u2014'}</Text>
                      <View style={[styles.routeColTravel, styles.routeTableTravel]}>
                        {leg ? (
                          <>
                            <Ionicons name={legModeIcon(leg.mode)} size={15} color="#787a99" />
                            <Text style={styles.routeTableCell}>{formatLegMinutes(leg.duration)}</Text>
                          </>
                        ) : (
                          <Text style={styles.routeTableCellMuted}>{isFirst ? 'Start' : '\u2014'}</Text>
                        )}
                      </View>
                      <Text style={[styles.routeTableCell, styles.routeColDistance]}>
                        {leg ? `${Math.round(Number(leg.distance) || 0)} mi` : ''}
                      </Text>
                      <Text style={[styles.routeTableCell, styles.routeColGas]}>
                        {leg && Number(leg.fuelCost) > 0 ? `$${Math.round(Number(leg.fuelCost))}` : ''}
                      </Text>
                    </View>
                  );
                })}
              </View>
            </ScrollView>
          </>
        )}
      </ScrollView>
    );
  };

  const renderRouteLayerBar = () => (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={styles.routeLayerBar}
      contentContainerStyle={styles.routeLayerBarContent}
    >
      <Text style={styles.routeLayerBarLabel}>LAYER YOUR ROUTE:</Text>
      {ROUTE_LAYER_BUTTONS.map(({ key, label }) => {
        const on = isRouteLayerOn(key);
        return (
          <TouchableOpacity
            key={key}
            onPress={() => toggleRouteLayer(key)}
            accessibilityRole="switch"
            accessibilityState={{ checked: on }}
            style={[
              styles.routeLayerBtn,
              on && { backgroundColor: routeLayerAccent, borderColor: routeLayerAccent },
            ]}
          >
            <Text style={[styles.routeLayerBtnText, on && styles.routeLayerBtnTextOn]}>{label}</Text>
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );

  return (
    <MapContributorGateContext.Provider value={requireContributor}>
    <View style={[
      styles.container,
      isMobile && styles.containerMobileFullScreen,
      calendarEmbed && {
        paddingHorizontal: 0,
        paddingBottom: 0,
        paddingTop: 0,
        minHeight: 0,
        height: '100%',
        maxHeight: '100%',
        overflow: 'hidden',
      },
      isMobile && !calendarEmbed && {
        position: 'fixed',
        top: headerHeight,
        left: 0,
        right: 0,
        width: '100%',
        height: `calc(100dvh - ${headerHeight}px)`,
        display: 'flex',
        flexDirection: 'column',
      },
      !isMobile && isMapFullscreen ? { zIndex: 2147483000 } : null,
    ]}>
      {/* Loading overlay */}
      {isInitializing && (
        <View style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 9999,
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <View style={{
            backgroundColor: '#fff',
            padding: 24,
            borderRadius: 12,
            alignItems: 'center',
            minWidth: 200
          }}>
            <Text style={{ fontSize: 18, fontWeight: '600', marginBottom: 12, color: '#333' }}>
              Loading Venues...
            </Text>
            <Text style={{ fontSize: 14, color: '#666', textAlign: 'center' }}>
              Fetching and processing venue data
            </Text>
          </View>
        </View>
      )}

      {/* Header — desktop only; calendar MAP tab already has tour chrome */}
      {!isMobile && !calendarEmbed && (
        <View style={[styles.header, { zIndex: 5, justifyContent: 'center', alignItems: 'center' }]}>
          <View style={styles.tourNameContainerRow}>
            <Text style={styles.title}>TOUR PLANNER</Text>
            <View style={styles.tourNameContainer}>
              <TextInput
                style={[styles.tourNameInput,
                  { color: isDarkMode ? '#d5b791' : theme.brand.primary },
                  { borderBottomColor: isDarkMode ? '#d5b791' : theme.brand.primary },
                  { backgroundColor: 'transparent', outlineColor: 'transparent' },
                ]}
                nativeID="tour-planner-name"
                value={tourName}
                onChangeText={setTourName}
                placeholder="NAME"
                placeholderTextColor={isDarkMode ? "#d5b791" : "rgba(230,126,34,0.65)"}
              />
            </View>
          </View>
          <View style={{ position: 'absolute', right: 24, top: 0, bottom: 0, flexDirection: 'row', alignItems: 'center' }}>
            {renderTourControls({ containerStyle: { marginLeft: 0 } })}
          </View>
        </View>
      )}

      {calendarEmbed && renderRouteLayerBar()}

      <View style={[
        styles.contentContainer,
        isMobile && styles.contentContainerMobileFullScreen,
        // flex fills what the route-layer bar leaves (height: 100% would overflow below it)
        calendarEmbed && { height: 'auto', flex: 1, minHeight: 320, borderRadius: 0 },
        { zIndex: 1 },
      ]}>
        <TourPlannerDndWrapper
          sensors={dndSensors}
          onDragStart={handleDndDragStart}
          onDragEnd={handleDndDragEnd}
          onDragOver={handleDndDragOver}
          onDragCancel={handleDndDragCancel}
          renderOverlay={renderDndDragOverlay}
        >
        {/* Left sidebar toggle hidden — left panel collapsed, map fills the space */}

        {/* Left Panel - Available Locations (hidden: content migrating to map overlays) */}
        <Animated.View style={[
          styles.venueList,
          isMobile ? styles.hiddenPanelMobile : { width: 0, padding: 0, opacity: 0, overflow: 'hidden' },
        ]}>
          <View style={styles.sectionHeader}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
              <Text style={styles.sectionTitle}>
                {showOnlyTourVenues ? 'Tour Venues' : 'Available Venues'}
              </Text>
              {tourVenues.length > 0 && (
                <TouchableOpacity
                  style={[styles.leftPanelToolbarButton, { paddingVertical: 4, alignItems: 'flex-end' }]}
                  onPress={() => setShowOnlyTourVenues(prev => !prev)}
                >
                  <Text style={[styles.filterModalCloseText, { fontSize: 13 }]}>
                    {showOnlyTourVenues ? 'Show all venues' : 'Tour only'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
            <Text style={styles.venueCountText}>
              {showOnlyTourVenues && tourVenues.length > 0
                ? `${displayedVenues.length} in tour${searchQuery.trim() ? ' (filtered)' : ''}`
                : `${displayedVenues.length} venues${searchQuery.trim() ? ' (filtered)' : venuesInBounds.length < filteredVenues.length ? ` (of ${venuesInBounds.length} in view)` : filteredVenues.length < venues.length ? ` (of ${filteredVenues.length} after filters)` : ''}`}
            </Text>

            {/* Compact: Search + Filter + Add Location */}
            <View style={styles.leftPanelToolbar}>
              <TextInput
                style={styles.searchInputCompact}
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder="Search venues..."
                placeholderTextColor="#666"
              />
              <View style={styles.leftPanelToolbarRow}>
                {(() => {
                  const activeFilterCount =
                    (filters.affiliate?.length || 0) +
                    (filters.features?.length || 0) +
                    (filters.resources?.length || 0) +
                    ((filters.capacityMin || filters.capacityMax) ? 1 : 0) +
                    (showFoodBanks || showFarms ? 1 : 0) +
                    (coordinateSourceFilter !== 'all' ? 1 : 0);
                  return (
                    <TouchableOpacity
                      style={[styles.leftPanelToolbarButton, styles.leftPanelToolbarButtonFilter]}
                      onPress={() => setFilterModalVisible(true)}
                    >
                      <Text style={styles.leftPanelToolbarButtonFilterText}>
                        Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                      </Text>
                    </TouchableOpacity>
                  );
                })()}
                <TouchableOpacity
                  style={[
                    styles.leftPanelToolbarButton, styles.leftPanelToolbarButtonAdd,
                    (addLocationMode || showAddLocationPanel || editingLocation) && styles.toggleButtonActiveGreen
                  ]}
                  onPress={() => {
                    console.log('Add a Location clicked, userData:', userData?.uid);
                    if (!userData?.uid) {
                      mapGateRef.current(MAP_ACTION.CONTRIBUTE);
                      return;
                    }
                    if (addLocationMode || showAddLocationPanel || editingLocation) {
                      setEditingLocation(null);
                      closeAddLocationPanel();
                    } else {
                      setAddLocationMode(true);
                      setShowAddLocationPanel(true);
                    }
                  }}
                >
                  <Text style={[
                    styles.leftPanelToolbarButtonFilterText,
                    (addLocationMode || showAddLocationPanel || editingLocation) && styles.toggleTextActive
                  ]}>{(addLocationMode || showAddLocationPanel || editingLocation) ? 'Cancel' : 'Add a Location'}</Text>
                </TouchableOpacity>
              </View>
              {addLocationMode && !showAddLocationPanel && (
                <View style={styles.addLocationHint}>
                  <Text style={styles.addLocationHintText}>
                    📍 Click anywhere on the map or search above
                  </Text>
                </View>
              )}
              {/* <View style={styles.filterDivider} />
              <View style={styles.filterRow}>
                <View style={styles.filterGroup}>
                  <Text style={styles.filterLabel}>Capacity</Text>
                  <TouchableOpacity
                    style={styles.filterButton}
                    onPress={() => setFilters(prev => ({
                      ...prev,
                      capacity: prev.capacity === 'asc' ? 'desc' : 'asc'
                    }))}
                  >
                    <Text style={styles.filterButtonText}>
                      {filters.capacity === 'asc' ? '↑ Ascending' : '↓ Descending'}
                    </Text>
                  </TouchableOpacity>
                </View>
                <View style={styles.filterGroup}>
                  <Text style={styles.filterLabel}>State</Text>
                  <TouchableOpacity
                    style={styles.filterButton}
                    onPress={() => setFilters(prev => ({
                      ...prev,
                      state: prev.state === 'asc' ? 'desc' : 'asc'
                    }))}
                  >
                    <Text style={styles.filterButtonText}>
                      {filters.state === 'asc' ? 'A-Z' : 'Z-A'}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View> */}
            </View>
            </View>

            {/* Filters modal (VenueList-style sections + Capacity/State/Coord Source) */}
            <FullscreenSafeModal
              visible={filterModalVisible}
              transparent
              animationType="fade"
              onRequestClose={() => setFilterModalVisible(false)}
              hostRef={mapStageRef}
            >
              <TouchableOpacity
                style={[styles.filterModalOverlay, isMobile && { justifyContent: 'flex-end', padding: 0 }]}
                activeOpacity={1}
                onPress={() => setFilterModalVisible(false)}
              >
                <TouchableOpacity style={[styles.filterModalContent, { maxWidth: 580 }, isMobile && { maxWidth: '100%', maxHeight: '88%', borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }]} activeOpacity={1} onPress={() => { }}>
                  <View style={styles.filterModalHeader}>
                    <Text style={styles.filterModalTitle}>Filters</Text>
                    <TouchableOpacity onPress={() => setFilterModalVisible(false)} style={styles.filterModalCloseBtn}>
                      <Text style={styles.filterModalCloseText}>Close</Text>
                    </TouchableOpacity>
                  </View>
                  <ScrollView style={styles.filterModalScroll} showsVerticalScrollIndicator>
                    {/* Visibility / Show on map */}
                    <View style={styles.filterModalSection}>
                      <Text style={[venueListStyles.desktopFilterTitle, { color: theme.text.tertiary, textDecorationLine: 'none', fontSize: 11, letterSpacing: 0.8, textTransform: 'uppercase' }]}>Show on map</Text>
                      <View style={styles.filterModalVisibilityRow}>
                        <TouchableOpacity
                          style={[
                            styles.toggleButton,
                            showVenues && styles.toggleButtonActive
                          ]}
                          onPress={() => setShowVenues(!showVenues)}
                        >
                          <Text style={styles.toggleIcon}>🏢</Text>
                          <Text style={[
                            styles.toggleText,
                            showVenues && styles.toggleTextActive
                          ]}>Venues</Text>
                        </TouchableOpacity>
                        {/* User locations filter — hidden for now
                        {userLocationsFilterVisible && (
                          <TouchableOpacity
                            style={[
                              styles.toggleButton,
                              showUserLocations && styles.toggleButtonActiveRed
                            ]}
                            onPress={() => {
                              const next = !showUserLocations;
                              setShowUserLocations(next);
                              if (typeof localStorage !== 'undefined') {
                                localStorage.setItem('vrc-show-user-created-locations', String(next));
                              }
                            }}
                          >
                            <Text style={styles.toggleIcon}>📌</Text>
                            <Text style={[
                              styles.toggleText,
                              showUserLocations && styles.toggleTextActive
                            ]}>User locations</Text>
                          </TouchableOpacity>
                        )}
                        */}
                        <TouchableOpacity
                          style={[
                            styles.toggleButton,
                            showScreenprinters && { backgroundColor: 'rgba(243,156,18,0.75)', borderColor: 'rgba(243,156,18,0.9)' }
                          ]}
                          onPress={() => setShowScreenprinters(!showScreenprinters)}
                        >
                          <Text style={styles.toggleIcon}>🖨</Text>
                          <Text style={[
                            styles.toggleText,
                            showScreenprinters && styles.toggleTextActive
                          ]}>Screenprinters</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                    {/* Affiliate (VenueList-style) */}
                    <View style={venueListStyles.desktopFilterContainer}>
                      <Text style={[venueListStyles.desktopFilterTitle, { color: theme.text.tertiary, textDecorationLine: 'none', fontSize: 11, letterSpacing: 0.8, textTransform: 'uppercase' }]}>Affiliate</Text>
                      <View style={[venueListStyles.desktopOptionsGrid, isMobile && {
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gridAutoFlow: 'row',
                        gridTemplateRows: undefined,
                        gap: 4,
                      }]}>
                        {affiliateOptions.map(option => (
                          <TouchableOpacity
                            key={option.value}
                            style={[
                              venueListStyles.desktopOption,
                              filters.affiliate?.includes(option.value) && venueListStyles.desktopOptionSelected
                            ]}
                            onPress={() => setFilters(prev => ({
                              ...prev,
                              affiliate: prev.affiliate?.includes(option.value)
                                ? prev.affiliate.filter(a => a !== option.value)
                                : [...(prev.affiliate || []), option.value]
                            }))}
                          >
                            <View style={[
                              venueListStyles.checkbox,
                              filters.affiliate?.includes(option.value) && venueListStyles.checkboxChecked
                            ]}>
                              {filters.affiliate?.includes(option.value) && <Text style={venueListStyles.checkmark}>✓</Text>}
                            </View>
                            {option.image && <Image source={option.image} style={venueListStyles.checkboxImage} />}
                            <View style={{ width: 9, height: 9, borderRadius: 4.5, backgroundColor: affiliateChipColor(option.value), marginRight: 5 }} />
                            <Text style={[
                              venueListStyles.desktopCheckboxLabel,
                              { color: theme.text.primary },
                              filters.affiliate?.includes(option.value) && { color: affiliateChipColor(option.value), fontStyle: 'italic' }
                            ]}>{option.label || option.value}</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>
                    {/* Features (VenueList-style) */}
                    <View style={venueListStyles.desktopFilterContainer}>
                      <Text style={[venueListStyles.desktopFilterTitle, { color: theme.text.tertiary, textDecorationLine: 'none', fontSize: 11, letterSpacing: 0.8, textTransform: 'uppercase' }]}>Features</Text>
                      <View style={[venueListStyles.desktopOptionsGrid, isMobile && {
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gridAutoFlow: 'row',
                        gridTemplateRows: undefined,
                        gap: 4,
                      }]}>
                        {featureOptions.map(option => (
                          <TouchableOpacity
                            key={option.value}
                            style={[
                              venueListStyles.desktopOption,
                              filters.features?.includes(option.value) && venueListStyles.desktopOptionSelected
                            ]}
                            onPress={() => setFilters(prev => ({
                              ...prev,
                              features: prev.features?.includes(option.value)
                                ? prev.features.filter(f => f !== option.value)
                                : [...(prev.features || []), option.value]
                            }))}
                          >
                            <View style={[
                              venueListStyles.checkbox,
                              filters.features?.includes(option.value) && venueListStyles.checkboxChecked
                            ]}>
                              {filters.features?.includes(option.value) && <Text style={venueListStyles.checkmark}>✓</Text>}
                            </View>
                            <Text style={[
                              venueListStyles.desktopCheckboxLabel,
                              { color: theme.text.primary },
                              filters.features?.includes(option.value) && { color: '#e67e22', fontStyle: 'italic' }
                            ]}>{option.label}</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>
                    {/* Capacity sort */}
                    <View style={styles.filterModalSection}>
                      <Text style={[venueListStyles.desktopFilterTitle, { color: theme.text.tertiary, textDecorationLine: 'none', fontSize: 11, letterSpacing: 0.8, textTransform: 'uppercase' }]}>Capacity</Text>
                      <TouchableOpacity
                        style={[styles.filterModalToggle, filters.capacity === 'desc' && styles.filterModalToggleActive]}
                        onPress={() => setFilters(prev => ({ ...prev, capacity: prev.capacity === 'asc' ? 'desc' : 'asc' }))}
                      >
                        <Text style={styles.filterModalToggleText}>
                          {filters.capacity === 'asc' ? '↑ Ascending' : '↓ Descending'}
                        </Text>
                      </TouchableOpacity>
                    </View>
                    {/* State sort */}
                    <View style={styles.filterModalSection}>
                      <Text style={[venueListStyles.desktopFilterTitle, { color: theme.text.tertiary, textDecorationLine: 'none', fontSize: 11, letterSpacing: 0.8, textTransform: 'uppercase' }]}>State</Text>
                      <TouchableOpacity
                        style={[styles.filterModalToggle, filters.state === 'desc' && styles.filterModalToggleActive]}
                        onPress={() => setFilters(prev => ({ ...prev, state: prev.state === 'asc' ? 'desc' : 'asc' }))}
                      >
                        <Text style={styles.filterModalToggleText}>
                          {filters.state === 'asc' ? 'A-Z' : 'Z-A'}
                        </Text>
                      </TouchableOpacity>
                    </View>
                    {/* Coord Source (Admin) */}
                    {(isAdmin || isDevelopment) && (
                      <View style={styles.filterModalSection}>
                        <Text style={venueListStyles.desktopFilterTitle}>Coord Source (Admin)</Text>
                        <View style={styles.coordFilterRow}>
                          <TouchableOpacity
                            style={[
                              styles.coordFilterButton,
                              coordinateSourceFilter === 'all' && styles.coordFilterButtonActive
                            ]}
                            onPress={() => setCoordinateSourceFilter('all')}
                          >
                            <Text style={[
                              styles.coordFilterText,
                              coordinateSourceFilter === 'all' && styles.coordFilterTextActive
                            ]}>All</Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[
                              styles.coordFilterButton,
                              coordinateSourceFilter === 'mapEmbed' && styles.coordFilterButtonActiveBlue
                            ]}
                            onPress={() => setCoordinateSourceFilter('mapEmbed')}
                          >
                            <Text style={[
                              styles.coordFilterText,
                              coordinateSourceFilter === 'mapEmbed' && styles.coordFilterTextActive
                            ]}>🔵 Embed</Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[
                              styles.coordFilterButton,
                              coordinateSourceFilter === 'stored' && styles.coordFilterButtonActivePurple
                            ]}
                            onPress={() => setCoordinateSourceFilter('stored')}
                          >
                            <Text style={[
                              styles.coordFilterText,
                              coordinateSourceFilter === 'stored' && styles.coordFilterTextActive
                            ]}>🟣 Stored</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    )}
                  </ScrollView>
                </TouchableOpacity>
              </TouchableOpacity>
            </FullscreenSafeModal>

            {/* Import from saved venue folders modal */}
            <FullscreenSafeModal
              visible={importModalVisible}
              transparent
              animationType="fade"
              onRequestClose={() => setImportModalVisible(false)}
              hostRef={mapStageRef}
            >
              <TouchableOpacity
                style={styles.filterModalOverlay}
                activeOpacity={1}
                onPress={() => setImportModalVisible(false)}
              >
                <TouchableOpacity style={styles.filterModalContent} activeOpacity={1} onPress={() => { }}>
                  <View style={styles.filterModalHeader}>
                    <Text style={styles.filterModalTitle}>Import from folder</Text>
                    <TouchableOpacity onPress={() => setImportModalVisible(false)} style={styles.filterModalCloseBtn}>
                      <Text style={styles.filterModalCloseText}>Close</Text>
                    </TouchableOpacity>
                  </View>
                  <ScrollView style={styles.filterModalScroll} showsVerticalScrollIndicator>
                    {importLoading ? (
                      <Text style={[styles.filterModalToggleText, { paddingVertical: 20 }]}>Loading folders...</Text>
                    ) : importFoldersList.length === 0 ? (
                      <Text style={[styles.filterModalToggleText, { paddingVertical: 20 }]}>
                        No folders or saved tours found. Save a tour or save venues to a folder first.
                      </Text>
                    ) : (
                      importFoldersList.map((folder) => (
                        <TouchableOpacity
                          key={folder.id}
                          style={styles.importFolderRow}
                          onPress={() => importFromFolder(folder.id, folder.name)}
                        >
                          <View style={{ flex: 1 }}>
                            <Text style={styles.importFolderName} numberOfLines={1}>{folder.name}</Text>
                            {folder.isTour && folder.eventStartDate ? (
                              <Text style={{ fontSize: 11, color: theme.text.tertiary, marginTop: 2 }}>
                                {formatScheduledDateShort(folder.eventStartDate)}
                                {folder.eventEndDate && folder.eventEndDate !== folder.eventStartDate
                                  ? ` – ${formatScheduledDateShort(folder.eventEndDate)}`
                                  : ''}
                              </Text>
                            ) : null}
                            {folder.createdAt ? (
                              <Text style={{ fontSize: 10, color: theme.text.tertiary, marginTop: 1, opacity: 0.7 }}>
                                {'Saved ' + (() => {
                                  const d = folder.createdAt?.toDate ? folder.createdAt.toDate() : new Date(folder.createdAt);
                                  return isNaN(d) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
                                })()}
                              </Text>
                            ) : null}
                          </View>
                          <Text style={styles.importFolderCount}>
                            {folder.isTour ? `${folder.venueCount} stop${folder.venueCount !== 1 ? 's' : ''}` : `${folder.venueCount} venue${folder.venueCount !== 1 ? 's' : ''}`}
                          </Text>
                        </TouchableOpacity>
                      ))
                    )}
                  </ScrollView>
                </TouchableOpacity>
              </TouchableOpacity>
            </FullscreenSafeModal>

            {/* Confirm New Tour popup (replaces Alert when there are unsaved changes) */}
            <FullscreenSafeModal
              visible={confirmNewTourVisible}
              transparent
              animationType="fade"
              onRequestClose={() => setConfirmNewTourVisible(false)}
              hostRef={mapStageRef}
            >
              <TouchableOpacity
                style={styles.filterModalOverlay}
                activeOpacity={1}
                onPress={() => setConfirmNewTourVisible(false)}
              >
                <TouchableOpacity style={styles.filterModalContent} activeOpacity={1} onPress={() => { }}>
                  <View style={styles.filterModalHeader}>
                    <Text style={styles.filterModalTitle}>Create New Tour</Text>
                    <TouchableOpacity onPress={() => setConfirmNewTourVisible(false)} style={styles.filterModalCloseBtn}>
                      <Text style={styles.filterModalCloseText}>Cancel</Text>
                    </TouchableOpacity>
                  </View>
                  <Text style={styles.confirmPopupMessage}>
                    Are you sure you want to start a new tour? Any unsaved changes will be lost.
                  </Text>
                  <View style={styles.confirmPopupActions}>
                    <TouchableOpacity
                      style={styles.confirmPopupButton}
                      onPress={() => setConfirmNewTourVisible(false)}
                    >
                      <Text style={styles.confirmPopupButtonText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.confirmPopupButton, styles.confirmPopupButtonPrimary]}
                      onPress={() => {
                        setConfirmNewTourVisible(false);
                        openNewTourModal();
                      }}
                    >
                      <Text style={styles.confirmPopupButtonPrimaryText}>Continue</Text>
                    </TouchableOpacity>
                  </View>
                </TouchableOpacity>
              </TouchableOpacity>
            </FullscreenSafeModal>

            {/* Import setup: organize imported venues into days (fresh import only) */}
            <FullscreenSafeModal
              visible={importSetupModalVisible}
              transparent
              animationType="fade"
              onRequestClose={() => setImportSetupModalVisible(false)}
              hostRef={mapStageRef}
            >
              <TouchableOpacity
                style={styles.filterModalOverlay}
                activeOpacity={1}
                onPress={() => setImportSetupModalVisible(false)}
              >
                <TouchableOpacity style={[styles.filterModalContent, { maxHeight: '80%' }]} activeOpacity={1} onPress={() => { }}>
                  <View style={styles.filterModalHeader}>
                    <Text style={styles.filterModalTitle}>Organize into days</Text>
                    <TouchableOpacity onPress={() => setImportSetupModalVisible(false)} style={styles.filterModalCloseBtn}>
                      <Text style={styles.filterModalCloseText}>Close</Text>
                    </TouchableOpacity>
                  </View>
                  <Text style={[styles.filterModalToggleText, { marginBottom: 12, padding: 12 }]}>
                    Split the list into days. Use &quot;Split day after this stop&quot; between any two venues.
                  </Text>
                  <ScrollView style={[styles.filterModalScroll, { flex: 1, maxHeight: 320 }]} showsVerticalScrollIndicator>
                    {(importSetupDays || []).map((dayVenues, dayIndex) => (
                      <View key={dayIndex} style={{ marginBottom: 16 }}>
                        <Text style={[styles.filterModalToggleText, { fontWeight: '600', marginBottom: 6 }]}>
                          Day {dayIndex + 1}
                        </Text>
                        {(dayVenues || []).map((venue, venueIndex) => (
                          <View key={`${dayIndex}-${venueIndex}-${venue?.id || venueIndex}`}>
                            <View style={[styles.importFolderRow, { paddingVertical: 8 }]}>
                              <Text style={styles.importFolderName} numberOfLines={1}>{venue?.name || 'Unnamed'}</Text>
                            </View>
                            {venueIndex < dayVenues.length - 1 && (
                              <TouchableOpacity
                                style={{ paddingVertical: 4, paddingLeft: 8 }}
                                onPress={() => {
                                  setImportSetupDays(prev => {
                                    const next = [...prev];
                                    const day = next[dayIndex];
                                    if (!day || day.length < 2 || venueIndex < 0 || venueIndex >= day.length - 1) return prev;
                                    const left = day.slice(0, venueIndex + 1);
                                    const right = day.slice(venueIndex + 1);
                                    next.splice(dayIndex, 1, left, right);
                                    return next;
                                  });
                                }}
                              >
                                <Text style={[styles.filterModalToggleText, { fontSize: 12, color: theme.brand?.primary || theme.brand?.accent || '#2c7be5' }]}>
                                  Split day after this stop
                                </Text>
                              </TouchableOpacity>
                            )}
                          </View>
                        ))}
                      </View>
                    ))}
                  </ScrollView>
                  <View style={styles.confirmPopupActions}>
                    <TouchableOpacity
                      style={[styles.confirmPopupButton, styles.confirmPopupButtonPrimary]}
                      onPress={() => {
                        const days = importSetupDays || [];
                        if (days.length === 0) {
                          setImportSetupModalVisible(false);
                          return;
                        }
                        const newDays = days.map((dayVenues, i) => ({
                          id: `day-${Date.now()}-${i}`,
                          name: `Day ${i + 1}`,
                          venues: dayVenues,
                          legModes: (dayVenues.length > 1) ? Array(dayVenues.length - 1).fill('drive') : undefined,
                        }));
                        const allVenues = newDays.flatMap(d => d.venues);
                        setTourDays(newDays);
                        setTourVenues(allVenues);
                        setInterDayLegModes(newDays.length > 1 ? Array(newDays.length - 1).fill('drive') : []);
                        setShowOnlyTourVenues(false); // fresh import: default to edit mode (show all venues)
                        setImportSetupModalVisible(false);
                        if (map.current && mapLoaded) {
                          setTimeout(() => directUpdateMapWithRoutes(allVenues, newDays, activeDayId, newDays.length > 1 ? Array(newDays.length - 1).fill('drive') : []), 50);
                        }
                        if (allVenues.length > 0 && map.current) {
                          const bounds = new mapboxgl.LngLatBounds();
                          allVenues.forEach(v => {
                            if (v?.longitude != null && v?.latitude != null) bounds.extend([v.longitude, v.latitude]);
                          });
                          if (!bounds.isEmpty()) {
                            map.current.fitBounds(bounds, { padding: 60, maxZoom: 9 });
                          }
                        }
                      }}
                    >
                      <Text style={styles.confirmPopupButtonPrimaryText}>Done</Text>
                    </TouchableOpacity>
                  </View>
                </TouchableOpacity>
              </TouchableOpacity>
            </FullscreenSafeModal>

            {/* Tour day calendar date: year + month chips + day grid */}
            <FullscreenSafeModal
              visible={dayDatePickerDayId != null}
              transparent
              animationType="fade"
              onRequestClose={closeDayDatePicker}
              hostRef={mapStageRef}
            >
              <TouchableOpacity
                style={styles.dayDatePickerOverlay}
                activeOpacity={1}
                onPress={closeDayDatePicker}
              >
                <TouchableOpacity style={styles.dayDatePickerCard} activeOpacity={1} onPress={() => { }}>
                  <Text style={styles.dayDatePickerTitle}>Day date (calendar)</Text>
                  <Text style={styles.dayDatePickerLabel}>Year</Text>
                  <TextInput
                    style={styles.dayDatePickerYearInput}
                    value={dayDatePickerYear}
                    onChangeText={(t) => {
                      const next = t.replace(/[^\d]/g, '').slice(0, 4);
                      setDayDatePickerYear(next);
                      const y = parseInt(next, 10);
                      if (Number.isFinite(y) && y >= 1900 && y <= 2100) {
                        const dim = new Date(y, dayDatePickerMonth, 0).getDate();
                        setDayDatePickerDay((prev) => Math.min(prev, dim));
                      }
                    }}
                    placeholder="2025"
                    placeholderTextColor={theme.text.muted}
                    keyboardType="number-pad"
                  />
                  <Text style={styles.dayDatePickerLabel}>Month</Text>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    style={styles.dayDatePickerMonthScroll}
                    contentContainerStyle={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 2 }}
                  >
                    {MONTH_SHORT.map((label, idx) => {
                      const mo = idx + 1;
                      const active = dayDatePickerMonth === mo;
                      return (
                        <TouchableOpacity
                          key={label}
                          style={[styles.dayDatePickerMonthChip, active && styles.dayDatePickerMonthChipActive]}
                          onPress={() => {
                            setDayDatePickerMonth(mo);
                            const y = parseInt(String(dayDatePickerYear).trim(), 10);
                            const yy =
                              Number.isFinite(y) && y >= 1900 && y <= 2100 ? y : new Date().getFullYear();
                            const dim = new Date(yy, mo, 0).getDate();
                            setDayDatePickerDay((prev) => Math.min(prev, dim));
                          }}
                        >
                          <Text style={styles.dayDatePickerMonthChipText}>{label}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                  <Text style={styles.dayDatePickerLabel}>Day</Text>
                  {(() => {
                    const y = parseInt(String(dayDatePickerYear).trim(), 10);
                    const yy =
                      Number.isFinite(y) && y >= 1900 && y <= 2100 ? y : new Date().getFullYear();
                    const maxD = new Date(yy, dayDatePickerMonth, 0).getDate();
                    return (
                      <View style={styles.dayDatePickerDayWrap}>
                        {Array.from({ length: maxD }, (_, i) => i + 1).map((dn) => {
                          const active = dayDatePickerDay === dn;
                          return (
                            <TouchableOpacity
                              key={dn}
                              style={[styles.dayDatePickerDayChip, active && styles.dayDatePickerDayChipActive]}
                              onPress={() => setDayDatePickerDay(dn)}
                            >
                              <Text style={styles.dayDatePickerDayChipText}>{dn}</Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    );
                  })()}
                  <View style={styles.dayDatePickerActions}>
                    <TouchableOpacity style={styles.dayDatePickerBtnGhost} onPress={clearDayDatePicker}>
                      <Text style={styles.dayDatePickerBtnGhostText}>Clear date</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.dayDatePickerBtnGhost} onPress={closeDayDatePicker}>
                      <Text style={styles.dayDatePickerBtnGhostText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.dayDatePickerBtnPrimary} onPress={applyDayDatePicker}>
                      <Text style={styles.dayDatePickerBtnPrimaryText}>Save</Text>
                    </TouchableOpacity>
                  </View>
                </TouchableOpacity>
              </TouchableOpacity>
            </FullscreenSafeModal>

            {/* New Tour Setup Modal: start/end locations + number of days */}
            <FullscreenSafeModal
              visible={newTourModalVisible}
              transparent
              animationType="fade"
              onRequestClose={() => setNewTourModalVisible(false)}
              hostRef={mapStageRef}
            >
              <TouchableOpacity
                style={styles.filterModalOverlay}
                activeOpacity={1}
                onPress={() => setNewTourModalVisible(false)}
              >
                <TouchableOpacity style={styles.filterModalContent} activeOpacity={1} onPress={() => { }}>
                  <View style={styles.filterModalHeader}>
                    <Text style={styles.filterModalTitle}>New Tour</Text>
                    <TouchableOpacity onPress={() => setNewTourModalVisible(false)} style={styles.filterModalCloseBtn}>
                      <Text style={styles.filterModalCloseText}>Cancel</Text>
                    </TouchableOpacity>
                  </View>
                  <ScrollView
                    style={styles.filterModalScroll}
                    showsVerticalScrollIndicator={false}
                    onScroll={() => {
                      setNewTourStartDropdownVisible(false);
                      setNewTourEndDropdownVisible(false);
                    }}
                    scrollEventThrottle={100}
                  >
                    {/* Top: community post posters (fetch on mount, select one like a poster) */}
                    {loadingUserPosts ? (
                      <View style={styles.newTourPostersLoading}>
                        <ActivityIndicator size="small" color={theme?.brand?.primary || '#e67e22'} />
                        <Text style={styles.newTourPostersLoadingText}>Loading your posts...</Text>
                      </View>
                    ) : userCommunityPosts.length > 0 ? (
                      <View style={styles.newTourPostersSection}>
                        <Text style={styles.newTourModalLabel}>Link an event (optional)</Text>
                        <ScrollView
                          horizontal
                          showsHorizontalScrollIndicator={false}
                          contentContainerStyle={styles.newTourPostersList}
                          style={styles.newTourPostersScroll}
                        >
                          {userCommunityPosts.map((p) => {
                            const isSelected = newTourLinkedPostId === p.id;
                            const postItems = Array.isArray(p.items) ? p.items : [];
                            return (
                              <TouchableOpacity
                                key={p.id}
                                style={[
                                  styles.newTourPosterCard,
                                  isSelected && styles.newTourPosterCardSelected,
                                  isSelected && { borderColor: theme?.brand?.primary || '#e67e22' },
                                ]}
                                onPress={() => {
                                  if (isSelected) {
                                    setNewTourLinkedPost(null);
                                    setNewTourLinkedPostId(null);
                                  } else {
                                    setNewTourLinkedPost(p);
                                    setNewTourLinkedPostId(p.id);
                                  }
                                }}
                                activeOpacity={0.8}
                              >
                                <View style={styles.newTourPosterHeader}>
                                  <Text style={styles.newTourPosterTitle} numberOfLines={2}>{p.postContent || p.location || 'Post'}</Text>
                                  {p.location ? <Text style={styles.newTourPosterMeta} numberOfLines={1}>{p.location}</Text> : null}
                                </View>
                                <PostGridDisplay items={postItems} colWidth={60} rowHeight={40} style={styles.newTourPosterGrid} compactMode />
                              </TouchableOpacity>
                            );
                          })}
                        </ScrollView>
                      </View>
                    ) : null}

                    <View style={styles.newTourModalSection}>
                      <Text style={styles.newTourModalLabel}>Tour name</Text>
                      <TextInput
                        style={styles.newTourModalInput}
                        value={newTourName}
                        onChangeText={setNewTourName}
                        placeholder="e.g. Summer 2025 Tour"
                        placeholderTextColor="#888"
                      />
                    </View>
                    <View style={styles.newTourModalSection}>
                      <Text style={styles.newTourModalLabel}>Start location</Text>
                      <TextInput
                        style={styles.newTourModalInput}
                        value={newTourStartVenue ? `${newTourStartVenue.name}, ${newTourStartVenue.city}, ${newTourStartVenue.state}` : newTourStartSearchQuery}
                        onChangeText={(t) => {
                          setNewTourStartSearchQuery(t);
                          setNewTourStartVenue(null);
                          setNewTourStartDropdownVisible(true);
                          setNewTourEndDropdownVisible(false);
                        }}
                        onFocus={() => {
                          setNewTourStartDropdownVisible(true);
                          setNewTourEndDropdownVisible(false);
                        }}
                        placeholder="Search venues..."
                        placeholderTextColor="#888"
                      />
                      {newTourStartDropdownVisible && (
                        <View style={styles.newTourDropdown}>
                          {newTourStartSearchQuery.trim().length < 2 ? (
                            <Text style={styles.newTourDropdownHint}>Type 2+ characters to search</Text>
                          ) : startVenueResults.length === 0 ? (
                            <Text style={styles.newTourDropdownHint}>No venues found</Text>
                          ) : (
                            <ScrollView nestedScrollEnabled keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator>
                              {startVenueResults.map((v) => (
                                <React.Fragment key={v.id}>
                                  {renderVenueSearchResultRow(v, newTourStartSearchQuery, {
                                    rowStyle: styles.newTourDropdownRow,
                                    nameStyle: styles.newTourDropdownName,
                                    subStyle: styles.newTourDropdownMeta,
                                    onPress: () => {
                                      setNewTourStartVenue(v);
                                      setNewTourStartSearchQuery(`${v.name}, ${v.city}, ${v.state}`);
                                      setNewTourStartDropdownVisible(false);
                                    },
                                  })}
                                </React.Fragment>
                              ))}
                            </ScrollView>
                          )}
                        </View>
                      )}
                    </View>
                    <View style={styles.newTourModalSection}>
                      <Text style={styles.newTourModalLabel}>End location</Text>
                      <TextInput
                        style={styles.newTourModalInput}
                        value={newTourEndVenue ? `${newTourEndVenue.name}, ${newTourEndVenue.city}, ${newTourEndVenue.state}` : newTourEndSearchQuery}
                        onChangeText={(t) => {
                          setNewTourEndSearchQuery(t);
                          setNewTourEndVenue(null);
                          setNewTourEndDropdownVisible(true);
                          setNewTourStartDropdownVisible(false);
                        }}
                        onFocus={() => {
                          setNewTourEndDropdownVisible(true);
                          setNewTourStartDropdownVisible(false);
                        }}
                        placeholder="Search venues..."
                        placeholderTextColor="#888"
                      />
                      {newTourEndDropdownVisible && (
                        <View style={styles.newTourDropdown}>
                          {newTourEndSearchQuery.trim().length < 2 ? (
                            <Text style={styles.newTourDropdownHint}>Type 2+ characters to search</Text>
                          ) : endVenueResults.length === 0 ? (
                            <Text style={styles.newTourDropdownHint}>No venues found</Text>
                          ) : (
                            <ScrollView nestedScrollEnabled keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator>
                              {endVenueResults.map((v) => (
                                <React.Fragment key={v.id}>
                                  {renderVenueSearchResultRow(v, newTourEndSearchQuery, {
                                    rowStyle: styles.newTourDropdownRow,
                                    nameStyle: styles.newTourDropdownName,
                                    subStyle: styles.newTourDropdownMeta,
                                    onPress: () => {
                                      setNewTourEndVenue(v);
                                      setNewTourEndSearchQuery(`${v.name}, ${v.city}, ${v.state}`);
                                      setNewTourEndDropdownVisible(false);
                                    },
                                  })}
                                </React.Fragment>
                              ))}
                            </ScrollView>
                          )}
                        </View>
                      )}
                    </View>
                    <View style={styles.newTourModalSection}>
                      <Text style={styles.newTourModalLabel}>Number of days</Text>
                      <TextInput
                        style={styles.newTourModalInput}
                        value={newTourNumberOfDays}
                        onChangeText={(t) => setNewTourNumberOfDays(t.replace(/\D/g, '').slice(0, 2) || '1')}
                        placeholder="1–99"
                        placeholderTextColor="#888"
                        keyboardType="number-pad"
                      />
                    </View>
                    <View style={styles.newTourModalSection}>
                      <Text style={styles.newTourModalLabel}>Tour start date (optional)</Text>
                      {Platform.OS === 'web' ? (
                        <input
                          type="date"
                          value={newTourStartDate}
                          onChange={(e) => setNewTourStartDate(e.target.value || '')}
                          style={{
                            width: '100%',
                            padding: 12,
                            fontSize: 15,
                            borderRadius: 8,
                            border: '1px solid rgba(0,0,0,0.1)',
                            backgroundColor: 'rgba(0,0,0,0.06)',
                            color: theme?.text?.primary || '#1d1d1d',
                          }}
                        />
                      ) : (
                        <TextInput
                          style={styles.newTourModalInput}
                          value={newTourStartDate}
                          onChangeText={setNewTourStartDate}
                          placeholder="YYYY-MM-DD"
                          placeholderTextColor="#888"
                        />
                      )}
                    </View>
                    <View style={styles.newTourModalSection}>
                      <Text style={styles.newTourModalLabel}>Tour end date (optional)</Text>
                      {Platform.OS === 'web' ? (
                        <input
                          type="date"
                          value={newTourEndDate}
                          onChange={(e) => setNewTourEndDate(e.target.value || '')}
                          style={{
                            width: '100%',
                            padding: 12,
                            fontSize: 15,
                            borderRadius: 8,
                            border: '1px solid rgba(0,0,0,0.1)',
                            backgroundColor: 'rgba(0,0,0,0.06)',
                            color: theme?.text?.primary || '#1d1d1d',
                          }}
                        />
                      ) : (
                        <TextInput
                          style={styles.newTourModalInput}
                          value={newTourEndDate}
                          onChangeText={setNewTourEndDate}
                          placeholder="YYYY-MM-DD"
                          placeholderTextColor="#888"
                        />
                      )}
                    </View>
                    <TouchableOpacity
                      style={styles.newTourModalCreateButton}
                      onPress={submitNewTourSetup}
                    >
                      <Text style={styles.newTourModalCreateButtonText}>Create Tour</Text>
                    </TouchableOpacity>
                  </ScrollView>
                </TouchableOpacity>
              </TouchableOpacity>
            </FullscreenSafeModal>

          {/* Show cluster venues if any are selected */}
          {clusterVenues.length > 0 && (
            <View style={styles.clusterSection}>
              <Text style={styles.clusterTitle}>
                Selected Cluster ({clusterVenues.length} venues)
              </Text>
            </View>
          )}

          <FlatList
            data={displayedVenues}
            keyExtractor={(item) => item.id || String(item.name)}
            renderItem={renderVenueCard}
            style={styles.venueListScroll}
            contentContainerStyle={{ paddingBottom: 20 }}
            initialNumToRender={15}
            maxToRenderPerBatch={10}
            windowSize={8}
            removeClippedSubviews={true}
            ListFooterComponent={
              <>
                {/* User Custom Locations Section */}
                {userLocations.length > 0 && (
                  <View style={styles.userLocationsSection}>
                    <Text style={styles.userLocationsSectionTitle}>
                      📌 My Custom Locations ({userLocations.length})
                    </Text>
                    {userLocations.map(location => {
                      const category = locationCategoriesAll.find(c => c.id === location.category) || locationCategoriesAll.find(c => c.id === 'other');
                      const optionsRow = (
                        <View style={[styles.userLocationActions, { marginTop: 0, paddingTop: 8, marginBottom: 0, borderTopWidth: 1, borderTopColor: theme.ui.border }]}>
                          <TouchableOpacity style={styles.userLocationActionBtn} onPress={(e) => { e.stopPropagation(); toggleLocationVisibility(location.id, location.isPublic); }}>
                            <Text style={styles.userLocationActionBtnText}>{location.isPublic ? '🔒 Lock' : '🌐 Public'}</Text>
                          </TouchableOpacity>
                          <TouchableOpacity style={styles.userLocationActionBtn} onPress={(e) => { e.stopPropagation(); setEditingLocation(location); }}>
                            <Text style={styles.userLocationActionBtnText}>✏️ Edit</Text>
                          </TouchableOpacity>
                          <TouchableOpacity style={styles.userLocationActionBtn} onPress={(e) => { e.stopPropagation(); deleteUserLocation(location.id); }}>
                            <Text style={[styles.userLocationActionBtnText, { color: theme.text.danger || '#e74c3c' }]}>🗑️ Delete</Text>
                          </TouchableOpacity>
                        </View>
                      );
                      const cardContent = (
                        <>
                          <View style={{ flexDirection: 'row', alignItems: 'flex-start', width: '100%' }}>
                            <View style={styles.venueImagePlaceholder}>
                              <Text style={styles.venueImageIcon}>{category?.icon || '📌'}</Text>
                            </View>
                            <View style={styles.venueInfo}>
                              <Text style={styles.venueName} numberOfLines={1}>{location.name}</Text>
                              <Text style={styles.venueLocation} numberOfLines={1}>
                                {location.address || location.notes || 'Custom location'}
                              </Text>
                              <View style={styles.venueMetaRow}>
                                <Text style={styles.venueMetaText}>{category?.label || 'Other'}</Text>
                                <Text style={styles.venueMetaDot}>•</Text>
                                <Text style={styles.venueMetaText}>{location.isPublic ? 'Public' : 'Private'}</Text>
                              </View>
                            </View>
                            <TouchableOpacity
                              style={[styles.addButtonSmall, routesCalculating && styles.addButtonSmallDisabled]}
                              onPress={(e) => { e.stopPropagation(); const venue = userLocationToVenue(location); if (venue) addVenueToDay(venue, activeDayId); }}
                              disabled={routesCalculating}
                            >
                              <Text style={styles.addButtonSmallText}>+</Text>
                            </TouchableOpacity>
                          </View>
                          {optionsRow}
                        </>
                      );
                      const cardStyle = [styles.venueCard, location.isPublic && styles.userLocationCardPublic, { flexDirection: 'column', alignItems: 'stretch' }];
                      return (
                        <View key={location.id} style={{ marginBottom: 12 }}>
                          {Platform.OS === 'web' ? (
                            <DndDraggableUserLocationCard
                              locationId={location.id}
                              style={cardStyle}
                              routesCalculating={routesCalculating}
                            >
                              {cardContent}
                            </DndDraggableUserLocationCard>
                          ) : (
                            <TouchableOpacity style={cardStyle}>{cardContent}</TouchableOpacity>
                          )}
                        </View>
                      );
                    })}
                  </View>
                )}

                {/* Public Locations from Other Users */}
                {showUserLocations && publicUserLocations.length > 0 && (
                  <View style={styles.userLocationsSection}>
                    <Text style={styles.userLocationsSectionTitle}>
                      🌐 Public Locations ({publicUserLocations.length})
                    </Text>
                    {publicUserLocations.map(location => {
                      const category = locationCategoriesAll.find(c => c.id === location.category) || locationCategoriesAll.find(c => c.id === 'other');
                      const cardContent = (
                        <>
                          <View style={{ flexDirection: 'row', alignItems: 'flex-start', width: '100%' }}>
                            <View style={styles.venueImagePlaceholder}>
                              <Text style={styles.venueImageIcon}>{category?.icon || '📌'}</Text>
                            </View>
                            <View style={styles.venueInfo}>
                              <Text style={styles.venueName} numberOfLines={1}>{location.name}</Text>
                              <Text style={styles.venueLocation} numberOfLines={1}>
                                {location.address || location.notes || 'Custom location'}
                              </Text>
                              <View style={styles.venueMetaRow}>
                                <Text style={styles.venueMetaText}>{category?.label || 'Other'}</Text>
                                <Text style={styles.venueMetaDot}>•</Text>
                                <Text style={[styles.venueMetaText, { fontSize: 11 }]}>By {location.userName || 'User'}</Text>
                              </View>
                            </View>
                            <TouchableOpacity
                              style={styles.zoomButtonSmall}
                              onPress={(e) => { e.stopPropagation(); if (map.current && location.latitude != null && location.longitude != null) { map.current.flyTo({ center: [location.longitude, location.latitude], zoom: 15, essential: true }); } }}
                            >
                              <Text style={styles.zoomButtonSmallText}>🔍</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={[styles.addButtonSmall, routesCalculating && styles.addButtonSmallDisabled]}
                              onPress={(e) => { e.stopPropagation(); const venue = userLocationToVenue(location); if (venue) addVenueToDay(venue, activeDayId); }}
                              disabled={routesCalculating}
                            >
                              <Text style={styles.addButtonSmallText}>+</Text>
                            </TouchableOpacity>
                          </View>
                        </>
                      );
                      const cardStyle = [styles.venueCard, styles.userLocationCardPublic, { flexDirection: 'column', alignItems: 'stretch' }];
                      return (
                        <View key={location.id} style={{ marginBottom: 12 }}>
                          {Platform.OS === 'web' ? (
                            <DndDraggableUserLocationCard
                              locationId={location.id}
                              style={cardStyle}
                              routesCalculating={routesCalculating}
                            >
                              {cardContent}
                            </DndDraggableUserLocationCard>
                          ) : (
                            <TouchableOpacity style={cardStyle}>{cardContent}</TouchableOpacity>
                          )}
                        </View>
                      );
                    })}
                  </View>
                )}
              </>
            }
          />
        </Animated.View>

        {/* Middle - Map (Main Component) + below-map bar */}
        <View style={[styles.mapColumn, isMobile && { flex: 1, minHeight: 0 }]}>
        {/* Real DOM shell: Fullscreen API takes over this node (map + rails + tools + day planner) */}
        <div
          ref={mapStageRef}
          className="vrc-map-stage"
          {...(Platform.OS === 'web' ? { [MAP_STAGE_FS_ATTR]: isMapFullscreen ? 'true' : 'false' } : {})}
          style={StyleSheet.flatten([
            styles.mapContainer,
            isMobile && styles.mapContainerMobileFullScreen,
            isMobile && { height: undefined, minHeight: 0, bottom: mobileMapBottomInsetPx },
            !isMobile && isMapFullscreen && styles.mapContainerViewportFullscreen,
            calendarEmbed && { borderRadius: 0, boxShadow: 'none', borderWidth: 0, minHeight: 0, flex: 1 },
            { display: 'flex', flexDirection: 'column' },
          ])}
        >
          {/* Bus call picking banner */}
          {pickingBusCallForDayId && (
            <View style={styles.busCallPickingBanner}>
              <Text style={styles.busCallPickingBannerText}>🚌 Click the map to set bus call location</Text>
              <TouchableOpacity onPress={() => setPickingBusCallForDayId(null)} style={{ marginLeft: 6, padding: 4 }}>
                <Text style={{ color: '#fff', fontWeight: '700', fontSize: 16 }}>✕</Text>
              </TouchableOpacity>
            </View>
          )}
          {/* On mobile: tap map to close day planner */}
          {isMobile && tourStopsVisible && (
            <TouchableOpacity
              style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 35 }}
              activeOpacity={1}
              onPress={toggleTourStops}
            />
          )}
          {Platform.OS === 'web' ? (
            <>
              <View style={isMobile ? styles.mobileMapCanvasWrap : { flex: 1, width: '100%', height: '100%' }}>
                <div
                  ref={(el) => {
                    mapContainer.current = el;
                    setMapContainerMounted(!!el);
                  }}
                  className={addLocationMode ? 'add-location-mode' : ''}
                  style={{ width: '100%', height: '100%', borderRadius: isMobile ? 0 : 8 }}
                />
              </View>
              {renderMapSideRail()}
              {masterTourUi ? (
              <MapDisplayModeRail
                isDarkMode={isDarkMode}
                isMobile={isMobile}
                displayMode={displayMode === 'clusters' ? 'pins' : displayMode}
                onDisplayModeChange={setDisplayMode}
                onLayout={(e) => setMapDisplayModeRailBox({ width: Math.round(e.nativeEvent.layout.width), height: Math.round(e.nativeEvent.layout.height) })}
              />
              ) : null}
              {renderVenueFilterControls()}
              {/* Hidden for now — venue search / filters floating panel
              {renderModeControls()}
              */}
              {renderRightMapToursRail()}
              {renderMapSideTools()}
              {renderAccessibilityMapButton()}
              {renderDrawControls()}

              {renderBottomControls()}

              {mapLoaded && (loadingPOIs || transitLoading) && (
                <View style={styles.poiLoadingOverlay} pointerEvents="none">
                  <View style={styles.poiLoadingPill}>
                    <ActivityIndicator size="small" color={isDarkMode ? '#ffffff' : '#e67e22'} />
                    <Text style={styles.poiLoadingText}>
                      {transitLoading ? 'Loading transit stops…' : 'Loading map POIs…'}
                    </Text>
                  </View>
                </View>
              )}

              {/* Hidden for now — weather / radar panel (bottom-left)
              {showWeatherPanel && !(isMobile && Platform.OS === 'web') && (
                <WeatherPanel
                  coords={mapCenter}
                  isDarkMode={isDarkMode}
                  radarEnabled={radarEnabled}
                  onToggleRadar={() => setRadarEnabled(v => !v)}
                  isMobile={isMobile}
                  expanded={weatherExpanded}
                  onExpandedChange={setWeatherExpanded}
                  onClose={() => setShowWeatherPanel(false)}
                  onPanelLayout={(layout) => {
                    const h = layout?.height;
                    if (typeof h === 'number' && h > 0) setWeatherPanelHeight(h);
                  }}
                />
              )}
              */}

              {userLocation && (
                <View style={styles.statsOverlay}>
                  <Text style={styles.statsText}>Location tracking enabled</Text>
                </View>
              )}

              {/* Add / Edit Location Panel (same sidebar) */}
              {(showAddLocationPanel || editingLocation) && (
                <View style={styles.addLocationPanel}>
                  <View style={styles.addLocationPanelHeader}>
                    <Text style={styles.addLocationPanelTitle}>{editingLocation ? 'Edit Location' : 'Add New Location'}</Text>
                    <TouchableOpacity
                      style={styles.addLocationPanelClose}
                      onPress={() => {
                        if (editingLocation) {
                          setEditingLocation(null);
                          setNewLocationData({ name: '', notes: '', category: 'other', address: '', isPublic: false, capacity: '', affiliate: '' });
                          setSuggestCategoryLabel('');
                          setSuggestCategoryIcon('');
                        } else {
                          closeAddLocationPanel();
                        }
                      }}
                    >
                      <Text style={styles.addLocationPanelCloseText}>✕</Text>
                    </TouchableOpacity>
                  </View>

                  <View style={{ flex: 1, position: 'relative' }}>
                  {editingLocation ? (
                    <ScrollView style={styles.addLocationPanelContent} contentContainerStyle={{ paddingBottom: 24 }} keyboardShouldPersistTaps="handled">
                      <Text style={[styles.addLocationLabel, { marginBottom: 4 }]}>Name *</Text>
                      <TextInput
                        style={styles.addLocationInput}
                        value={newLocationData.name}
                        onChangeText={(text) => setNewLocationData(prev => ({ ...prev, name: text }))}
                        placeholder="Location name"
                        placeholderTextColor="#666"
                      />
                      <View style={styles.addLocationSection}>
                        <Text style={styles.addLocationLabel}>Capacity</Text>
                        <TextInput
                          style={styles.addLocationInput}
                          value={newLocationData.capacity}
                          onChangeText={(text) => setNewLocationData(prev => ({ ...prev, capacity: text.replace(/\D/g, '') }))}
                          placeholder="Number only (optional)"
                          placeholderTextColor="#666"
                          keyboardType="number-pad"
                        />
                      </View>
                      <View style={styles.addLocationSection}>
                        <Text style={styles.addLocationLabel}>Location type</Text>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                          {LOCATION_TYPE_OPTIONS.map(opt => (
                            <TouchableOpacity
                              key={opt.value}
                              style={[styles.categoryButton, newLocationData.affiliate === opt.value && styles.categoryButtonActive]}
                              onPress={() => setNewLocationData(prev => ({ ...prev, affiliate: prev.affiliate === opt.value ? '' : opt.value }))}
                            >
                              <Text style={[styles.categoryButtonText, newLocationData.affiliate === opt.value && styles.categoryButtonTextActive]}>{opt.label}</Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      </View>
                      <View style={styles.addLocationSection}>
                        <Text style={styles.addLocationLabel}>Category</Text>
                        <View style={styles.categoryGrid}>
                          {locationCategoriesAll.map((cat) => (
                            <TouchableOpacity
                              key={cat.id}
                              style={[
                                styles.categoryButton,
                                newLocationData.category === cat.id && styles.categoryButtonActive,
                              ]}
                              onPress={() => setNewLocationData((prev) => ({ ...prev, category: cat.id }))}
                            >
                              <Text style={styles.categoryButtonIcon}>{cat.icon}</Text>
                              <Text
                                style={[
                                  styles.categoryButtonText,
                                  newLocationData.category === cat.id && styles.categoryButtonTextActive,
                                ]}
                              >
                                {cat.label}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      </View>
                      <View style={styles.addLocationSection}>
                        <Text style={styles.addLocationLabel}>Suggest a new category</Text>
                        <Text style={{ fontSize: 11, color: theme.text.tertiary, marginBottom: 8 }}>
                          Suggestions are reviewed by an admin. Once approved, they appear in this list for everyone.
                        </Text>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                          <TextInput
                            style={[styles.addLocationInput, { flex: 1, minWidth: 120 }]}
                            value={suggestCategoryLabel}
                            onChangeText={setSuggestCategoryLabel}
                            placeholder="Category name"
                            placeholderTextColor="#666"
                          />
                          <TextInput
                            style={[styles.addLocationInput, { width: 56 }]}
                            value={suggestCategoryIcon}
                            onChangeText={(t) => setSuggestCategoryIcon(t.slice(0, 8))}
                            placeholder="Icon"
                            placeholderTextColor="#666"
                          />
                          <TouchableOpacity
                            style={[
                              styles.saveLocationButton,
                              { paddingHorizontal: 12 },
                              (!suggestCategoryLabel.trim() || categorySuggestionSubmitting) && styles.saveLocationButtonDisabled,
                            ]}
                            onPress={submitCategorySuggestion}
                            disabled={!suggestCategoryLabel.trim() || categorySuggestionSubmitting}
                          >
                            {categorySuggestionSubmitting ? (
                              <ActivityIndicator color="#fff" size="small" />
                            ) : (
                              <Text style={styles.saveLocationButtonText}>Submit</Text>
                            )}
                          </TouchableOpacity>
                        </View>
                      </View>
                      <View style={styles.addLocationSection}>
                        <Text style={styles.addLocationLabel}>Address (optional)</Text>
                        <TextInput
                          style={styles.addLocationInput}
                          value={newLocationData.address}
                          onChangeText={(text) => setNewLocationData(prev => ({ ...prev, address: text }))}
                          placeholder="Address"
                          placeholderTextColor="#666"
                        />
                      </View>
                      <View style={styles.addLocationSection}>
                        <Text style={styles.addLocationLabel}>Notes (optional)</Text>
                        <TextInput
                          style={styles.addLocationTextarea}
                          value={newLocationData.notes}
                          onChangeText={(text) => setNewLocationData(prev => ({ ...prev, notes: text }))}
                          placeholder="Notes"
                          placeholderTextColor="#666"
                          multiline
                          numberOfLines={3}
                        />
                      </View>
                      <View style={styles.addLocationSection}>
                        <TouchableOpacity
                          style={styles.editLocationCheckbox}
                          onPress={() => setNewLocationData(prev => ({ ...prev, isPublic: !prev.isPublic }))}
                        >
                          <View style={{
                            width: 22,
                            height: 22,
                            borderRadius: 4,
                            borderWidth: 2,
                            borderColor: newLocationData.isPublic ? '#27ae60' : theme.ui.border,
                            backgroundColor: newLocationData.isPublic ? '#27ae60' : 'transparent',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {newLocationData.isPublic && <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>✓</Text>}
                          </View>
                          <View>
                            <Text style={[styles.editLocationCheckboxLabel, { fontWeight: '500' }]}>Make this location public</Text>
                          </View>
                        </TouchableOpacity>
                      </View>
                      <View style={styles.addLocationActions}>
                        <TouchableOpacity
                          style={[styles.cancelLocationButton, { marginRight: 8 }]}
                          onPress={() => {
                            setEditingLocation(null);
                            setNewLocationData({ name: '', notes: '', category: 'other', address: '', isPublic: false, capacity: '', affiliate: '' });
                            setSuggestCategoryLabel('');
                            setSuggestCategoryIcon('');
                          }}
                        >
                          <Text style={styles.cancelLocationButtonText}>Cancel</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[styles.saveLocationButton, (!newLocationData.name.trim() && !updateLocationLoading) && styles.saveLocationButtonDisabled]}
                          onPress={async () => {
                            if (!newLocationData.name.trim()) return;
                            setUpdateLocationLoading(true);
                            try {
                              const cap = newLocationData.capacity !== '' && newLocationData.capacity != null
                                ? Number(String(newLocationData.capacity).replace(/\D/g, '')) || null
                                : null;
                              await updateUserLocation(editingLocation.id, {
                                name: newLocationData.name.trim(),
                                notes: newLocationData.notes.trim(),
                                address: newLocationData.address.trim(),
                                isPublic: newLocationData.isPublic,
                                capacity: cap != null && !Number.isNaN(cap) ? cap : null,
                                affiliate: newLocationData.affiliate?.trim() || null,
                                category: newLocationData.category || 'other',
                              });
                            } finally {
                              setUpdateLocationLoading(false);
                            }
                          }}
                          disabled={!newLocationData.name.trim() || updateLocationLoading}
                        >
                          {updateLocationLoading ? (
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                              <ActivityIndicator size="small" color="#fff" />
                              <Text style={styles.saveLocationButtonText}>Saving…</Text>
                            </View>
                          ) : (
                            <Text style={styles.saveLocationButtonText}>Save</Text>
                          )}
                        </TouchableOpacity>
                      </View>
                    </ScrollView>
                  ) : (
                  <>
                  <ScrollView style={styles.addLocationPanelContent}>
                    {/* Address Search - wrapper keeps dropdown directly under input and above rest of form */}
                    <View style={styles.addLocationSection}>
                      <Text style={styles.addLocationLabel}>Search address or location</Text>
                      <View style={styles.addressSearchContainer}>
                        <TextInput
                          style={styles.addLocationInput}
                          value={addressSearchQuery}
                          onChangeText={handleAddressSearchChange}
                          placeholder="Type an address or place (worldwide)..."
                          placeholderTextColor="#666"
                        />
                        {addressSearchResults.length > 0 && (
                          <ScrollView style={styles.addressSearchResults}>
                            {addressSearchResults.map(result => (
                              <TouchableOpacity
                                key={result.id}
                                style={styles.addressSearchResult}
                                onPress={() => selectAddressResult(result)}
                              >
                                <Text style={styles.addressSearchResultText}>{result.name}</Text>
                                <Text style={styles.addressSearchResultSubtext} numberOfLines={1}>
                                  {result.fullAddress}
                                </Text>
                              </TouchableOpacity>
                            ))}
                          </ScrollView>
                        )}
                        {isSearchingAddress && (
                          <Text style={{ padding: 8, color: theme.text.tertiary, fontSize: 12 }}>
                            Searching...
                          </Text>
                        )}
                      </View>
                      <Text style={{ fontSize: 11, color: theme.text.tertiary, marginTop: 6 }}>
                        Or click on the map to set location
                      </Text>
                    </View>

                    {/* Show selected coordinates */}
                    {pendingLocationCoords && (
                      <View style={styles.coordsDisplay}>
                        <Text style={styles.coordsDisplayText}>
                          📍 {pendingLocationCoords.lat.toFixed(5)}, {pendingLocationCoords.lng.toFixed(5)}
                        </Text>
                      </View>
                    )}

                    {/* Name */}
                    <View style={styles.addLocationSection}>
                      <Text style={styles.addLocationLabel}>Name *</Text>
                      <TextInput
                        style={styles.addLocationInput}
                        value={newLocationData.name}
                        onChangeText={(text) => setNewLocationData(prev => ({ ...prev, name: text }))}
                        placeholder="Enter a name for this location"
                        placeholderTextColor="#666"
                      />
                    </View>

                    {/* Capacity */}
                    <View style={styles.addLocationSection}>
                      <Text style={styles.addLocationLabel}>Capacity</Text>
                      <TextInput
                        style={styles.addLocationInput}
                        value={newLocationData.capacity}
                        onChangeText={(text) => setNewLocationData(prev => ({ ...prev, capacity: text.replace(/\D/g, '') }))}
                        placeholder="Number only (optional)"
                        placeholderTextColor="#666"
                        keyboardType="number-pad"
                      />
                    </View>

                    {/* Location type (affiliate) */}
                    <View style={styles.addLocationSection}>
                      <Text style={styles.addLocationLabel}>Location type</Text>
                        <View style={{ flexDirection: 'row', width: '100%', flexWrap: 'wrap', gap: 8 }}>
                          {LOCATION_TYPE_OPTIONS.map(opt => (
                            <TouchableOpacity
                              key={opt.value}
                              style={[
                                styles.categoryButton,
                                newLocationData.affiliate === opt.value && styles.categoryButtonActive
                              ]}
                              onPress={() => setNewLocationData(prev => ({ ...prev, affiliate: prev.affiliate === opt.value ? '' : opt.value }))}
                            >
                              <Text style={[
                                styles.categoryButtonText,
                                newLocationData.affiliate === opt.value && styles.categoryButtonTextActive
                              ]}>{opt.label}</Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                    </View>

                    {/* Category */}
                    <View style={styles.addLocationSection}>
                      <Text style={styles.addLocationLabel}>Category</Text>
                      <View style={styles.categoryGrid}>
                        {locationCategoriesAll.map(cat => (
                          <TouchableOpacity
                            key={cat.id}
                            style={[
                              styles.categoryButton,
                              newLocationData.category === cat.id && styles.categoryButtonActive
                            ]}
                            onPress={() => setNewLocationData(prev => ({ ...prev, category: cat.id }))}
                          >
                            <Text style={styles.categoryButtonIcon}>{cat.icon}</Text>
                            <Text style={[
                              styles.categoryButtonText,
                              newLocationData.category === cat.id && styles.categoryButtonTextActive
                            ]}>{cat.label}</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>

                    <View style={styles.addLocationSection}>
                      <Text style={styles.addLocationLabel}>Suggest a new category</Text>
                      <Text style={{ fontSize: 11, color: theme.text.tertiary, marginBottom: 8 }}>
                        Suggestions are reviewed by an admin. Once approved, they appear in this list for everyone.
                      </Text>
                      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                        <TextInput
                          style={[styles.addLocationInput, { flex: 1, minWidth: 120 }]}
                          value={suggestCategoryLabel}
                          onChangeText={setSuggestCategoryLabel}
                          placeholder="Category name"
                          placeholderTextColor="#666"
                        />
                        <TextInput
                          style={[styles.addLocationInput, { width: 56 }]}
                          value={suggestCategoryIcon}
                          onChangeText={(t) => setSuggestCategoryIcon(t.slice(0, 8))}
                          placeholder="Icon"
                          placeholderTextColor="#666"
                        />
                        <TouchableOpacity
                          style={[
                            styles.saveLocationButton,
                            { paddingHorizontal: 12 },
                            (!suggestCategoryLabel.trim() || categorySuggestionSubmitting) && styles.saveLocationButtonDisabled,
                          ]}
                          onPress={submitCategorySuggestion}
                          disabled={!suggestCategoryLabel.trim() || categorySuggestionSubmitting}
                        >
                          {categorySuggestionSubmitting ? (
                            <ActivityIndicator color="#fff" size="small" />
                          ) : (
                            <Text style={styles.saveLocationButtonText}>Submit</Text>
                          )}
                        </TouchableOpacity>
                      </View>
                    </View>

                    {/* Description */}
                    <View style={styles.addLocationSection}>
                      <Text style={styles.addLocationLabel}>Description</Text>
                      <TextInput
                        style={styles.addLocationTextarea}
                        value={newLocationData.notes}
                        onChangeText={(text) => setNewLocationData(prev => ({ ...prev, notes: text }))}
                        placeholder="Add a description for this location (optional)"
                        placeholderTextColor="#666"
                        multiline
                        numberOfLines={3}
                      />
                    </View>

                    {/* Public Toggle */}
                    <View style={styles.addLocationSection}>
                      <TouchableOpacity
                        style={styles.editLocationCheckbox}
                        onPress={() => setNewLocationData(prev => ({ ...prev, isPublic: !prev.isPublic }))}
                      >
                        <View style={{
                          width: 22,
                          height: 22,
                          borderRadius: 4,
                          borderWidth: 2,
                          borderColor: newLocationData.isPublic ? '#27ae60' : theme.ui.border,
                          backgroundColor: newLocationData.isPublic ? '#27ae60' : 'transparent',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {newLocationData.isPublic && (
                            <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>✓</Text>
                          )}
                        </View>
                        <View>
                          <Text style={[styles.editLocationCheckboxLabel, { fontWeight: '500' }]}>
                            Make this location public
                          </Text>
                          <Text style={{ fontSize: 11, color: theme.text.tertiary }}>
                            Other users will be able to see this location
                          </Text>
                        </View>
                      </TouchableOpacity>
                    </View>
                  </ScrollView>

                  {/* Action Buttons */}
                  <View style={styles.addLocationActions}>
                    <TouchableOpacity
                      style={[
                        styles.saveLocationButton,
                        (!newLocationData.name.trim() || !pendingLocationCoords) && !saveLocationLoading && styles.saveLocationButtonDisabled
                      ]}
                      onPress={saveNewLocation}
                      disabled={!newLocationData.name.trim() || !pendingLocationCoords || saveLocationLoading}
                    >
                      {saveLocationLoading ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <ActivityIndicator size="small" color="#fff" />
                          <Text style={styles.saveLocationButtonText}>Saving…</Text>
                        </View>
                      ) : (
                        <Text style={styles.saveLocationButtonText}>Save Location</Text>
                      )}
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.cancelLocationButton}
                      onPress={closeAddLocationPanel}
                    >
                      <Text style={styles.cancelLocationButtonText}>Cancel</Text>
                    </TouchableOpacity>
                  </View>
                  </>
                  )}
                  {(saveLocationLoading || updateLocationLoading) && (
                    <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', zIndex: 10 }}>
                      <ActivityIndicator size="large" color="#fff" />
                      <Text style={{ color: '#fff', marginTop: 8, fontSize: 16 }}>Saving…</Text>
                    </View>
                  )}
                </View>
                </View>
              )}
            </>
          ) : (
            <Text style={styles.mobileMapText}>
              Map view is available on the web version
            </Text>
          )}

        {/* Day Planner — mobile bottom sheet; desktop panel below Map/Tours tabs */}
        {(isMobile || (tourStopsVisible && rightRailTab !== 'map' && rightRailTab !== 'tours' && rightRailTab !== 'funds')) && (
        <Animated.View style={[
          styles.venueList,
          isMobile ? {
            // Mobile: viewport-height bottom sheet aligned with weather chrome
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: tourStopsVisible ? mobilePlannerSheetPx : 0,
            width: '100%',
            zIndex: 40,
            paddingTop: tourStopsVisible ? 16 : 0,
            paddingHorizontal: tourStopsVisible ? 16 : 0,
            paddingBottom: tourStopsVisible ? MOBILE_MAP_BOTTOM_GAP : 0,
            opacity: tourStopsAnim,
            backgroundColor: isDarkMode ? 'rgba(6,9,22,0.92)' : 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(28px) saturate(180%)',
            WebkitBackdropFilter: 'blur(28px) saturate(180%)',
            borderRadius: 0,
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            borderTopWidth: 1,
            borderLeftWidth: 0,
            borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)',
            boxShadow: isDarkMode ? '0 -12px 48px rgba(0,0,0,0.6)' : '0 -12px 48px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transition: 'height 0.3s ease',
            boxSizing: 'border-box',
          } : {
            // Desktop: stacked below Map / Tours tabs (top-right)
            position: 'absolute',
            top: RIGHT_RAIL_CONTENT_TOP,
            right: 10,
            bottom: 10,
            width: RIGHT_RAIL_PANEL_WIDTH,
            zIndex: 14,
            padding: tourStopsVisible ? 18 : 0,
            opacity: tourStopsAnim,
            backgroundColor: isDarkMode ? 'rgba(6,9,22,0.72)' : 'rgba(255,255,255,0.88)',
            backdropFilter: 'blur(28px) saturate(180%)',
            WebkitBackdropFilter: 'blur(28px) saturate(180%)',
            borderRadius: 14,
            borderWidth: 1,
            borderColor: isDarkMode ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.10)',
            boxShadow: isDarkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(0,0,0,0.12)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }
        ]}>
          {/* Mobile drag handle */}
          {isMobile && (
            <View style={{ alignItems: 'center', marginBottom: 8, marginTop: -4 }}>
              <View style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.2)' }} />
            </View>
          )}

          {/* Compact sidebar header: tour name + action buttons in one row */}
          <View style={{ flexDirection: 'row', alignItems: 'center', paddingBottom: 10, borderBottomWidth: 1, borderBottomColor: isDarkMode ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.10)', marginBottom: 8, zIndex: 100 }}>
            <TextInput
              style={[styles.tourNameInput,
                { color: isDarkMode ? '#d5b791' : theme.brand.primary },
                { flex: 1, width: undefined, fontSize: 12, letterSpacing: 2, textAlign: 'left', backgroundColor: 'transparent', outlineColor: 'transparent', borderBottomWidth: 0 }
              ]}
              nativeID="tour-planner-sidebar-name"
              value={tourName}
              onChangeText={setTourName}
              placeholder="NAeME"
              placeholderTextColor={isDarkMode ? "#d5b791" : "rgba(230,126,34,0.65)"}
            />
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <TouchableOpacity
                style={styles.plusTourIconButton}
                onPress={() => {
                  if (!mapGateRef.current(MAP_ACTION.PLAN)) return;
                  if (currentTourId || ((tourName && tourName !== 'My Tour') || tourDays[0].venues.length > 0)) {
                    setConfirmNewTourVisible(true);
                  } else {
                    openNewTourModal();
                  }
                }}
              >
                <Ionicons name="add" size={22} color={theme.brand.secondary} />
              </TouchableOpacity>
              <View style={styles.tourMenuWrap}>
                <TouchableOpacity
                  style={styles.folderMenuButton}
                  onPress={() => setOpenSidebarMenu((m) => (m === 'folder' ? null : 'folder'))}
                >
                  <Ionicons name="folder-open" size={20} color="#bca785" />
                </TouchableOpacity>
                {openSidebarMenu === 'folder' && (
                  <View style={styles.tourHeaderDropdown}>
                    <TouchableOpacity
                      style={styles.tourHeaderDropdownItem}
                      onPress={() => { setOpenSidebarMenu(null); openImportModal(); }}
                    >
                      <Text style={styles.tourHeaderDropdownItemText}>Import</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.tourHeaderDropdownItem}
                      onPress={() => { setOpenSidebarMenu(null); saveTour(); }}
                    >
                      <Text style={styles.tourHeaderDropdownItemText}>Save Tour</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
              <View style={styles.tourMenuWrap}>
                <TouchableOpacity
                  style={styles.envelopeMenuButton}
                  onPress={() => setOpenSidebarMenu((m) => (m === 'envelope' ? null : 'envelope'))}
                >
                  <Ionicons name="mail" size={20} color="#9b59b6" />
                </TouchableOpacity>
                {openSidebarMenu === 'envelope' && (
                  <View style={styles.tourHeaderDropdown}>
                    <TouchableOpacity
                      style={styles.tourHeaderDropdownItem}
                      onPress={() => { setOpenSidebarMenu(null); exportTourAsPdf(); }}
                      disabled={exportingTourPdf}
                    >
                      <Text style={styles.tourHeaderDropdownItemText}>
                        {exportingTourPdf ? 'Exporting…' : 'Export as PDF'}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.tourHeaderDropdownItem}
                      onPress={() => { setOpenSidebarMenu(null); shareTour(); }}
                    >
                      <Text style={styles.tourHeaderDropdownItemText}>Share Link</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            </View>
          </View>

          <View style={styles.sectionHeader}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Text style={styles.sectionTitle}>Tour Stops</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                {showOnlyTourVenues && (
                  <TouchableOpacity
                    style={[styles.leftPanelToolbarButton, { paddingVertical: 4 }]}
                    onPress={() => setShowOnlyTourVenues(false)}
                  >
                    <Text style={[styles.filterModalCloseText, { fontSize: 13 }]}>Edit Tour</Text>
                  </TouchableOpacity>
                )}
                {isMobile && (
                  <TouchableOpacity
                    onPress={toggleTourStops}
                    style={{ padding: 4, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.08)' }}
                  >
                    <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 18, lineHeight: 20, fontWeight: '300' }}>✕</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
            <Text style={styles.venueCountText}>{tourVenues.length} venues selected</Text>
          </View>
          {routesCalculating && (
            <View style={styles.routesCalculatingBar}>
              <Text style={styles.routesCalculatingText}>Calculating routes…</Text>
            </View>
          )}

          <ScrollView
            style={[styles.tourVenuesList, isMobile && { flex: 1, minHeight: 0, marginBottom: 0 }]}
            contentContainerStyle={isMobile ? { paddingBottom: 16 } : undefined}
          >
            {tourDays.map((day, dayIndex) => {
              const dayContainerStyle = [
                styles.dayContainer,
                dayIndex % 2 === 1 && styles.dayContainerAlternate,
                activeDayId === day.id && styles.activeDayContainer,
                dragOverDayId === day.id && !dragInsertAt && styles.dayContainerDropTarget
              ];
              const handleDropOnDay = (dayId, insertIndex, payload) => {
                console.log("Drop on day", dayId, insertIndex, payload);
                if (routesCalculating) return;
                if (payload.startsWith('add:')) {
                  const venueId = payload.slice(4);
                  let venue = displayedVenues.find(v => v.id === venueId) || venues.find(v => v.id === venueId);
                  if (!venue && venueId.startsWith('userloc-')) {
                    const locationId = venueId.replace(/^userloc-/, '');
                    const loc = userLocations.find(l => l.id === locationId) || publicUserLocations.find(l => l.id === locationId);
                    if (loc) venue = userLocationToVenue(loc);
                  }
                  if (venue) addVenueToDay(venue, dayId, insertIndex);
                } else if (payload.startsWith('move:')) {
                  const parts = payload.split(':');
                  if (parts.length >= 3) {
                    const fromDayId = parts[1];
                    const fromIndex = parseInt(parts[2], 10);
                    if (!isNaN(fromIndex)) moveVenueInTour(fromDayId, fromIndex, dayId, insertIndex);
                  }
                }
                setDragOverDayId(null);
                setDragInsertAt(null);
              };
              const onDropWithPayload = (payload) => handleDropOnDay(day.id, day.venues.length, payload);
              const dayContent = (
                <>
                  <TouchableOpacity
                    style={[
                      styles.dayHeader,
                      activeDayId === day.id && styles.activeDayHeader
                    ]}
                    onPress={() => setActiveDayId(day.id)}
                  >
                    <View style={styles.dayTitleContainer}>
                      <View style={styles.dayTitleRowMain}>
                        {editingDayId === day.id ? (
                          <View style={styles.dayEditContainer}>
                            <TextInput
                              style={styles.dayNameInput}
                              value={newDayName}
                              onChangeText={setNewDayName}
                              autoFocus
                            />
                            <TouchableOpacity
                              style={styles.editDayButton}
                              onPress={() => {
                                setTourDays(days =>
                                  days.map(d =>
                                    d.id === day.id
                                      ? { ...d, name: newDayName }
                                      : d
                                  )
                                );
                                setEditingDayId(null);
                              }}
                            >
                              <Text style={styles.saveIcon}>✓</Text>
                            </TouchableOpacity>
                          </View>
                        ) : (
                          <>
                            <Text
                              style={[
                                styles.dayTitle,
                                activeDayId === day.id && styles.activeDayTitle
                              ]}
                              numberOfLines={1}
                            >
                              {day.name}
                            </Text>
                            <TouchableOpacity
                              style={styles.editDayButton}
                              onPress={() => {
                                setEditingDayId(day.id);
                                setNewDayName(day.name);
                              }}
                            >
                              <Text style={styles.editIcon}>✎</Text>
                            </TouchableOpacity>
                          </>
                        )}
                      </View>
                      <TouchableOpacity
                        style={styles.dayHeaderDateBtn}
                        onPress={() => openDayDatePicker(day)}
                        hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
                      >
                        <Ionicons name="calendar-outline" size={14} color={theme.brand.primary} />
                        {day.scheduledDate != null && String(day.scheduledDate).trim() !== '' ? (
                          <Text style={styles.dayHeaderDateText} numberOfLines={1}>
                            {formatScheduledDateShort(day.scheduledDate)}
                          </Text>
                        ) : (
                          <Text style={[styles.dayHeaderDateHint, { color: theme.text.muted }]}>Date</Text>
                        )}
                      </TouchableOpacity>
                    </View>
                    {tourDays.length > 1 && (
                      <TouchableOpacity
                        style={styles.removeDayButton}
                        onPress={() => {
                          setTourDays(days => days.filter(d => d.id !== day.id));
                          if (activeDayId === day.id) {
                            setActiveDayId(tourDays[0].id);
                          }
                        }}
                      >
                        <Text style={styles.removeDayButtonText}>×</Text>
                      </TouchableOpacity>
                    )}
                  </TouchableOpacity>

                  {day.venues.length === 0 ? (
                      Platform.OS === 'web' ? (
                      <TourDroppable
                        id={`drop-${day.id}-0`}
                        dayId={day.id}
                        insertIndex={0}
                        styleIsOver={{ borderColor: 'blue' }}
                        style={[
                          styles.emptyDayContainer,
                          { borderColor: 'red', borderWidth: 1 },
                          activeDayId === day.id && styles.activeEmptyDayContainer
                        ]}
                      >
                        {/* <WebDropTargetVenueRow
                          dayId={day.id}
                          index={0}
                          setDragInsertAt={setDragInsertAt}
                          onDropWithPayload={(payload) => handleDropOnDay(day.id, 0, payload)}
                          routesCalculating={routesCalculating}
                          dragInsertAt={dragInsertAt}
                          style={[
                            styles.emptyDayContainer,
                            { borderColor: 'red', borderWidth: 1 },
                            activeDayId === day.id && styles.activeEmptyDayContainer
                          ]}
                        > */}
                          <Text style={styles.emptyDayText}>
                            Drop venues here
                          </Text>
                        {/* </WebDropTargetVenueRow> */}
                      </TourDroppable>
                    ) : (
                      <View style={[
                        styles.emptyDayContainer,
                        activeDayId === day.id && styles.activeEmptyDayContainer
                      ]}>
                        <Text style={styles.emptyDayText}>
                          Drop venues here
                        </Text>
                      </View>
                    )
                  ) : (
                    <>
                      {Platform.OS === 'web' ? (
                        <SortableContext items={day.venues.map((_, i) => `tour-${day.id}-${i}`)} strategy={verticalListSortingStrategy}>
                          {(() => {
                            const isDraggingFromLeftPanel = activeDragId && (activeDragId.startsWith('add-venue-') || activeDragId.startsWith('add-userloc-'));
                            const showInsertAt = (idx) => isDraggingFromLeftPanel && dragInsertAt?.dayId === day.id && dragInsertAt?.index === idx;
                            let dragPreviewVenue = null;
                            if (activeDragId?.startsWith('add-venue-')) {
                              const venueId = activeDragId.slice('add-venue-'.length);
                              dragPreviewVenue = displayedVenues.find(v => v.id === venueId) || venues.find(v => v.id === venueId);
                            } else if (activeDragId?.startsWith('add-userloc-')) {
                              const locationId = activeDragId.slice('add-userloc-'.length);
                              const loc = userLocations.find(l => l.id === locationId) || publicUserLocations.find(l => l.id === locationId);
                              if (loc) dragPreviewVenue = userLocationToVenue(loc);
                            }
                            const renderInsertGhost = (insertIndex) => (
                              <View
                                style={[
                                  styles.tourVenueCard,
                                  {
                                    opacity: 0.75,
                                    borderWidth: 2,
                                    borderStyle: 'dashed',
                                    borderColor: theme.brand?.primary || '#2c7be5',
                                    backgroundColor: (theme.brand?.primary || '#2c7be5') + '18',
                                    marginBottom: SORTABLE_ROW_GAP,
                                  },
                                ]}
                                pointerEvents="none"
                              >
                                <View style={styles.tourVenueHeaderRow}>
                                  <View style={styles.tourVenueHeader}>
                                    <View style={styles.tourVenueInfo}>
                                      <Text style={styles.tourVenueName} numberOfLines={1}>
                                        {dragPreviewVenue ? dragPreviewVenue.name : 'New stop'}
                                      </Text>
                                      <Text style={styles.tourVenueLocation} numberOfLines={1}>
                                        {dragPreviewVenue ? `${dragPreviewVenue.city || ''}, ${dragPreviewVenue.state || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || '—' : 'Drop to add'}
                                      </Text>
                                    </View>
                                  </View>
                                </View>
                              </View>
                            );
                            const mappedRows = day.venues.map((venue, index) => {
                            const venueId = String(venue?.id || '');
                            const isDbVenue = isVenuesDbTourStop(venue);
                            const tourStopTypeLabel = getTourStopTypeLabel(venue);
                            const isLodgingNight = isDayLodgingVenue(day, venueId);
                            const stopKey = stopKeyForVenue(venueId);
                            const participantIds = getStopParticipants(day, stopKey);
                            const plannerUid = auth.currentUser?.uid;
                            const isJoinedStop = plannerUid ? participantIds.includes(plannerUid) : false;
                            const accentColor = isLodgingNight ? LODGING_ACCENT_COLOR : getStopAccentColor(venue);
                            const tourCardStyle = [
                              styles.tourVenueCard,
                              isDbVenue && styles.tourVenueCardVenuesDb,
                              { borderLeftColor: accentColor },
                              isLodgingNight && lodgingCardStyle(isDarkMode),
                              tourVenueOptionsId === `${day.id}-${index}` && { zIndex: 200 },
                            ];
                            const tourCardContent = (
                              <>
                                <View style={styles.tourVenueDropdownWrap}>
                                  <View style={styles.tourVenueHeaderRow}>
                                    <View style={[styles.tourStopNumberBadge, { backgroundColor: `${accentColor}22`, borderColor: `${accentColor}66` }]}>
                                      <Text style={[styles.tourStopNumberBadgeText, { color: accentColor }]}>{index + 1}</Text>
                                    </View>
                                    <TouchableOpacity
                                      style={styles.tourVenueHeader}
                                      onPress={() => handleVenueSelect(venue)}
                                    >
                                      <View style={styles.tourVenueInfo}>
                                        <Text style={styles.tourStopTypeBadgeText}>{tourStopTypeLabel}</Text>
                                        {isLodgingNight ? (
                                          <Text style={styles.tourStopLodgingPill}>Tonight's lodging</Text>
                                        ) : null}
                                        <Text style={[styles.tourVenueName, isDbVenue && styles.tourVenueNameVenuesDb]}>{venue.name}</Text>
                                        <Text numberOfLines={1} style={[styles.tourVenueLocation, isDbVenue && styles.tourVenueLocationVenuesDb]}>
                                          {formatTourDayVenueLocationLine(venue)}
                                        </Text>
                                        {(() => {
                                          const lg = getLetterGrade(venue?.approved?.overallGrade);
                                          const cap = venue.capacity;
                                          const vType = venue.affiliate || venue.venueType;
                                          if (!lg && !cap && !vType) return null;
                                          return (
                                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 4, flexWrap: 'wrap' }}>
                                              {lg && (
                                                <View style={{ backgroundColor: `${gradeColor(lg)}22`, borderWidth: 1, borderColor: `${gradeColor(lg)}66`, borderRadius: 4, paddingHorizontal: 5, paddingVertical: 1 }}>
                                                  <Text style={{ fontSize: 10, fontWeight: '800', color: gradeColor(lg) }}>{lg}</Text>
                                                </View>
                                              )}
                                              {cap && <Text style={{ fontSize: 10, color: isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.38)' }}>Cap {cap}</Text>}
                                              {vType && <Text style={{ fontSize: 10, color: isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.38)' }} numberOfLines={1}>{vType}</Text>}
                                            </View>
                                          );
                                        })()}
                                      </View>
                                    </TouchableOpacity>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 0 }}>
                                      <TouchableOpacity
                                        style={styles.tourVenueZoomButton}
                                        onPress={() => zoomToVenue(venue)}
                                      >
                                        <Text style={styles.tourVenueZoomButtonIcon}>⊙</Text>
                                      </TouchableOpacity>
                                      <TouchableOpacity
                                        style={styles.tourVenueOptionsButton}
                                        onPress={() => setTourVenueOptionsId(prev => prev === `${day.id}-${index}` ? null : `${day.id}-${index}`)}
                                      >
                                        <Text style={[styles.tourVenueOptionsButtonText, isDbVenue && styles.tourVenueOptionsButtonTextVenuesDb]}>⋮</Text>
                                      </TouchableOpacity>
                                    </View>
                                  </View>
                                  <View style={styles.tourStopSocialWrap}>
                                    <TourStopSocialFooter
                                      compact
                                      theme={theme}
                                      isDarkMode={isDarkMode}
                                      isLodging={isLodgingNight}
                                      isJoined={isJoinedStop}
                                      canEditLodging={Boolean(plannerUid)}
                                      onToggleLodging={
                                        plannerUid
                                          ? () => toggleDayLodgingVenue(dayIndex, venueId)
                                          : null
                                      }
                                      onToggleJoin={
                                        plannerUid
                                          ? () => toggleDayStopParticipation(dayIndex, stopKey)
                                          : null
                                      }
                                      participantIds={participantIds}
                                      members={tourPartyMembers}
                                    />
                                  </View>
                                  {tourVenueOptionsId === `${day.id}-${index}` && (
                                    <View style={styles.tourVenueDropdown}>
                                      {day.venues.length >= 2 && index < day.venues.length - 1 && (
                                        <TouchableOpacity
                                          style={styles.tourVenueDropdownItem}
                                          onPress={() => {
                                            setTourVenueOptionsId(null);
                                            splitDayAfter(day.id, index);
                                          }}
                                        >
                                          <Text style={styles.tourVenueDropdownItemText}>Split day after this stop</Text>
                                        </TouchableOpacity>
                                      )}
                                      <TouchableOpacity
                                        style={[styles.tourVenueDropdownItem, styles.tourVenueDropdownItemDanger]}
                                        onPress={() => {
                                          setTourVenueOptionsId(null);
                                          const newTourDays = tourDays.map(d => {
                                            if (d.id !== day.id) return d;
                                            const newVenues = [...d.venues.slice(0, index), ...d.venues.slice(index + 1)];
                                            const legModes = d.legModes || [];
                                            const newLegModes = [...legModes.slice(0, index), ...legModes.slice(index + 1)];
                                            return { ...d, venues: newVenues, legModes: newLegModes.length ? newLegModes : undefined };
                                          });
                                          const newTourVenues = newTourDays.flatMap(d => d.venues);
                                          setTourVenues(newTourVenues);
                                          setTourDays(newTourDays);
                                          directUpdateMapWithRoutes(newTourVenues, newTourDays, activeDayId);
                                        }}
                                      >
                                        <Text style={[styles.tourVenueDropdownItemText, styles.tourVenueDropdownItemTextDanger]}>Remove from Tour</Text>
                                      </TouchableOpacity>
                                      <TouchableOpacity
                                        style={styles.tourVenueDropdownItem}
                                        onPress={() => {
                                          setTourVenueOptionsId(null);
                                          setMapDetailsItem({ type: 'venue', data: venue });
                                        }}
                                      >
                                        <Text style={styles.tourVenueDropdownItemText}>View Details</Text>
                                      </TouchableOpacity>
                                      <TouchableOpacity
                                        style={styles.tourVenueDropdownItem}
                                        onPress={() => {
                                          setTourVenueOptionsId(null);
                                          zoomToVenue(venue);
                                        }}
                                      >
                                        <Text style={styles.tourVenueDropdownItemText}>Zoom to venue</Text>
                                      </TouchableOpacity>
                                      <TouchableOpacity
                                        style={[styles.tourVenueDropdownItem, (dayIndex === 0 && index === 0) && styles.tourVenueDropdownItemDisabled]}
                                        disabled={dayIndex === 0 && index === 0}
                                        onPress={() => {
                                          setTourVenueOptionsId(null);
                                          if (routesCalculating) return;
                                          if (index > 0) {
                                            moveVenueInTour(day.id, index, day.id, index - 1);
                                          } else if (dayIndex > 0) {
                                            const prevDay = tourDays[dayIndex - 1];
                                            moveVenueInTour(day.id, index, prevDay.id, prevDay.venues.length);
                                          }
                                        }}
                                      >
                                        <Text style={[styles.tourVenueDropdownItemText, (dayIndex === 0 && index === 0) && styles.tourVenueDropdownItemTextDisabled]}>Move up</Text>
                                      </TouchableOpacity>
                                      <TouchableOpacity
                                        style={[styles.tourVenueDropdownItem, (dayIndex === tourDays.length - 1 && index === day.venues.length - 1) && styles.tourVenueDropdownItemDisabled]}
                                        disabled={dayIndex === tourDays.length - 1 && index === day.venues.length - 1}
                                        onPress={() => {
                                          setTourVenueOptionsId(null);
                                          if (routesCalculating) return;
                                          if (index < day.venues.length - 1) {
                                            moveVenueInTour(day.id, index, day.id, index + 1);
                                          } else if (dayIndex < tourDays.length - 1) {
                                            const nextDay = tourDays[dayIndex + 1];
                                            moveVenueInTour(day.id, index, nextDay.id, 0);
                                          }
                                        }}
                                      >
                                        <Text style={[styles.tourVenueDropdownItemText, (dayIndex === tourDays.length - 1 && index === day.venues.length - 1) && styles.tourVenueDropdownItemTextDisabled]}>Move down</Text>
                                      </TouchableOpacity>
                                    </View>
                                  )}
                                </View>
                              </>
                            );
                            const legRowContent = index < day.venues.length - 1 ? (
                              <View style={styles.legModeRow}>
                                <Text style={[styles.legModeLabel, isDbVenue && { color: '#ffffff' }]}>Travel:</Text>
                                {[
                                  { key: 'drive',  icon: 'car-outline',      label: 'Drive'  },
                                  { key: 'bus',    icon: 'bus-outline',       label: 'Bus'    },
                                  { key: 'walk',   icon: 'walk-outline',      label: 'Walk'   },
                                  { key: 'train',  icon: 'train-outline',     label: 'Train'  },
                                  { key: 'flight', icon: 'airplane-outline',  label: 'Flight' },
                                ].map(({ key, icon, label }) => {
                                  const current = day.legModes?.[index] ?? 'drive';
                                  const selected = current === key;
                                  return (
                                    <TouchableOpacity
                                      key={key}
                                      style={[styles.legModeButton, selected && styles.legModeButtonSelected]}
                                      onPress={() => setLegModeForDay(day.id, index, key)}
                                    >
                                      <Ionicons name={icon} size={12} color={selected ? '#fff' : (isDbVenue ? '#fff' : (isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)'))} />
                                      <Text style={[styles.legModeButtonText, isDbVenue && { color: '#ffffff' }, selected && styles.legModeButtonTextSelected]}>{label}</Text>
                                    </TouchableOpacity>
                                  );
                                })}
                              </View>
                            ) : null;
                            return (
                              <React.Fragment key={`${day.id}-${index}`}>
                                {showInsertAt(index) && renderInsertGhost(index)}
                                <SortableTourVenueRow
                                  dayId={day.id}
                                  index={index}
                                  style={tourCardStyle}
                                  slotKey={`${day.id}-${index}`}
                                  tourVenueOptionsId={tourVenueOptionsId}
                                  legRowContent={legRowContent}
                                >
                                  {tourCardContent}
                                </SortableTourVenueRow>
                              </React.Fragment>
                            );
                          });
                            return (
                              <>
                                {mappedRows}
                                {showInsertAt(day.venues.length) && renderInsertGhost(day.venues.length)}
                              </>
                            );
                          })()}
                        </SortableContext>
                      ) : (
                        day.venues.map((venue, index) => {
                          const venueId = String(venue?.id || '');
                          const isDbVenue = isVenuesDbTourStop(venue);
                          const tourStopTypeLabel = getTourStopTypeLabel(venue);
                          const isLodgingNight = isDayLodgingVenue(day, venueId);
                          const stopKey = stopKeyForVenue(venueId);
                          const participantIds = getStopParticipants(day, stopKey);
                          const plannerUid = auth.currentUser?.uid;
                          const isJoinedStop = plannerUid ? participantIds.includes(plannerUid) : false;
                          const accentColor = isLodgingNight ? LODGING_ACCENT_COLOR : getStopAccentColor(venue);
                          const tourCardStyle = [
                            styles.tourVenueCard,
                            isDbVenue && styles.tourVenueCardVenuesDb,
                            { borderLeftColor: accentColor },
                            isLodgingNight && lodgingCardStyle(isDarkMode),
                            selectedVenueId === venue.id && (isDbVenue ? styles.tourVenueCardVenuesDbSelected : styles.selectedVenueCard),
                            tourVenueOptionsId === `${day.id}-${index}` && { zIndex: 200 },
                          ];
                          const tourCardContent = (
                            <>
                              <View style={styles.tourVenueDropdownWrap}>
                                <View style={styles.tourVenueHeaderRow}>
                                  <View style={[styles.tourStopNumberBadge, { backgroundColor: `${accentColor}22`, borderColor: `${accentColor}66` }]}>
                                    <Text style={[styles.tourStopNumberBadgeText, { color: accentColor }]}>{index + 1}</Text>
                                  </View>
                                  <TouchableOpacity
                                    style={styles.tourVenueHeader}
                                    onPress={() => handleVenueSelect(venue)}
                                  >
                                    <View style={styles.tourVenueInfo}>
                                      <Text style={styles.tourStopTypeBadgeText}>{tourStopTypeLabel}</Text>
                                      {isLodgingNight ? (
                                        <Text style={styles.tourStopLodgingPill}>Tonight's lodging</Text>
                                      ) : null}
                                      <Text style={[styles.tourVenueName, isDbVenue && styles.tourVenueNameVenuesDb]}>{venue.name}</Text>
                                      <Text numberOfLines={1} style={[styles.tourVenueLocation, isDbVenue && styles.tourVenueLocationVenuesDb]}>
                                        {formatTourDayVenueLocationLine(venue)}
                                      </Text>
                                      {(() => {
                                        const lg = getLetterGrade(venue?.approved?.overallGrade);
                                        const cap = venue.capacity;
                                        const vType = venue.affiliate || venue.venueType;
                                        if (!lg && !cap && !vType) return null;
                                        return (
                                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 4, flexWrap: 'wrap' }}>
                                            {lg && (
                                              <View style={{ backgroundColor: `${gradeColor(lg)}22`, borderWidth: 1, borderColor: `${gradeColor(lg)}66`, borderRadius: 4, paddingHorizontal: 5, paddingVertical: 1 }}>
                                                <Text style={{ fontSize: 10, fontWeight: '800', color: gradeColor(lg) }}>{lg}</Text>
                                              </View>
                                            )}
                                            {cap && <Text style={{ fontSize: 10, color: isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.38)' }}>Cap {cap}</Text>}
                                            {vType && <Text style={{ fontSize: 10, color: isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.38)' }} numberOfLines={1}>{vType}</Text>}
                                          </View>
                                        );
                                      })()}
                                    </View>
                                  </TouchableOpacity>
                                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 0 }}>
                                    <TouchableOpacity
                                      style={styles.tourVenueZoomButton}
                                      onPress={() => zoomToVenue(venue)}
                                    >
                                      <Text style={styles.tourVenueZoomButtonIcon}>⊙</Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                      style={styles.tourVenueOptionsButton}
                                      onPress={() => setTourVenueOptionsId(prev => prev === `${day.id}-${index}` ? null : `${day.id}-${index}`)}
                                    >
                                      <Text style={[styles.tourVenueOptionsButtonText, isDbVenue && styles.tourVenueOptionsButtonTextVenuesDb]}>⋮</Text>
                                    </TouchableOpacity>
                                  </View>
                                </View>
                                <View style={styles.tourStopSocialWrap}>
                                  <TourStopSocialFooter
                                    compact
                                    theme={theme}
                                    isDarkMode={isDarkMode}
                                    isLodging={isLodgingNight}
                                    isJoined={isJoinedStop}
                                    canEditLodging={Boolean(plannerUid)}
                                    onToggleLodging={
                                      plannerUid
                                        ? () => toggleDayLodgingVenue(dayIndex, venueId)
                                        : null
                                    }
                                    onToggleJoin={
                                      plannerUid
                                        ? () => toggleDayStopParticipation(dayIndex, stopKey)
                                        : null
                                    }
                                    participantIds={participantIds}
                                    members={tourPartyMembers}
                                  />
                                </View>
                                {tourVenueOptionsId === `${day.id}-${index}` && (
                                  <View style={styles.tourVenueDropdown}>
                                    {day.venues.length >= 2 && index < day.venues.length - 1 && (
                                      <TouchableOpacity
                                        style={styles.tourVenueDropdownItem}
                                        onPress={() => {
                                          setTourVenueOptionsId(null);
                                          splitDayAfter(day.id, index);
                                        }}
                                      >
                                        <Text style={styles.tourVenueDropdownItemText}>Split day after this stop</Text>
                                      </TouchableOpacity>
                                    )}
                                    <TouchableOpacity
                                      style={[styles.tourVenueDropdownItem, styles.tourVenueDropdownItemDanger]}
                                      onPress={() => {
                                        setTourVenueOptionsId(null);
                                        const newTourDays = tourDays.map(d => {
                                          if (d.id !== day.id) return d;
                                          const newVenues = [...d.venues.slice(0, index), ...d.venues.slice(index + 1)];
                                          const legModes = d.legModes || [];
                                          const newLegModes = [...legModes.slice(0, index), ...legModes.slice(index + 1)];
                                          return { ...d, venues: newVenues, legModes: newLegModes.length ? newLegModes : undefined };
                                        });
                                        const newTourVenues = newTourDays.flatMap(d => d.venues);
                                        setTourVenues(newTourVenues);
                                        setTourDays(newTourDays);
                                        directUpdateMapWithRoutes(newTourVenues, newTourDays, activeDayId);
                                      }}
                                    >
                                      <Text style={[styles.tourVenueDropdownItemText, styles.tourVenueDropdownItemTextDanger]}>Remove from Tour</Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                      style={styles.tourVenueDropdownItem}
                                      onPress={() => {
                                        setTourVenueOptionsId(null);
                                        setMapDetailsItem({ type: 'venue', data: venue });
                                      }}
                                    >
                                      <Text style={styles.tourVenueDropdownItemText}>View Details</Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                      style={styles.tourVenueDropdownItem}
                                      onPress={() => {
                                        setTourVenueOptionsId(null);
                                        zoomToVenue(venue);
                                      }}
                                    >
                                      <Text style={styles.tourVenueDropdownItemText}>Zoom to venue</Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                      style={[styles.tourVenueDropdownItem, (dayIndex === 0 && index === 0) && styles.tourVenueDropdownItemDisabled]}
                                      disabled={dayIndex === 0 && index === 0}
                                      onPress={() => {
                                        setTourVenueOptionsId(null);
                                        if (routesCalculating) return;
                                        if (index > 0) {
                                          moveVenueInTour(day.id, index, day.id, index - 1);
                                        } else if (dayIndex > 0) {
                                          const prevDay = tourDays[dayIndex - 1];
                                          moveVenueInTour(day.id, index, prevDay.id, prevDay.venues.length);
                                        }
                                      }}
                                    >
                                      <Text style={[styles.tourVenueDropdownItemText, (dayIndex === 0 && index === 0) && styles.tourVenueDropdownItemTextDisabled]}>Move up</Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                      style={[styles.tourVenueDropdownItem, (dayIndex === tourDays.length - 1 && index === day.venues.length - 1) && styles.tourVenueDropdownItemDisabled]}
                                      disabled={dayIndex === tourDays.length - 1 && index === day.venues.length - 1}
                                      onPress={() => {
                                        setTourVenueOptionsId(null);
                                        if (routesCalculating) return;
                                        if (index < day.venues.length - 1) {
                                          moveVenueInTour(day.id, index, day.id, index + 1);
                                        } else if (dayIndex < tourDays.length - 1) {
                                          const nextDay = tourDays[dayIndex + 1];
                                          moveVenueInTour(day.id, index, nextDay.id, 0);
                                        }
                                      }}
                                    >
                                      <Text style={[styles.tourVenueDropdownItemText, (dayIndex === tourDays.length - 1 && index === day.venues.length - 1) && styles.tourVenueDropdownItemTextDisabled]}>Move down</Text>
                                    </TouchableOpacity>
                                  </View>
                                )}
                              </View>
                            </>
                          );
                          return (
                            <React.Fragment key={`${day.id}-${index}`}>
                              <View style={tourCardStyle}>
                                {tourCardContent}
                              </View>
                              {index < day.venues.length - 1 && (
                                <View style={styles.legModeRow}>
                                  <Text style={[styles.legModeLabel, isDbVenue && { color: '#ffffff' }]}>Travel:</Text>
                                  {[
                                    { key: 'drive', label: '\uD83D\uDE97 Drive' },
                                    { key: 'train', label: '\uD83D\uDE82 Train' },
                                    { key: 'flight', label: '\u2708 Flight' }
                                  ].map(({ key, label }) => {
                                    const current = day.legModes?.[index] ?? 'drive';
                                    const selected = current === key;
                                    return (
                                      <TouchableOpacity
                                        key={key}
                                        style={[styles.legModeButton, selected && styles.legModeButtonSelected]}
                                        onPress={() => setLegModeForDay(day.id, index, key)}
                                      >
                                        <Text style={[styles.legModeButtonText, isDbVenue && { color: '#ffffff' }, selected && styles.legModeButtonTextSelected]}>{label}</Text>
                                      </TouchableOpacity>
                                    );
                                  })}
                                </View>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                      {Platform.OS === 'web' && (
                        <TourDroppable
                          id={`drop-${day.id}-${day.venues.length}`}
                          dayId={day.id}
                          insertIndex={day.venues.length}
                          style={{ minHeight: 24, width: '100%', justifyContent: 'center', alignItems: 'center', paddingVertical: 8 }}
                        >
                          <Text style={[styles.emptyDayText, { margin: 0, opacity: 0.7 }]}>Drop here to add at end</Text>
                        </TourDroppable>
                      )}

                      {renderDayRouteInfo(day, dayIndex)}

                      {/* Bus call button / card at end of this day */}
                      {(() => {
                        const bc = day.busCall;
                        const isFormOpen = busCallFormDayId === day.id;
                        if (bc?.lat) {
                          return (
                            <View style={styles.busCallCard}>
                              <View style={styles.busCallCardRow}>
                                <View style={styles.busCallDot}>
                                  <Text style={styles.busCallDotText}>🚌</Text>
                                </View>
                                <View style={styles.busCallInfo}>
                                  <Text style={styles.busCallEyebrow}>Bus Call</Text>
                                  <Text style={styles.busCallName} numberOfLines={2}>{bc.name || 'Bus Call Location'}</Text>
                                  {bc.time ? (
                                    <Text style={styles.busCallTime}>🕐 {bc.time}</Text>
                                  ) : null}
                                </View>
                                <View style={styles.busCallActions}>
                                  <TouchableOpacity style={styles.busCallActionBtn} onPress={() => setBusCallFormDayId(isFormOpen ? null : day.id)}>
                                    <Text style={styles.busCallActionBtnText}>{isFormOpen ? 'Done' : 'Edit'}</Text>
                                  </TouchableOpacity>
                                  <TouchableOpacity style={[styles.busCallActionBtn, { borderColor: 'rgba(244,63,94,0.3)' }]} onPress={() => { setTourDays(prev => prev.map(d => d.id === day.id ? { ...d, busCall: null } : d)); setBusCallFormDayId(null); }}>
                                    <Text style={[styles.busCallActionBtnText, { color: '#f43f5e' }]}>×</Text>
                                  </TouchableOpacity>
                                </View>
                              </View>
                              {isFormOpen && (
                                <View style={styles.busCallForm}>
                                  <View style={styles.busCallFormRow}>
                                    <Text style={styles.busCallFormLabel}>Time</Text>
                                    {Platform.OS === 'web' ? (
                                      <input
                                        type="time"
                                        value={bc._timeRaw || ''}
                                        onChange={e => {
                                          const raw = e.target.value; // "HH:MM" 24h
                                          // Convert to 12h display label
                                          let display = raw;
                                          if (raw) {
                                            const [hh, mm] = raw.split(':').map(Number);
                                            const ampm = hh >= 12 ? 'PM' : 'AM';
                                            const h12 = hh % 12 || 12;
                                            display = `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
                                          }
                                          setTourDays(prev => prev.map(d => d.id === day.id ? { ...d, busCall: { ...d.busCall, time: display, _timeRaw: raw } } : d));
                                        }}
                                        style={{
                                          flex: 1,
                                          border: '1px solid rgba(244,63,94,0.35)',
                                          borderRadius: 6,
                                          padding: '6px 9px',
                                          fontSize: 13,
                                          color: isDarkMode ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.8)',
                                          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.9)',
                                          outline: 'none',
                                          fontFamily: 'inherit',
                                          colorScheme: isDarkMode ? 'dark' : 'light',
                                        }}
                                      />
                                    ) : (
                                      <TextInput
                                        style={styles.busCallFormInput}
                                        value={bc.time || ''}
                                        onChangeText={v => setTourDays(prev => prev.map(d => d.id === day.id ? { ...d, busCall: { ...d.busCall, time: v } } : d))}
                                        placeholder="e.g. 9:00 AM"
                                        placeholderTextColor="rgba(127,127,127,0.5)"
                                      />
                                    )}
                                  </View>
                                  <TouchableOpacity style={styles.busCallPickMapBtn} onPress={() => { setBusCallFormDayId(null); setPickingBusCallForDayId(day.id); }}>
                                    <Text style={styles.busCallPickMapBtnText}>📍 Change Location</Text>
                                  </TouchableOpacity>
                                </View>
                              )}
                            </View>
                          );
                        }
                        return (
                          <TouchableOpacity style={styles.addBusCallButton} onPress={() => { setBusCallFormDayId(null); setPickingBusCallForDayId(day.id); }}>
                            <Text style={[styles.addBusCallButtonText, { fontSize: 13 }]}>🚌</Text>
                            <Text style={styles.addBusCallButtonText}>Add Bus Call</Text>
                          </TouchableOpacity>
                        );
                      })()}
                    </>
                  )}
                </>
              );
              const nextDay = tourDays[dayIndex + 1];
              const prevDay = tourDays[dayIndex - 1];
              const showInterDayRow = dayIndex < tourDays.length - 1;
              const prevDayBusCall = prevDay?.busCall;
              return (
                <React.Fragment key={day.id}>
                  {/* Bus call from previous day shown as first item of this day */}
                  {prevDayBusCall?.lat && (
                    <View style={[styles.busCallCard, { marginTop: 4 }]}>
                      <View style={styles.busCallCardRow}>
                        <View style={styles.busCallDot}>
                          <Text style={styles.busCallDotText}>🚌</Text>
                        </View>
                        <View style={styles.busCallInfo}>
                          <Text style={styles.busCallEyebrow}>Bus Call · Start of {day.name}</Text>
                          <Text style={styles.busCallName} numberOfLines={2}>{prevDayBusCall.name || 'Bus Call Location'}</Text>
                          {prevDayBusCall.time ? (
                            <Text style={styles.busCallTime}>🕐 {prevDayBusCall.time}</Text>
                          ) : null}
                        </View>
                        <TouchableOpacity style={styles.busCallActionBtn} onPress={() => { if (map.current) map.current.flyTo({ center: [prevDayBusCall.lng, prevDayBusCall.lat], zoom: 14, essential: true }); }}>
                          <Text style={styles.busCallActionBtnText}>⊙</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  )}
                  {Platform.OS === 'web' ? (
                    <WebDropTargetDay
                      dayId={day.id}
                      style={dayContainerStyle}
                      setDragOverDayId={setDragOverDayId}
                      setDragInsertAt={setDragInsertAt}
                      onDropWithPayload={onDropWithPayload}
                      routesCalculating={routesCalculating}
                      dragInsertAt={dragInsertAt}
                    >
                      {dayContent}
                    </WebDropTargetDay>
                  ) : (
                    <View style={dayContainerStyle}>
                      {dayContent}
                    </View>
                  )}
                  {showInterDayRow && (() => {
                    const hasVenues = day.venues.length > 0 && nextDay?.venues?.length > 0;
                    const lastVenue = day.venues[day.venues.length - 1];
                    const firstVenue = nextDay?.venues?.[0];
                    const interMode = interDayLegModes[dayIndex] ?? 'drive';
                    const travel = hasVenues ? estimateInterDayTravel(lastVenue, firstVenue, interMode) : null;
                    return (
                      <View style={styles.interDayConnector}>
                        <View style={styles.interDayConnectorHeader}>
                          <Text style={styles.interDayConnectorLabel}>↓ {nextDay?.name}</Text>
                          {travel && !travel.same && (
                            <View style={{ alignItems: 'flex-end', gap: 2 }}>
                              <Text style={styles.interDayConnectorStat}>{travel.modeLabel} · {travel.miles} mi · {travel.timeLabel}</Text>
                              {travel.fuelLabel && (
                                <Text style={[styles.interDayConnectorStat, { color: isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.38)', fontSize: 10 }]}>⛽ {travel.fuelLabel}</Text>
                              )}
                            </View>
                          )}
                          {travel?.same && (
                            <Text style={styles.interDayConnectorStat}>Same location</Text>
                          )}
                        </View>
                        {hasVenues && (
                          <View style={styles.interDayLegRow}>
                            {[
                              { key: 'drive',  icon: 'car-outline',     label: 'Drive'  },
                              { key: 'bus',    icon: 'bus-outline',      label: 'Bus'    },
                              { key: 'walk',   icon: 'walk-outline',     label: 'Walk'   },
                              { key: 'train',  icon: 'train-outline',    label: 'Train'  },
                              { key: 'flight', icon: 'airplane-outline', label: 'Flight' },
                            ].map(({ key, icon, label }) => {
                              const selected = interMode === key;
                              return (
                                <TouchableOpacity
                                  key={key}
                                  style={[styles.legModeButton, selected && styles.legModeButtonSelected]}
                                  onPress={() => {
                                    setInterDayLegMode(dayIndex, key);
                                    const nextModes = [...(interDayLegModes || [])];
                                    while (nextModes.length <= dayIndex) nextModes.push('drive');
                                    nextModes[dayIndex] = key;
                                    if (map.current && mapLoaded) directUpdateMapWithRoutes(tourVenues, tourDays, activeDayId, nextModes);
                                  }}
                                  disabled={routesCalculating}
                                >
                                  <Ionicons name={icon} size={12} color={selected ? '#fff' : (isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)')} />
                                  <Text style={[styles.legModeButtonText, selected && styles.legModeButtonTextSelected]}>{label}</Text>
                                </TouchableOpacity>
                              );
                            })}
                          </View>
                        )}
                      </View>
                    );
                  })()}
                </React.Fragment>
              );
            })}

            <TouchableOpacity
              style={styles.addDayButton}
              onPress={() => {
                const newDayName = `Day ${tourDays.length + 1}`;
                const newDayId = `day-${tourDays.length + 1}`;
                setTourDays(prev => [
                  ...prev,
                  {
                    id: newDayId,
                    name: newDayName,
                    venues: []
                  }
                ]);
                setActiveDayId(newDayId);
                setEditingDayId(newDayId);
                setNewDayName(newDayName);
              }}
            >
              <Text style={styles.addDayButtonText}>+ Add Day</Text>
            </TouchableOpacity>

            {isMobile && tourVenues.length >= 2 && (
              <View style={[styles.routeInfo, { marginBottom: 8 }]}>
                <Text style={styles.routeInfoTitle}>Route Information</Text>
                <React.Suspense fallback={
                  <Text style={styles.routeInfoText}>Calculating route information...</Text>
                }>
                  <RouteInfoDisplay tourDays={tourDays} interDayLegModes={interDayLegModes} textStyle={styles.routeInfoText} />
                </React.Suspense>
              </View>
            )}
          </ScrollView>

          {/* Route information — desktop only (mobile lives inside scroll) */}
          {!isMobile && tourVenues.length >= 2 && (
            <View style={styles.routeInfo}>
              <Text style={styles.routeInfoTitle}>Route Information</Text>
              <React.Suspense fallback={
                <Text style={styles.routeInfoText}>Calculating route information...</Text>
              }>
                <RouteInfoDisplay tourDays={tourDays} interDayLegModes={interDayLegModes} textStyle={styles.routeInfoText} />
              </React.Suspense>
            </View>
          )}

        </Animated.View>
        )}
        </div>
        {renderMapBelowBar()}
        </View>


        </TourPlannerDndWrapper>
      </View>

      {calendarEmbed && renderCalendarRouteSummary()}

      {/* Map View Details modal (venue or user location) */}
      <FullscreenSafeModal
        visible={!!mapDetailsItem}
        transparent
        animationType="fade"
        onRequestClose={() => setMapDetailsItem(null)}
        hostRef={mapStageRef}
      >
        <TouchableOpacity
          style={styles.filterModalOverlay}
          activeOpacity={1}
          onPress={() => setMapDetailsItem(null)}
        >
          <TouchableOpacity style={[styles.filterModalContent, { maxWidth: 480 }]} activeOpacity={1} onPress={() => { }}>
            <View style={styles.filterModalHeader}>
              <Text style={styles.filterModalTitle}>
                {mapDetailsItem?.type === 'userLocation' ? 'Location details' : 'Venue details'}
              </Text>
              <TouchableOpacity onPress={() => setMapDetailsItem(null)} style={styles.filterModalCloseBtn}>
                <Text style={styles.filterModalCloseText}>Close</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={[styles.filterModalScroll, { maxHeight: 500 }]} showsVerticalScrollIndicator>
              {mapDetailsItem?.type === 'userLocation' && mapDetailsItem?.data && (() => {
                const loc = mapDetailsItem.data;
                return (
                  <View style={{ padding: 4 }}>
                    <Text style={{ fontSize: 16, fontWeight: '600', color: theme?.text?.primary || '#1a1a1a', marginBottom: 8 }}>{loc.name}</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginBottom: 8, gap: 12 }}>
                      <Text style={{ fontSize: 13, color: theme?.text?.secondary || '#666' }}>Capacity: <Text style={{ fontWeight: '600', color: theme?.text?.primary || '#1a1a1a' }}>{loc.capacity != null && loc.capacity !== '' ? String(loc.capacity) : 'N/A'}</Text></Text>
                      <Text style={{ fontSize: 13, color: theme?.text?.secondary || '#666' }}>Type: <Text style={{ fontWeight: '600', color: theme?.text?.primary || '#1a1a1a' }}>{loc.affiliate || 'N/A'}</Text></Text>
                    </View>
                    {loc.notes ? (
                      <View style={{ marginTop: 8, paddingTop: 12, borderTopWidth: 1, borderTopColor: theme?.ui?.border || '#eee' }}>
                        <Text style={{ fontSize: 12, fontWeight: '600', color: theme?.text?.secondary || '#666', marginBottom: 6 }}>Description</Text>
                        <Text style={{ fontSize: 14, color: theme?.text?.primary || '#1a1a1a', lineHeight: 20 }}>{loc.notes}</Text>
                      </View>
                    ) : null}
                  </View>
                );
              })()}
              {mapDetailsItem?.type === 'venue' && mapDetailsItem?.data && (() => {
                const venue = mapDetailsItem.data;
                const techSpecs = [
                  { label: 'Capacity', value: venue.capacity != null && venue.capacity !== '' ? String(venue.capacity) : venue.capacity },
                  { label: 'Type', value: venue.affiliate || venue.venueType },
                  { label: 'ADA Accessibility for Artist', value: venue.adaAccessibilityArtist },
                  { label: 'Parking', value: venue.parking },
                  { label: 'Shore Power', value: venue.shorePower },
                  { label: 'Backstage', value: venue.backstage },
                  { label: 'Merch', value: venue.merch },
                  { label: 'Stage', value: venue.stage },
                  { label: 'Backline', value: venue.backline },
                  { label: 'Video', value: venue.video },
                  { label: 'Monitors', value: venue.monitors },
                  { label: 'PA System', value: venue.pa },
                  { label: 'FOH', value: venue.foh },
                  { label: 'Lighting Control', value: venue.lightingControl },
                  { label: 'Lighting Fixtures', value: venue.lightingFixtures },
                  { label: 'Notes', value: venue.notes }
                ];
                const urlRegex = /(https?:\/\/[^\s]+)/g;
                return (
                  <View style={{ padding: 4 }}>
                    <Text style={{ fontSize: 17, fontWeight: '700', color: isDarkMode ? 'rgba(255,255,255,0.92)' : '#111', marginBottom: 12, letterSpacing: 0.2 }}>{venue.name}</Text>
                    {techSpecs.map((spec, index, array) => (
                      <View key={spec.label} style={{ width: '100%', alignItems: 'center', flexShrink: 0 }}>
                        <View style={{ width: '100%', paddingVertical: 10, paddingHorizontal: 8, alignItems: 'flex-start' }}>
                          <Text style={{ fontSize: 11, fontWeight: '700', color: isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.4)', marginBottom: 4, width: '100%', textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.8 }}>{spec.label}</Text>
                          {spec.value ? (
                            (() => {
                              const str = String(spec.value);
                              const lines = str.split(/\r?\n/);
                              return lines.map((line, lineIndex) => {
                                const parts = line.split(urlRegex);
                                return (
                                  <Text key={lineIndex} style={{ fontSize: 14, color: isDarkMode ? 'rgba(255,255,255,0.88)' : '#1a1a1a', width: '100%', textAlign: line.includes('•') || line.includes('▪') ? 'left' : (lineIndex === 0 ? 'center' : 'left'), minHeight: '1em' }}>
                                    {parts.map((part, partIndex) => {
                                      if (part.match(urlRegex)) {
                                        return (
                                          <Text
                                            key={partIndex}
                                            style={{ color: '#4ecdc4', textDecorationLine: 'underline', ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}) }}
                                            onPress={() => { if (Platform.OS === 'web') window.open(part, '_blank'); else Linking.openURL(part); }}
                                          >{part}</Text>
                                        );
                                      }
                                      return part || ' ';
                                    })}
                                  </Text>
                                );
                              });
                            })()
                          ) : (
                            <Text style={{ fontSize: 14, color: isDarkMode ? 'rgba(255,255,255,0.32)' : 'rgba(0,0,0,0.3)', width: '100%', textAlign: 'center' }}>Unknown</Text>
                          )}
                        </View>
                        {index < array.length - 1 && (
                          <View style={{ width: '60%', height: 1, backgroundColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)', marginVertical: 4 }} />
                        )}
                      </View>
                    ))}
                    {/* REQ-104: For fans / For artists from VRC data only (no Google on the Mapbox map). */}
                    <VenueAccessibilitySection
                      resolved={resolveVenueAccessibility(venue, { approvedNotes: mapDetailsAccessNotes })}
                      showGoogle={false}
                      isDarkMode={isDarkMode}
                      onAddNote={() => {
                        setMapDetailsItem(null);
                        openAccessibilityFlagModal(venue);
                      }}
                    />
                  </View>
                );
              })()}
            </ScrollView>
            {mapDetailsItem?.type === 'venue' && mapDetailsItem?.data && (
              <View style={{ borderTopWidth: 1, borderTopColor: isDarkMode ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.1)', padding: 14, gap: 10 }}>
                {(isAdmin || isDevelopment) && (
                  <TouchableOpacity
                    style={{
                      paddingVertical: 11,
                      backgroundColor: mapDetailsItem.data.isEcoConscious
                        ? 'rgba(26,188,156,0.18)'
                        : isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                      borderRadius: 10,
                      alignItems: 'center',
                      borderWidth: 1,
                      borderColor: mapDetailsItem.data.isEcoConscious
                        ? 'rgba(26,188,156,0.6)'
                        : isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                    }}
                    onPress={async () => {
                      try {
                        const db = getFirestore();
                        const venueRef = doc(db, 'venues', mapDetailsItem.data.id);
                        const newVal = !mapDetailsItem.data.isEcoConscious;
                        await updateDoc(venueRef, { isEcoConscious: newVal });
                        setVenues(prev => prev.map(v =>
                          v.id === mapDetailsItem.data.id ? { ...v, isEcoConscious: newVal } : v
                        ));
                        setMapDetailsItem(prev => ({
                          ...prev,
                          data: { ...prev.data, isEcoConscious: newVal }
                        }));
                      } catch (err) {
                        console.warn('Failed to update isEcoConscious:', err.message);
                      }
                    }}
                  >
                    <Text style={{ fontSize: 13, fontWeight: '600', color: mapDetailsItem.data.isEcoConscious ? '#1abc9c' : isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)', letterSpacing: 0.2 }}>
                      {mapDetailsItem.data.isEcoConscious ? '🌍 Remove Eco-Conscious Flag' : '🌍 Mark as Eco-Conscious Venue'}
                    </Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity
                  style={{ paddingVertical: 13, backgroundColor: '#e67e22', borderRadius: 10, alignItems: 'center', boxShadow: '0 2px 12px rgba(230,126,34,0.4)' }}
                  onPress={() => {
                    const venue = mapDetailsItem.data;
                    setMapDetailsItem(null);
                    if (Platform.OS === 'web' && typeof window !== 'undefined') {
                      const url = `${window.location.origin}/venue/${venue.id}`;
                      window.open(url, '_blank');
                    } else {
                      navigation.navigate('Venue', { venueId: mapDetailsItem.data.id });
                    }
                  }}
                >
                  <Text style={{ fontSize: 14, fontWeight: '700', color: '#fff', letterSpacing: 0.3 }}>View More Details</Text>
                </TouchableOpacity>
              </View>
            )}
          </TouchableOpacity>
        </TouchableOpacity>
      </FullscreenSafeModal>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onHide={() => setToast(null)}
          hostRef={mapStageRef}
        />
      )}

      {renderAccessibilityFlagModal()}

      {/* Hidden for now — mobile weather / radar
      {renderMobileWeatherPanel()}
      */}
      {renderMobileMapBottomChrome()}

      <MapAccessPrompt
        prompt={mapAccessPrompt}
        onClose={() => setMapAccessPrompt(null)}
        navigation={navigation}
        isDarkMode={isDarkMode}
        onBeforeNavigate={exitMapFullscreen}
      />
    </View>
    </MapContributorGateContext.Provider>
  );
}

// Add the RouteInfoDisplay component
// interDayLegModes: optional; when omitted (simplified/copy branch) only within-day legs are calculated (merge-friendly)
const RouteInfoDisplay = React.memo(({ tourDays, interDayLegModes, textStyle }) => {
  const [routeInfo, setRouteInfo] = useState({
    totalDistance: 0,
    totalDuration: 0,
    totalFuelUsed: 0,
    totalFuelCost: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchRouteInfo = async () => {
      setIsLoading(true);
      try {
        let totalDistance = 0;
        let totalDuration = 0;
        let totalFuelUsed = 0;
        let totalFuelCost = 0;
        const avgFuelEfficiency = 25;
        const avgGasPrice = 3.50;

        // Within-day legs (respect Drive/Train/Flight per leg)
        for (const day of tourDays) {
          const legModes = day.legModes || [];
          if (day.venues.length >= 2) {
            for (let i = 0; i < day.venues.length - 1; i++) {
              const start = day.venues[i];
              const end = day.venues[i + 1];
              const mode = legModes[i] || 'drive';
              if (start.latitude && start.longitude && end.latitude && end.longitude) {
                const startCoords = [parseFloat(start.longitude), parseFloat(start.latitude)];
                const endCoords = [parseFloat(end.longitude), parseFloat(end.latitude)];
                let route;
                if (mode === 'train') {
                  route = getTrainLegGeometry(startCoords, endCoords);
                } else if (mode === 'flight') {
                  route = getFlightLegGeometry(startCoords, endCoords);
                } else {
                  route = await getDirections(startCoords, endCoords, { forceDrive: true });
                }
                if (route && isMounted) {
                  totalDistance += route.distance || 0;
                  totalDuration += route.duration || 0;
                  if (!route.requiresFlight && !route.isTrain) {
                    const distanceInMiles = (route.distance || 0) * 0.621371;
                    totalFuelUsed += distanceInMiles / avgFuelEfficiency;
                    totalFuelCost += (distanceInMiles / avgFuelEfficiency) * avgGasPrice;
                  }
                }
              }
            }
          }
        }

        // Inter-day legs (only when interDayLegModes is provided — merge-friendly: simplified version omits this prop)
        if (interDayLegModes != null && Array.isArray(interDayLegModes) && tourDays.length > 1) {
        const interModes = interDayLegModes;
        for (let gapIndex = 0; gapIndex < tourDays.length - 1; gapIndex++) {
          const dayFrom = tourDays[gapIndex];
          const dayTo = tourDays[gapIndex + 1];
          if (!dayFrom?.venues?.length || !dayTo?.venues?.length) continue;
          const lastVenue = dayFrom.venues[dayFrom.venues.length - 1];
          const firstVenue = dayTo.venues[0];
          if (!lastVenue?.longitude || !lastVenue?.latitude || !firstVenue?.longitude || !firstVenue?.latitude) continue;
          const startCoords = [parseFloat(lastVenue.longitude), parseFloat(lastVenue.latitude)];
          const endCoords = [parseFloat(firstVenue.longitude), parseFloat(firstVenue.latitude)];
          const mode = interModes[gapIndex] || 'drive';

          if (mode === 'train') {
            const leg = getTrainLegGeometry(startCoords, endCoords);
            totalDistance += leg.distance || 0;
            totalDuration += leg.duration || 0;
          } else if (mode === 'flight') {
            const leg = getFlightLegGeometry(startCoords, endCoords);
            totalDistance += leg.distance || 0;
            totalDuration += leg.duration || 0;
          } else {
            const route = await getDirections(startCoords, endCoords, { forceDrive: true });
            if (route && isMounted) {
              totalDistance += route.distance || 0;
              totalDuration += route.duration || 0;
              if (!route.requiresFlight) {
                const distanceInMiles = (route.distance || 0) * 0.621371;
                totalFuelUsed += distanceInMiles / avgFuelEfficiency;
                totalFuelCost += (distanceInMiles / avgFuelEfficiency) * avgGasPrice;
              }
            }
          }
        }
        }

        if (isMounted) {
          setRouteInfo({
            totalDistance: Math.round(totalDistance),
            totalDuration: Math.round(totalDuration),
            totalFuelUsed: Math.round(totalFuelUsed * 100) / 100,
            totalFuelCost: Math.round(totalFuelCost * 100) / 100
          });
        }
      } catch (error) {
        console.error('Error calculating route info:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchRouteInfo();
    return () => {
      isMounted = false;
    };
  }, [tourDays, interDayLegModes]);

  // Default style for route info text
  const defaultTextStyle = {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 13,
    marginBottom: 6,
  };

  const resolvedStyle = textStyle || defaultTextStyle;

  if (isLoading) {
    return <Text style={resolvedStyle}>Calculating route information...</Text>;
  }

  const hasInterDay = interDayLegModes != null && tourDays.length > 1;
  return (
    <>
      <Text style={resolvedStyle}>
        Total Distance: {routeInfo.totalDistance} km ({Math.round(routeInfo.totalDistance * 0.621371)} miles)
        {hasInterDay ? ' (incl. between days)' : ''}
      </Text>
      <Text style={resolvedStyle}>
        Driving Time: {Math.floor(routeInfo.totalDuration / 60)}h {Math.round(routeInfo.totalDuration % 60)}m
        {hasInterDay ? ' (incl. between days)' : ''}
      </Text>
      <Text style={resolvedStyle}>
        Estimated Fuel: {routeInfo.totalFuelUsed} gallons (${routeInfo.totalFuelCost.toFixed(2)})
        {hasInterDay ? ' (incl. between days)' : ''}
      </Text>
      {routeInfo.totalCo2Kg > 0 && (
        <Text style={[resolvedStyle, { color: routeInfo.totalCo2Kg < 50 ? '#2ecc71' : routeInfo.totalCo2Kg < 300 ? '#f39c12' : '#e74c3c' }]}>
          🌱 Est. CO₂: {routeInfo.totalCo2Kg} kg total
          {hasInterDay ? ' (incl. between days)' : ''}
        </Text>
      )}
    </>
  );
});

export default TourPlanner; 